import React, { useState, useEffect, useCallback, useMemo, createContext, useContext, useRef } from 'react';

// ============================================================================
// SPLITCAST WEB PORTAL - 2026 Glassmorphism Redesign
// Modern Dashboard with Bento Box Layout & Collapsible Sidebar
// ============================================================================

const API_URL = import.meta.env?.VITE_API_URL || 'http://localhost:3000/api';
const CSRF_STORAGE_KEY = 'splitcast_csrf';
const SESSION_TOKEN_KEY = 'splitcast_session_token';
const CONSENT_COOKIE = 'splitcast_consent';
const CONSENT_ID_COOKIE = 'splitcast_consent_id';
const CONSENT_MAX_AGE_DAYS = 365;
const PUBLIC_MARKETING_CONSENT = 'marketing';
const GOOGLE_ADS_TAG_ID = 'AW-18063982191';
const GOOGLE_ADS_SIGN_UP_SEND_TO = 'AW-18063982191/od9mCPPJu9gcEO_8yaVD';
const GOOGLE_ADS_SUBSCRIBE_SEND_TO = 'AW-18063982191/VkwnCInUjdgcEO_8yaVD';
const GOOGLE_ADS_SUBSCRIBE_VALUE = 19;
const PENDING_SUBSCRIPTION_CONVERSION_KEY = 'splitcast_pending_subscription_conversion';
const PENDING_SUBSCRIPTION_CONVERSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const APPLE_APP_STORE_URL = 'https://apps.apple.com/us/app/splitcast/id6792397695';
const GOOGLE_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=app.splitcast.mobile';
const APP_STORE_BADGE_IMAGE_URL = 'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg';
const GOOGLE_PLAY_BADGE_IMAGE_URL = 'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png';
const PREVIEW_ROUND_QUESTION_COUNT = 5;
const PRODUCT_DEMO_VIDEO_ID = '3FDCd-dxE8o';
const PRODUCT_DEMO_EMBED_URL = `https://www.youtube.com/embed/${PRODUCT_DEMO_VIDEO_ID}?feature=oembed&autoplay=1&rel=0&playsinline=1`;
const PRODUCT_DEMO_WATCH_URL = `https://www.youtube.com/watch?v=${PRODUCT_DEMO_VIDEO_ID}`;
const PRODUCT_DEMO_THUMBNAIL_URL = `https://i.ytimg.com/vi/${PRODUCT_DEMO_VIDEO_ID}/maxresdefault.jpg`;
const PRODUCT_DEMO_FALLBACK_THUMBNAIL_URL = `https://i.ytimg.com/vi/${PRODUCT_DEMO_VIDEO_ID}/hqdefault.jpg`;
const MARKETING_ATTRIBUTION_KEYS = [
  'audience',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'gbraid',
  'wbraid',
  'gad_source',
  'gad_campaignid'
];

const buildAttributedPath = (pathname, funnelParams = {}) => {
  if (typeof window === 'undefined') return pathname;

  const currentParams = new URLSearchParams(window.location.search);
  const destinationParams = new URLSearchParams();

  MARKETING_ATTRIBUTION_KEYS.forEach((key) => {
    const value = currentParams.get(key);
    if (value) destinationParams.set(key, value);
  });

  Object.entries(funnelParams).forEach(([key, value]) => {
    if (value) destinationParams.set(key, value);
  });

  const query = destinationParams.toString();
  return `${pathname}${query ? `?${query}` : ''}`;
};

const getCookieValue = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
};

const setCookieValue = (name, value, days) => {
  if (typeof document === 'undefined') return;
  const maxAge = days ? days * 24 * 60 * 60 : 0;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; samesite=lax`;
};

const getOrCreateConsentId = () => {
  const existing = getCookieValue(CONSENT_ID_COOKIE);
  if (existing) return existing;
  const id = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  setCookieValue(CONSENT_ID_COOKIE, id, CONSENT_MAX_AGE_DAYS);
  return id;
};

const hasMarketingConsent = () => getCookieValue(CONSENT_COOKIE) === PUBLIC_MARKETING_CONSENT;

const trackPublicMarketingEvent = (eventName, params = {}) => {
  if (
    typeof window === 'undefined'
    || !hasMarketingConsent()
    || typeof window.gtag !== 'function'
  ) {
    return;
  }

  window.gtag('event', eventName, params);
};

const trackGoogleAdsSignUp = (authResult) => {
  if (
    authResult?.isNewUser !== true
    || typeof window === 'undefined'
    || !hasMarketingConsent()
    || typeof window.gtag !== 'function'
  ) {
    return false;
  }

  window.gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_SIGN_UP_SEND_TO,
    transaction_id: authResult.user?.id,
  });
  return true;
};

const getPendingSubscriptionConversion = () => {
  try {
    const rawValue = sessionStorage.getItem(PENDING_SUBSCRIPTION_CONVERSION_KEY);
    if (!rawValue) return null;

    const pendingConversion = JSON.parse(rawValue);
    const isExpired = !pendingConversion.createdAt
      || Date.now() - pendingConversion.createdAt > PENDING_SUBSCRIPTION_CONVERSION_MAX_AGE_MS;
    if (!pendingConversion.accountId || !pendingConversion.transactionId || isExpired) {
      sessionStorage.removeItem(PENDING_SUBSCRIPTION_CONVERSION_KEY);
      return null;
    }

    return pendingConversion;
  } catch (error) {
    try {
      sessionStorage.removeItem(PENDING_SUBSCRIPTION_CONVERSION_KEY);
    } catch (storageError) {
      // Ignore storage errors.
    }
    return null;
  }
};

const setPendingSubscriptionConversion = (pendingConversion) => {
  try {
    sessionStorage.setItem(
      PENDING_SUBSCRIPTION_CONVERSION_KEY,
      JSON.stringify(pendingConversion)
    );
  } catch (error) {
    // Ignore storage errors; checkout should still proceed.
  }
};

const rememberSubscriptionCheckout = (accountId, checkout) => {
  if (!accountId || !checkout?.sessionId) return;

  setPendingSubscriptionConversion({
    accountId,
    transactionId: checkout.sessionId,
    createdAt: Date.now(),
    verified: false,
  });
};

const markSubscriptionCheckoutVerified = (accountId) => {
  const pendingConversion = getPendingSubscriptionConversion();
  if (!pendingConversion || pendingConversion.accountId !== accountId) return;

  setPendingSubscriptionConversion({
    ...pendingConversion,
    verified: true,
  });
};

const flushPendingSubscriptionConversion = () => {
  const pendingConversion = getPendingSubscriptionConversion();
  if (
    !pendingConversion?.verified
    || typeof window === 'undefined'
    || !hasMarketingConsent()
    || typeof window.gtag !== 'function'
  ) {
    return false;
  }

  window.gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_SUBSCRIBE_SEND_TO,
    value: GOOGLE_ADS_SUBSCRIBE_VALUE,
    currency: 'USD',
    transaction_id: pendingConversion.transactionId,
  });

  try {
    sessionStorage.removeItem(PENDING_SUBSCRIPTION_CONVERSION_KEY);
  } catch (error) {
    // The transaction ID also lets Google Ads deduplicate a retry.
  }

  return true;
};

const getStoredSessionToken = () => {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch (error) {
    return null;
  }
};

const setStoredSessionToken = (token) => {
  try {
    if (!token) {
      sessionStorage.removeItem(SESSION_TOKEN_KEY);
      return;
    }
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
  } catch (error) {
    // Ignore storage errors (private browsing or blocked storage).
  }
};

const clearStoredSessionToken = () => setStoredSessionToken(null);

const getStoredCsrfToken = () => {
  try {
    return sessionStorage.getItem(CSRF_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const setStoredCsrfToken = (token) => {
  try {
    if (!token) {
      sessionStorage.removeItem(CSRF_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(CSRF_STORAGE_KEY, token);
  } catch (error) {
    // Ignore storage errors (private browsing or blocked storage).
  }
};

const clearStoredCsrfToken = () => setStoredCsrfToken(null);
// ============================================================================
// API CLIENT
// ============================================================================

class ApiClient {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
    this.token = getStoredSessionToken();
    this.csrfToken = getStoredCsrfToken();
  }

  setToken(token) {
    this.token = token;
    setStoredSessionToken(token);
  }

  clearCsrfToken() {
    this.csrfToken = null;
    clearStoredCsrfToken();
  }

  clearSessionToken() {
    this.token = null;
    clearStoredSessionToken();
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const method = options.method || 'GET';
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      if (this.csrfToken) {
        headers['X-CSRF-Token'] = this.csrfToken;
      }
    }

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    const data = await response.json();
    if (data?.csrfToken) {
      this.csrfToken = data.csrfToken;
      setStoredCsrfToken(data.csrfToken);
    }

    if (!response.ok) {
      const message = typeof data?.error === 'string'
        ? data.error
        : data?.message || 'Request failed';
      const error = new Error(message);
      error.status = response.status;
      error.details = data.details;
      error.code = data.code;
      error.upgradeRequired = Boolean(data.upgradeRequired);
      throw error;
    }

    return data;
  }

  // Auth
  async requestMagicLink(email) {
    return this.request('/auth/magic-link', { method: 'POST', body: { email } });
  }

  async verifyToken(token) {
    return this.request('/auth/verify', { method: 'POST', body: { token } });
  }

  async verifyCode(email, code) {
    return this.request('/auth/verify-code', { method: 'POST', body: { email, code } });
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async getModerationStatus() {
    return this.request('/trivia/community/moderation/status');
  }

  async logout() {
    return this.request('/auth/logout', { method: 'POST' });
  }

  async deleteUserAccount(confirmEmail) {
    return this.request('/auth/gdpr/account', { method: 'DELETE', body: { confirmEmail } });
  }

  // Accounts
  async getAccounts() {
    return this.request('/accounts');
  }

  async createAccount(name) {
    return this.request('/accounts', { method: 'POST', body: { name } });
  }

  async getAccount(accountId) {
    return this.request(`/accounts/${accountId}`);
  }

  async updateAccount(accountId, updates) {
    return this.request(`/accounts/${accountId}`, { method: 'PATCH', body: updates });
  }

  async deleteAccount(accountId) {
    return this.request(`/accounts/${accountId}`, { method: 'DELETE' });
  }

  async getAccountMembers(accountId) {
    return this.request(`/accounts/${accountId}/members`);
  }

  async inviteMember(accountId, email, role) {
    return this.request(`/accounts/${accountId}/members`, { method: 'POST', body: { email, role } });
  }

  async removeMember(accountId, userId) {
    return this.request(`/accounts/${accountId}/members/${userId}`, { method: 'DELETE' });
  }

  async updateMemberRole(accountId, userId, role) {
    return this.request(`/accounts/${accountId}/members/${userId}`, { method: 'PATCH', body: { role } });
  }

  async submitAccountFeedback(accountId, message, page = null) {
    return this.request(`/accounts/${accountId}/feedback`, { method: 'POST', body: { message, page } });
  }

  async getAccountTier(accountId) {
    return this.request(`/accounts/${accountId}/tier`);
  }

  async getSmsRegistrations(accountId) {
    return this.request(`/sms/registrations?accountId=${encodeURIComponent(accountId)}`);
  }

  async registerSmsNumber(accountId, boardId, phoneNumber) {
    return this.request('/sms/register', { method: 'POST', body: { accountId, boardId, phoneNumber } });
  }

  async verifySmsNumber(phoneNumber, code) {
    return this.request('/sms/verify', { method: 'POST', body: { phoneNumber, code } });
  }

  async deleteSmsRegistration(registrationId) {
    return this.request(`/sms/registrations/${registrationId}`, { method: 'DELETE' });
  }

  // Devices
  async getDevices(accountId) {
    return this.request(`/devices/account/${accountId}`);
  }

  async pairDevice(pairingCode, accountId, setup = {}) {
    return this.request('/devices/pair', {
      method: 'POST',
      body: {
        pairingCode,
        accountId,
        name: setup.name,
        assignedBoardId: setup.assignedBoardId || null
      }
    });
  }

  async updateDevice(deviceId, updates) {
    return this.request(`/devices/${deviceId}`, { method: 'PATCH', body: updates });
  }

  async unpairDevice(deviceId) {
    return this.request(`/devices/${deviceId}`, { method: 'DELETE' });
  }

  // Boards
  async getBoards(accountId) {
    return this.request(`/boards/account/${accountId}`);
  }

  async createBoard(accountId, board) {
    return this.request(`/boards/account/${accountId}`, { method: 'POST', body: board });
  }

  async getBoard(boardId) {
    return this.request(`/boards/${boardId}`);
  }

  async updateBoard(boardId, updates) {
    return this.request(`/boards/${boardId}`, { method: 'PATCH', body: updates });
  }

  async deleteBoard(boardId) {
    return this.request(`/boards/${boardId}`, { method: 'DELETE' });
  }

  async duplicateBoard(boardId) {
    return this.request(`/boards/${boardId}/duplicate`, { method: 'POST' });
  }

  // Messages
  async getMessages(boardId) {
    return this.request(`/boards/${boardId}/messages`);
  }

  async createMessage(boardId, payload) {
    return this.request(`/boards/${boardId}/messages`, { method: 'POST', body: payload });
  }

  async updateMessage(boardId, messageId, updates) {
    return this.request(`/boards/${boardId}/messages/${messageId}`, { method: 'PUT', body: updates });
  }

  async deleteMessage(boardId, messageId) {
    return this.request(`/boards/${boardId}/messages/${messageId}`, { method: 'DELETE' });
  }

  async reorderMessages(boardId, messageIds) {
    return this.request(`/boards/${boardId}/messages/reorder`, { method: 'PUT', body: { messageIds } });
  }

  // Templates
  async getTemplates(accountId) {
    return this.request(`/templates/account/${accountId}`);
  }

  async createTemplate(accountId, template) {
    return this.request(`/templates/account/${accountId}`, { method: 'POST', body: template });
  }

  async updateTemplate(templateId, updates) {
    return this.request(`/templates/message/${templateId}`, { method: 'PUT', body: updates });
  }

  async deleteTemplate(templateId) {
    return this.request(`/templates/message/${templateId}`, { method: 'DELETE' });
  }

  // Schedules
  async getSchedules(accountId) {
    return this.request(`/schedules/account/${accountId}`);
  }

  async createSchedule(accountId, schedule) {
    return this.request(`/schedules/account/${accountId}`, { method: 'POST', body: schedule });
  }

  async deleteSchedule(scheduleId) {
    return this.request(`/schedules/${scheduleId}`, { method: 'DELETE' });
  }

  // Game Queues
  async getGameQueues(accountId) {
    return this.request(`/queues?accountId=${accountId}`);
  }

  async getGameQueue(queueId) {
    return this.request(`/queues/${queueId}`);
  }

  async createGameQueue(payload) {
    return this.request('/queues', { method: 'POST', body: payload });
  }

  async updateGameQueue(queueId, updates) {
    return this.request(`/queues/${queueId}`, { method: 'PATCH', body: updates });
  }

  async deleteGameQueue(queueId) {
    return this.request(`/queues/${queueId}`, { method: 'DELETE' });
  }

  async addGameQueueItem(queueId, quizId, position) {
    return this.request(`/queues/${queueId}/items`, { method: 'POST', body: { quizId, position } });
  }

  async updateGameQueueItem(queueId, itemId, updates) {
    return this.request(`/queues/${queueId}/items/${itemId}`, { method: 'PATCH', body: updates });
  }

  async deleteGameQueueItem(queueId, itemId) {
    return this.request(`/queues/${queueId}/items/${itemId}`, { method: 'DELETE' });
  }

  async startGameQueue(queueId) {
    return this.request(`/queues/${queueId}/start`, { method: 'POST' });
  }

  async stopGameQueue(queueId) {
    return this.request(`/queues/${queueId}/stop`, { method: 'POST' });
  }
  async resetGameQueue(queueId) {
    return this.request(`/queues/${queueId}/reset`, { method: 'POST' });
  }

  async getDeviceQueueState(deviceId) {
    return this.request(`/devices/${deviceId}/current`);
  }

  // Subscriptions
  async getSubscriptionStatus(accountId) {
    return this.request(`/subscriptions/status/${accountId}`);
  }

  async startTrial(accountId) {
    return this.request(`/subscriptions/start-trial/${accountId}`, { method: 'POST' });
  }

  async createCheckout(accountId, deviceCount = 1) {
    const checkout = await this.request(
      `/subscriptions/checkout/${accountId}`,
      { method: 'POST', body: { deviceCount } }
    );
    rememberSubscriptionCheckout(accountId, checkout);
    return checkout;
  }

  async getBillingPortal(accountId, billingAccountId = null) {
    if (billingAccountId) {
      return this.request(`/billing/accounts/${billingAccountId}/portal`, { method: 'POST' });
    }
    return this.request(`/subscriptions/portal/${accountId}`, { method: 'POST' });
  }

  async syncSubscription(accountId) {
    return this.request(`/subscriptions/sync/${accountId}`, { method: 'POST' });
  }

  // Aliases for the billing methods used in Settings
  async createCheckoutSession(accountId) {
    return this.createCheckout(accountId, 1);
  }

  async createBillingPortalSession(accountId, billingAccountId = null) {
    return this.getBillingPortal(accountId, billingAccountId);
  }

  async getAccountActivity(accountId, page = 1, limit = 50) {
    return this.request(`/accounts/${accountId}/activity?page=${page}&limit=${limit}`);
  }

  // Trivia Games
  async getTriviaGames(accountId, params = {}) {
    const searchParams = new URLSearchParams({ accountId });
    if (params.categoryId) searchParams.set('categoryId', params.categoryId);
    return this.request(`/trivia/games?${searchParams.toString()}`);
  }

  async createTriviaGame(accountId, dataOrTitle, description, categoryId, settings) {
    // Support both object format and individual params
    if (typeof dataOrTitle === 'object') {
      const data = dataOrTitle;
      return this.request('/trivia/games', { 
        method: 'POST', 
        body: { 
          accountId, 
          title: data.title, 
          description: data.description, 
          categoryId: data.category_id, 
          questions: data.questions,
          settings: data.settings 
        } 
      });
    }
    const settingsPayload = settings || {};
    const questions = settingsPayload.questions;
    const cleanedSettings = { ...settingsPayload };
    delete cleanedSettings.questions;
    return this.request('/trivia/games', { 
      method: 'POST', 
      body: { accountId, title: dataOrTitle, description, categoryId, questions, settings: cleanedSettings } 
    });
  }

  async getTriviaGame(gameId) {
    return this.request(`/trivia/games/${gameId}`);
  }

  async updateTriviaGame(gameId, updates) {
    return this.request(`/trivia/games/${gameId}`, { method: 'PUT', body: updates });
  }

  async deleteTriviaGame(gameId) {
    return this.request(`/trivia/games/${gameId}`, { method: 'DELETE' });
  }

  async duplicateTriviaGame(gameId, accountId) {
    return this.request(`/trivia/games/${gameId}/duplicate`, { 
      method: 'POST', 
      body: { accountId } 
    });
  }

  async shareTriviaGame(gameId) {
    return this.request(`/trivia/games/${gameId}/share`, { method: 'POST' });
  }

  async unshareTriviaGame(gameId) {
    return this.request(`/trivia/games/${gameId}/unshare`, { method: 'POST' });
  }

  async getTriviaCategories() {
    return this.request('/trivia/games/meta/categories');
  }

  async createTriviaSession(gameId, deviceId, countdownSeconds, hideQuestionsOnMobile) {
    return this.request('/trivia/sessions', { 
      method: 'POST', 
      body: { gameId, deviceId, countdownSeconds, hideQuestionsOnMobile } 
    });
  }

  async scheduleTriviaSession(gameId, payload) {
    return this.request('/trivia/sessions/schedule', {
      method: 'POST',
      body: { gameId, ...payload }
    });
  }

  async getScheduledTriviaSessions(accountId) {
    return this.request(`/trivia/sessions/scheduled?accountId=${accountId}`);
  }

  async getActiveTriviaSessions(accountId) {
    return this.request(`/trivia/sessions/active?accountId=${accountId}`);
  }

  async rescheduleTriviaSession(sessionId, payload) {
    return this.request(`/trivia/sessions/${sessionId}/schedule`, {
      method: 'PATCH',
      body: payload
    });
  }

  async cancelTriviaSession(sessionId) {
    return this.request(`/trivia/sessions/${sessionId}/cancel`, { method: 'POST' });
  }

  // Alias for the Go Live modal
  async createGameSession(accountId, gameId, sessionData) {
    return this.request('/trivia/sessions', { 
      method: 'POST', 
      body: { 
        accountId, 
        gameId, 
        deviceId: sessionData.device_id, 
        countdownSeconds: sessionData.countdown_seconds,
        hideQuestionsOnMobile: sessionData.hide_questions_mobile
      } 
    });
  }

  async getTriviaSession(sessionId) {
    return this.request(`/trivia/sessions/${sessionId}`);
  }

  async startTriviaSession(sessionId) {
    return this.request(`/trivia/sessions/${sessionId}/start`, { method: 'POST' });
  }

  async nextTriviaQuestion(sessionId) {
    return this.request(`/trivia/sessions/${sessionId}/next-question`, { method: 'POST' });
  }

  async endTriviaQuestion(sessionId) {
    return this.request(`/trivia/sessions/${sessionId}/end-question`, { method: 'POST' });
  }

  async endTriviaSession(sessionId) {
    return this.request(`/trivia/sessions/${sessionId}/end`, { method: 'POST' });
  }

  async kickTriviaPlayer(sessionId, playerId, reason) {
    return this.request(`/trivia/sessions/${sessionId}/kick`, { 
      method: 'POST', 
      body: { playerId, reason } 
    });
  }

  async getTriviaHistory(accountId, limit = 50, offset = 0) {
    return this.request(`/trivia/sessions/history/list?accountId=${accountId}&limit=${limit}&offset=${offset}`);
  }

  async getTriviaHistoryDetail(historyId) {
    return this.request(`/trivia/sessions/history/${historyId}`);
  }

  async browseCommunityGames(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.categoryId) searchParams.set('categoryId', params.categoryId);
    if (params.search) searchParams.set('search', params.search);
    if (params.sort) searchParams.set('sort', params.sort);
    return this.request(`/trivia/community?${searchParams.toString()}`);
  }

  // Public community (no auth)
  async getPublicCommunityGames(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.categoryId && params.categoryId !== 'undefined' && params.categoryId !== 'null') {
      searchParams.set('categoryId', params.categoryId);
    }
    if (params.search) searchParams.set('search', params.search);
    if (params.sort) searchParams.set('sort', params.sort);
    if (Number.isFinite(params.limit)) searchParams.set('limit', params.limit);
    if (Number.isFinite(params.offset)) searchParams.set('offset', params.offset);
    const query = searchParams.toString();
    return this.request(`/public/community${query ? `?${query}` : ''}`);
  }

  async getPublicCommunityCategories() {
    return this.request('/public/community/categories');
  }

  async getPublicCommunityDetails(gameId) {
    return this.request(`/public/community/${gameId}/details`);
  }

  async getCommunityGameDetails(gameId) {
    return this.request(`/trivia/community/${gameId}/details`);
  }

  async getPendingCommunityGames() {
    return this.request('/trivia/community/moderation/pending');
  }

  async getAdminMetrics() {
    return this.request('/trivia/community/moderation/metrics');
  }

  async getMobileAppLaunchCampaign() {
    return this.request('/marketing/admin/mobile-app-launch');
  }

  async sendMobileAppLaunchTest(segment) {
    return this.request('/marketing/admin/mobile-app-launch/test', {
      method: 'POST',
      body: { segment }
    });
  }

  async sendMobileAppLaunchCampaign(confirmation) {
    return this.request('/marketing/admin/mobile-app-launch/send', {
      method: 'POST',
      body: { confirmation }
    });
  }

  async getModerationGameDetails(gameId) {
    return this.request(`/trivia/community/moderation/${gameId}`);
  }

  async getPreviewPoolGames(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set('search', params.search);
    if (Number.isFinite(params.limit)) searchParams.set('limit', params.limit);
    if (Number.isFinite(params.offset)) searchParams.set('offset', params.offset);
    const query = searchParams.toString();
    return this.request(`/trivia/community/moderation/preview-pool${query ? `?${query}` : ''}`);
  }

  async setPreviewPoolGame(gameId, enabled) {
    return this.request(`/trivia/community/moderation/preview-pool/${gameId}`, {
      method: 'POST',
      body: { enabled }
    });
  }

  async approveCommunityGame(gameId) {
    return this.request(`/trivia/community/moderation/${gameId}/approve`, { method: 'POST' });
  }

  async rejectCommunityGame(gameId, reason) {
    return this.request(`/trivia/community/moderation/${gameId}/reject`, {
      method: 'POST',
      body: { reason }
    });
  }

  async copyCommunityGame(gameId, accountId) {
    return this.request(`/trivia/community/${gameId}/copy`, { 
      method: 'POST', 
      body: { accountId } 
    });
  }

  async getQuestionBank(accountId, params = {}) {
    const searchParams = new URLSearchParams({ accountId });
    if (params.search) searchParams.set('search', params.search);
    return this.request(`/trivia/question-bank?${searchParams.toString()}`);
  }

  async deleteQuestionBankItem(questionId) {
    return this.request(`/trivia/question-bank/${questionId}`, { method: 'DELETE' });
  }

  async copyQuestionToGame(questionId, gameId) {
    return this.request(`/trivia/question-bank/${questionId}/copy-to-game`, { 
      method: 'POST', 
      body: { gameId } 
    });
  }

  async saveQuestionToBank(questionId, accountId, question = null) {
    return this.request('/trivia/question-bank/save-from-game', { 
      method: 'POST', 
      body: question ? { questionId, accountId, question } : { questionId, accountId }
    });
  }

  async getMessageTemplates(accountId) {
    return this.request(`/templates/account/${accountId}`);
  }

  async createMessageTemplate(accountId, payload) {
    return this.request(`/templates/account/${accountId}`, {
      method: 'POST',
      body: payload
    });
  }

  async deleteMessageTemplate(templateId) {
    return this.request(`/templates/message/${templateId}`, { method: 'DELETE' });
  }

  async generateTriviaQuestionsAI(accountId, topic, count = 10, idempotencyKey) {
    return this.request('/trivia/games/ai-generate', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: { accountId, topic, count }
    });
  }

  async getTriviaGenerationJob(jobId) {
    return this.request(`/trivia/games/ai-generate/${jobId}`);
  }
}

const api = new ApiClient(API_URL);

// ============================================================================
// ROUTING
// ============================================================================

function useRouter() {
  const [path, setPath] = useState(window.location.pathname);
  const [hash, setHash] = useState(window.location.hash);

  useEffect(() => {
    const handlePopState = () => {
      setPath(window.location.pathname);
      setHash(window.location.hash);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (newPath) => {
    if (newPath.includes('#')) {
      const [pathname, hashPart] = newPath.split('#');
      const targetPath = pathname || '/';
      
      if (targetPath === '/' && window.location.pathname === '/') {
        const element = document.getElementById(hashPart);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          window.history.pushState({}, '', `/#${hashPart}`);
          setHash(`#${hashPart}`);
          return;
        }
      }
      
      window.history.pushState({}, '', newPath);
      setPath(targetPath);
      setHash(`#${hashPart}`);
      
      setTimeout(() => {
        const element = document.getElementById(hashPart);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return;
    }
    
    window.history.pushState({}, '', newPath);
    setPath(newPath);
    setHash('');
    window.scrollTo(0, 0);
  };

  return { path, hash, navigate };
}

// ============================================================================
// AUTH CONTEXT
// ============================================================================

const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [accountTier, setAccountTier] = useState(null);
  const [isModerator, setIsModerator] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (currentAccount?.id) {
      fetchAccountTier(currentAccount.id);
    } else {
      setAccountTier(null);
    }
  }, [currentAccount?.id]);

  async function fetchAccountTier(accountId) {
    try {
      const tierInfo = await api.getAccountTier(accountId);
      setAccountTier(tierInfo);
    } catch (err) {
      setAccountTier({ 
        tier: 'free', 
        billingStatus: null,
        planType: 'free',
        trialEndsAt: null,
        trialDaysRemaining: 0,
        billingPortalAvailable: false,
        features: { 
          maxDevices: 1, 
          maxBoards: 1,
          maxGames: 3,
          maxQuestionsPerGame: 10,
          maxPlayersPerSession: 10,
          maxHostedGamesPerMonth: 1,
          trivia: true, 
          boards: true, 
          aiGeneration: false,
          gameHistory: false,
          postGameAnalytics: false,
          communitySharing: false,
          messageBank: false,
          schedulingEnabled: false,
          customStyles: false,
          messageStatus: false
        },
        limits: {
          maxGames: 3,
          maxQuestionsPerGame: 10,
          maxPlayersPerSession: 10,
          maxHostedGamesPerMonth: 1,
          aiQuestions: false,
          gameHistory: false,
          communitySharing: false,
          postGameAnalytics: false
        },
        usage: {
          gameCount: 0,
          hostedGamesThisMonth: 0,
          hostedGamesResetAt: null
        }
      });
    }
  }

  async function checkAuth() {
    try {
      localStorage.removeItem('splitcast_token');
      const data = await api.getCurrentUser();
      setUser(data.user);
      fetchModeratorStatus();
      setAccounts(data.accounts);
      if (data.accounts.length > 0) {
        const savedAccountId = localStorage.getItem('splitcast_account');
        const account = data.accounts.find(a => a.id === savedAccountId) || data.accounts[0];
        setCurrentAccount(account);
      }
    } catch (err) {
      api.clearSessionToken();
      api.clearCsrfToken();
    } finally {
      setLoading(false);
    }
  }

  async function login(token) {
    const data = await api.verifyToken(token);
    if (data.sessionToken) {
      api.setToken(data.sessionToken);
    }
    setUser(data.user);
    fetchModeratorStatus();
    setAccounts(data.accounts);
    if (data.accounts.length > 0) {
      setCurrentAccount(data.accounts[0]);
      localStorage.setItem('splitcast_account', data.accounts[0].id);
    }
    setLoading(false);
    return data;
  }

  function loginWithData(data) {
    if (data.sessionToken) {
      api.setToken(data.sessionToken);
    }
    setUser(data.user);
    fetchModeratorStatus();
    setAccounts(data.accounts);
    if (data.accounts.length > 0) {
      setCurrentAccount(data.accounts[0]);
      localStorage.setItem('splitcast_account', data.accounts[0].id);
    }
    setLoading(false);
    return data;
  }

  async function logout() {
    try { await api.logout(); } catch (err) {}
    api.clearSessionToken();
    api.clearCsrfToken();
    setUser(null);
    setAccounts([]);
    setCurrentAccount(null);
    setAccountTier(null);
    setIsModerator(false);
    localStorage.removeItem('splitcast_account');
  }

  function switchAccount(account) {
    setCurrentAccount(account);
    localStorage.setItem('splitcast_account', account.id);
  }

  async function createAccount(name) {
    const { account } = await api.createAccount(name);
    const updatedAccounts = [...accounts, { ...account, role: 'admin' }];
    setAccounts(updatedAccounts);
    setCurrentAccount({ ...account, role: 'admin' });
    localStorage.setItem('splitcast_account', account.id);
    return account;
  }

  async function refreshAccounts() {
    const data = await api.getCurrentUser();
    setAccounts(data.accounts);
    if (currentAccount?.id) {
      const updatedAccount = data.accounts.find(account => account.id === currentAccount.id);
      if (updatedAccount) {
        setCurrentAccount(updatedAccount);
      }
    }
  }

  async function refreshAccountsWithCounts() {
    const data = await api.getAccounts();
    setAccounts(data.accounts);
    if (currentAccount?.id) {
      const updatedAccount = data.accounts.find(account => account.id === currentAccount.id);
      if (updatedAccount) {
        setCurrentAccount(updatedAccount);
      }
    }
    return data.accounts;
  }

  async function deleteAccount(accountId) {
    await api.deleteAccount(accountId);
    const data = await api.getCurrentUser();
    setAccounts(data.accounts);
    if (currentAccount?.id === accountId) {
      const nextAccount = data.accounts[0] || null;
      setCurrentAccount(nextAccount);
      if (nextAccount) {
        localStorage.setItem('splitcast_account', nextAccount.id);
      } else {
        localStorage.removeItem('splitcast_account');
      }
    }
  }

  async function refreshTier() {
    if (currentAccount?.id) {
      await fetchAccountTier(currentAccount.id);
    }
  }

  async function fetchModeratorStatus() {
    try {
      const result = await api.getModerationStatus();
      setIsModerator(!!result.isModerator);
    } catch (error) {
      setIsModerator(false);
    }
  }

  return (
    <AuthContext.Provider value={{
      user, accounts, currentAccount, accountTier, loading,
      login, loginWithData, logout, switchAccount, createAccount, refreshAccounts, refreshAccountsWithCounts, refreshTier,
      deleteAccount,
      isAdmin: currentAccount?.role === 'admin',
      isModerator,
      isPro: accountTier?.tier === 'pro',
      canUseBoards: accountTier?.features?.boards || false,
      canUseAI: accountTier?.features?.aiGeneration || false,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  return useContext(AuthContext);
}

// ============================================================================
// ICONS (Thin-line minimalist icons with 2px stroke)
// ============================================================================

const Icons = {
  Dashboard: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1"/>
      <rect x="14" y="3" width="7" height="5" rx="1"/>
      <rect x="14" y="12" width="7" height="9" rx="1"/>
      <rect x="3" y="16" width="7" height="5" rx="1"/>
    </svg>
  ),
  Boards: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <path d="M3 9h18"/>
      <path d="M9 21V9"/>
    </svg>
  ),
  Games: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 12h4"/>
      <path d="M8 10v4"/>
      <path d="M15 13h.01"/>
      <path d="M18 11h.01"/>
      <rect x="2" y="6" width="20" height="12" rx="2"/>
    </svg>
  ),
  Devices: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2"/>
      <path d="M8 21h8"/>
      <path d="M12 17v4"/>
    </svg>
  ),
  Mobile: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="2" width="12" height="20" rx="2"/>
      <path d="M10 18h4"/>
    </svg>
  ),
  Activity: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
    </svg>
  ),
  Settings: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Shield: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V6l7-3z"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  ),
  ChevronLeft: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6"/>
    </svg>
  ),
  ChevronRight: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18l6-6-6-6"/>
    </svg>
  ),
  ChevronDown: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6"/>
    </svg>
  ),
  ChevronUp: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 15l-6-6-6 6"/>
    </svg>
  ),
  Plus: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14"/>
      <path d="M5 12h14"/>
    </svg>
  ),
  Edit: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  Trash: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18"/>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
  ),
  Copy: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  ),
  Play: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  ),
  Stop: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="6" width="12" height="12" rx="2"/>
    </svg>
  ),
  Users: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Trophy: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
      <path d="M4 22h16"/>
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
    </svg>
  ),
  Clock: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v6l4 2"/>
    </svg>
  ),
  Search: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <path d="M21 21l-4.35-4.35"/>
    </svg>
  ),
  X: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18"/>
      <path d="M6 6l12 12"/>
    </svg>
  ),
  Check: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5"/>
    </svg>
  ),
  Wifi: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
      <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
      <circle cx="12" cy="20" r="1"/>
    </svg>
  ),
  WifiOff: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 1l22 22"/>
      <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/>
      <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
      <path d="M10.71 5.05A16 16 0 0 1 22.58 9"/>
      <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/>
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
      <circle cx="12" cy="20" r="1"/>
    </svg>
  ),
  TrendingUp: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 6l-9.5 9.5-5-5L1 18"/>
      <path d="M17 6h6v6"/>
    </svg>
  ),
  TrendingDown: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 18l-9.5-9.5-5 5L1 6"/>
      <path d="M17 18h6v-6"/>
    </svg>
  ),
  LogOut: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  Help: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
      <path d="M12 17h.01"/>
    </svg>
  ),
  Mail: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2"/>
      <path d="m4 7 8 6 8-6"/>
    </svg>
  ),
  MessageCircle: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/>
    </svg>
  ),
  Lock: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="10" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
  Key: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7.5" cy="15.5" r="5.5"/>
      <path d="m21 2-9.6 9.6"/>
      <path d="m15.5 7.5 3 3"/>
      <path d="m18.5 4.5 3 3"/>
    </svg>
  ),
  RotateCw: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2v6h-6"/>
      <path d="M3 12a9 9 0 0 1 15.55-6.36L21 8"/>
      <path d="M3 22v-6h6"/>
      <path d="M21 12a9 9 0 0 1-15.55 6.36L3 16"/>
    </svg>
  ),
  FileText: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <path d="M14 2v6h6"/>
      <path d="M16 13H8"/>
      <path d="M16 17H8"/>
      <path d="M10 9H8"/>
    </svg>
  ),
  Share: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3"/>
      <circle cx="6" cy="12" r="3"/>
      <circle cx="18" cy="19" r="3"/>
      <path d="M8.59 13.51l6.83 3.98"/>
      <path d="M15.41 6.51l-6.82 3.98"/>
    </svg>
  ),
  Sparkles: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3Z"/>
      <path d="M5 3v4"/>
      <path d="M19 17v4"/>
      <path d="M3 5h4"/>
      <path d="M17 19h4"/>
    </svg>
  ),
  Globe: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M2 12h20"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  ),
  BookOpen: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </svg>
  ),
  Zap: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
};

// ============================================================================
// UI COMPONENTS - Glassmorphism Design System
// ============================================================================

// Glass Card Component
function GlassCard({ children, className = '', hover = true, padding = 'md', ...props }) {
  const paddingClasses = {
    none: '',
    sm: 'p-3',
    md: 'p-5',
    lg: 'p-6',
    xl: 'p-8'
  };
  
  return (
    <div 
      className={`glass-card ${hover ? 'glass-card-hover' : ''} ${paddingClasses[padding]} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

// KPI Card Component
function KPICard({ title, value, change, changeType = 'neutral', icon: Icon, suffix = '' }) {
  const changeColors = {
    positive: 'text-emerald-400',
    negative: 'text-rose-400',
    neutral: 'text-slate-400'
  };

  return (
    <GlassCard className="kpi-card">
      <div className="kpi-header">
        <span className="kpi-title">{title}</span>
        {Icon && (
          <div className="kpi-icon">
            <Icon />
          </div>
        )}
      </div>
      <div className="kpi-value">
        <span className="kpi-number">{value}</span>
        {suffix && <span className="kpi-suffix">{suffix}</span>}
      </div>
      {change !== undefined && (
        <div className={`kpi-change ${changeColors[changeType]}`}>
          {changeType === 'positive' ? <Icons.TrendingUp /> : changeType === 'negative' ? <Icons.TrendingDown /> : null}
          <span>{change}</span>
          <span className="kpi-period">vs last month</span>
        </div>
      )}
    </GlassCard>
  );
}

// Button Component
function Button({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  loading = false,
  disabled = false,
  icon: Icon,
  className = '',
  ...props 
}) {
  const variants = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    ghost: 'btn-ghost',
    danger: 'btn-danger'
  };
  
  const sizes = {
    sm: 'btn-sm',
    md: 'btn-md',
    lg: 'btn-lg'
  };

  return (
    <button 
      className={`btn ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <span className="btn-spinner" />
      ) : (
        <>
          {Icon && <Icon />}
          {children}
        </>
      )}
    </button>
  );
}

// Input Component
function Input({ label, error, className = '', ...props }) {
  return (
    <div className={`input-group ${className}`}>
      {label && <label className="input-label">{label}</label>}
      <input className={`input-field ${error ? 'input-error' : ''}`} {...props} />
      {error && <span className="input-error-text">{error}</span>}
    </div>
  );
}

// Select Component
function Select({ label, options = [], error, className = '', ...props }) {
  return (
    <div className={`input-group ${className}`}>
      {label && <label className="input-label">{label}</label>}
      <select className={`select-field ${error ? 'input-error' : ''}`} {...props}>
        {options.map((opt, i) => (
          <option key={i} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && <span className="input-error-text">{error}</span>}
    </div>
  );
}

// Badge Component
function Badge({ children, variant = 'default', size = 'md' }) {
  const variants = {
    default: 'badge-default',
    success: 'badge-success',
    warning: 'badge-warning',
    danger: 'badge-danger',
    info: 'badge-info',
    pro: 'badge-pro'
  };

  return (
    <span className={`badge ${variants[variant]} badge-${size}`}>
      {children}
    </span>
  );
}

// Toast Component
function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const types = {
    success: 'toast-success',
    error: 'toast-error',
    info: 'toast-info',
    warning: 'toast-warning'
  };

  return (
    <div className={`toast ${types[type]}`}>
      <span>{message}</span>
      <button onClick={onClose} className="toast-close">
        <Icons.X />
      </button>
    </div>
  );
}

// Modal Component
function Modal({ isOpen, title, children, onClose, size = 'md' }) {
  if (!isOpen) return null;

  const sizes = {
    sm: 'modal-sm',
    md: 'modal-md',
    lg: 'modal-lg',
    xl: 'modal-xl'
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal-container ${sizes[size]}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <Icons.X />
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}

// Empty State Component
function EmptyState({ icon: Icon, title, description, action, actionLabel }) {
  return (
    <div className="empty-state">
      {Icon && (
        <div className="empty-state-icon">
          <Icon />
        </div>
      )}
      <h3 className="empty-state-title">{title}</h3>
      {description && <p className="empty-state-description">{description}</p>}
      {action && (
        <Button onClick={action} icon={Icons.Plus}>
          {actionLabel || 'Get Started'}
        </Button>
      )}
    </div>
  );
}

// Loading Spinner
function Spinner({ size = 'md' }) {
  const sizes = {
    sm: 'spinner-sm',
    md: 'spinner-md',
    lg: 'spinner-lg'
  };
  return <div className={`spinner ${sizes[size]}`} />;
}

// ============================================================================
// SIDEBAR NAVIGATION
// ============================================================================

function Sidebar({ currentPage, onNavigate, collapsed, onToggleCollapse, onLogout, isOpen }) {
  const { user, currentAccount, accounts, switchAccount, createAccount, deleteAccount, refreshAccountsWithCounts, isModerator } = useAuth();
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showAddOrgModal, setShowAddOrgModal] = useState(false);
  const [showManageOrgsModal, setShowManageOrgsModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [deletingOrgId, setDeletingOrgId] = useState(null);
  const [orgList, setOrgList] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const currentOrgLabel = currentAccount?.is_primary
    ? 'Primary'
    : currentAccount?.parent_account_id
      ? 'Sub-org'
      : null;

  const navItems = [
    { id: 'boards', label: 'Boards', icon: Icons.Boards },
    { id: 'games', label: 'Games', icon: Icons.Games },
    { id: 'schedules', label: 'Schedules', icon: Icons.Clock },
    { id: 'devices', label: 'Devices', icon: Icons.Devices },
    { id: 'activity', label: 'Activity', icon: Icons.Activity },
    { id: 'settings', label: 'Settings', icon: Icons.Settings },
    ...(isModerator ? [{ id: 'admin', label: 'Admin', icon: Icons.Shield }] : [])
  ];

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) return;
    setCreatingOrg(true);
    try {
      await createAccount(newOrgName.trim());
      setNewOrgName('');
      setShowAddOrgModal(false);
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleDeleteOrg = async (account) => {
    if (!account?.id) return;
    const confirmed = confirm(`Delete ${account.name}? This permanently removes boards, devices, games, and members.`);
    if (!confirmed) return;
    setDeletingOrgId(account.id);
    try {
      await deleteAccount(account.id);
      setShowManageOrgsModal(false);
    } finally {
      setDeletingOrgId(null);
    }
  };

  useEffect(() => {
    if (!showManageOrgsModal) return;
    let isActive = true;
    setLoadingOrgs(true);
    refreshAccountsWithCounts()
      .then((list) => {
        if (!isActive) return;
        setOrgList(list || []);
      })
      .catch(() => {
        if (!isActive) return;
        setOrgList(accounts);
      })
      .finally(() => {
        if (isActive) setLoadingOrgs(false);
      });
    return () => { isActive = false; };
  }, [showManageOrgsModal]);

  return (
    <aside className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''} ${isOpen ? 'sidebar-open' : ''}`}>
      {/* Logo */}
      <div className="sidebar-header">
        <div className="sidebar-logo" onClick={() => onNavigate('boards')}>
          <span className="logo-icon">▣</span>
          {!collapsed && <span className="logo-text">SPLITCAST</span>}
        </div>
        <button className="sidebar-toggle" onClick={onToggleCollapse}>
          {collapsed ? <Icons.ChevronRight /> : <Icons.ChevronLeft />}
        </button>
      </div>

      {/* Account Selector */}
      <div className="sidebar-account">
        <button 
          className="account-button"
          onClick={() => setShowAccountMenu(!showAccountMenu)}
        >
          <div className="account-avatar">
            {currentAccount?.name?.charAt(0).toUpperCase() || 'A'}
          </div>
          {!collapsed && (
            <>
              <div className="account-info">
                <span className="account-name">{currentAccount?.name}</span>
                <span className="account-role">
                  {currentAccount?.role}
                  {currentOrgLabel ? ` · ${currentOrgLabel}` : ''}
                </span>
              </div>
              <Icons.ChevronDown />
            </>
          )}
        </button>
        
        {showAccountMenu && (
          <div className="account-dropdown">
            {accounts.map(acc => (
              <button
                key={acc.id}
                className={`account-option ${acc.id === currentAccount?.id ? 'active' : ''}`}
                onClick={() => { switchAccount(acc); setShowAccountMenu(false); }}
              >
                <div className="account-avatar-small">
                  {acc.name?.charAt(0).toUpperCase()}
                </div>
                <span>{acc.name}</span>
                {acc.id === currentAccount?.id && <Icons.Check />}
              </button>
            ))}
            <div className="account-dropdown-divider" />
            <button
              className="account-action"
              onClick={() => { setShowAddOrgModal(true); setShowAccountMenu(false); }}
            >
              <Icons.Plus />
              Add Org
            </button>
            <button
              className="account-action"
              onClick={() => { setShowManageOrgsModal(true); setShowAccountMenu(false); }}
            >
              <Icons.Settings />
              Manage Orgs
            </button>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {navItems.map(item => (
          <button
            key={item.id}
            className={`nav-item ${currentPage === item.id ? 'active' : ''}`}
            onClick={() => onNavigate(item.id)}
            title={collapsed ? item.label : undefined}
          >
            <item.icon />
            {!collapsed && <span>{item.label}</span>}
          </button>
        ))}
      </nav>

      {/* Bottom Actions */}
      <div className="sidebar-footer">
        <a 
          href="/guide" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="nav-item"
          title={collapsed ? 'Help' : undefined}
        >
          <Icons.Help />
          {!collapsed && <span>Help & Guide</span>}
        </a>
        <button className="nav-item logout" onClick={onLogout} title={collapsed ? 'Sign Out' : undefined}>
          <Icons.LogOut />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>

      {showAddOrgModal && (
        <Modal isOpen={showAddOrgModal} title="Add Organization" onClose={() => setShowAddOrgModal(false)}>
          <div className="form-group">
            <Input
              label="Organization Name"
              value={newOrgName}
              onChange={e => setNewOrgName(e.target.value)}
              placeholder="Enter Name"
              onKeyDown={e => e.key === 'Enter' && handleCreateOrg()}
            />
            <p className="input-help">Enter Name</p>
          </div>
          <div className="modal-actions">
            <Button variant="ghost" onClick={() => setShowAddOrgModal(false)}>Cancel</Button>
            <Button onClick={handleCreateOrg} loading={creatingOrg} disabled={!newOrgName.trim()}>
              Create Organization
            </Button>
          </div>
        </Modal>
      )}

      {showManageOrgsModal && (
        <Modal isOpen={showManageOrgsModal} title="Manage Organizations" onClose={() => setShowManageOrgsModal(false)}>
          <div className="org-manage-list">
            {loadingOrgs && (
              <p className="muted">Loading organizations...</p>
            )}
            {!loadingOrgs && (orgList.length ? orgList : accounts).map(acc => (
              <div key={acc.id} className="org-manage-row">
                <div className="org-manage-info">
                  <div className="org-manage-name">{acc.name}</div>
                  <div className="org-manage-meta">
                    <span className="org-meta-item">{acc.is_primary ? 'Primary' : acc.parent_account_id ? 'Sub-org' : 'Org'}</span>
                    <span className="org-meta-item">{acc.role}</span>
                    <span className="org-meta-item">{acc.board_count ?? 0} boards</span>
                    <span className="org-meta-item">{acc.device_count ?? 0} devices</span>
                  </div>
                </div>
                <div className="org-manage-actions">
                  {acc.id !== currentAccount?.id && acc.role === 'admin' && (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteOrg(acc)}
                      loading={deletingOrgId === acc.id}
                    >
                      Remove
                    </Button>
                  )}
                  {acc.id === currentAccount?.id && (
                    <span className="org-manage-current">Current</span>
                  )}
                </div>
              </div>
            ))}
            {accounts.length === 0 && (
              <p className="muted">No organizations found.</p>
            )}
          </div>
        </Modal>
      )}
    </aside>
  );
}

// ============================================================================
// BOARDS PAGE
// ============================================================================

const DEFAULT_ROW_ALIGN = 'left';
const DEFAULT_ROW_COLOR = '#E8E4D9';
const DEFAULT_BOARD_BACKGROUND_COLOR = '#0F0F0D';
const BOARD_BACKGROUND_PRESETS = [
  { label: 'Matte Black', value: DEFAULT_BOARD_BACKGROUND_COLOR },
  { label: 'Charcoal', value: '#181A1B' },
  { label: 'Midnight', value: '#0B1320' },
  { label: 'Forest', value: '#0B1A15' },
  { label: 'Burgundy', value: '#1A0D10' },
  { label: 'Walnut', value: '#1A130D' },
];

const normalizeBoardBackground = (value) => (
  /^#[0-9a-fA-F]{6}$/.test(value || '')
    ? value.toUpperCase()
    : DEFAULT_BOARD_BACKGROUND_COLOR
);

const getBoardBackground = (board) => normalizeBoardBackground(
  board?.background_color || board?.backgroundColor
);

const toSafeString = (value) => {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
};

const normalizeRowArray = (value, rows, fallback) => {
  const safeRows = Math.max(0, rows || 0);
  const input = Array.isArray(value) ? value : [];
  const normalized = [];
  for (let i = 0; i < safeRows; i++) {
    const item = input[i];
    normalized.push(item !== undefined && item !== null ? item : fallback);
  }
  return normalized;
};

const formatMessageRows = (rawContent, rows, cols) => {
  const safeRows = Math.max(0, rows || 0);
  const safeCols = Math.max(0, cols || 0);
  let lines = [];

  if (Array.isArray(rawContent)) {
    lines = rawContent.map(toSafeString);
  } else {
    const text = toSafeString(rawContent).replace(/\r\n/g, '\n').trimEnd();
    if (text.includes('\n')) {
      lines = text.split('\n');
    } else if (text.trim()) {
      const words = text.split(/\s+/).filter(Boolean);
      let currentLine = '';
      for (const word of words) {
        if (!currentLine) {
          if (word.length <= safeCols) {
            currentLine = word;
            continue;
          }
          lines.push(word.slice(0, safeCols));
          currentLine = word.slice(safeCols);
          continue;
        }
        if (currentLine.length + 1 + word.length <= safeCols) {
          currentLine = `${currentLine} ${word}`;
        } else {
          lines.push(currentLine);
          currentLine = word.length <= safeCols ? word : word.slice(0, safeCols);
        }
      }
      if (currentLine) lines.push(currentLine);
    } else {
      lines = [''];
    }
  }

  lines = lines.map((line) => toSafeString(line).slice(0, safeCols));
  lines = lines.slice(0, safeRows);
  while (lines.length < safeRows) lines.push('');
  return lines;
};

const alignRow = (text, cols, alignment) => {
  const value = toSafeString(text).toUpperCase().slice(0, cols);
  const padding = Math.max(0, cols - value.length);
  if (!padding) return value;
  if (alignment === 'right') {
    return `${' '.repeat(padding)}${value}`;
  }
  if (alignment === 'center') {
    const leftPad = Math.floor(padding / 2);
    return `${' '.repeat(leftPad)}${value}${' '.repeat(padding - leftPad)}`;
  }
  return `${value}${' '.repeat(padding)}`;
};

function BoardBackgroundPicker({ value, onChange, disabled = false }) {
  const normalizedValue = normalizeBoardBackground(value);

  return (
    <div className="input-group board-background-picker">
      <div className="board-background-heading">
        <label className="input-label">Board Background</label>
        {disabled && <span className="pro-feature-chip">Pro</span>}
      </div>
      <div className="board-background-options">
        {BOARD_BACKGROUND_PRESETS.map((preset) => {
          const isLocked = disabled && preset.value !== DEFAULT_BOARD_BACKGROUND_COLOR;
          return (
            <button
              key={preset.value}
              type="button"
              className={`board-background-option ${normalizedValue === preset.value ? 'selected' : ''}`}
              onClick={() => onChange(preset.value)}
              disabled={isLocked}
              title={isLocked ? 'Only Available in Pro Subscription' : preset.label}
              aria-label={`${preset.label} background`}
              aria-pressed={normalizedValue === preset.value}
            >
              <span
                className="board-background-swatch"
                style={{ backgroundColor: preset.value }}
              />
              <span>{preset.label}</span>
            </button>
          );
        })}
        <label
          className={`board-background-option board-background-custom ${disabled ? 'disabled' : ''}`}
          title={disabled ? 'Only Available in Pro Subscription' : 'Choose a custom dark color'}
        >
          <input
            type="color"
            value={normalizedValue}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
            disabled={disabled}
            aria-label="Custom board background"
          />
          <span>Custom</span>
        </label>
      </div>
      <p className="input-help">
        {disabled
          ? 'Custom backgrounds require Pro. Matte Black remains available on Free.'
          : 'Dark, low-saturation colors preserve contrast and look best on a TV.'}
      </p>
    </div>
  );
}

// Interactive Split-Flap Board Preview Component
function SplitFlapPreview({
  rows,
  cols,
  messages,
  font,
  currentMessageIndex,
  backgroundColor = DEFAULT_BOARD_BACKGROUND_COLOR,
}) {
  const [displayText, setDisplayText] = useState([]);
  const [flipping, setFlipping] = useState([]);
  
  // Get current message or default (support both content and text fields)
  const msg = messages[currentMessageIndex];
  const rawMessage = msg?.content || msg?.text || '';
  const messageKey = Array.isArray(rawMessage) ? rawMessage.join('\n') : toSafeString(rawMessage);
  const rowAlignments = normalizeRowArray(
    msg?.row_alignments || msg?.rowAlignments,
    rows,
    DEFAULT_ROW_ALIGN
  );
  const rowColors = normalizeRowArray(
    msg?.row_colors || msg?.rowColors,
    rows,
    DEFAULT_ROW_COLOR
  );
  
  // Split message into lines (by row count)
  const getDisplayLines = () => {
    const lines = formatMessageRows(rawMessage, rows, cols);
    return lines.map((line, rowIdx) => {
      const aligned = alignRow(line, cols, rowAlignments[rowIdx] || DEFAULT_ROW_ALIGN);
      return aligned.split('').slice(0, cols);
    });
  };
  
  useEffect(() => {
    const newDisplay = getDisplayLines();
    
    // Mark characters that are changing as flipping
    const newFlipping = newDisplay.map((row, rowIdx) => 
      row.map((char, colIdx) => {
        const oldChar = displayText[rowIdx]?.[colIdx] || ' ';
        return char !== oldChar;
      })
    );
    
    setFlipping(newFlipping);
    
    // Animate flip
    const timer = setTimeout(() => {
      setDisplayText(newDisplay);
      setTimeout(() => setFlipping([]), 150);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [messageKey, rows, cols, rowAlignments]);
  
  // Initialize display
  useEffect(() => {
    setDisplayText(getDisplayLines());
  }, [messageKey, rows, cols, rowAlignments]);
  
  const getFontClass = () => {
    switch (font) {
      case 'terminal': return 'font-terminal';
      case 'classic': return 'font-classic';
      case 'modern': return 'font-modern';
      case 'condensed': return 'font-condensed';
      default: return 'font-mechanical';
    }
  };
  
  return (
    <div className={`split-flap-preview ${getFontClass()}`}>
      <div
        className="flap-board"
        style={{ backgroundColor: normalizeBoardBackground(backgroundColor) }}
      >
        {(displayText.length > 0 ? displayText : getDisplayLines()).map((row, rowIdx) => (
          <div key={rowIdx} className="flap-row" style={{ color: rowColors[rowIdx] || DEFAULT_ROW_COLOR }}>
            {row.map((char, colIdx) => (
              <div 
                key={colIdx} 
                className={`flap-char ${flipping[rowIdx]?.[colIdx] ? 'flipping' : ''}`}
              >
                <span>{char}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function BoardsPage({ activationAction, onActivationActionHandled }) {
  const { currentAccount, canUseBoards, isPro, accountTier } = useAuth();
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [setupDeviceId, setSetupDeviceId] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [messageAlignments, setMessageAlignments] = useState([]);
  const [messageColors, setMessageColors] = useState([]);
  const [messageStatus, setMessageStatus] = useState('active');
  const [messageScheduleStart, setMessageScheduleStart] = useState('');
  const [messageScheduleEnd, setMessageScheduleEnd] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  
  // Board form data
  const [formData, setFormData] = useState({ 
    name: '', 
    rows: 4, 
    cols: 20, 
    font: 'mechanical',
    sound_enabled: true,
    message_interval: 15,
    background_color: DEFAULT_BOARD_BACKGROUND_COLOR,
  });
  const [originalDimensions, setOriginalDimensions] = useState({ rows: 4, cols: 20 });

  useEffect(() => {
    if (currentAccount?.id) loadBoards();
  }, [currentAccount?.id]);

  useEffect(() => {
    if (selectedBoard?.id) loadMessages(selectedBoard.id);
  }, [selectedBoard?.id]);

  useEffect(() => {
    if (!activationAction || loading) return;

    if (activationAction.type === 'create-board') {
      setSetupDeviceId(activationAction.deviceId || null);
      setFormData({
        name: '',
        rows: 4,
        cols: 20,
        font: 'mechanical',
        sound_enabled: true,
        message_interval: 15,
        background_color: DEFAULT_BOARD_BACKGROUND_COLOR,
      });
      setShowCreateModal(true);
      onActivationActionHandled?.();
      return;
    }

    if (activationAction.type === 'add-message') {
      setSetupDeviceId(null);
      const targetBoard = boards.find(board => board.id === activationAction.boardId);
      if (!targetBoard) {
        setToast({ message: 'That board is no longer available.', type: 'error' });
        onActivationActionHandled?.();
        return;
      }

      setSelectedBoard(targetBoard);
      setEditingMessage(null);
      setMessageText('');
      setMessageAlignments(normalizeRowArray([], targetBoard.rows, DEFAULT_ROW_ALIGN));
      setMessageColors(normalizeRowArray([], targetBoard.rows, DEFAULT_ROW_COLOR));
      setMessageStatus('active');
      setMessageScheduleStart('');
      setMessageScheduleEnd('');
      setShowMessageModal(true);
      onActivationActionHandled?.();
    }
  }, [activationAction, boards, loading, onActivationActionHandled]);
  
  // Cycle through messages for preview
  useEffect(() => {
    if (messages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentPreviewIndex(prev => (prev + 1) % messages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [messages.length]);

  const loadBoards = async () => {
    try {
      setLoading(true);
      const result = await api.getBoards(currentAccount.id);
      setBoards(result.boards || []);
      if (result.boards?.length > 0 && !selectedBoard) {
        setSelectedBoard(result.boards[0]);
      }
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  const loadMessages = async (boardId) => {
    try {
      setMessagesLoading(true);
      const result = await api.getMessages(boardId);
      const parseJsonArray = (value) => {
        if (typeof value !== 'string') return null;
        const trimmed = value.trim();
        if (!trimmed.startsWith('[')) return null;
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? parsed : null;
        } catch {
          return null;
        }
      };

      const normalizedMessages = (result.messages || []).map((message) => {
        let content = message.content;
        if (Array.isArray(content)) {
          content = content.map(toSafeString);
        } else {
          const parsed = parseJsonArray(content);
          if (parsed) {
            content = parsed.map(toSafeString);
          } else {
            content = formatMessageRows(content || '', selectedBoard?.rows, selectedBoard?.cols);
          }
        }

        let rowAlignments = message.row_alignments ?? message.rowAlignments;
        if (typeof rowAlignments === 'string') {
          const parsed = parseJsonArray(rowAlignments);
          rowAlignments = parsed || [];
        }
        rowAlignments = normalizeRowArray(
          rowAlignments,
          selectedBoard?.rows || content.length,
          DEFAULT_ROW_ALIGN
        );

        let rowColors = message.row_colors ?? message.rowColors;
        if (typeof rowColors === 'string') {
          const parsed = parseJsonArray(rowColors);
          rowColors = parsed || [];
        }
        rowColors = normalizeRowArray(
          rowColors,
          selectedBoard?.rows || content.length,
          DEFAULT_ROW_COLOR
        );

        return {
          ...message,
          content,
          row_alignments: rowAlignments,
          row_colors: rowColors,
          rowAlignments,
          rowColors,
        };
      });

      setMessages(normalizedMessages);
      setCurrentPreviewIndex(0);
    } catch (error) {
      console.error('Failed to load messages:', error);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleCreateBoard = async () => {
    if (!canCreateBoard) {
      showToast('Upgrade to Pro to add more boards.', 'error');
      return;
    }
    if (!formData.name.trim()) {
      showToast('Please enter a board name', 'error');
      return;
    }
    setSaving(true);
    try {
      const backgroundColor = isPro
        ? normalizeBoardBackground(formData.background_color)
        : DEFAULT_BOARD_BACKGROUND_COLOR;
      const result = await api.createBoard(currentAccount.id, {
        name: formData.name.trim(),
        rows: parseInt(formData.rows),
        cols: parseInt(formData.cols),
        font: formData.font,
        soundEnabled: formData.sound_enabled,
        messageInterval: formData.message_interval,
        backgroundColor,
      });
      const createdBoard = result.board;
      const deviceIdToAssign = setupDeviceId;
      let setupAssignmentSucceeded = false;

      if (deviceIdToAssign) {
        try {
          await api.updateDevice(deviceIdToAssign, { assignedBoardId: createdBoard.id });
          setupAssignmentSucceeded = true;
        } catch (assignmentError) {
          showToast(
            `Board created, but it could not be assigned to the device: ${assignmentError.message}`,
            'error'
          );
        }
      }

      setShowCreateModal(false);
      setSetupDeviceId(null);
      setFormData({
        name: '',
        rows: 4,
        cols: 20,
        font: 'mechanical',
        sound_enabled: true,
        message_interval: 15,
        background_color: DEFAULT_BOARD_BACKGROUND_COLOR,
      });
      setSelectedBoard(createdBoard);
      loadBoards();

      if (setupAssignmentSucceeded) {
        setEditingMessage(null);
        setMessageText('');
        setMessageAlignments(normalizeRowArray([], createdBoard.rows, DEFAULT_ROW_ALIGN));
        setMessageColors(normalizeRowArray([], createdBoard.rows, DEFAULT_ROW_COLOR));
        setMessageStatus('active');
        setMessageScheduleStart('');
        setMessageScheduleEnd('');
        setShowMessageModal(true);
        showToast('Board created and assigned. Add its first message.');
      } else if (!deviceIdToAssign) {
        showToast('Board created successfully!');
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenSettings = (board) => {
    setFormData({ 
      name: board.name, 
      rows: board.rows, 
      cols: board.cols,
      font: board.font || 'mechanical',
      sound_enabled: board.sound_enabled !== false,
      message_interval: board.message_interval || 15,
      background_color: getBoardBackground(board),
    });
    setOriginalDimensions({ rows: board.rows, cols: board.cols });
    setShowSettingsModal(true);
  };

  const handleSaveSettings = async () => {
    if (!formData.name.trim()) {
      showToast('Please enter a board name', 'error');
      return;
    }
    
    const dimensionsChanged = 
      parseInt(formData.rows) !== originalDimensions.rows || 
      parseInt(formData.cols) !== originalDimensions.cols;
    
    if (dimensionsChanged && messages.length > 0) {
      if (!confirm('Changing rows or columns may affect how your messages display. Continue?')) {
        return;
      }
    }

    const backgroundColor = normalizeBoardBackground(formData.background_color);
    const currentBackgroundColor = getBoardBackground(selectedBoard);
    const changesToCustomBackground = (
      backgroundColor !== currentBackgroundColor
      && backgroundColor !== DEFAULT_BOARD_BACKGROUND_COLOR
    );
    if (!isPro && changesToCustomBackground) {
      showToast('Custom board backgrounds require a Pro subscription.', 'error');
      return;
    }
    
    setSaving(true);
    try {
      const updatePayload = {
        name: formData.name.trim(),
        rows: parseInt(formData.rows),
        cols: parseInt(formData.cols),
        font: formData.font,
        soundEnabled: formData.sound_enabled,
        messageInterval: formData.message_interval
      };
      if (isPro || backgroundColor === DEFAULT_BOARD_BACKGROUND_COLOR) {
        updatePayload.backgroundColor = backgroundColor;
      }

      await api.updateBoard(selectedBoard.id, updatePayload);
      setShowSettingsModal(false);
      
      // Update selected board locally
      setSelectedBoard(prev => ({
        ...prev,
        name: formData.name.trim(),
        rows: parseInt(formData.rows),
        cols: parseInt(formData.cols),
        font: formData.font,
        sound_enabled: formData.sound_enabled,
        message_interval: formData.message_interval,
        ...(updatePayload.backgroundColor
          ? { background_color: updatePayload.backgroundColor }
          : {}),
      }));
      
      loadBoards();
      showToast('Board settings saved!');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateBoard = async (board) => {
    if (!canCreateBoard) {
      showToast('Upgrade to Pro to add more boards.', 'error');
      return;
    }
    try {
      await api.duplicateBoard(board.id);
      loadBoards();
      showToast('Board duplicated!');
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleDeleteBoard = async (board) => {
    if (!confirm(`Are you sure you want to delete "${board.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await api.deleteBoard(board.id);
      if (selectedBoard?.id === board.id) {
        setSelectedBoard(null);
        setMessages([]);
      }
      loadBoards();
      showToast('Board deleted');
    } catch (error) {
      showToast(error.message, 'error');
    }
  };
  
  // Message handlers
  const handleAddMessage = () => {
    setEditingMessage(null);
    setMessageText('');
    setMessageAlignments(normalizeRowArray([], selectedBoard?.rows, DEFAULT_ROW_ALIGN));
    setMessageColors(normalizeRowArray([], selectedBoard?.rows, DEFAULT_ROW_COLOR));
    setMessageStatus('active');
    setMessageScheduleStart('');
    setMessageScheduleEnd('');
    setShowMessageModal(true);
  };
  
  const handleEditMessage = (message) => {
    setEditingMessage(message);
    const text = Array.isArray(message.content)
      ? message.content.join('\n')
      : (message.content || message.text || '');
    setMessageText(toSafeString(text));
    setMessageAlignments(
      normalizeRowArray(message.row_alignments || message.rowAlignments, selectedBoard?.rows, DEFAULT_ROW_ALIGN)
    );
    setMessageColors(
      normalizeRowArray(message.row_colors || message.rowColors, selectedBoard?.rows, DEFAULT_ROW_COLOR)
    );
    setMessageStatus(message.status || (message.is_active === false ? 'inactive' : 'active'));
    const toLocalInputValue = (value) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const offset = date.getTimezoneOffset() * 60000;
      return new Date(date.getTime() - offset).toISOString().slice(0, 16);
    };
    setMessageScheduleStart(toLocalInputValue(message.schedule_start || message.scheduleStart));
    setMessageScheduleEnd(toLocalInputValue(message.schedule_end || message.scheduleEnd));
    setShowMessageModal(true);
  };
  
  const handleSaveMessage = async () => {
    const text = toSafeString(messageText);
    if (!text.trim()) {
      showToast('Please enter a message', 'error');
      return;
    }
    if (!isPro) {
      if (messageStatus !== 'active') {
        showToast('Message status controls require a Pro subscription.', 'error');
        return;
      }
      if (messageScheduleStart || messageScheduleEnd) {
        showToast('Message scheduling requires a Pro subscription.', 'error');
        return;
      }
    }
    const content = formatMessageRows(text, selectedBoard?.rows, selectedBoard?.cols);
    const rowAlignments = normalizeRowArray(messageAlignments, selectedBoard?.rows, DEFAULT_ROW_ALIGN);
    const rowColors = normalizeRowArray(messageColors, selectedBoard?.rows, DEFAULT_ROW_COLOR);
    const scheduleStart = messageScheduleStart ? new Date(messageScheduleStart).toISOString() : null;
    const scheduleEnd = messageScheduleEnd ? new Date(messageScheduleEnd).toISOString() : null;
    setSaving(true);
    try {
      if (editingMessage) {
        await api.updateMessage(selectedBoard.id, editingMessage.id, {
          content,
          row_alignments: rowAlignments,
          row_colors: rowColors,
          status: messageStatus,
          scheduleStart,
          scheduleEnd
        });
        showToast('Message updated!');
      } else {
        await api.createMessage(selectedBoard.id, {
          content,
          row_alignments: rowAlignments,
          row_colors: rowColors,
          status: messageStatus,
          scheduleStart,
          scheduleEnd
        });
        showToast('Message added!');
      }
      setShowMessageModal(false);
      setEditingMessage(null);
      setMessageText('');
      setMessageAlignments([]);
      setMessageColors([]);
      setMessageStatus('active');
      setMessageScheduleStart('');
      setMessageScheduleEnd('');
      loadMessages(selectedBoard.id);
      loadBoards(); // Refresh message counts
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteMessage = async (message) => {
    if (!confirm('Delete this message?')) return;
    try {
      await api.deleteMessage(selectedBoard.id, message.id);
      showToast('Message deleted');
      loadMessages(selectedBoard.id);
      loadBoards();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };
  
  const handleReorderMessage = async (index, direction) => {
    const newMessages = [...messages];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= messages.length) return;
    
    [newMessages[index], newMessages[targetIndex]] = [newMessages[targetIndex], newMessages[index]];
    setMessages(newMessages);
    
    try {
      await api.reorderMessages(selectedBoard.id, newMessages.map(m => m.id));
    } catch (error) {
      showToast(error.message, 'error');
      loadMessages(selectedBoard.id);
    }
  };

  const parseJsonArray = (value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed.startsWith('[')) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };

  const loadTemplates = async () => {
    if (!currentAccount?.id) return;
    try {
      setTemplatesLoading(true);
      const result = await api.getMessageTemplates(currentAccount.id);
      setTemplates(result.templates || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleOpenTemplateModal = () => {
    if (!isPro) {
      showToast('Message Bank is available on the Pro plan.', 'error');
      return;
    }
    setShowTemplateModal(true);
    loadTemplates();
  };

  const handleApplyTemplate = (template) => {
    const rawContent = Array.isArray(template.content)
      ? template.content
      : parseJsonArray(template.content) || [];
    const contentRows = rawContent.map(toSafeString);
    setMessageText(contentRows.join('\n'));
    setMessageAlignments(
      normalizeRowArray(
        template.row_alignments || template.rowAlignments,
        selectedBoard?.rows,
        DEFAULT_ROW_ALIGN
      )
    );
    setMessageColors(
      normalizeRowArray(
        template.row_colors || template.rowColors,
        selectedBoard?.rows,
        DEFAULT_ROW_COLOR
      )
    );
    setShowTemplateModal(false);
  };

  const handleSaveTemplate = async () => {
    if (!isPro) {
      showToast('Message Bank is available on the Pro plan.', 'error');
      return;
    }
    if (!currentAccount?.id) return;
    if (!selectedBoard) {
      showToast('Select a board first.', 'error');
      return;
    }
    const name = templateName.trim();
    if (!name) {
      showToast('Template name is required.', 'error');
      return;
    }
    const text = toSafeString(messageText);
    if (!text.trim()) {
      showToast('Enter a message before saving.', 'error');
      return;
    }
    const content = formatMessageRows(text, selectedBoard.rows, selectedBoard.cols);
    const rowAlignments = normalizeRowArray(messageAlignments, selectedBoard.rows, DEFAULT_ROW_ALIGN);
    const rowColors = normalizeRowArray(messageColors, selectedBoard.rows, DEFAULT_ROW_COLOR);
    try {
      await api.createMessageTemplate(currentAccount.id, {
        name,
        content,
        rowAlignments,
        rowColors
      });
      showToast('Saved to Message Bank!');
      setTemplateName('');
      loadTemplates();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Delete this template?')) return;
    try {
      await api.deleteMessageTemplate(templateId);
      showToast('Template deleted');
      loadTemplates();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  // KPI calculations
  const totalBoards = boards.length;
  const resolveBoardStatus = (board) => {
    if (board.board_status) return board.board_status;
    if (board.is_paused) return 'paused';
    if (board.is_active) return 'active';
    return 'inactive';
  };
  const activeBoards = boards.filter(b => resolveBoardStatus(b) === 'active').length;
  const totalMessages = boards.reduce((sum, b) => sum + (b.message_count || 0), 0);
  const maxBoards = accountTier?.features?.maxBoards ?? 1;
  const canCreateBoard = isPro || totalBoards < maxBoards;

  const handleUpgradeToPro = async () => {
    if (!currentAccount?.id) return;
    try {
      const result = await api.createCheckout(currentAccount.id);
      if (result.url) {
        window.location.href = result.url;
      } else {
        showToast('Unable to start checkout. Please try again.', 'error');
      }
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  if (!canUseBoards && !isPro) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Split-Flap Boards</h1>
          <div className="page-header-actions">
            <div className="header-video-links">
              <a
                className="header-video-link"
                href="https://www.youtube.com/watch?v=1di4APAyhfo"
                target="_blank"
                rel="noreferrer"
              >
                <Icons.Play /> Boards Basics
              </a>
              <a
                className="header-video-link"
                href="https://www.youtube.com/watch?v=uyBMi_xgpH8"
                target="_blank"
                rel="noreferrer"
              >
                <Icons.Play /> Boards Power Features
              </a>
            </div>
          </div>
        </div>
        <GlassCard className="upgrade-prompt">
          <div className="upgrade-content">
            <div className="upgrade-icon">
              <Icons.Boards />
            </div>
            <h2>Upgrade to Pro</h2>
            <p>Split-flap boards and Marathon Mode game queuing are available on the Pro plan. Upgrade to create beautiful retro displays and run back-to-back trivia sessions.</p>
            <Button variant="primary" size="lg" onClick={handleUpgradeToPro}>
              Upgrade to Pro - $19/month
            </Button>
          </div>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="page-container community-page">
      <div className="page-header">
        <div>
          <h1>Split-Flap Boards</h1>
          <p className="page-subtitle">Create and manage your retro displays</p>
        </div>
        <div className="page-header-actions">
          <Button
            onClick={() => {
              setSetupDeviceId(null);
              setFormData({
                name: '',
                rows: 4,
                cols: 20,
                font: 'mechanical',
                sound_enabled: true,
                message_interval: 15,
                background_color: DEFAULT_BOARD_BACKGROUND_COLOR,
              });
              setShowCreateModal(true);
            }}
            icon={Icons.Plus}
            disabled={!canCreateBoard}
            title={!canCreateBoard ? 'Only Available in Pro Subscription' : undefined}
          >
            New Board
          </Button>
          {!canCreateBoard && !isPro && (
            <Button
              variant="secondary"
              onClick={handleUpgradeToPro}
            >
              Upgrade to Pro - $19/month
            </Button>
          )}
          <div className="header-video-links">
            <a
              className="header-video-link"
              href="https://www.youtube.com/watch?v=1di4APAyhfo"
              target="_blank"
              rel="noreferrer"
            >
              <Icons.Play /> Boards Basics
            </a>
            <a
              className="header-video-link"
              href="https://www.youtube.com/watch?v=uyBMi_xgpH8"
              target="_blank"
              rel="noreferrer"
            >
              <Icons.Play /> Boards Power Features
            </a>
          </div>
        </div>
      </div>

      {/* KPI Row - Bento Box */}
      <div className="kpi-grid">
        <KPICard 
          title="Total Boards" 
          value={totalBoards}
          icon={Icons.Boards}
        />
        <KPICard 
          title="Active Boards" 
          value={activeBoards}
          change={activeBoards > 0 ? '+' + activeBoards : '0'}
          changeType={activeBoards > 0 ? 'positive' : 'neutral'}
          icon={Icons.Zap}
        />
        <KPICard 
          title="Total Messages" 
          value={totalMessages}
          icon={Icons.Activity}
        />
      </div>

      {loading ? (
        <div className="loading-container">
          <Spinner size="lg" />
        </div>
      ) : boards.length === 0 ? (
        <EmptyState
          icon={Icons.Boards}
          title="No boards yet"
          description="Create your first split-flap board to get started"
          action={() => {
            setSetupDeviceId(null);
            setShowCreateModal(true);
          }}
          actionLabel="Create Board"
        />
      ) : (
        <div className="boards-layout">
          {/* Board List Sidebar */}
          <div className="boards-list">
            <h3>Your Boards</h3>
            {boards.map(board => (
              <div 
                key={board.id} 
                className={`board-list-item ${selectedBoard?.id === board.id ? 'selected' : ''}`}
                onClick={() => setSelectedBoard(board)}
              >
                <div className="board-list-info">
                  <span className="board-list-name">{board.name}</span>
                  <span className="board-list-meta">{board.rows}×{board.cols} • {board.message_count || 0} messages</span>
                </div>
                {(() => {
                  const status = resolveBoardStatus(board);
                  const variant = status === 'active' ? 'success' : status === 'paused' ? 'warning' : 'default';
                  const label = status === 'active' ? 'Active' : status === 'paused' ? 'Paused' : 'Inactive';
                  return (
                    <Badge variant={variant} size="sm">
                      {label}
                    </Badge>
                  );
                })()}
              </div>
            ))}
          </div>
          
          {/* Board Detail Panel */}
          {selectedBoard && (
            <div className="board-detail">
              {/* Preview Section */}
              <GlassCard className="board-preview-card">
                <div className="board-preview-header">
                  <h3>{selectedBoard.name}</h3>
                  <div className="board-preview-actions">
                    <Button variant="ghost" size="sm" icon={Icons.Settings} onClick={() => handleOpenSettings(selectedBoard)}>
                      Settings
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Icons.Copy}
                      onClick={() => handleDuplicateBoard(selectedBoard)}
                      disabled={!canCreateBoard}
                      title={!canCreateBoard ? 'Only Available in Pro Subscription' : undefined}
                    >
                      Duplicate
                    </Button>
                    <Button variant="ghost" size="sm" icon={Icons.Trash} onClick={() => handleDeleteBoard(selectedBoard)} className="btn-danger-ghost">
                      Delete
                    </Button>
                  </div>
                </div>
                
                <SplitFlapPreview 
                  rows={selectedBoard.rows}
                  cols={selectedBoard.cols}
                  messages={messages.length > 0 ? messages : [{ content: 'WELCOME' }]}
                  font={selectedBoard.font || 'mechanical'}
                  currentMessageIndex={currentPreviewIndex}
                  backgroundColor={getBoardBackground(selectedBoard)}
                />
                
                {messages.length > 1 && (
                  <div className="preview-indicators">
                    {messages.map((_, idx) => (
                      <button 
                        key={idx}
                        className={`preview-dot ${idx === currentPreviewIndex ? 'active' : ''}`}
                        onClick={() => setCurrentPreviewIndex(idx)}
                      />
                    ))}
                  </div>
                )}
                
                <div className="board-info-bar">
                  <span><Icons.Activity /> {selectedBoard.rows} rows × {selectedBoard.cols} columns</span>
                  <span>Font: {selectedBoard.font || 'Mechanical'}</span>
                  <span className="board-background-summary">
                    <span
                      className="board-background-summary-swatch"
                      style={{ backgroundColor: getBoardBackground(selectedBoard) }}
                    />
                    Background
                  </span>
                  <span>{selectedBoard.sound_enabled !== false ? '🔊 Sound On' : '🔇 Sound Off'}</span>
                </div>
              </GlassCard>
              
              {/* Messages Section */}
              <GlassCard className="board-messages-card">
                <div className="messages-header">
                  <h3>Messages</h3>
                  <Button variant="primary" size="sm" icon={Icons.Plus} onClick={handleAddMessage}>
                    Add Message
                  </Button>
                </div>
                
                {messagesLoading ? (
                  <div className="loading-container small">
                    <Spinner />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="empty-messages">
                    <Icons.Activity />
                    <p>No messages yet</p>
                    <span>Add messages to display on your board</span>
                  </div>
                ) : (
                  <div className="messages-list">
                    {messages.map((message, index) => (
                      <div key={message.id} className="message-item">
                        <div className="message-order">
                          <button 
                            className="order-btn"
                            onClick={() => handleReorderMessage(index, 'up')}
                            disabled={index === 0}
                          >
                            ↑
                          </button>
                          <span>{index + 1}</span>
                          <button 
                            className="order-btn"
                            onClick={() => handleReorderMessage(index, 'down')}
                            disabled={index === messages.length - 1}
                          >
                            ↓
                          </button>
                        </div>
                        <div className="message-content">
                          <p className="message-text">
                            {Array.isArray(message.content)
                              ? message.content.filter(Boolean).join(' / ') || '(Empty message)'
                              : (message.content || message.text || '(Empty message)')}
                          </p>
                          {message.duration && (
                            <span className="message-duration">{message.duration}s display time</span>
                          )}
                          <div className="message-status-row">
                            {(() => {
                              const status = message.status || (message.is_active === false ? 'inactive' : 'active');
                              const variant = status === 'active' ? 'success' : status === 'scheduled' ? 'warning' : 'default';
                              const label = status === 'active' ? 'Active' : status === 'scheduled' ? 'Scheduled' : 'Inactive';
                              return <Badge variant={variant} size="sm">{label}</Badge>;
                            })()}
                            {(message.schedule_start || message.schedule_end) && (
                              <span className="message-schedule">
                                {message.schedule_start ? new Date(message.schedule_start).toLocaleString() : 'Anytime'}
                                {' → '}
                                {message.schedule_end ? new Date(message.schedule_end).toLocaleString() : 'No end'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="message-actions">
                          <Button variant="ghost" size="sm" icon={Icons.Edit} onClick={() => handleEditMessage(message)} />
                          <Button variant="ghost" size="sm" icon={Icons.Trash} onClick={() => handleDeleteMessage(message)} className="btn-danger-ghost" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </GlassCard>
            </div>
          )}
        </div>
      )}

      {/* Create Board Modal */}
      <Modal
        isOpen={showCreateModal}
        title="Create New Board"
        onClose={() => {
          setShowCreateModal(false);
          setSetupDeviceId(null);
        }}
      >
        <div className="modal-form">
          <Input
            label="Board Name"
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Lobby Display"
          />
          <div className="form-row">
            <Input
              label="Rows"
              type="number"
              min={1}
              max={10}
              value={formData.rows}
              onChange={e => setFormData({ ...formData, rows: e.target.value })}
            />
            <Input
              label="Columns"
              type="number"
              min={10}
              max={30}
              value={formData.cols}
              onChange={e => setFormData({ ...formData, cols: e.target.value })}
            />
          </div>
          <BoardBackgroundPicker
            value={formData.background_color}
            onChange={(background_color) => setFormData({ ...formData, background_color })}
            disabled={!isPro}
          />
          <div className="input-group">
            <label className="input-label">Message rotation</label>
            <div className="range-row">
              <input
                type="range"
                className="range-field"
                min={15}
                max={60}
                step={5}
                value={formData.message_interval}
                onChange={e => setFormData({ ...formData, message_interval: parseInt(e.target.value, 10) })}
              />
              <span className="range-value">{formData.message_interval}s</span>
            </div>
          </div>
          <div className="input-group">
            <label className="input-label">Font Style</label>
            <select 
              className="select-field"
              value={formData.font}
              onChange={e => setFormData({ ...formData, font: e.target.value })}
              disabled={!isPro}
              title={!isPro ? 'Only Available in Pro Subscription' : undefined}
            >
              <option value="mechanical">Mechanical</option>
              <option value="terminal">Terminal</option>
              <option value="classic">Classic</option>
              <option value="modern">Modern</option>
              <option value="condensed">Condensed</option>
            </select>
          </div>
          <div className="toggle-row">
            <label className="toggle-label">
              <span>Sound Effects</span>
              <span className="toggle-hint">Play flip sound during animations</span>
            </label>
            <button 
              className={`toggle-switch ${formData.sound_enabled ? 'on' : ''}`}
              onClick={() => setFormData({ ...formData, sound_enabled: !formData.sound_enabled })}
            >
              <span className="toggle-knob" />
            </button>
          </div>
        </div>
        <div className="modal-actions">
          <Button
            variant="ghost"
            onClick={() => {
              setShowCreateModal(false);
              setSetupDeviceId(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleCreateBoard} loading={saving}>Create Board</Button>
        </div>
      </Modal>

      {/* Board Settings Modal */}
      <Modal isOpen={showSettingsModal} title="Board Settings" onClose={() => setShowSettingsModal(false)}>
        <div className="modal-form">
          <Input
            label="Board Name"
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Lobby Display"
          />
          <div className="form-row">
            <Input
              label="Rows"
              type="number"
              min={1}
              max={10}
              value={formData.rows}
              onChange={e => setFormData({ ...formData, rows: e.target.value })}
            />
            <Input
              label="Columns"
              type="number"
              min={10}
              max={30}
              value={formData.cols}
              onChange={e => setFormData({ ...formData, cols: e.target.value })}
            />
          </div>
          <div className="input-group">
            <label className="input-label">Message rotation</label>
            <div className="range-row">
              <input
                type="range"
                className="range-field"
                min={15}
                max={60}
                step={5}
                value={formData.message_interval}
                onChange={e => setFormData({ ...formData, message_interval: parseInt(e.target.value, 10) })}
              />
              <span className="range-value">{formData.message_interval}s</span>
            </div>
          </div>
          {(parseInt(formData.rows) !== originalDimensions.rows || parseInt(formData.cols) !== originalDimensions.cols) && (
            <div className="settings-warning">
              <Icons.Help />
              <span>Changing rows or columns may reset all messages for this board.</span>
            </div>
          )}
          <BoardBackgroundPicker
            value={formData.background_color}
            onChange={(background_color) => setFormData({ ...formData, background_color })}
            disabled={!isPro}
          />
          <div className="input-group">
            <label className="input-label">Font Style</label>
            <select 
              className="select-field"
              value={formData.font}
              onChange={e => setFormData({ ...formData, font: e.target.value })}
              disabled={!isPro}
              title={!isPro ? 'Only Available in Pro Subscription' : undefined}
            >
              <option value="mechanical">Mechanical</option>
              <option value="terminal">Terminal</option>
              <option value="classic">Classic</option>
              <option value="modern">Modern</option>
              <option value="condensed">Condensed</option>
            </select>
          </div>
          <div className="toggle-row">
            <label className="toggle-label">
              <span>Sound Effects</span>
              <span className="toggle-hint">Play flip sound during animations</span>
            </label>
            <button 
              className={`toggle-switch ${formData.sound_enabled ? 'on' : ''}`}
              onClick={() => setFormData({ ...formData, sound_enabled: !formData.sound_enabled })}
            >
              <span className="toggle-knob" />
            </button>
          </div>
        </div>
        <div className="modal-actions">
          <Button variant="ghost" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
          <Button onClick={handleSaveSettings} loading={saving}>Save Settings</Button>
        </div>
      </Modal>

      {/* Add/Edit Message Modal */}
      <Modal 
        isOpen={showMessageModal} 
        title={editingMessage ? 'Edit Message' : 'Add Message'} 
        onClose={() => {
          setShowMessageModal(false);
          setEditingMessage(null);
          setMessageText('');
          setMessageAlignments([]);
          setMessageColors([]);
          setMessageStatus('active');
          setMessageScheduleStart('');
          setMessageScheduleEnd('');
        }}
      >
        <div className="modal-form">
          <div className="input-group">
            <label className="input-label">Message Text</label>
            <textarea
              className="textarea-field"
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
              placeholder="Enter your message..."
              rows={3}
            />
            <p className="input-help">
              {messageText.length} characters • Best results: {selectedBoard?.cols || 20} characters per line
            </p>
          </div>
          <div className="message-bank-bar">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleOpenTemplateModal}
              disabled={!isPro}
              title={!isPro ? 'Only Available in Pro Subscription' : undefined}
            >
              Message Bank
            </Button>
            <div className="message-bank-save">
              <Input
                label="Save As Template"
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                placeholder="Template name"
                disabled={!isPro}
                title={!isPro ? 'Only Available in Pro Subscription' : undefined}
              />
              <Button
                size="sm"
                onClick={handleSaveTemplate}
                disabled={!isPro}
                title={!isPro ? 'Only Available in Pro Subscription' : undefined}
              >
                Save
              </Button>
            </div>
          </div>
          <div className="message-status-fields">
            <Select
              label="Message Status"
              value={messageStatus}
              onChange={e => setMessageStatus(e.target.value)}
              disabled={!isPro}
              title={!isPro ? 'Only Available in Pro Subscription' : undefined}
              options={[
                { value: 'active', label: 'Active (show always)' },
                { value: 'scheduled', label: 'Scheduled (show in time window)', disabled: !isPro },
                { value: 'inactive', label: 'Inactive (hidden)', disabled: !isPro }
              ]}
            />
            {messageStatus === 'scheduled' && (
              <div className="message-schedule-fields">
                <Input
                  label="Start Time"
                  type="datetime-local"
                  value={messageScheduleStart}
                  onChange={e => setMessageScheduleStart(e.target.value)}
                  disabled={!isPro}
                  title={!isPro ? 'Only Available in Pro Subscription' : undefined}
                />
                <Input
                  label="End Time"
                  type="datetime-local"
                  value={messageScheduleEnd}
                  onChange={e => setMessageScheduleEnd(e.target.value)}
                  disabled={!isPro}
                  title={!isPro ? 'Only Available in Pro Subscription' : undefined}
                />
              </div>
            )}
          </div>

          {selectedBoard && (
            <div className="row-style-grid">
              {Array.from({ length: selectedBoard.rows }).map((_, rowIdx) => (
                <div key={rowIdx} className="row-style-item">
                  <span className="row-style-label">Row {rowIdx + 1}</span>
                  <select
                    className="select-field"
                    value={messageAlignments[rowIdx] || DEFAULT_ROW_ALIGN}
                    onChange={(e) => {
                      const updated = [...messageAlignments];
                      updated[rowIdx] = e.target.value;
                      setMessageAlignments(updated);
                    }}
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                  <input
                    type="color"
                    className="color-field"
                    value={messageColors[rowIdx] || DEFAULT_ROW_COLOR}
                    onChange={(e) => {
                      const updated = [...messageColors];
                      updated[rowIdx] = e.target.value;
                      setMessageColors(updated);
                    }}
                    disabled={!isPro}
                    title={!isPro ? 'Only Available in Pro Subscription' : undefined}
                  />
                </div>
              ))}
            </div>
          )}
          
          {/* Mini Preview */}
          {selectedBoard && messageText && (
            <div className="message-preview">
              <label className="input-label">Preview</label>
              <SplitFlapPreview 
                rows={selectedBoard.rows}
                cols={selectedBoard.cols}
                messages={[{
                  content: formatMessageRows(messageText, selectedBoard.rows, selectedBoard.cols),
                  row_alignments: normalizeRowArray(messageAlignments, selectedBoard.rows, DEFAULT_ROW_ALIGN),
                  row_colors: normalizeRowArray(messageColors, selectedBoard.rows, DEFAULT_ROW_COLOR)
                }]}
                font={selectedBoard.font || 'mechanical'}
                currentMessageIndex={0}
                backgroundColor={getBoardBackground(selectedBoard)}
              />
            </div>
          )}
        </div>
        <div className="modal-actions">
          <Button variant="ghost" onClick={() => {
            setShowMessageModal(false);
            setEditingMessage(null);
            setMessageText('');
            setMessageAlignments([]);
            setMessageColors([]);
            setMessageStatus('active');
            setMessageScheduleStart('');
            setMessageScheduleEnd('');
          }}>Cancel</Button>
          <Button onClick={handleSaveMessage} loading={saving}>
            {editingMessage ? 'Save Changes' : 'Add Message'}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={showTemplateModal}
        title="Message Bank"
        onClose={() => setShowTemplateModal(false)}
      >
        <div className="modal-form">
          {templatesLoading ? (
            <div className="loading-container small">
              <Spinner />
            </div>
          ) : templates.length === 0 ? (
            <GlassCard className="no-devices-warning" padding="sm">
              <Icons.Activity />
              <p>No message templates yet. Save one to get started.</p>
            </GlassCard>
          ) : (
            <div className="template-list">
              {templates.map((template) => {
                const rawContent = Array.isArray(template.content)
                  ? template.content
                  : parseJsonArray(template.content) || [];
                const preview = rawContent.filter(Boolean).join(' / ') || '(Empty template)';
                return (
                  <GlassCard key={template.id} className="template-item" padding="sm">
                    <div className="template-info">
                      <p className="template-name">{template.name}</p>
                      <p className="template-preview">{preview}</p>
                    </div>
                    <div className="template-actions">
                      <Button variant="secondary" size="sm" onClick={() => handleApplyTemplate(template)}>
                        Load
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Icons.Trash}
                        className="btn-danger-ghost"
                        onClick={() => handleDeleteTemplate(template.id)}
                      />
                    </div>
                  </GlassCard>
                );
              })}
            </div>
          )}
        </div>
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============================================================================
// GAMES PAGE
// ============================================================================

function GamesPage({ onUpgrade }) {
  const { currentAccount, canUseAI, isPro, accountTier, refreshTier } = useAuth();
  const [activeTab, setActiveTab] = useState('my-games');
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [showGoLive, setShowGoLive] = useState(false);
  const [selectedGameForLive, setSelectedGameForLive] = useState(null);
  const [communityGames, setCommunityGames] = useState([]);
  const [communityPreviewOpen, setCommunityPreviewOpen] = useState(false);
  const [communityPreviewLoading, setCommunityPreviewLoading] = useState(false);
  const [communityPreviewGame, setCommunityPreviewGame] = useState(null);
  const [communityPreviewPage, setCommunityPreviewPage] = useState(0);
  const [scheduledSessions, setScheduledSessions] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [editingScheduleSession, setEditingScheduleSession] = useState(null);
  const [endingSessionId, setEndingSessionId] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  const [showHistoryDetail, setShowHistoryDetail] = useState(false);
  const [questionBank, setQuestionBank] = useState([]);
  const [showAddToGameModal, setShowAddToGameModal] = useState(false);
  const [selectedBankQuestion, setSelectedBankQuestion] = useState(null);
  const [selectedGameId, setSelectedGameId] = useState('');
  const [addingToGame, setAddingToGame] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [communityCategoryFilter, setCommunityCategoryFilter] = useState('');
  const [communitySort, setCommunitySort] = useState('popular');
  const [showMySharedGames, setShowMySharedGames] = useState(false);
  const [queues, setQueues] = useState([]);
  const [queuesLoading, setQueuesLoading] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [queueItems, setQueueItems] = useState([]);
  const [queueDetailLoading, setQueueDetailLoading] = useState(false);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [showQueueCreateModal, setShowQueueCreateModal] = useState(false);
  const [queueFormName, setQueueFormName] = useState('');
  const [queueFormDeviceId, setQueueFormDeviceId] = useState('');
  const [queueFormLobbyMinutes, setQueueFormLobbyMinutes] = useState(5);
  const [queueFormCooldownMinutes, setQueueFormCooldownMinutes] = useState(5);
  const [queueFormLoopEnabled, setQueueFormLoopEnabled] = useState(false);
  const [queueFormSaving, setQueueFormSaving] = useState(false);
  const [queueAddGameId, setQueueAddGameId] = useState('');
  const [queueActionLoading, setQueueActionLoading] = useState(false);
  const [queueDevices, setQueueDevices] = useState([]);
  const [planRestriction, setPlanRestriction] = useState(null);
  const communityPreviewPageSize = 6;

  const freeLimits = accountTier?.limits || accountTier?.features || {};
  const maxFreeGames = Number(freeLimits.maxGames ?? 3);
  const maxFreeQuestions = Number(freeLimits.maxQuestionsPerGame ?? 10);
  const maxFreeHostedGames = Number(freeLimits.maxHostedGamesPerMonth ?? 1);
  const accountGameCount = Number(accountTier?.usage?.gameCount ?? games.length);
  const hostedGamesThisMonth = Number(accountTier?.usage?.hostedGamesThisMonth ?? 0);
  const isFree = accountTier?.tier === 'free';
  const gameCreationLimitReached = isFree && accountGameCount >= maxFreeGames;
  const hasTrialGameCarryover = isFree && accountGameCount > maxFreeGames;
  const monthlyHostLimitReached = isFree && hostedGamesThisMonth >= maxFreeHostedGames;
  const hostedGamesResetAt = accountTier?.usage?.hostedGamesResetAt;
  const hostedGamesResetLabel = hostedGamesResetAt
    ? new Date(hostedGamesResetAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })
    : 'the first of next month';

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const showPlanRestriction = (message, code = 'PRO_FEATURE_REQUIRED') => {
    setPlanRestriction({ message, code });
    showToast(message, 'error');
  };

  const handleRestrictedError = (error) => {
    if (error?.upgradeRequired) {
      showPlanRestriction(error.message, error.code);
      return;
    }
    showToast(error.message, 'error');
  };

  useEffect(() => {
    if (currentAccount?.id) {
      loadGames();
      loadCategories();
    }
  }, [currentAccount?.id]);

  useEffect(() => {
    if (currentAccount?.id && activeTab === 'my-games') {
      loadGames();
    }
  }, [currentAccount?.id, activeTab, categoryFilter]);

  useEffect(() => {
    if (currentAccount?.id && activeTab === 'my-games') {
      loadScheduledSessions();
    }
  }, [currentAccount?.id, activeTab]);

  useEffect(() => {
    if (!currentAccount?.id || activeTab !== 'my-games') return undefined;
    loadActiveSessions();
    const interval = setInterval(loadActiveSessions, 10000);
    return () => clearInterval(interval);
  }, [currentAccount?.id, activeTab]);

  useEffect(() => {
    if (!currentAccount?.id || activeTab !== 'queues') return;
    loadQueues();
    loadQueueDevices();
    if (!games.length) {
      loadGames();
    }
  }, [currentAccount?.id, activeTab]);

  useEffect(() => {
    if (activeTab === 'community') loadCommunityGames();
    else if (activeTab === 'history' && isPro) loadHistory();
    else if (activeTab === 'question-bank') loadQuestionBank();
  }, [activeTab, communityCategoryFilter, communitySort, isPro]);

  const mySharedGames = useMemo(() => {
    return games.filter(game =>
      game.shared_status === 'public' || game.shared_status === 'pending_moderation'
    );
  }, [games]);

  const loadGames = async () => {
    try {
      setLoading(true);
      const result = await api.getTriviaGames(currentAccount.id, {
        categoryId: categoryFilter || undefined
      });
      setGames(result.games || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const result = await api.getTriviaCategories();
      setCategories(result.categories || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadScheduledSessions = async () => {
    try {
      setScheduleLoading(true);
      const result = await api.getScheduledTriviaSessions(currentAccount.id);
      setScheduledSessions(result.sessions || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setScheduleLoading(false);
    }
  };

  const loadActiveSessions = async () => {
    try {
      const result = await api.getActiveTriviaSessions(currentAccount.id);
      setActiveSessions(result.sessions || []);
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const loadCommunityGames = async () => {
    try {
      setLoading(true);
      const result = await api.browseCommunityGames({
        sort: communitySort,
        categoryId: communityCategoryFilter || undefined
      });
      setCommunityGames(result.games || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      setLoading(true);
      const result = await api.getTriviaHistory(currentAccount.id);
      setHistory(result.history || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistoryDetail = async (historyItem) => {
    if (!historyItem?.id) return;
    setHistoryDetailLoading(true);
    setShowHistoryDetail(true);
    try {
      const result = await api.getTriviaHistoryDetail(historyItem.id);
      setHistoryDetail(result);
    } catch (error) {
      showToast(error.message || 'Failed to load game details', 'error');
      setShowHistoryDetail(false);
    } finally {
      setHistoryDetailLoading(false);
    }
  };

  const handleCloseHistoryDetail = () => {
    setShowHistoryDetail(false);
    setHistoryDetail(null);
    setHistoryDetailLoading(false);
  };

  const loadQuestionBank = async () => {
    try {
      setLoading(true);
      const result = await api.getQuestionBank(currentAccount.id);
      setQuestionBank(result.questions || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadQueues = async () => {
    try {
      setQueuesLoading(true);
      const result = await api.getGameQueues(currentAccount.id);
      setQueues(result.queues || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setQueuesLoading(false);
    }
  };

  const loadQueueDevices = async () => {
    try {
      const result = await api.getDevices(currentAccount.id);
      setQueueDevices(result.devices || []);
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  };

  const resetQueueForm = (queue = null) => {
    setQueueFormName(queue?.name || '');
    setQueueFormDeviceId(queue?.device_id || '');
    setQueueFormLobbyMinutes(
      Math.max(1, Math.round((queue?.lobby_duration_seconds || 300) / 60))
    );
    setQueueFormCooldownMinutes(
      Math.max(1, Math.round((queue?.cooldown_duration_seconds || 300) / 60))
    );
    setQueueFormLoopEnabled(Boolean(queue?.loop_enabled));
  };

  const openQueueDetail = async (queueId) => {
    setQueueDetailLoading(true);
    setShowQueueModal(true);
    try {
      const result = await api.getGameQueue(queueId);
      setSelectedQueue(result.queue || null);
      setQueueItems(result.items || []);
      const fallbackGameId = games.find((game) => Number(game.question_count) > 0)?.id || '';
      setQueueAddGameId(result.items?.[0]?.quiz_id || fallbackGameId);
      resetQueueForm(result.queue || null);
    } catch (error) {
      showToast(error.message, 'error');
      setShowQueueModal(false);
    } finally {
      setQueueDetailLoading(false);
    }
  };

  const handleCreateGame = () => {
    if (gameCreationLimitReached) {
      showPlanRestriction(
        `Free plan includes up to ${maxFreeGames} games. Upgrade to Pro for unlimited games.`,
        'FREE_GAME_LIMIT_REACHED'
      );
      return;
    }
    setEditingGame(null);
    setShowEditor(true);
  };

  const normalizeGameForEditor = (game) => {
    const questions = (game?.questions || []).map((q) => {
      const answers = (q.answers || []).map((answer, index) => ({
        id: answer.id || `ans-${q.id}-${index}`,
        text: answer.text || ''
      }));
      const correctAnswer = (q.answers || []).find((answer) => answer.isCorrect || answer.is_correct);
      return {
        id: q.id,
        prompt: q.prompt || '',
        answers,
        correct_answer_id: correctAnswer?.id || null,
        time_limit_seconds: q.time_limit_seconds ?? q.timeLimitSeconds ?? 20,
        points: q.points ?? 100,
        explanation: q.explanation || ''
      };
    });

    return {
      ...game,
      questions
    };
  };

  const handleEditGame = async (game) => {
    setEditorLoading(true);
    try {
      const result = await api.getTriviaGame(game.id);
      setEditingGame(normalizeGameForEditor(result.game));
      setShowEditor(true);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setEditorLoading(false);
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (!confirm('Delete this game? This cannot be undone.')) return;
    try {
      await api.deleteTriviaGame(gameId);
      await Promise.all([loadGames(), refreshTier()]);
      setPlanRestriction(null);
      showToast('Game deleted');
    } catch (error) {
      handleRestrictedError(error);
    }
  };

  const handleDuplicateGame = async (gameId) => {
    if (gameCreationLimitReached) {
      showPlanRestriction(
        `Free plan includes up to ${maxFreeGames} games. Upgrade to Pro for unlimited games.`,
        'FREE_GAME_LIMIT_REACHED'
      );
      return;
    }
    try {
      await api.duplicateTriviaGame(gameId, currentAccount.id);
      await Promise.all([loadGames(), refreshTier()]);
      showToast('Game duplicated!');
    } catch (error) {
      handleRestrictedError(error);
    }
  };

  const handleShareGame = async (gameId) => {
    if (isFree) {
      showPlanRestriction(
        'Sharing games with the community is a Pro feature.',
        'PRO_FEATURE_REQUIRED'
      );
      return;
    }
    try {
      await api.shareTriviaGame(gameId);
      showToast('Game submitted for community sharing.');
      loadGames();
    } catch (error) {
      handleRestrictedError(error);
    }
  };

  const handleUnshareGame = async (gameId) => {
    try {
      await api.unshareTriviaGame(gameId);
      showToast('Game removed from community.');
      loadGames();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleGoLive = (game) => {
    if (monthlyHostLimitReached) {
      showPlanRestriction(
        `You've used your ${maxFreeHostedGames} free hosted game this month. Upgrade to Pro for unlimited hosting.`,
        'FREE_MONTHLY_HOST_LIMIT_REACHED'
      );
      return;
    }
    setSelectedGameForLive(game);
    setEditingScheduleSession(null);
    setShowGoLive(true);
  };

  const handleScheduleGame = (game, session = null) => {
    if (!isPro) {
      showToast('Game scheduling is available on the Pro plan.', 'error');
      return;
    }
    setSelectedGameForLive(game);
    setEditingScheduleSession(session);
    setShowGoLive(true);
  };

  const handleCancelScheduledSession = async (sessionId) => {
    if (!confirm('Cancel this scheduled session?')) return;
    try {
      await api.cancelTriviaSession(sessionId);
      showToast('Scheduled session canceled.');
      loadScheduledSessions();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleEndLiveSession = async (session) => {
    if (!session?.id) return;
    if (!confirm('End this live game now? Players will see the final results.')) return;
    setEndingSessionId(session.id);
    try {
      await api.endTriviaSession(session.id);
      showToast('Game ended early.');
      loadActiveSessions();
      loadGames();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setEndingSessionId(null);
    }
  };

  const handleCopyCommunityGame = async (gameId) => {
    if (gameCreationLimitReached) {
      showPlanRestriction(
        `Free plan includes up to ${maxFreeGames} games. Upgrade to Pro for unlimited games.`,
        'FREE_GAME_LIMIT_REACHED'
      );
      return;
    }
    try {
      await api.copyCommunityGame(gameId, currentAccount.id);
      showToast('Game copied to My Games!');
      setActiveTab('my-games');
      await Promise.all([loadGames(), refreshTier()]);
    } catch (error) {
      handleRestrictedError(error);
    }
  };

  const handleOpenCommunityPreview = async (gameId) => {
    setCommunityPreviewOpen(true);
    setCommunityPreviewLoading(true);
    setCommunityPreviewPage(0);
    try {
      const result = await api.getCommunityGameDetails(gameId);
      setCommunityPreviewGame(result.game);
    } catch (error) {
      showToast(error.message, 'error');
      setCommunityPreviewGame(null);
    } finally {
      setCommunityPreviewLoading(false);
    }
  };

  const handleOpenAddToGame = (question) => {
    const firstEligibleGame = games.find(
      (game) => !isFree || Number(game.question_count || 0) < maxFreeQuestions
    );
    setSelectedBankQuestion(question);
    setSelectedGameId(firstEligibleGame?.id || '');
    setShowAddToGameModal(true);
  };

  const handleAddToGame = async () => {
    if (!selectedBankQuestion?.id || !selectedGameId) {
      showToast('Select a game first.', 'error');
      return;
    }
    setAddingToGame(true);
    try {
      await api.copyQuestionToGame(selectedBankQuestion.id, selectedGameId);
      showToast('Question added to game!');
      setShowAddToGameModal(false);
      setSelectedBankQuestion(null);
      setSelectedGameId('');
      loadGames();
    } catch (error) {
      handleRestrictedError(error);
    } finally {
      setAddingToGame(false);
    }
  };

  const handleCreateQueue = () => {
    if (!isPro) {
      showToast('Marathon Mode is available on the Pro plan.', 'error');
      return;
    }
    resetQueueForm();
    setQueueFormDeviceId(queueDevices[0]?.id || '');
    setShowQueueCreateModal(true);
  };

  const handleSubmitQueue = async () => {
    if (!queueFormName.trim()) {
      showToast('Marathon name is required.', 'error');
      return;
    }
    if (!queueFormDeviceId) {
      showToast('Select a device for this marathon.', 'error');
      return;
    }

    setQueueFormSaving(true);
    try {
      await api.createGameQueue({
        accountId: currentAccount.id,
        name: queueFormName.trim(),
        deviceId: queueFormDeviceId,
        lobbyDurationSeconds: Math.max(1, Number(queueFormLobbyMinutes || 1)) * 60,
        cooldownDurationSeconds: Math.max(1, Number(queueFormCooldownMinutes || 1)) * 60,
        loopEnabled: queueFormLoopEnabled
      });
      showToast('Marathon created.');
      setShowQueueCreateModal(false);
      loadQueues();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setQueueFormSaving(false);
    }
  };

  const handleUpdateQueueSettings = async () => {
    if (!selectedQueue?.id) return;
    setQueueFormSaving(true);
    try {
      await api.updateGameQueue(selectedQueue.id, {
        name: queueFormName.trim(),
        lobbyDurationSeconds: Math.max(1, Number(queueFormLobbyMinutes || 1)) * 60,
        cooldownDurationSeconds: Math.max(1, Number(queueFormCooldownMinutes || 1)) * 60,
        loopEnabled: queueFormLoopEnabled
      });
      const refreshed = await api.getGameQueue(selectedQueue.id);
      setSelectedQueue(refreshed.queue);
      setQueueItems(refreshed.items || []);
      showToast('Marathon updated.');
      loadQueues();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setQueueFormSaving(false);
    }
  };

  const handleAddQueueItem = async () => {
    if (!selectedQueue?.id || !queueAddGameId) {
      showToast('Select a game to add.', 'error');
      return;
    }
    setQueueActionLoading(true);
    try {
      await api.addGameQueueItem(selectedQueue.id, queueAddGameId);
      const result = await api.getGameQueue(selectedQueue.id);
      setSelectedQueue(result.queue);
      setQueueItems(result.items || []);
      showToast('Game added to marathon.');
      loadQueues();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setQueueActionLoading(false);
    }
  };

  const handleMoveQueueItem = async (item, direction) => {
    if (!selectedQueue?.id || !item?.id) return;
    const newPosition = item.position + direction;
    setQueueActionLoading(true);
    try {
      await api.updateGameQueueItem(selectedQueue.id, item.id, { position: newPosition });
      const result = await api.getGameQueue(selectedQueue.id);
      setSelectedQueue(result.queue);
      setQueueItems(result.items || []);
      loadQueues();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setQueueActionLoading(false);
    }
  };

  const handleRemoveQueueItem = async (item) => {
    if (!selectedQueue?.id || !item?.id) return;
    if (!confirm('Remove this game from the marathon?')) return;
    setQueueActionLoading(true);
    try {
      await api.deleteGameQueueItem(selectedQueue.id, item.id);
      const result = await api.getGameQueue(selectedQueue.id);
      setSelectedQueue(result.queue);
      setQueueItems(result.items || []);
      loadQueues();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setQueueActionLoading(false);
    }
  };

  const handleStartQueue = async (queueId) => {
    setQueueActionLoading(true);
    try {
      await api.startGameQueue(queueId);
      showToast('Marathon started.');
      loadQueues();
      if (selectedQueue?.id === queueId) {
        const result = await api.getGameQueue(queueId);
        setSelectedQueue(result.queue);
        setQueueItems(result.items || []);
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setQueueActionLoading(false);
    }
  };

  const handleStopQueue = async (queueId) => {
    if (!confirm('Stop this marathon? The device will return to board rotation.')) return;
    setQueueActionLoading(true);
    try {
      await api.stopGameQueue(queueId);
      showToast('Marathon stopped.');
      loadQueues();
      if (selectedQueue?.id === queueId) {
        const result = await api.getGameQueue(queueId);
        setSelectedQueue(result.queue);
        setQueueItems(result.items || []);
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setQueueActionLoading(false);
    }
  };

  const handleResetQueue = async (queueId) => {
    if (!confirm('Start this marathon over from the beginning?')) return;
    setQueueActionLoading(true);
    try {
      await api.resetGameQueue(queueId);
      showToast('Marathon restarted.');
      loadQueues();
      if (selectedQueue?.id === queueId) {
        const result = await api.getGameQueue(queueId);
        setSelectedQueue(result.queue);
        setQueueItems(result.items || []);
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setQueueActionLoading(false);
    }
  };

  const handleDeleteQueue = async (queueId) => {
    if (!confirm('Delete this marathon?')) return;
    setQueueActionLoading(true);
    try {
      await api.deleteGameQueue(queueId);
      showToast('Marathon deleted.');
      loadQueues();
      if (selectedQueue?.id === queueId) {
        setSelectedQueue(null);
        setQueueItems([]);
        setShowQueueModal(false);
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setQueueActionLoading(false);
    }
  };

  // KPI calculations
  const totalGames = games.length;
  const publicGames = games.filter(g => g.shared_status === 'public').length;
  const totalPlays = games.reduce((sum, g) => sum + (g.play_count || 0), 0);
  const totalQuestions = games.reduce((sum, g) => sum + (g.question_count || 0), 0);
  const allQuestionTargetsAtLimit = isFree
    && games.length > 0
    && games.every((game) => Number(game.question_count || 0) >= maxFreeQuestions);
  const communityPreviewQuestions = communityPreviewGame?.questions || [];
  const communityPreviewTotalPages = Math.max(
    1,
    Math.ceil(communityPreviewQuestions.length / communityPreviewPageSize)
  );
  const communityPreviewPageStart = communityPreviewPage * communityPreviewPageSize;
  const communityPreviewPageEnd = communityPreviewPageStart + communityPreviewPageSize;
  const communityPreviewSlice = communityPreviewQuestions.slice(
    communityPreviewPageStart,
    communityPreviewPageEnd
  );

  const scheduledByGame = useMemo(() => {
    const byGame = {};
    scheduledSessions.forEach((session) => {
      const existing = byGame[session.game_id];
      if (!existing || new Date(session.scheduled_start_at) < new Date(existing.scheduled_start_at)) {
        byGame[session.game_id] = session;
      }
    });
    return byGame;
  }, [scheduledSessions]);

  const activeByGame = useMemo(() => {
    const byGame = {};
    activeSessions.forEach((session) => {
      if (!byGame[session.game_id]) {
        byGame[session.game_id] = session;
      }
    });
    return byGame;
  }, [activeSessions]);

  const formatScheduleDateTime = (isoString) => {
    if (!isoString) return 'TBD';
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return 'TBD';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatCountdownSeconds = (seconds) => {
    const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
    if (totalSeconds < 60) {
      return `${totalSeconds}s`;
    }
    if (totalSeconds % 60 === 0 && totalSeconds < 3600) {
      return `${totalSeconds / 60}m`;
    }
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m ${totalSeconds % 60}s`;
  };

  const formatDuration = (seconds) => {
    const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m`;
    }
    return `${totalSeconds}s`;
  };

  const tabs = [
    { id: 'my-games', label: 'My Games' },
    { id: 'queues', label: 'Marathon Mode' },
    { id: 'question-bank', label: 'Question Bank' },
    { id: 'community', label: 'Community' },
    { id: 'history', label: 'History' },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Trivia Games</h1>
          <p className="page-subtitle">Create interactive trivia experiences</p>
        </div>
        <div className="page-header-actions">
          {activeTab === 'my-games' && (
            <Button
              onClick={handleCreateGame}
              icon={Icons.Plus}
              disabled={gameCreationLimitReached}
              title={gameCreationLimitReached ? 'Upgrade to Pro to create another game.' : undefined}
            >
              New Game
            </Button>
          )}
          {activeTab === 'queues' && (
            <Button
              onClick={handleCreateQueue}
              icon={Icons.Plus}
              disabled={!isPro}
              title={!isPro ? 'Marathon Mode requires a Pro subscription.' : undefined}
            >
              New Marathon
            </Button>
          )}
          {activeTab === 'queues' && (
            <a
              className="header-video-link"
              href="https://www.youtube.com/watch?v=gYajfAFF1To"
              target="_blank"
              rel="noreferrer"
            >
              <Icons.Play /> Marathon Mode
            </a>
          )}
          {activeTab === 'my-games' && (
            <a
              className="header-video-link"
              href="https://www.youtube.com/watch?v=llB49oqe5OE"
              target="_blank"
              rel="noreferrer"
            >
              <Icons.Play /> Create & Edit
            </a>
          )}
          {activeTab === 'community' && (
            <a
              className="header-video-link"
              href="https://www.youtube.com/watch?v=bVOcMIrjL7s"
              target="_blank"
              rel="noreferrer"
            >
              <Icons.Play /> Browse & Preview
            </a>
          )}
          {activeTab === 'history' && (
            <a
              className="header-video-link"
              href="https://www.youtube.com/watch?v=DgyDAodp5Ec"
              target="_blank"
              rel="noreferrer"
            >
              <Icons.Play /> Game Sessions
            </a>
          )}
        </div>
      </div>

      {activeTab === 'my-games' && gameCreationLimitReached && (
        <GlassCard className="plan-limit-banner" padding="sm">
          <Icons.Shield />
          <div className="plan-limit-copy">
            <strong>
              {hasTrialGameCarryover
                ? `You have ${accountGameCount} games from your Pro trial.`
                : `Free plan includes up to ${maxFreeGames} games.`}
            </strong>
            <span>
              {hasTrialGameCarryover
                ? 'Upgrade to Pro to create new games and keep full access.'
                : 'Upgrade to Pro for unlimited games.'}
            </span>
          </div>
          <Button size="sm" onClick={onUpgrade}>Upgrade to Pro</Button>
        </GlassCard>
      )}

      {activeTab === 'my-games' && monthlyHostLimitReached && (
        <GlassCard className="plan-limit-banner" padding="sm">
          <Icons.Clock />
          <div className="plan-limit-copy">
            <strong>You've used your {maxFreeHostedGames} free hosted game this month.</strong>
            <span>Upgrade to Pro for unlimited hosting. Your free game resets on {hostedGamesResetLabel}.</span>
          </div>
          <Button size="sm" onClick={onUpgrade}>Upgrade to Pro</Button>
        </GlassCard>
      )}

      {activeTab === 'my-games' && isFree && games.some((game) => Number(game.question_count) > 0) && (
        <GlassCard className="plan-limit-banner" padding="sm">
          <Icons.Globe />
          <div className="plan-limit-copy">
            <strong>Sharing games with the community is a Pro feature.</strong>
            <span>You can still browse and copy games shared by the community.</span>
          </div>
          <Button size="sm" onClick={onUpgrade}>Upgrade to Pro</Button>
        </GlassCard>
      )}

      {activeTab === 'community' && gameCreationLimitReached && (
        <GlassCard className="plan-limit-banner" padding="sm">
          <Icons.Games />
          <div className="plan-limit-copy">
            <strong>Your game library is full.</strong>
            <span>Delete a game or upgrade to Pro before copying another community game.</span>
          </div>
          <Button size="sm" onClick={onUpgrade}>Upgrade to Pro</Button>
        </GlassCard>
      )}

      {planRestriction
        && !(
          planRestriction.code === 'FREE_GAME_LIMIT_REACHED' && gameCreationLimitReached
        )
        && !(
          planRestriction.code === 'FREE_MONTHLY_HOST_LIMIT_REACHED' && monthlyHostLimitReached
        ) && (
          <GlassCard className="plan-limit-banner" padding="sm">
            <Icons.Shield />
            <div className="plan-limit-copy">
              <strong>Pro feature</strong>
              <span>{planRestriction.message}</span>
            </div>
            <Button size="sm" onClick={onUpgrade}>Upgrade to Pro</Button>
          </GlassCard>
        )}

      {/* KPI Row */}
      <div className="kpi-grid">
        <KPICard 
          title="Total Games" 
          value={totalGames}
          icon={Icons.Games}
        />
        <KPICard 
          title="Total Plays" 
          value={totalPlays}
          change={totalPlays > 0 ? `+${Math.round(totalPlays * 0.1)}` : '0'}
          changeType={totalPlays > 0 ? 'positive' : 'neutral'}
          icon={Icons.Play}
        />
        <KPICard 
          title="Questions" 
          value={totalQuestions}
          icon={Icons.BookOpen}
        />
        <KPICard 
          title="Shared" 
          value={publicGames}
          icon={Icons.Globe}
        />
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'my-games' && (
        <div className="games-filter-bar">
          <Select
            label="Category Filter"
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            options={[
              { value: '', label: 'All Categories' },
              ...categories.map(cat => ({
                value: cat.id,
                label: `${cat.icon} ${cat.name}`
              }))
            ]}
          />
        </div>
      )}

      {/* Tab Content */}
      {loading ? (
        <div className="loading-container">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          {/* My Games */}
          {activeTab === 'my-games' && (
            games.length === 0 ? (
              <EmptyState
                icon={Icons.Games}
                title="No games yet"
                description="Create your first trivia game to engage your audience"
                action={handleCreateGame}
                actionLabel="Create Game"
              />
            ) : (
              <div className="games-grid">
                {games.map(game => {
                  const scheduledSession = scheduledByGame[game.id];
                  const activeSession = activeByGame[game.id];
                  return (
                    <GlassCard key={game.id} className="game-card">
                      <div className="game-card-header">
                        <h3>{game.title}</h3>
                        <div className="game-badges">
                          {game.category_name && (
                            <Badge variant="info">{game.category_icon} {game.category_name}</Badge>
                          )}
                          {activeSession && <Badge variant="success">Live</Badge>}
                          {scheduledSession && <Badge variant="warning">Scheduled</Badge>}
                          {game.shared_status === 'public' && <Badge variant="success">Public</Badge>}
                          {game.shared_status === 'pending_moderation' && <Badge variant="warning">Pending</Badge>}
                        </div>
                      </div>
                      <p className="game-meta">
                        <span><Icons.BookOpen /> {game.question_count || 0} questions</span>
                        <span><Icons.Users /> {game.play_count || 0} plays</span>
                      </p>
                      {scheduledSession && (
                        <p className="game-schedule">
                          <Icons.Clock />
                          Scheduled {formatScheduleDateTime(scheduledSession.scheduled_start_at)}
                          {' • '}
                          {scheduledSession.schedule_target === 'device'
                            ? (scheduledSession.device_name || 'Device')
                            : 'Web'}
                        </p>
                      )}
                      <div className="game-card-actions">
                        <Button variant="ghost" size="sm" icon={Icons.Edit} onClick={() => handleEditGame(game)}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Icons.Copy}
                          onClick={() => handleDuplicateGame(game.id)}
                          disabled={gameCreationLimitReached}
                          title={gameCreationLimitReached ? 'Upgrade to Pro to copy another game.' : undefined}
                        >
                          Copy
                        </Button>
                        {scheduledSession ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={Icons.Edit}
                              onClick={() => handleScheduleGame(game, scheduledSession)}
                              disabled={!isPro}
                              title={!isPro ? 'Only Available in Pro Subscription' : undefined}
                            >
                              Reschedule
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={Icons.Trash}
                              onClick={() => handleCancelScheduledSession(scheduledSession.id)}
                              className="btn-danger-ghost"
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Icons.Clock}
                            onClick={() => handleScheduleGame(game)}
                            disabled={!Number(game.question_count) || !isPro}
                            title={
                              !isPro
                                ? 'Only Available in Pro Subscription'
                                : !Number(game.question_count)
                                  ? 'Add at least one question to schedule'
                                  : undefined
                            }
                          >
                            Schedule
                          </Button>
                        )}
                        {(game.shared_status === 'public' || game.shared_status === 'pending_moderation') ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Icons.Globe}
                            onClick={() => handleUnshareGame(game.id)}
                          >
                            Unshare
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Icons.Globe}
                            onClick={() => handleShareGame(game.id)}
                            disabled={!Number(game.question_count) || isFree}
                            title={isFree ? 'Sharing games with the community is a Pro feature.' : undefined}
                          >
                            Share
                          </Button>
                        )}
                        <Button 
                          variant="primary" 
                          size="sm" 
                          icon={Icons.Play}
                          onClick={() => handleGoLive(game)}
                          disabled={!!activeSession || !Number(game.question_count) || monthlyHostLimitReached}
                          className={activeSession ? 'btn-live' : ''}
                          title={monthlyHostLimitReached ? `Free hosting resets on ${hostedGamesResetLabel}.` : undefined}
                        >
                          {activeSession ? 'Live' : 'Go Live'}
                        </Button>
                        {activeSession && (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Icons.Stop}
                            onClick={() => handleEndLiveSession(activeSession)}
                            disabled={endingSessionId === activeSession.id}
                            className="btn-danger-ghost"
                          >
                            End
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Icons.Trash}
                          onClick={() => handleDeleteGame(game.id)}
                          className="btn-danger-ghost"
                        >
                          Delete
                        </Button>
                      </div>
                    </GlassCard>
                  );
                })}
              </div>
            )
          )}

          {/* Marathon Mode */}
          {activeTab === 'queues' && (
            !isPro ? (
              <GlassCard className="settings-warning" padding="sm">
                <Icons.Shield />
                <div>
                  <p><strong>Marathon Mode Game Queuing is a Pro feature.</strong></p>
                  <p className="muted">Upgrade to Pro to queue and run multiple trivia games back-to-back on a device.</p>
                </div>
              </GlassCard>
            ) : queuesLoading ? (
              <div className="loading-container">
                <Spinner size="lg" />
              </div>
            ) : queues.length === 0 ? (
              <EmptyState
                icon={Icons.Play}
                title="No marathons yet"
                description="Create a Marathon Mode run to play multiple trivia games back-to-back on a device."
                action={handleCreateQueue}
                actionLabel="Create Marathon"
              />
            ) : (
              <div className="queue-grid">
                {queues.map(queue => (
                  <GlassCard
                    key={queue.id}
                    className={`queue-card ${queue.status === 'active' ? 'queue-card-active' : ''}`}
                  >
                    <div className="queue-card-header">
                      <div>
                        <h3>{queue.name}</h3>
                        <p className="queue-meta">
                          {queue.device_name || queue.device_group_name || 'Unassigned'} • {queue.item_count || 0} games
                          {queue.loop_enabled ? ' • Looping' : ''}
                        </p>
                      </div>
                      <Badge
                        variant={
                          queue.status === 'active'
                            ? 'success'
                            : queue.status === 'paused'
                              ? 'warning'
                              : queue.status === 'completed'
                                ? 'info'
                                : 'default'
                        }
                      >
                        {queue.status}
                      </Badge>
                    </div>
                    <div className="queue-runtime">
                      <div>Estimated runtime: {formatDuration(queue.estimated_total_seconds || 0)}</div>
                      {queue.status === 'active' ? (
                        <div>Time remaining: {formatDuration(queue.estimated_remaining_seconds || 0)}</div>
                      ) : (
                        <div>Time remaining: —</div>
                      )}
                    </div>
                    <div className="queue-actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Icons.Edit}
                        onClick={() => openQueueDetail(queue.id)}
                      >
                        View
                      </Button>
                      {queue.status === 'active' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Icons.Stop}
                          onClick={() => handleStopQueue(queue.id)}
                          disabled={queueActionLoading}
                          className="btn-danger-ghost"
                        >
                          Stop
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          icon={Icons.Play}
                          onClick={() => handleStartQueue(queue.id)}
                          disabled={queueActionLoading}
                        >
                          Start
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Icons.Trash}
                        onClick={() => handleDeleteQueue(queue.id)}
                        disabled={queueActionLoading}
                        className="btn-danger-ghost"
                      >
                        Delete
                      </Button>
                    </div>
                  </GlassCard>
                ))}
              </div>
            )
          )}

          {/* Question Bank */}
          {activeTab === 'question-bank' && (
            questionBank.length === 0 ? (
              <EmptyState
                icon={Icons.BookOpen}
                title="Question Bank Empty"
                description="Save questions from your games to reuse them across different trivia games"
              />
            ) : (
              <div className="questions-list">
                {questionBank.map(q => (
                  <GlassCard key={q.id} className="question-item" padding="sm">
                    <div className="question-content">
                      <p className="question-prompt">{q.prompt}</p>
                      <div className="question-meta">
                        <span>{q.answers?.length || 0} answers</span>
                        {q.tags?.length > 0 && <span>{q.tags.join(', ')}</span>}
                      </div>
                    </div>
                    <div className="question-actions">
                      <Button variant="secondary" size="sm" onClick={() => handleOpenAddToGame(q)}>
                        Add to Game
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Icons.Trash}
                        onClick={async () => {
                          if (!confirm('Delete this question from the bank?')) return;
                          try {
                            await api.deleteQuestionBankItem(q.id);
                            showToast('Question deleted');
                            loadQuestionBank();
                          } catch (error) {
                            showToast(error.message, 'error');
                          }
                        }}
                        className="btn-danger-ghost"
                      />
                    </div>
                  </GlassCard>
                ))}
              </div>
            )
          )}

          {/* Community */}
          {activeTab === 'community' && (
            <>
              <div className="games-filter-bar">
                <Select
                  label="Category Filter"
                  value={communityCategoryFilter}
                  onChange={e => setCommunityCategoryFilter(e.target.value)}
                  disabled={showMySharedGames}
                  options={[
                    { value: '', label: 'All Categories' },
                    ...categories.map(cat => ({
                      value: cat.id,
                      label: `${cat.icon} ${cat.name}`
                    }))
                  ]}
                />
                <Select
                  label="Sort By"
                  value={communitySort}
                  onChange={e => setCommunitySort(e.target.value)}
                  disabled={showMySharedGames}
                  options={[
                    { value: 'popular', label: 'Most Popular' },
                    { value: 'most_used', label: 'Most Uses' },
                    { value: 'newest', label: 'Newest' }
                  ]}
                />
                <Button
                  variant={showMySharedGames ? 'primary' : 'ghost'}
                  size="sm"
                  icon={Icons.Globe}
                  onClick={() => setShowMySharedGames(prev => !prev)}
                >
                  {showMySharedGames ? 'All Community Games' : 'My Shared Games'}
                </Button>
              </div>
              <GlassCard className="community-warning" padding="sm">
                <Icons.Help />
                <span>Community games are user-generated. Please review questions before playing.</span>
              </GlassCard>
              {showMySharedGames ? (
                mySharedGames.length === 0 ? (
                  <EmptyState
                    icon={Icons.Globe}
                    title="No shared games yet"
                    description="Share a game from the My Games tab to see it here."
                  />
                ) : (
                  <div className="games-grid">
                    {mySharedGames.map(game => (
                      <GlassCard key={game.id} className="game-card community-card">
                        <div className="game-card-header">
                          <h3>{game.title}</h3>
                          <div className="game-badges">
                            {game.category_name && (
                              <Badge variant="info">{game.category_icon} {game.category_name}</Badge>
                            )}
                            {game.shared_status === 'public' && <Badge variant="success">Public</Badge>}
                            {game.shared_status === 'pending_moderation' && <Badge variant="warning">Pending</Badge>}
                          </div>
                        </div>
                        <p className="game-meta">
                          <span><Icons.BookOpen /> {game.question_count || 0} questions</span>
                          <span><Icons.Users /> {game.play_count || 0} plays</span>
                          <span>
                            <Icons.Copy /> {game.copy_count || 0} {(game.copy_count || 0) === 1 ? 'use' : 'uses'}
                          </span>
                        </p>
                        {game.description && <p className="game-description">{game.description}</p>}
                        <div className="game-card-actions">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Icons.Globe}
                            onClick={() => handleUnshareGame(game.id)}
                          >
                            Unshare
                          </Button>
                        </div>
                      </GlassCard>
                    ))}
                  </div>
                )
              ) : communityGames.length === 0 ? (
                <EmptyState
                  icon={Icons.Globe}
                  title="No Community Games"
                  description="Be the first to share a game with the community!"
                />
              ) : (
                <div className="games-grid">
                  {communityGames.map(game => (
                    <GlassCard
                      key={game.id}
                      className="game-card community-card"
                      onClick={() => handleOpenCommunityPreview(game.id)}
                    >
                      <div className="game-card-header">
                        <h3>{game.title}</h3>
                        {game.category_name && (
                          <Badge variant="info">{game.category_icon} {game.category_name}</Badge>
                        )}
                      </div>
                      <p className="game-meta">
                        <span><Icons.BookOpen /> {game.question_count} questions</span>
                        <span><Icons.Users /> {game.play_count} plays</span>
                        <span>
                          <Icons.Copy /> {game.copy_count || 0} {(game.copy_count || 0) === 1 ? 'use' : 'uses'}
                        </span>
                      </p>
                      {game.description && <p className="game-description">{game.description}</p>}
                      <div className="game-card-actions">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleCopyCommunityGame(game.id);
                          }}
                          disabled={gameCreationLimitReached}
                          title={gameCreationLimitReached ? 'Upgrade to Pro to copy another game.' : undefined}
                        >
                          Use This Game
                        </Button>
                      </div>
                    </GlassCard>
                  ))}
                </div>
              )}
            </>
          )}

          {/* History */}
          {activeTab === 'history' && (
            isFree ? (
              <div className="upgrade-prompt">
                <div className="upgrade-content">
                  <div className="upgrade-icon"><Icons.TrendingUp /></div>
                  <h2>Game history is a Pro feature</h2>
                  <p>Game history and analytics are available on the Pro plan.</p>
                  <Button onClick={onUpgrade}>Upgrade to Pro</Button>
                </div>
              </div>
            ) : history.length === 0 ? (
              <EmptyState
                icon={Icons.Clock}
                title="No Games Played"
                description="Your game history will appear here after you play your first game"
              />
            ) : (
              <div className="history-list">
                {history.map(h => (
                  <GlassCard key={h.id} className="history-item" padding="sm">
                    <div className="history-info">
                      <h4>{h.game_title}</h4>
                      <p className="history-meta">
                        <span><Icons.Clock /> {new Date(h.played_at).toLocaleDateString()}</span>
                        <span><Icons.Users /> {h.player_count} players</span>
                        <span><Icons.BookOpen /> {h.question_count} questions</span>
                      </p>
                    </div>
                    <div className="history-actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewHistoryDetail(h)}
                      >
                        Details
                      </Button>
                    </div>
                    <div className="history-rankings">
                      {(h.final_rankings || []).slice(0, 3).map((r, i) => (
                        <span key={i} className={`rank rank-${i + 1}`}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'} {r.displayName} ({r.score})
                        </span>
                      ))}
                    </div>
                  </GlassCard>
                ))}
              </div>
            )
          )}
        </>
      )}

      <Modal
        isOpen={showHistoryDetail}
        title="Game Performance"
        onClose={handleCloseHistoryDetail}
        size="lg"
      >
        {historyDetailLoading ? (
          <div className="loading-container">
            <Spinner size="lg" />
          </div>
        ) : historyDetail?.history ? (
          <div className="history-detail">
            <div className="history-detail-header">
              <div>
                <h3>{historyDetail.history.game_title}</h3>
                <p className="history-detail-meta">
                  <span><Icons.Clock /> {new Date(historyDetail.history.played_at).toLocaleString()}</span>
                  <span><Icons.Users /> {historyDetail.history.player_count} players</span>
                  <span><Icons.BookOpen /> {historyDetail.history.question_count} questions</span>
                  {historyDetail.history.duration_seconds ? (
                    <span><Icons.Clock /> {Math.round(historyDetail.history.duration_seconds / 60)} min</span>
                  ) : null}
                </p>
              </div>
            </div>

            <div className="history-summary">
              <span className="history-chip">Avg Score: {historyDetail.summary?.avgScore ?? 0}</span>
              <span className="history-chip">Avg Accuracy: {historyDetail.summary?.avgAccuracy ?? 0}%</span>
              <span className="history-chip">Avg Completion: {historyDetail.summary?.avgCompletion ?? 0}%</span>
              <span className="history-chip">
                Completed: {historyDetail.summary?.completedPlayers ?? 0}/{historyDetail.summary?.totalPlayers ?? 0}
              </span>
            </div>

            <div className="history-leaderboard">
              <div className="history-leaderboard-header">
                <span>Rank</span>
                <span>Player</span>
                <span>Score</span>
                <span>Correct</span>
                <span>Answered</span>
                <span>Completion</span>
                <span>Avg Time</span>
              </div>
              {(historyDetail.players || []).map((player, index) => {
                const completion = Math.round((player.completion_rate || 0) * 100);
                const accuracy = Math.round((player.accuracy_rate || 0) * 100);
                return (
                  <div key={player.id} className="history-leaderboard-row">
                    <span className="history-rank">#{index + 1}</span>
                    <span className="history-player">{player.display_name}</span>
                    <span>{player.score}</span>
                    <span>{player.correct_total} ({accuracy}%)</span>
                    <span>{player.answers_total}</span>
                    <span>{completion}%</span>
                    <span>{player.avg_response_time_ms ? `${player.avg_response_time_ms} ms` : '--'}</span>
                  </div>
                );
              })}
              {(!historyDetail.players || historyDetail.players.length === 0) && (
                <p className="muted">No player data available for this session.</p>
              )}
            </div>
          </div>
        ) : (
          <p className="muted">No history details available.</p>
        )}
      </Modal>

      {/* Game Editor Modal */}
      <Modal 
        isOpen={showEditor} 
        title={editingGame?.id ? 'Edit Game' : 'Create New Game'} 
        onClose={() => { setShowEditor(false); setEditingGame(null); }}
        size="lg"
      >
        {editorLoading ? (
          <div className="loading-container">
            <Spinner size="lg" />
          </div>
        ) : (
        <GameEditor 
          game={editingGame}
          categories={categories}
          accountId={currentAccount?.id}
          canUseAI={canUseAI}
          isPro={isPro}
          maxQuestions={maxFreeQuestions}
          onUpgrade={onUpgrade}
          onSave={async (gameData) => {
            const normalizedQuestions = (gameData.questions || []).map((question) => ({
              ...question,
              answers: (question.answers || []).map((answer) => ({
                ...answer,
                isCorrect: answer.id === question.correct_answer_id
              }))
            }));
            try {
              if (editingGame?.id) {
                const { category_id: categoryId, ...rest } = gameData;
                await api.updateTriviaGame(editingGame.id, {
                  ...rest,
                  categoryId: categoryId || null,
                  questions: normalizedQuestions
                });
                showToast('Game updated!');
              } else {
                await api.createTriviaGame(currentAccount.id, {
                  ...gameData,
                  category_id: gameData.category_id || null,
                  questions: normalizedQuestions
                });
                showToast('Game created!');
              }
              setShowEditor(false);
              setEditingGame(null);
              await Promise.all([loadGames(), refreshTier()]);
              setPlanRestriction(null);
            } catch (error) {
              handleRestrictedError(error);
            }
          }}
          onSaveToBank={async (questionId, question) => api.saveQuestionToBank(questionId, currentAccount.id, question)}
          onToast={showToast}
          onCancel={() => { setShowEditor(false); setEditingGame(null); }}
        />
        )}
      </Modal>

      {/* Add Question to Game Modal */}
      <Modal
        isOpen={showAddToGameModal}
        title="Add Question to Game"
        onClose={() => { setShowAddToGameModal(false); setSelectedBankQuestion(null); setSelectedGameId(''); }}
      >
        <div className="modal-form">
          <p className="modal-description">
            Select a game to receive this question.
          </p>
          {games.length === 0 ? (
            <GlassCard className="no-devices-warning" padding="sm">
              <Icons.Games />
              <p>No games available. Create a game first.</p>
            </GlassCard>
          ) : (
            <div className="input-group">
              <label className="input-label">Select Game</label>
              <select
                className="select-field"
                value={selectedGameId}
                onChange={(e) => setSelectedGameId(e.target.value)}
              >
                {games.map((game) => (
                  <option
                    key={game.id}
                    value={game.id}
                    disabled={isFree && Number(game.question_count || 0) >= maxFreeQuestions}
                  >
                    {game.title}
                    {isFree && Number(game.question_count || 0) >= maxFreeQuestions
                      ? ` (${maxFreeQuestions}-question limit reached)`
                      : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          {allQuestionTargetsAtLimit && (
            <GlassCard className="plan-limit-banner" padding="sm">
              <Icons.BookOpen />
              <div className="plan-limit-copy">
                <strong>Free plan includes up to {maxFreeQuestions} questions per game.</strong>
                <span>Upgrade to Pro for unlimited questions.</span>
              </div>
              <Button size="sm" onClick={onUpgrade}>Upgrade to Pro</Button>
            </GlassCard>
          )}
        </div>
        <div className="modal-actions">
          <Button variant="ghost" onClick={() => { setShowAddToGameModal(false); setSelectedBankQuestion(null); setSelectedGameId(''); }}>
            Cancel
          </Button>
          <Button onClick={handleAddToGame} loading={addingToGame} disabled={!selectedGameId || allQuestionTargetsAtLimit}>
            Add to Game
          </Button>
        </div>
      </Modal>

      {/* Community Game Preview Modal */}
      <Modal
        isOpen={communityPreviewOpen}
        title="Community Game Preview"
        onClose={() => { setCommunityPreviewOpen(false); setCommunityPreviewGame(null); }}
        size="lg"
      >
        {communityPreviewLoading ? (
          <div className="community-preview-shimmer">
            <div className="shimmer-line shimmer-title" />
            <div className="shimmer-line shimmer-meta" />
            <div className="shimmer-card" />
            <div className="shimmer-card" />
            <div className="shimmer-card" />
          </div>
        ) : communityPreviewGame ? (
          <div className="community-preview">
            <div className="community-preview-header">
              <div>
                <h3>{communityPreviewGame.title}</h3>
                {communityPreviewGame.category_name && (
                  <Badge variant="info">
                    {communityPreviewGame.category_icon} {communityPreviewGame.category_name}
                  </Badge>
                )}
              </div>
              <div className="community-preview-meta">
                <span><Icons.Users /> {communityPreviewGame.play_count} plays</span>
                <span>
                  <Icons.Copy /> {communityPreviewGame.copy_count || 0}{' '}
                  {(communityPreviewGame.copy_count || 0) === 1 ? 'use' : 'uses'}
                </span>
                {communityPreviewGame.creator_name && (
                  <span>By {communityPreviewGame.creator_name}</span>
                )}
              </div>
            </div>
            {communityPreviewGame.description && (
              <p className="community-preview-description">{communityPreviewGame.description}</p>
            )}
            <div className="community-preview-questions">
              {communityPreviewSlice.map((question, index) => (
                <GlassCard key={question.id} className="community-question" padding="sm">
                  <div className="community-question-header">
                    <span className="question-number">Q{communityPreviewPageStart + index + 1}</span>
                    <span className="question-preview">{question.prompt}</span>
                  </div>
                  <div className="community-answers">
                    {(question.answers || []).map((answer) => (
                      <div
                        key={answer.id}
                        className={`community-answer ${answer.isCorrect ? 'correct' : ''}`}
                      >
                        <span>{answer.text}</span>
                        {answer.isCorrect && <Icons.Check className="correct-icon" />}
                      </div>
                    ))}
                  </div>
                  {question.explanation && (
                    <div className="community-explanation">{question.explanation}</div>
                  )}
                </GlassCard>
              ))}
            </div>
            {communityPreviewTotalPages > 1 && (
              <div className="community-preview-pagination">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCommunityPreviewPage(Math.max(0, communityPreviewPage - 1))}
                  disabled={communityPreviewPage === 0}
                >
                  Previous
                </Button>
                <span>
                  Page {communityPreviewPage + 1} of {communityPreviewTotalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setCommunityPreviewPage(
                      Math.min(communityPreviewTotalPages - 1, communityPreviewPage + 1)
                    )
                  }
                  disabled={communityPreviewPage >= communityPreviewTotalPages - 1}
                >
                  Next
                </Button>
              </div>
            )}
            <div className="modal-actions">
              <Button variant="ghost" onClick={() => setCommunityPreviewOpen(false)}>Close</Button>
              <Button
                onClick={() => handleCopyCommunityGame(communityPreviewGame.id)}
                disabled={gameCreationLimitReached}
                title={gameCreationLimitReached ? 'Upgrade to Pro to copy another game.' : undefined}
              >
                Use This Game
              </Button>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <p>Unable to load community game details.</p>
          </div>
        )}
      </Modal>

      {/* Go Live Modal */}
      <Modal 
        isOpen={showGoLive} 
        title="Start Game Session" 
        onClose={() => { setShowGoLive(false); setSelectedGameForLive(null); setEditingScheduleSession(null); }}
      >
      <GoLiveModal
        game={selectedGameForLive}
        accountId={currentAccount?.id}
        accountTimezone={currentAccount?.timezone}
        scheduledSession={editingScheduleSession}
        isPro={isPro}
        onSubmit={async (sessionData) => {
            try {
              let result = null;
              if (sessionData.isScheduled) {
                const payload = {
                  scheduleTarget: sessionData.schedule_target,
                  deviceId: sessionData.device_id || null,
                  scheduledStartAt: sessionData.scheduled_start_at,
                  countdownSeconds: sessionData.countdown_seconds,
                  hideQuestionsOnMobile: sessionData.hide_questions_mobile
                };
                if (editingScheduleSession?.id) {
                  result = await api.rescheduleTriviaSession(editingScheduleSession.id, payload);
                  showToast('Session rescheduled!');
                } else {
                  result = await api.scheduleTriviaSession(selectedGameForLive.id, payload);
                  showToast('Session scheduled!');
                }
                loadScheduledSessions();
              } else {
                result = await api.createTriviaSession(
                  selectedGameForLive.id,
                  sessionData.device_id,
                  sessionData.countdown_seconds,
                  sessionData.hide_questions_mobile
                );
                showToast('Game session started!');
                loadActiveSessions();
                await refreshTier();
                setPlanRestriction(null);
              }
              // Could redirect to a session management view here
              return result;
            } catch (error) {
              handleRestrictedError(error);
              return null;
            }
          }}
          onCancel={() => { setShowGoLive(false); setSelectedGameForLive(null); setEditingScheduleSession(null); }}
        />
      </Modal>

      {/* Create Queue Modal */}
      <Modal
        isOpen={showQueueCreateModal}
        title="Create Marathon Mode"
        onClose={() => setShowQueueCreateModal(false)}
      >
        <div className="modal-form">
          <Input
            label={<span>Marathon Name<br />&nbsp;</span>}
            value={queueFormName}
            onChange={(e) => setQueueFormName(e.target.value)}
            placeholder="Friday Trivia Playlist"
          />
          {queueDevices.length === 0 ? (
            <GlassCard className="no-devices-warning" padding="sm">
              <Icons.Devices />
              <p>Add a paired device before creating a marathon.</p>
            </GlassCard>
          ) : (
            <Select
              label="Device"
              value={queueFormDeviceId}
              onChange={(e) => setQueueFormDeviceId(e.target.value)}
              options={[
                { value: '', label: 'Select a device' },
                ...queueDevices.map((device) => ({
                  value: device.id,
                  label: device.name
                }))
              ]}
            />
          )}
          <div className="queue-duration-grid">
            <Input
              label={<span>Lobby Countdown<br />(min)</span>}
              type="number"
              min="1"
              value={queueFormLobbyMinutes}
              onChange={(e) => setQueueFormLobbyMinutes(Number(e.target.value))}
            />
            <Input
              label={<span>Cooldown Between Games<br />(min)</span>}
              type="number"
              min="1"
              value={queueFormCooldownMinutes}
              onChange={(e) => setQueueFormCooldownMinutes(Number(e.target.value))}
            />
          </div>
          <div className="toggle-row">
            <label className="toggle-label">
              <span>Loop games</span>
              <span className="toggle-hint">Repeat the marathon when all games finish</span>
            </label>
            <button
              type="button"
              className={`toggle-switch ${queueFormLoopEnabled ? 'on' : ''}`}
              onClick={() => setQueueFormLoopEnabled(!queueFormLoopEnabled)}
            >
              <span className="toggle-knob" />
            </button>
          </div>
        </div>
        <div className="modal-actions">
          <Button variant="ghost" onClick={() => setShowQueueCreateModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitQueue}
            loading={queueFormSaving}
            disabled={!queueFormName.trim() || !queueFormDeviceId}
          >
            Create Marathon
          </Button>
        </div>
      </Modal>

      {/* Queue Detail Modal */}
      <Modal
        isOpen={showQueueModal}
        title={selectedQueue?.name || 'Marathon Mode'}
        onClose={() => { setShowQueueModal(false); setSelectedQueue(null); setQueueItems([]); }}
        size="lg"
      >
        {queueDetailLoading ? (
          <div className="loading-container">
            <Spinner size="lg" />
          </div>
        ) : selectedQueue ? (
          <div className="queue-detail">
            <div className="queue-detail-header">
              <div>
                <h3>{selectedQueue.name}</h3>
                <p className="queue-detail-meta">
                  {selectedQueue.device_name || selectedQueue.device_group_name || 'Unassigned'} •{' '}
                  {queueItems.length} games
                  {selectedQueue.loop_enabled ? ' • Looping' : ''}
                </p>
                <Badge
                  variant={
                    selectedQueue.status === 'active'
                      ? 'success'
                      : selectedQueue.status === 'paused'
                        ? 'warning'
                        : selectedQueue.status === 'completed'
                          ? 'info'
                          : 'default'
                  }
                >
                  {selectedQueue.status}
                </Badge>
              </div>
              <div className="queue-detail-actions">
                {selectedQueue.status === 'paused' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Icons.Play}
                    onClick={() => handleResetQueue(selectedQueue.id)}
                    disabled={queueActionLoading}
                  >
                    Start Over
                  </Button>
                )}
                {selectedQueue.status === 'active' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Icons.Stop}
                    onClick={() => handleStopQueue(selectedQueue.id)}
                    className="btn-danger-ghost"
                    disabled={queueActionLoading}
                  >
                    Stop
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    icon={Icons.Play}
                    onClick={() => handleStartQueue(selectedQueue.id)}
                    disabled={queueActionLoading}
                  >
                    Start
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Icons.Trash}
                  onClick={() => handleDeleteQueue(selectedQueue.id)}
                  className="btn-danger-ghost"
                  disabled={queueActionLoading}
                >
                  Delete
                </Button>
              </div>
            </div>

            <GlassCard padding="sm">
              <div className="queue-duration-grid">
                <Input
                  label={<span>Marathon Name<br />&nbsp;</span>}
                  value={queueFormName}
                  onChange={(e) => setQueueFormName(e.target.value)}
                />
                <Input
                  label={<span>Lobby Countdown<br />(min)</span>}
                  type="number"
                  min="1"
                  value={queueFormLobbyMinutes}
                  onChange={(e) => setQueueFormLobbyMinutes(Number(e.target.value))}
                />
                <Input
                  label={<span>Cooldown Between Games<br />(min)</span>}
                  type="number"
                  min="1"
                  value={queueFormCooldownMinutes}
                  onChange={(e) => setQueueFormCooldownMinutes(Number(e.target.value))}
                />
              </div>
              <div className="toggle-row">
                <label className="toggle-label">
                  <span>Loop games</span>
                  <span className="toggle-hint">Repeat the marathon when all games finish</span>
                </label>
                <button
                  type="button"
                  className={`toggle-switch ${queueFormLoopEnabled ? 'on' : ''}`}
                  onClick={() => setQueueFormLoopEnabled(!queueFormLoopEnabled)}
                >
                  <span className="toggle-knob" />
                </button>
              </div>
              <div className="modal-actions">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleUpdateQueueSettings}
                  loading={queueFormSaving}
                  disabled={!queueFormName.trim()}
                >
                  Save Settings
                </Button>
              </div>
            </GlassCard>

            <div className="queue-items-header">
              <h4>Marathon Games</h4>
              <span className="queue-runtime">
                Estimated runtime: {formatDuration(selectedQueue.estimated_total_seconds || 0)}
                {selectedQueue.status === 'active' ? (
                  <> • Time remaining: {formatDuration(selectedQueue.estimated_remaining_seconds || 0)}</>
                ) : (
                  <> • Time remaining: —</>
                )}
              </span>
            </div>

            <div className="queue-add">
              <Select
                label="Add Game"
                value={queueAddGameId}
                onChange={(e) => setQueueAddGameId(e.target.value)}
                options={[
                  { value: '', label: 'Select a game' },
                  ...games
                    .filter((game) => Number(game.question_count) > 0)
                    .map((game) => ({
                      value: game.id,
                      label: `${game.title} (${game.question_count || 0} Qs)`
                    }))
                ]}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddQueueItem}
                disabled={!queueAddGameId || queueActionLoading}
              >
                Add to Marathon
              </Button>
            </div>

            {queueItems.length === 0 ? (
              <GlassCard padding="sm">
                <p className="muted">No games in this marathon yet.</p>
              </GlassCard>
            ) : (
              <div className="queue-items-list">
                {queueItems.map((item) => (
                  <div
                    key={item.id}
                    className={`queue-item ${item.status === 'active' ? 'queue-item-active' : ''}`}
                  >
                    <div className="queue-item-info">
                      <span className="queue-item-position">{item.position}</span>
                      <div>
                        <div>{item.title}</div>
                        <div className="queue-item-meta">
                          {item.question_count || 0} questions • {formatDuration(item.estimated_duration_seconds || 0)}
                        </div>
                      </div>
                    </div>
                    <div className="queue-item-actions">
                      {item.status === 'active' && <Badge variant="success">Active</Badge>}
                      {item.status === 'completed' && <Badge variant="info">Completed</Badge>}
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Icons.ChevronUp}
                        onClick={() => handleMoveQueueItem(item, -1)}
                        disabled={item.status !== 'pending' || queueActionLoading}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Icons.ChevronDown}
                        onClick={() => handleMoveQueueItem(item, 1)}
                        disabled={item.status !== 'pending' || queueActionLoading}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Icons.Trash}
                        onClick={() => handleRemoveQueueItem(item)}
                        disabled={item.status !== 'pending' || queueActionLoading}
                        className="btn-danger-ghost"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="muted">Marathon not found.</p>
        )}
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// Game Editor Component
const TRIVIA_ANSWER_MAX_CHARS = 26;

function GameEditor({
  game,
  categories,
  accountId,
  canUseAI,
  isPro,
  maxQuestions = 10,
  onUpgrade,
  onSave,
  onSaveToBank,
  onToast,
  onCancel
}) {
  const [title, setTitle] = useState(game?.title || '');
  const [description, setDescription] = useState(game?.description || '');
  const [categoryId, setCategoryId] = useState(game?.category_id || '');
  const [questions, setQuestions] = useState(game?.questions || []);
  const [saving, setSaving] = useState(false);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(null);
  const [savingBankIds, setSavingBankIds] = useState([]);
  const [savingAllToBank, setSavingAllToBank] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiCount, setAiCount] = useState(10);
  const [generatingAI, setGeneratingAI] = useState(false);
  const questionLimitReached = !isPro && questions.length >= maxQuestions;

  const addQuestion = () => {
    if (questionLimitReached) {
      onToast?.(
        `Free plan includes up to ${maxQuestions} questions per game. Upgrade to Pro for unlimited questions.`,
        'error'
      );
      return;
    }
    const newQuestion = {
      id: `temp-${Date.now()}`,
      prompt: '',
      answers: [
        { id: `ans-${Date.now()}-1`, text: '' },
        { id: `ans-${Date.now()}-2`, text: '' },
        { id: `ans-${Date.now()}-3`, text: '' },
        { id: `ans-${Date.now()}-4`, text: '' },
      ],
      correct_answer_id: null,
      time_limit_seconds: 20,
      points: 100,
      explanation: ''
    };
    setQuestions([...questions, newQuestion]);
    setActiveQuestionIndex(questions.length);
  };

  const updateQuestion = (index, updates) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], ...updates };
    setQuestions(updated);
  };

  const deleteQuestion = (index) => {
    if (!confirm('Delete this question?')) return;
    const updated = questions.filter((_, i) => i !== index);
    setQuestions(updated);
    setActiveQuestionIndex(null);
  };

  const isPersistedQuestion = (question) => {
    if (!question?.id) return false;
    return !String(question.id).startsWith('temp-');
  };

  const canSaveQuestionToBank = (question) => {
    if (!question?.id) return false;
    const questionId = String(question.id);
    return isPersistedQuestion(question) || questionId.startsWith('ai-');
  };

  const handleSaveQuestionToBank = async (question) => {
    if (!onSaveToBank) return;
    const questionId = question?.id;
    setSavingBankIds((prev) => Array.from(new Set([...prev, questionId])));
    try {
      await onSaveToBank(questionId, question);
      onToast?.('Question saved to bank!');
    } catch (error) {
      onToast?.(error.message || 'Failed to save question', 'error');
    } finally {
      setSavingBankIds((prev) => prev.filter((id) => id !== questionId));
    }
  };

  const handleSaveAllQuestionsToBank = async () => {
    if (!onSaveToBank) return;
    const saveableQuestions = questions.filter(canSaveQuestionToBank);
    if (saveableQuestions.length === 0) {
      onToast?.('Save the game before adding questions to the bank.', 'error');
      return;
    }
    setSavingAllToBank(true);
    try {
      await Promise.all(saveableQuestions.map((q) => onSaveToBank(q.id, q)));
      onToast?.('Questions saved to bank!');
    } catch (error) {
      onToast?.(error.message || 'Failed to save questions', 'error');
    } finally {
      setSavingAllToBank(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Please enter a game title');
      return;
    }
    if (questions.length === 0) {
      alert('Please add at least one question');
      return;
    }
    // Validate questions
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.prompt.trim()) {
        alert(`Question ${i + 1} needs a prompt`);
        return;
      }
      if (!q.correct_answer_id) {
        alert(`Question ${i + 1} needs a correct answer selected`);
        return;
      }
      const overlongAnswerIndex = (q.answers || []).findIndex(
        (answer) => Array.from(String(answer?.text || '').trim()).length > TRIVIA_ANSWER_MAX_CHARS
      );
      if (overlongAnswerIndex >= 0) {
        alert(
          `Question ${i + 1}, answer ${String.fromCharCode(65 + overlongAnswerIndex)} must be `
          + `${TRIVIA_ANSWER_MAX_CHARS} characters or fewer to fit the TV display.`
        );
        return;
      }
    }
    setSaving(true);
    try {
      await onSave({ title, description, category_id: categoryId || null, questions });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!accountId) {
      onToast?.('Account not available for AI generation.', 'error');
      return;
    }
    if (!aiTopic.trim()) {
      onToast?.('Enter a topic to generate questions.', 'error');
      return;
    }
    if (questions.length > 0) {
      const shouldReplace = confirm('Replace your current questions with AI-generated questions?');
      if (!shouldReplace) return;
    }
    setGeneratingAI(true);
    try {
      const idempotencyKey = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `web_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      const queued = await api.generateTriviaQuestionsAI(
        accountId,
        aiTopic.trim(),
        aiCount,
        idempotencyKey
      );
      const deadline = Date.now() + 180000;
      let result = queued;
      while (!['completed', 'failed'].includes(result.status) && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        result = await api.getTriviaGenerationJob(queued.jobId);
      }
      if (result.status === 'failed') {
        throw new Error(result.error || 'Generation failed. Try again.');
      }
      if (result.status !== 'completed') {
        throw new Error('Generation is taking longer than expected. Try again in a moment.');
      }
      const generated = (result.questions || []).map((question, index) => {
        const baseId = `ai-${Date.now()}-${index}`;
        const answers = (question.answers || []).map((answer, answerIndex) => ({
          id: `${baseId}-ans-${answerIndex}`,
          text: answer.text || ''
        }));
        const correctIndex = (question.answers || []).findIndex((answer) => answer.isCorrect);
        return {
          id: baseId,
          prompt: question.prompt || '',
          answers,
          correct_answer_id: answers[correctIndex]?.id || null,
          time_limit_seconds: question.time_limit_seconds || 20,
          points: question.points || 100,
          explanation: question.explanation || ''
        };
      });
      if (generated.length === 0) {
        onToast?.('AI generation returned no questions.', 'error');
        return;
      }
      setQuestions(generated);
      setActiveQuestionIndex(0);
      onToast?.('AI questions generated!');
    } catch (error) {
      onToast?.(error.message || 'Failed to generate questions', 'error');
    } finally {
      setGeneratingAI(false);
    }
  };

  return (
    <div className="game-editor">
      <div className="game-editor-header">
        <Input
          label="Game Title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g., Friday Night Trivia"
        />
        <Input
          label="Description (optional)"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="A brief description of your game"
        />
        {categories.length > 0 && (
          <div className="input-group">
            <label className="input-label">Category</label>
            <select 
              className="select-field"
              value={categoryId} 
              onChange={e => setCategoryId(e.target.value)}
            >
              <option value="">Select a category...</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
              ))}
            </select>
          </div>
        )}
        {canUseAI ? (
          <GlassCard className="ai-generate-card" padding="sm">
            <div className="ai-generate-header">
              <div>
                <h4>Create with AI</h4>
                <p>Generate a full 10-question set with answers in seconds.</p>
              </div>
              <Badge variant="pro" size="sm">Pro</Badge>
            </div>
            <div className="ai-generate-controls">
              <Input
                label="Topic"
                value={aiTopic}
                onChange={e => setAiTopic(e.target.value)}
                placeholder="e.g., 90s movies, World Capitals, Space Exploration"
              />
              <div className="input-group">
                <label className="input-label">Question Count</label>
                <select
                  className="select-field"
                  value={aiCount}
                  onChange={e => setAiCount(parseInt(e.target.value, 10))}
                >
                  {[5, 10, 15, 20].map((count) => (
                    <option key={count} value={count}>{count} questions</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="ai-generate-actions">
              <Button
                variant="secondary"
                onClick={handleGenerateAI}
                loading={generatingAI}
                disabled={!aiTopic.trim()}
              >
                Generate Questions
              </Button>
              <span className="ai-generate-note">
                AI creates 4 TV-safe answers, each limited to {TRIVIA_ANSWER_MAX_CHARS} characters.
              </span>
            </div>
          </GlassCard>
        ) : (
          <GlassCard className="plan-limit-banner" padding="sm">
            <Icons.Sparkles />
            <div className="plan-limit-copy">
              <strong>AI question generation is a Pro feature.</strong>
              <span>Upgrade to Pro to generate TV-ready trivia questions.</span>
            </div>
            <Button size="sm" onClick={onUpgrade}>Upgrade to Pro</Button>
          </GlassCard>
        )}
      </div>

      <div className="questions-section">
        <div className="questions-header">
          <h3>Questions ({questions.length})</h3>
          <div className="question-header-actions">
            <Button variant="ghost" size="sm" onClick={handleSaveAllQuestionsToBank} disabled={savingAllToBank}>
              {savingAllToBank ? 'Saving...' : 'Save All to Bank'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={Icons.Plus}
              onClick={addQuestion}
              disabled={questionLimitReached}
              title={questionLimitReached ? 'Upgrade to Pro for unlimited questions.' : undefined}
            >
              Add Question
            </Button>
          </div>
        </div>

        {questionLimitReached && (
          <GlassCard className="plan-limit-banner" padding="sm">
            <Icons.BookOpen />
            <div className="plan-limit-copy">
              <strong>Free plan includes up to {maxQuestions} questions per game.</strong>
              <span>Upgrade to Pro for unlimited questions.</span>
            </div>
            <Button size="sm" onClick={onUpgrade}>Upgrade to Pro</Button>
          </GlassCard>
        )}

        {questions.length === 0 ? (
          <div className="empty-questions">
            <p>No questions yet. Add your first question to get started!</p>
            <Button onClick={addQuestion} icon={Icons.Plus} disabled={questionLimitReached}>
              Add Question
            </Button>
          </div>
        ) : (
          <div className="questions-list-editor">
            {questions.map((q, index) => (
              <div 
                key={q.id} 
                className={`question-editor-item ${activeQuestionIndex === index ? 'active' : ''}`}
              >
                <div 
                  className="question-summary"
                  onClick={() => setActiveQuestionIndex(activeQuestionIndex === index ? null : index)}
                >
                  <span className="question-number">{index + 1}</span>
                  <span className="question-preview">{q.prompt || 'Untitled question'}</span>
                  <div className="question-item-actions">
                    <Button variant="ghost" size="sm" icon={Icons.Trash} onClick={(e) => { e.stopPropagation(); deleteQuestion(index); }} />
                  </div>
                </div>
                
                {activeQuestionIndex === index && (
                  <div className="question-editor-details">
                    <Input
                      label="Question"
                      value={q.prompt}
                      onChange={e => updateQuestion(index, { prompt: e.target.value })}
                      placeholder="Enter your question..."
                    />
                    
                    <div className="answers-editor">
                      <label className="input-label">Answers (click to mark correct)</label>
                      {q.answers.map((ans, ansIndex) => (
                        <div 
                          key={ans.id} 
                          className={`answer-editor-item ${q.correct_answer_id === ans.id ? 'correct' : ''}`}
                          onClick={() => updateQuestion(index, { correct_answer_id: ans.id })}
                        >
                          <span className="answer-letter">{String.fromCharCode(65 + ansIndex)}</span>
                          <input
                            type="text"
                            value={ans.text}
                            onChange={(e) => {
                              e.stopPropagation();
                              const newAnswers = [...q.answers];
                              newAnswers[ansIndex] = { ...ans, text: e.target.value };
                              updateQuestion(index, { answers: newAnswers });
                            }}
                            onClick={e => e.stopPropagation()}
                            placeholder={`Answer ${String.fromCharCode(65 + ansIndex)}`}
                            maxLength={TRIVIA_ANSWER_MAX_CHARS}
                          />
                          {q.correct_answer_id === ans.id && <Icons.Check className="correct-icon" />}
                        </div>
                      ))}
                    </div>

                    <div className="question-settings">
                      <Input
                        label="Time Limit (seconds)"
                        type="number"
                        min={5}
                        max={60}
                        value={q.time_limit_seconds}
                        onChange={e => updateQuestion(index, { time_limit_seconds: parseInt(e.target.value) })}
                      />
                      <Input
                        label="Points"
                        type="number"
                        min={1}
                        max={10}
                        value={q.points}
                        onChange={e => updateQuestion(index, { points: parseInt(e.target.value) })}
                      />
                    </div>

                    <Input
                      label="Explanation (optional)"
                      value={q.explanation || ''}
                      onChange={e => updateQuestion(index, { explanation: e.target.value })}
                      placeholder="Shown after players answer"
                    />
                    <div className="question-bank-actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSaveQuestionToBank(q)}
                        disabled={!canSaveQuestionToBank(q) || savingBankIds.includes(q.id)}
                      >
                        {savingBankIds.includes(q.id) ? 'Saving...' : 'Save to Question Bank'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="modal-actions">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave} loading={saving}>
          {game?.id ? 'Save Changes' : 'Create Game'}
        </Button>
      </div>
    </div>
  );
}

// Go Live Modal Component
function GoLiveModal({ game, accountId, accountTimezone, scheduledSession, onSubmit, onCancel, isPro }) {
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [countdownSeconds, setCountdownSeconds] = useState(120);
  const [hideQuestionsOnMobile, setHideQuestionsOnMobile] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [sessionJoinCode, setSessionJoinCode] = useState('');
  const [sessionLink, setSessionLink] = useState('');


  useEffect(() => {
    loadDevices();
  }, []);

  const isEditingSchedule = Boolean(scheduledSession?.id);
  const timeZoneLabel = accountTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time';
  const scheduledLabel = scheduledDateTime
    ? new Date(scheduledDateTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';

  useEffect(() => {
    if (scheduledSession) {
      setIsScheduled(true);
      setCountdownSeconds(scheduledSession.countdown_seconds || 120);
      setScheduledDateTime(toLocalDateTimeInput(scheduledSession.scheduled_start_at));
      setDeliveryMethod(scheduledSession.schedule_target || (scheduledSession.device_id ? 'device' : 'web'));
      setHideQuestionsOnMobile(Boolean(scheduledSession.hide_questions_mobile));
      if (scheduledSession.device_id) {
        setSelectedDeviceId(scheduledSession.device_id);
      }
    } else {
      setIsScheduled(false);
      setScheduledDateTime('');
      setHideQuestionsOnMobile(false);
    }
  }, [scheduledSession]);

  useEffect(() => {
    if (deliveryMethod !== 'device') {
      setHideQuestionsOnMobile(false);
    }
  }, [deliveryMethod]);

  const loadDevices = async () => {
    try {
      const result = await api.getDevices(accountId);
      setDevices(result.devices || []);
      if (result.devices?.length > 0) {
        if (!selectedDeviceId) {
          setSelectedDeviceId(result.devices[0].id);
        }
        if (!deliveryMethod) {
          setDeliveryMethod('device');
        }
      } else {
        setDeliveryMethod('web');
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const toLocalDateTimeInput = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (value) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const handleSubmit = async () => {
    if (deliveryMethod === 'device' && !selectedDeviceId) {
      alert('Please select a device');
      return;
    }
    if (isScheduled && !scheduledDateTime) {
      alert('Please choose a start time');
      return;
    }
    if (isScheduled && !isPro) {
      alert('Scheduling requires a Pro subscription.');
      return;
    }
    setStarting(true);
    try {
      const result = await onSubmit({
        isScheduled,
        schedule_target: deliveryMethod === 'device' ? 'device' : 'web',
        device_id: deliveryMethod === 'device' ? selectedDeviceId : null,
        countdown_seconds: countdownSeconds,
        scheduled_start_at: scheduledDateTime ? new Date(scheduledDateTime).toISOString() : null,
        hide_questions_mobile: hideQuestionsOnMobile
      });
      const joinCode = result?.session?.join_code || result?.session?.joinCode;
      const qrUrl = result?.session?.qrUrl || result?.session?.qr_url;
      if (joinCode) {
        setSessionJoinCode(joinCode);
        setSessionLink(qrUrl || `https://splitcast.games/join/${joinCode}`);
      } else if (!isScheduled) {
        onCancel();
      }
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="go-live-loading">
        <Spinner />
        <p>Loading devices...</p>
      </div>
    );
  }

  return (
    <div className="go-live-modal">
      <div className="go-live-game-info">
        <h3>{game?.title}</h3>
        <p>{game?.question_count || 0} questions</p>
      </div>

      {sessionJoinCode ? (
        <GlassCard className="go-live-join-card" padding="md">
          <p className="go-live-join-label">Web Browser Code</p>
          <div className="go-live-join-code">{sessionJoinCode}</div>
          <p className="go-live-join-hint">Open splitcast.games and enter this code to join.</p>
          {isScheduled && scheduledLabel && (
            <p className="go-live-scheduled">
              Scheduled for {scheduledLabel} ({timeZoneLabel})
            </p>
          )}
          <div className="modal-actions">
            <Button
              variant="ghost"
              onClick={() => navigator.clipboard?.writeText(sessionLink || `https://splitcast.games/join/${sessionJoinCode}`)}
            >
              Copy Link
            </Button>
            <Button variant="secondary" onClick={() => window.open(sessionLink, '_blank', 'noopener')}>
              Open splitcast.games
            </Button>
            <Button onClick={onCancel}>Done</Button>
          </div>
        </GlassCard>
      ) : (
        <>
          <div className="input-group">
            <label className="input-label">Session Timing</label>
            <div className="toggle-row">
              <button
                type="button"
                className={`pill-toggle ${!isScheduled ? 'active' : ''}`}
                onClick={() => setIsScheduled(false)}
                disabled={isEditingSchedule}
              >
                Start Now
              </button>
              <button
                type="button"
                className={`pill-toggle ${isScheduled ? 'active' : ''}`}
                onClick={() => setIsScheduled(true)}
                disabled={!isPro}
                title={!isPro ? 'Only Available in Pro Subscription' : undefined}
              >
                Schedule
              </button>
            </div>
          </div>

          {isScheduled && (
            <div className="input-group">
              <label className="input-label">Scheduled Start</label>
              <input
                type="datetime-local"
                className="input-field"
                value={scheduledDateTime}
                onChange={(e) => setScheduledDateTime(e.target.value)}
                disabled={!isPro}
                title={!isPro ? 'Only Available in Pro Subscription' : undefined}
              />
              <p className="input-help">
                Times shown in {timeZoneLabel}. Countdown begins when the lobby opens.
              </p>
            </div>
          )}

          <div className="input-group">
            <label className="input-label">Display Mode</label>
            <div className="toggle-row">
              <button
                type="button"
                className={`pill-toggle ${deliveryMethod === 'device' ? 'active' : ''}`}
                onClick={() => setDeliveryMethod('device')}
                disabled={devices.length === 0}
              >
                <Icons.Devices /> Device
              </button>
              <button
                type="button"
                className={`pill-toggle ${deliveryMethod === 'web' ? 'active' : ''}`}
                onClick={() => setDeliveryMethod('web')}
              >
                <Icons.Globe /> Web Browser
              </button>
            </div>
          </div>

          {deliveryMethod === 'device' && devices.length === 0 ? (
            <GlassCard className="no-devices-warning" padding="md">
              <Icons.Devices />
              <p>No devices paired. You need to pair a TV device to go live.</p>
            </GlassCard>
          ) : deliveryMethod === 'device' ? (
            <>
              <div className="input-group">
                <label className="input-label">Select Display Device</label>
                <select 
                  className="select-field"
                  value={selectedDeviceId} 
                  onChange={e => setSelectedDeviceId(e.target.value)}
                >
                  {devices.map(device => (
                    <option key={device.id} value={device.id}>
                      {device.name || 'Unnamed Device'} {device.is_online ? '(Online)' : '(Offline)'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="toggle-row">
                <label className="toggle-label">
                  <span>Hide questions on mobile</span>
                  <span className="toggle-hint">Players only see answers on phones and web</span>
                </label>
                <button
                  className={`toggle-switch ${hideQuestionsOnMobile ? 'on' : ''}`}
                  onClick={() => setHideQuestionsOnMobile(!hideQuestionsOnMobile)}
                >
                  <span className="toggle-knob" />
                </button>
              </div>
            </>
          ) : (
            <GlassCard className="go-live-web-info" padding="sm">
              <Icons.Globe />
              <span>Players will join in their browser at splitcast.games.</span>
            </GlassCard>
          )}

          <div className="input-group">
            <label className="input-label">Lobby Countdown</label>
            <select 
              className="select-field"
              value={countdownSeconds} 
              onChange={e => setCountdownSeconds(parseInt(e.target.value))}
            >
              <option value={60}>1 minute</option>
              <option value={120}>2 minutes</option>
              <option value={180}>3 minutes</option>
              <option value={300}>5 minutes</option>
            </select>
            <p className="input-help">Time for players to join before the game starts</p>
          </div>
        </>
      )}

      {!sessionJoinCode && (
        <div className="modal-actions">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            loading={starting} 
            disabled={
              (deliveryMethod === 'device' && (!selectedDeviceId || devices.length === 0)) ||
              (isScheduled && !scheduledDateTime)
            }
            icon={Icons.Play}
          >
            {isScheduled ? (isEditingSchedule ? 'Update Schedule' : 'Schedule Session') : 'Start Session'}
          </Button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SCHEDULES PAGE
// ============================================================================

function SchedulesPage() {
  const { currentAccount, isPro } = useAuth();
  const [scheduledSessions, setScheduledSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    if (currentAccount?.id && isPro) {
      loadScheduledSessions();
    }
  }, [currentAccount?.id, isPro]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleUpgrade = async () => {
    if (!currentAccount?.id) return;
    setUpgrading(true);
    try {
      const result = await api.createCheckout(currentAccount.id);
      if (result.url) {
        window.location.href = result.url;
      } else {
        showToast('Unable to start checkout. Please try again.', 'error');
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setUpgrading(false);
    }
  };

  const loadScheduledSessions = async () => {
    try {
      setLoading(true);
      const result = await api.getScheduledTriviaSessions(currentAccount.id);
      setScheduledSessions(result.sessions || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatScheduleDateTime = (isoString) => {
    if (!isoString) return 'TBD';
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return 'TBD';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatCountdownSeconds = (seconds) => {
    const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
    if (totalSeconds < 60) {
      return `${totalSeconds}s`;
    }
    if (totalSeconds % 60 === 0 && totalSeconds < 3600) {
      return `${totalSeconds / 60}m`;
    }
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m ${totalSeconds % 60}s`;
  };

  const handleReschedule = (session) => {
    setSelectedGame({
      id: session.game_id,
      title: session.game_title,
      question_count: session.question_count || 0
    });
    setEditingSchedule(session);
    setShowScheduleModal(true);
  };

  const handleCancelSchedule = async (sessionId) => {
    if (!confirm('Cancel this scheduled session?')) return;
    try {
      await api.cancelTriviaSession(sessionId);
      showToast('Scheduled session canceled.');
      loadScheduledSessions();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Manage Schedules</h1>
          <p className="page-subtitle">Review, reschedule, or cancel upcoming sessions</p>
        </div>
        <div className="page-header-actions">
          <a
            className="header-video-link"
            href="https://www.youtube.com/watch?v=4gpXKvv2SX4"
            target="_blank"
            rel="noreferrer"
          >
            <Icons.Play /> Manage Schedules
          </a>
        </div>
      </div>

      {!isPro ? (
        <GlassCard className="upgrade-prompt">
          <div className="upgrade-content">
            <div className="upgrade-icon">
              <Icons.Clock />
            </div>
            <h2>Upgrade to Pro</h2>
            <p>Game scheduling and Marathon Mode game queuing are available on the Pro plan.</p>
            <Button variant="primary" onClick={handleUpgrade} loading={upgrading}>
              Upgrade to Pro - $19/month
            </Button>
          </div>
        </GlassCard>
      ) : (
        loading ? (
          <div className="loading-container">
            <Spinner size="lg" />
          </div>
        ) : scheduledSessions.length === 0 ? (
          <EmptyState
            icon={Icons.Clock}
            title="No scheduled sessions"
            description="Schedule games from the Games tab when you're ready."
          />
        ) : (
          <div className="schedule-list">
            {scheduledSessions.map((session) => (
              <GlassCard key={session.id} className="schedule-item" padding="sm">
                <div className="schedule-header">
                  <div>
                    <h3>{session.game_title}</h3>
                    <div className="schedule-badges">
                      {session.category_name && (
                        <Badge variant="info">{session.category_icon} {session.category_name}</Badge>
                      )}
                      <Badge variant="warning">Scheduled</Badge>
                    </div>
                  </div>
                  <div className="schedule-meta">
                    <span><Icons.Clock /> {formatScheduleDateTime(session.scheduled_start_at)}</span>
                    <span>
                      {session.schedule_target === 'device'
                        ? (session.device_name || 'Device')
                        : 'Web'}
                    </span>
                  </div>
                </div>
                <div className="schedule-details">
                  <span>{session.question_count || 0} questions</span>
                  <span>Countdown {formatCountdownSeconds(session.countdown_seconds || 120)}</span>
                </div>
                <div className="schedule-actions">
                  <Button variant="ghost" size="sm" icon={Icons.Edit} onClick={() => handleReschedule(session)}>
                    Reschedule
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Icons.Trash}
                    onClick={() => handleCancelSchedule(session.id)}
                    className="btn-danger-ghost"
                  >
                    Cancel
                  </Button>
                </div>
              </GlassCard>
            ))}
          </div>
        )
      )}

      <Modal
        isOpen={showScheduleModal}
        title="Update Scheduled Session"
        onClose={() => { setShowScheduleModal(false); setEditingSchedule(null); setSelectedGame(null); }}
      >
        <GoLiveModal
          game={selectedGame}
          accountId={currentAccount?.id}
          accountTimezone={currentAccount?.timezone}
          scheduledSession={editingSchedule}
          isPro={isPro}
          onSubmit={async (sessionData) => {
            try {
              const payload = {
                scheduleTarget: sessionData.schedule_target,
                deviceId: sessionData.device_id || null,
                scheduledStartAt: sessionData.scheduled_start_at,
                countdownSeconds: sessionData.countdown_seconds
              };
              const result = await api.rescheduleTriviaSession(editingSchedule.id, payload);
              showToast('Session rescheduled!');
              loadScheduledSessions();
              return result;
            } catch (error) {
              showToast(error.message, 'error');
              return null;
            }
          }}
          onCancel={() => { setShowScheduleModal(false); setEditingSchedule(null); setSelectedGame(null); }}
        />
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============================================================================
// ADMIN MODERATION PAGE
// ============================================================================

function AdminModerationPage() {
  const { isModerator } = useAuth();
  const [pendingGames, setPendingGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [adminMetrics, setAdminMetrics] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [toast, setToast] = useState(null);
  const [previewPoolGames, setPreviewPoolGames] = useState([]);
  const [previewPoolLoading, setPreviewPoolLoading] = useState(true);
  const [previewPoolSearch, setPreviewPoolSearch] = useState('');
  const [previewPoolSavingId, setPreviewPoolSavingId] = useState(null);
  const [mobileCampaignOverview, setMobileCampaignOverview] = useState(null);
  const [mobileCampaignLoading, setMobileCampaignLoading] = useState(true);
  const [mobileCampaignAction, setMobileCampaignAction] = useState(null);
  const [mobileCampaignTestSegment, setMobileCampaignTestSegment] = useState('expired');
  const [mobileCampaignConfirmation, setMobileCampaignConfirmation] = useState('');

  useEffect(() => {
    if (isModerator) {
      loadPendingGames();
      loadAdminMetrics();
      loadPreviewPoolGames();
      loadMobileCampaign();
    }
  }, [isModerator]);

  const numberFormatter = useMemo(() => new Intl.NumberFormat('en-US'), []);
  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }),
    []
  );

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const loadPendingGames = async () => {
    try {
      setLoading(true);
      const result = await api.getPendingCommunityGames();
      setPendingGames(result.games || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAdminMetrics = async () => {
    try {
      setMetricsLoading(true);
      const result = await api.getAdminMetrics();
      setAdminMetrics(result.metrics || null);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadPreviewPoolGames = async (search = previewPoolSearch) => {
    try {
      setPreviewPoolLoading(true);
      const result = await api.getPreviewPoolGames({ search, limit: 50 });
      setPreviewPoolGames(result.games || []);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setPreviewPoolLoading(false);
    }
  };

  const loadMobileCampaign = async () => {
    try {
      setMobileCampaignLoading(true);
      const result = await api.getMobileAppLaunchCampaign();
      setMobileCampaignOverview(result);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setMobileCampaignLoading(false);
    }
  };

  const handleMobileCampaignTest = async () => {
    try {
      setMobileCampaignAction('test');
      const result = await api.sendMobileAppLaunchTest(mobileCampaignTestSegment);
      showToast(`Test sent to your email using the ${result.result?.segment || mobileCampaignTestSegment} version.`);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setMobileCampaignAction(null);
    }
  };

  const handleMobileCampaignSend = async () => {
    try {
      setMobileCampaignAction('send');
      await api.sendMobileAppLaunchCampaign(mobileCampaignConfirmation);
      setMobileCampaignConfirmation('');
      showToast('Mobile app launch campaign completed.');
      await loadMobileCampaign();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setMobileCampaignAction(null);
    }
  };

  const openGameDetails = async (game) => {
    setSelectedGame(game);
    setSelectedDetails(null);
    setRejectReason('');
    setDetailLoading(true);
    try {
      const result = await api.getModerationGameDetails(game.id);
      setSelectedDetails(result.game);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedGame) return;
    try {
      await api.approveCommunityGame(selectedGame.id);
      showToast('Game approved.');
      setSelectedGame(null);
      setSelectedDetails(null);
      loadPendingGames();
      loadAdminMetrics();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleReject = async () => {
    if (!selectedGame) return;
    try {
      await api.rejectCommunityGame(selectedGame.id, rejectReason.trim() || 'Rejected by moderator');
      showToast('Game rejected.');
      setSelectedGame(null);
      setSelectedDetails(null);
      loadPendingGames();
      loadAdminMetrics();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleTogglePreviewPool = async (gameId, enabled) => {
    try {
      setPreviewPoolSavingId(gameId);
      await api.setPreviewPoolGame(gameId, enabled);
      showToast(enabled ? 'Added to preview pool.' : 'Removed from preview pool.');
      loadPreviewPoolGames();
      loadAdminMetrics();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setPreviewPoolSavingId(null);
    }
  };

  if (!isModerator) {
    return (
      <div className="page-container">
        <EmptyState
          icon={Icons.Shield}
          title="Admin Access Required"
          description="You do not have access to moderation tools."
        />
      </div>
    );
  }

  const subscriptions = adminMetrics?.subscriptions || {};
  const platform = adminMetrics?.platform || {};
  const activity = adminMetrics?.activity || {};
  const monthChurn = Number(subscriptions.churnRateMonth || 0);

  const metricCards = [
    { title: 'Total Subscribers', value: numberFormatter.format(Number(subscriptions.totalSubscribers || 0)), icon: Icons.Users },
    { title: 'Total Paid Subscribers', value: numberFormatter.format(Number(subscriptions.paidSubscribers || 0)), icon: Icons.Users },
    { title: 'New This Month', value: numberFormatter.format(Number(subscriptions.newSubscribersMonth || 0)), icon: Icons.TrendingUp, changeType: 'positive' },
    { title: 'Lost This Month', value: numberFormatter.format(Number(subscriptions.lostSubscribersMonth || 0)), icon: Icons.TrendingDown, changeType: 'negative' },
    { title: 'Gross MRR', value: currencyFormatter.format((Number(subscriptions.grossMrrCents || 0)) / 100), icon: Icons.Activity },
    { title: 'Projected MRR', value: currencyFormatter.format((Number(subscriptions.projectedMrrCents || 0)) / 100), icon: Icons.Activity },
    { title: 'Monthly Churn', value: `${monthChurn.toFixed(2)}%`, icon: Icons.TrendingDown, changeType: monthChurn > 0 ? 'negative' : 'neutral' },
    { title: 'Total Boards', value: numberFormatter.format(Number(platform.totalBoards || 0)), icon: Icons.Boards },
    { title: 'Total Games', value: numberFormatter.format(Number(platform.totalGames || 0)), icon: Icons.Games },
    { title: 'Active Games', value: numberFormatter.format(Number(platform.totalActiveGames || 0)), icon: Icons.Play },
    { title: 'Live Games', value: numberFormatter.format(Number(platform.totalLiveGames || 0)), icon: Icons.Zap },
    { title: 'Total Devices', value: numberFormatter.format(Number(platform.totalDevices || 0)), icon: Icons.Devices },
    { title: 'Online Devices (24h)', value: numberFormatter.format(Number(platform.onlineDevices24h || 0)), icon: Icons.Wifi },
    { title: 'Total Accounts', value: numberFormatter.format(Number(platform.totalAccounts || 0)), icon: Icons.Dashboard },
    { title: 'New Accounts (Month)', value: numberFormatter.format(Number(activity.newAccountsMonth || 0)), icon: Icons.TrendingUp, changeType: 'positive' },
    { title: 'Pending Moderation', value: numberFormatter.format(Number(platform.pendingModerationGames || 0)), icon: Icons.Shield },
    { title: 'Pending Reports', value: numberFormatter.format(Number(platform.pendingReports || 0)), icon: Icons.Shield },
    { title: 'Preview Pool Games', value: numberFormatter.format(Number(platform.previewPoolGames || 0)), icon: Icons.Play },
    { title: 'Games Played', value: numberFormatter.format(Number(activity.gamesPlayedAllTime || 0)), icon: Icons.Activity },
    { title: 'Games Played (30d)', value: numberFormatter.format(Number(activity.gamesPlayed30d || 0)), icon: Icons.Activity }
  ];
  const mobileCampaignConfig = mobileCampaignOverview?.config || {};
  const mobileCampaignAudience = mobileCampaignOverview?.audience || {};
  const mobileCampaign = mobileCampaignOverview?.campaign || null;
  const mobileCampaignTotals = mobileCampaign?.totals || {};
  const mobileCampaignConfirmed = mobileCampaignConfirmation === mobileCampaignConfig.confirmationText;
  const mobileCampaignTestReady = Boolean(
    mobileCampaignConfig.stream?.ready
    && mobileCampaignConfig.clickBaseUrl
    && !mobileCampaignConfig.configurationError
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Admin Moderation</h1>
          <p className="page-subtitle">Platform metrics and community moderation</p>
        </div>
        <div className="page-header-actions">
          <Button variant="ghost" size="sm" onClick={() => {
            loadAdminMetrics();
            loadPendingGames();
            loadMobileCampaign();
          }}>
            Refresh
          </Button>
        </div>
      </div>

      {metricsLoading ? (
        <div className="loading-container admin-metrics-loading">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="kpi-grid admin-kpi-grid">
          {metricCards.map((card) => (
            <KPICard
              key={card.title}
              title={card.title}
              value={card.value}
              icon={card.icon}
              changeType={card.changeType}
            />
          ))}
        </div>
      )}

      <div className="admin-section-header">
        <h2>Mobile App Product Update</h2>
        <Badge variant={mobileCampaign ? 'success' : (mobileCampaignConfig.readyToSend ? 'info' : 'warning')}>
          {mobileCampaign ? mobileCampaign.status : (mobileCampaignConfig.readyToSend ? 'Ready' : 'Not Ready')}
        </Badge>
      </div>

      {mobileCampaignLoading ? (
        <div className="loading-container admin-metrics-loading">
          <Spinner size="md" />
        </div>
      ) : (
        <GlassCard className="admin-item marketing-campaign-card" padding="sm">
          <div className="admin-header">
            <div>
              <h3>SplitCast Mobile Apps Launch</h3>
              <p className="admin-description">
                One segmented Broadcast send for paid, active-trial, and expired or free users.
                Tests are delivered only to your signed-in moderator email.
              </p>
            </div>
            <div className="admin-badges">
              <Badge variant={mobileCampaignConfig.stream?.ready ? 'success' : 'warning'}>
                Postmark {mobileCampaignConfig.stream?.ready ? 'Ready' : 'Unavailable'}
              </Badge>
              <Badge variant="info">{mobileCampaignConfig.messageStream || 'product-updates'}</Badge>
            </div>
          </div>

          <div className="marketing-campaign-grid">
            {[
              ['Active Paid Recipients', mobileCampaignAudience.paid],
              ['Active Trial', mobileCampaignAudience.trial],
              ['Expired / Free', mobileCampaignAudience.expired],
              ['Eligible Total', mobileCampaignAudience.total]
            ].map(([label, value]) => (
              <div className="marketing-campaign-stat" key={label}>
                <span>{label}</span>
                <strong>{numberFormatter.format(Number(value || 0))}</strong>
              </div>
            ))}
          </div>

          <div className="marketing-campaign-meta">
            <span>Minimum account age: {mobileCampaignConfig.minUserAgeDays || 0} days</span>
            <span>Excluded addresses: {mobileCampaignConfig.excludedEmailCount || 0}</span>
            <a href={mobileCampaignConfig.appleAppStoreUrl} target="_blank" rel="noreferrer">Apple listing</a>
            <a href={mobileCampaignConfig.googlePlayStoreUrl} target="_blank" rel="noreferrer">Google Play listing</a>
          </div>

          <div className="admin-notes">
            Nothing sends automatically. Review the test email and audience before launching;
            the current audience is snapshotted only when the final send begins.
            <br />
            Counts represent eligible email recipients, not unique subscriptions. Members of the
            same paid account are counted separately.
            {mobileCampaignConfig.configurationError && (
              <><br /><strong>Configuration:</strong> {mobileCampaignConfig.configurationError}</>
            )}
            {mobileCampaignConfig.stream?.error && (
              <><br /><strong>Postmark:</strong> {mobileCampaignConfig.stream.error}</>
            )}
          </div>

          {mobileCampaign ? (
            <>
              <div className="marketing-campaign-grid">
                {[
                  ['Sent', mobileCampaignTotals.sent],
                  ['Suppressed', mobileCampaignTotals.suppressed],
                  ['App Store Clicks', mobileCampaignTotals.appStoreClicked],
                  ['Logged In', mobileCampaignTotals.loggedIn],
                  ['Paired Device', mobileCampaignTotals.pairedDevice],
                  ['Board Live', mobileCampaignTotals.activatedBoard],
                  ['Subscribed', mobileCampaignTotals.subscribed]
                ].map(([label, value]) => (
                  <div className="marketing-campaign-stat" key={label}>
                    <span>{label}</span>
                    <strong>{numberFormatter.format(Number(value || 0))}</strong>
                  </div>
                ))}
              </div>
              <p className="admin-description">
                Campaign completed {mobileCampaign.completedAt
                  ? new Date(mobileCampaign.completedAt).toLocaleString()
                  : 'status pending'}.
                Postmark remains the source of truth for delivery, bounce, and unsubscribe reporting.
              </p>
            </>
          ) : (
            <>
              <div className="marketing-campaign-controls">
                <div className="input-group">
                  <label className="input-label" htmlFor="mobile-campaign-test-segment">Test email version</label>
                  <select
                    id="mobile-campaign-test-segment"
                    className="select-field"
                    value={mobileCampaignTestSegment}
                    onChange={(event) => setMobileCampaignTestSegment(event.target.value)}
                  >
                    <option value="paid">Paid user</option>
                    <option value="trial">Active trial user</option>
                    <option value="expired">Expired or free user</option>
                  </select>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!mobileCampaignTestReady || !!mobileCampaignAction}
                  onClick={handleMobileCampaignTest}
                >
                  {mobileCampaignAction === 'test' ? 'Sending Test...' : 'Send Test to Me'}
                </Button>
              </div>

              <div className="input-group marketing-campaign-confirmation">
                <label className="input-label" htmlFor="mobile-campaign-confirmation">
                  Type <code>{mobileCampaignConfig.confirmationText}</code> to launch once
                </label>
                <input
                  id="mobile-campaign-confirmation"
                  className="input-field"
                  value={mobileCampaignConfirmation}
                  onChange={(event) => setMobileCampaignConfirmation(event.target.value)}
                  placeholder={mobileCampaignConfig.confirmationText || 'Confirmation phrase'}
                  autoComplete="off"
                />
              </div>
              <div className="admin-actions">
                <Button
                  disabled={
                    !mobileCampaignConfig.readyToSend
                    || !mobileCampaignConfirmed
                    || !!mobileCampaignAction
                  }
                  onClick={handleMobileCampaignSend}
                >
                  {mobileCampaignAction === 'send'
                    ? 'Sending Campaign...'
                    : `Send to ${numberFormatter.format(Number(mobileCampaignAudience.total || 0))} Users`}
                </Button>
              </div>
            </>
          )}
        </GlassCard>
      )}

      <div className="admin-section-header">
        <h2>Always-On Preview Pool</h2>
        <Badge variant="info">{numberFormatter.format(previewPoolGames.filter((game) => game.preview_enabled).length)} Enabled</Badge>
      </div>

      <GlassCard className="admin-item" padding="sm">
        <div className="admin-actions" style={{ marginBottom: 16 }}>
          <input
            className="input-field"
            type="search"
            value={previewPoolSearch}
            onChange={(e) => setPreviewPoolSearch(e.target.value)}
            placeholder="Search public community games"
          />
          <Button variant="ghost" size="sm" onClick={() => loadPreviewPoolGames(previewPoolSearch)}>
            Search
          </Button>
        </div>
        <p className="page-subtitle" style={{ marginBottom: 0 }}>
          Enable public community games with at least {PREVIEW_ROUND_QUESTION_COUNT} questions.
          Each rotating `/play-preview` round uses {PREVIEW_ROUND_QUESTION_COUNT} questions.
        </p>
      </GlassCard>

      {previewPoolLoading ? (
        <div className="loading-container">
          <Spinner size="lg" />
        </div>
      ) : previewPoolGames.length === 0 ? (
        <EmptyState
          icon={Icons.Play}
          title="No eligible preview games found"
          description={`Public community games with at least ${PREVIEW_ROUND_QUESTION_COUNT} questions will appear here.`}
        />
      ) : (
        <div className="admin-list">
          {previewPoolGames.map((game) => (
            <GlassCard key={game.id} className="admin-item" padding="sm">
              <div className="admin-header">
                <div>
                  <h3>{game.title}</h3>
                  <div className="admin-badges">
                    {game.category_name && (
                      <Badge variant="info">{game.category_icon} {game.category_name}</Badge>
                    )}
                    {game.preview_enabled ? <Badge variant="success">Enabled</Badge> : <Badge variant="warning">Disabled</Badge>}
                    {!game.eligibleForPreview && (
                      <Badge variant="danger">Needs {PREVIEW_ROUND_QUESTION_COUNT} questions</Badge>
                    )}
                  </div>
                </div>
                <div className="admin-meta">
                  <span>{game.question_count || 0} questions</span>
                  {game.creator_name && <span>By {game.creator_name}</span>}
                  {game.last_selected_at && <span>Last used {new Date(game.last_selected_at).toLocaleDateString()}</span>}
                </div>
              </div>
              {game.description && <p className="admin-description">{game.description}</p>}
              <div className="admin-actions">
                <Button
                  variant={game.preview_enabled ? 'secondary' : 'ghost'}
                  size="sm"
                  disabled={previewPoolSavingId === game.id || (!game.preview_enabled && !game.eligibleForPreview)}
                  onClick={() => handleTogglePreviewPool(game.id, !game.preview_enabled)}
                >
                  {previewPoolSavingId === game.id ? 'Saving...' : (game.preview_enabled ? 'Remove From Preview' : 'Add To Preview')}
                </Button>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      <div className="admin-section-header">
        <h2>Pending Community Submissions</h2>
        <Badge variant="warning">{numberFormatter.format(pendingGames.length)} Pending</Badge>
      </div>

      {loading ? (
        <div className="loading-container">
          <Spinner size="lg" />
        </div>
      ) : pendingGames.length === 0 ? (
        <EmptyState
          icon={Icons.Shield}
          title="No pending submissions"
          description="Community submissions will appear here for review."
        />
      ) : (
        <div className="admin-list">
          {pendingGames.map((game) => (
            <GlassCard key={game.id} className="admin-item" padding="sm">
              <div className="admin-header">
                <div>
                  <h3>{game.title}</h3>
                  <div className="admin-badges">
                    {game.category_name && (
                      <Badge variant="info">{game.category_icon} {game.category_name}</Badge>
                    )}
                    <Badge variant="warning">Pending</Badge>
                  </div>
                </div>
                <div className="admin-meta">
                  <span>{game.question_count || 0} questions</span>
                  {game.creator_name && <span>By {game.creator_name}</span>}
                </div>
              </div>
              {game.description && <p className="admin-description">{game.description}</p>}
              {game.moderation_notes && (
                <div className="admin-notes">Flagged: {game.moderation_notes}</div>
              )}
              <div className="admin-actions">
                <Button variant="ghost" size="sm" icon={Icons.BookOpen} onClick={() => openGameDetails(game)}>
                  Review Details
                </Button>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      <Modal
        isOpen={!!selectedGame}
        title="Review Community Submission"
        onClose={() => { setSelectedGame(null); setSelectedDetails(null); }}
        size="lg"
      >
        {detailLoading ? (
          <div className="loading-container">
            <Spinner size="lg" />
          </div>
        ) : selectedDetails ? (
          <div className="community-preview">
            <div className="community-preview-header">
              <div>
                <h3>{selectedDetails.title}</h3>
                {selectedDetails.category_name && (
                  <Badge variant="info">{selectedDetails.category_icon} {selectedDetails.category_name}</Badge>
                )}
              </div>
              <div className="community-preview-meta">
                {selectedDetails.creator_name && (
                  <span>By {selectedDetails.creator_name}</span>
                )}
                {selectedDetails.creator_email && (
                  <span>{selectedDetails.creator_email}</span>
                )}
              </div>
            </div>
            {selectedDetails.description && (
              <p className="community-preview-description">{selectedDetails.description}</p>
            )}
            {selectedDetails.moderation_notes && (
              <div className="admin-notes">Flagged: {selectedDetails.moderation_notes}</div>
            )}
            <div className="community-preview-questions">
              {(selectedDetails.questions || []).map((question, index) => (
                <GlassCard key={question.id} className="community-question" padding="sm">
                  <div className="community-question-header">
                    <span className="question-number">Q{index + 1}</span>
                    <span className="question-preview">{question.prompt}</span>
                  </div>
                  <div className="community-answers">
                    {(question.answers || []).map((answer) => (
                      <div
                        key={answer.id}
                        className={`community-answer ${answer.isCorrect ? 'correct' : ''}`}
                      >
                        <span>{answer.text}</span>
                        {answer.isCorrect && <Icons.Check className="correct-icon" />}
                      </div>
                    ))}
                  </div>
                  {question.explanation && (
                    <div className="community-explanation">{question.explanation}</div>
                  )}
                </GlassCard>
              ))}
            </div>
            <div className="input-group">
              <label className="input-label">Rejection Reason (optional)</label>
              <textarea
                className="textarea-field"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide a brief reason if rejecting."
              />
            </div>
            <div className="modal-actions">
              <Button variant="ghost" onClick={() => { setSelectedGame(null); setSelectedDetails(null); }}>
                Close
              </Button>
              <Button variant="secondary" onClick={handleReject}>
                Reject
              </Button>
              <Button onClick={handleApprove}>
                Approve
              </Button>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={Icons.Shield}
            title="Unable to load submission"
            description="Try again or refresh the list."
          />
        )}
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============================================================================
// DEVICES PAGE
// ============================================================================

function DevicesPage({ onBoardAction }) {
  const { currentAccount, accountTier, isPro } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showPairModal, setShowPairModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renamingDevice, setRenamingDevice] = useState(null);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [pairingCode, setPairingCode] = useState('');
  const [pairDeviceName, setPairDeviceName] = useState('');
  const [pairBoardId, setPairBoardId] = useState('');
  const [pairingNextStep, setPairingNextStep] = useState(null);
  const [pairing, setPairing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    if (currentAccount?.id) {
      loadDevices();
      loadBoards();
    }
  }, [currentAccount?.id]);

  const loadDevices = async () => {
    try {
      setLoading(true);
      const result = await api.getDevices(currentAccount.id);
      setDevices(result.devices || []);
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadBoards = async () => {
    try {
      const result = await api.getBoards(currentAccount.id);
      setBoards(result.boards || []);
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    }
  };

  const openPairModal = () => {
    setPairingCode('');
    setPairDeviceName('');
    setPairBoardId('');
    setShowPairModal(true);
  };

  const closePairModal = () => {
    setShowPairModal(false);
    setPairingCode('');
    setPairDeviceName('');
    setPairBoardId('');
  };

  const handlePairDevice = async () => {
    const normalizedCode = pairingCode.trim().toUpperCase();
    if (!normalizedCode) return;
    if (!/^[A-Z0-9]{8}$/.test(normalizedCode)) {
      setToast({ message: 'Enter the 8-character pairing code shown on your device.', type: 'error' });
      return;
    }
    const normalizedName = pairDeviceName.trim();
    if (!normalizedName) {
      setToast({ message: 'Enter a name for this device.', type: 'error' });
      return;
    }
    setPairing(true);
    try {
      const pairResult = await api.pairDevice(normalizedCode, currentAccount.id, {
        name: normalizedName,
        assignedBoardId: pairBoardId || null
      });

      const assignedBoard = boards.find(board => board.id === pairBoardId);
      closePairModal();
      await loadDevices();

      if (boards.length === 0) {
        setPairingNextStep({
          title: 'Create a Board First',
          description: `${normalizedName} is paired. Create a board next so it has content to display.`,
          actionLabel: 'Create Board',
          action: { type: 'create-board', deviceId: pairResult.device.id }
        });
      } else if (assignedBoard && Number(assignedBoard.message_count || 0) === 0) {
        setPairingNextStep({
          title: 'Add Your First Message',
          description: `${normalizedName} is assigned to ${assignedBoard.name}, but that board does not have a message yet.`,
          actionLabel: 'Add Message',
          action: { type: 'add-message', boardId: assignedBoard.id }
        });
      } else {
        setToast({
          message: pairBoardId
            ? 'Device paired and board assigned!'
            : 'Device paired successfully!',
          type: 'success'
        });
      }
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setPairing(false);
    }
  };

  const handlePairingNextStep = () => {
    const action = pairingNextStep?.action;
    setPairingNextStep(null);
    if (action) onBoardAction?.(action);
  };

  const handleUnpairDevice = async (deviceId) => {
    if (!confirm('Unpair this device? It will stop displaying content.')) return;
    try {
      await api.unpairDevice(deviceId);
      setToast({ message: 'Device unpaired', type: 'success' });
      loadDevices();
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    }
  };

  const handleRenameClick = (device) => {
    setRenamingDevice(device);
    setNewDeviceName(device.name || '');
    setSelectedBoardId(device.assigned_board_id || '');
    setShowRenameModal(true);
  };

  const handleSaveDevice = async () => {
    if (!newDeviceName.trim()) {
      setToast({ message: 'Please enter a name', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      await api.updateDevice(renamingDevice.id, { 
        name: newDeviceName.trim(),
        assignedBoardId: selectedBoardId || null
      });
      setToast({ message: 'Device updated!', type: 'success' });
      setShowRenameModal(false);
      setRenamingDevice(null);
      setNewDeviceName('');
      setSelectedBoardId('');
      loadDevices();
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpgrade = async () => {
    if (!currentAccount?.id) return;
    setUpgrading(true);
    try {
      const result = await api.createCheckout(currentAccount.id);
      if (result.url) {
        window.location.href = result.url;
      } else {
        setToast({ message: 'Unable to start checkout. Please try again.', type: 'error' });
      }
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    } finally {
      setUpgrading(false);
    }
  };

  const onlineDevices = devices.filter(d => d.is_online).length;
  const pairedCount = devices.filter(d => d.paired_at).length;
  const maxDevices = isPro
    ? (accountTier?.features?.maxDevices || Infinity)
    : (accountTier?.features?.maxDevices || 1);
  const isAtLimit = Number.isFinite(maxDevices) && pairedCount >= maxDevices;
  const deviceLimitLabel = Number.isFinite(maxDevices) ? maxDevices : 'Unlimited';

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>TV Devices</h1>
          <p className="page-subtitle">Manage your connected displays</p>
        </div>
        <div className="page-header-actions">
          {!isPro && (
            <Button variant="secondary" onClick={handleUpgrade} loading={upgrading}>
              Upgrade to Pro - $19/month
            </Button>
          )}
          <Button 
            onClick={openPairModal}
            icon={Icons.Plus}
            disabled={isAtLimit}
            title={isAtLimit ? (isPro ? 'Device limit reached' : 'Upgrade to Pro for more devices') : undefined}
          >
            Pair Device
          </Button>
          <div className="header-video-links">
            <a
              className="header-video-link"
              href="https://www.youtube.com/watch?v=d40kxl8jQaY"
              target="_blank"
              rel="noreferrer"
            >
              <Icons.Play /> Pairing & Assignment
            </a>
            <a
              className="header-video-link"
              href="https://www.youtube.com/watch?v=1XWl9sZRyU8"
              target="_blank"
              rel="noreferrer"
            >
              <Icons.Play /> Device Management
            </a>
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div className="kpi-grid">
        <KPICard 
          title="Total Devices" 
          value={devices.length}
          suffix={Number.isFinite(maxDevices) ? `/ ${maxDevices}` : ''}
          icon={Icons.Devices}
        />
        <KPICard 
          title="Online Now" 
          value={onlineDevices}
          changeType={onlineDevices > 0 ? 'positive' : 'neutral'}
          icon={Icons.Wifi}
        />
        <KPICard 
          title="Device Limit" 
          value={deviceLimitLabel}
          icon={Icons.Zap}
        />
      </div>

      {/* Devices Grid */}
      {loading ? (
        <div className="loading-container">
          <Spinner size="lg" />
        </div>
      ) : devices.length === 0 ? (
        <EmptyState
          icon={Icons.Devices}
          title="No devices paired"
          description="Pair your TV or display device to start showing content"
          action={openPairModal}
          actionLabel="Pair Device"
        />
      ) : (
        <div className="devices-grid">
          {devices.map(device => (
            <GlassCard key={device.id} className="device-card">
              <div className="device-header">
                <div className="device-icon">
                  <Icons.Devices />
                </div>
                <div className="device-info">
                  <h3>{device.name || 'Unnamed Device'}</h3>
                  {device.platform && (
                    <span className="device-type">{device.platform}</span>
                  )}
                </div>
                <Badge variant={device.is_online ? 'success' : 'default'}>
                  {device.is_online ? 'Online' : 'Offline'}
                </Badge>
              </div>
              <div className="device-meta">
                {device.assigned_board_name && (
                  <p className="device-board">
                    <Icons.Boards /> {device.assigned_board_name}
                  </p>
                )}
                {device.last_seen && (
                  <p className="device-seen">
                    <Icons.Clock /> Last seen: {new Date(device.last_seen).toLocaleString()}
                  </p>
                )}
              </div>
              <div className="device-actions">
                <Button variant="ghost" size="sm" icon={Icons.Edit} onClick={() => handleRenameClick(device)}>Settings</Button>
                <Button variant="ghost" size="sm" icon={Icons.Trash} onClick={() => handleUnpairDevice(device.id)}>
                  Unpair
                </Button>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      {/* Pair Modal */}
      <Modal isOpen={showPairModal} title="Pair and Set Up Device" onClose={closePairModal}>
        <p className="modal-description">
          Enter the code shown on your TV, name the display, and optionally assign its board.
        </p>
        <div className="modal-form">
          <Input
            label="Pairing Code"
            value={pairingCode}
            onChange={e => setPairingCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            placeholder="Enter 8-character code"
            maxLength={8}
          />
          <Input
            label="Device Name"
            value={pairDeviceName}
            onChange={e => setPairDeviceName(e.target.value)}
            placeholder="e.g., Lobby TV, Cafe Display"
            maxLength={255}
          />
          {boards.length > 0 ? (
            <>
              <Select
                label="Assign Board (Optional)"
                value={pairBoardId}
                onChange={e => setPairBoardId(e.target.value)}
                options={[
                  { value: '', label: 'Assign later' },
                  ...boards.map(board => ({
                    value: board.id,
                    label: `${board.name}${Number(board.message_count || 0) === 0 ? ' (No messages yet)' : ''}`
                  }))
                ]}
              />
              <div className="pairing-note">
                Assigning a board now reduces setup steps. You can change it later in Device Settings.
              </div>
            </>
          ) : (
            <div className="pairing-note">
              <strong>No boards available yet.</strong>
              <span>Pair this device now, then create a board and add its first message.</span>
            </div>
          )}
        </div>
        <div className="modal-actions">
          <Button variant="ghost" onClick={closePairModal}>Cancel</Button>
          <Button
            onClick={handlePairDevice}
            loading={pairing}
            disabled={
              !/^[A-Z0-9]{8}$/.test(pairingCode.trim().toUpperCase())
              || !pairDeviceName.trim()
            }
          >
            Pair Device
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(pairingNextStep)}
        title={pairingNextStep?.title || 'Finish Device Setup'}
        onClose={() => setPairingNextStep(null)}
      >
        <p className="modal-description">{pairingNextStep?.description}</p>
        <div className="modal-actions">
          <Button variant="ghost" onClick={() => setPairingNextStep(null)}>Not Now</Button>
          <Button onClick={handlePairingNextStep}>{pairingNextStep?.actionLabel || 'Continue'}</Button>
        </div>
      </Modal>

      {/* Device Settings Modal */}
      <Modal isOpen={showRenameModal} title="Device Settings" onClose={() => setShowRenameModal(false)}>
        <Input
          label="Device Name"
          value={newDeviceName}
          onChange={e => setNewDeviceName(e.target.value)}
          placeholder="e.g., Lobby TV, Kitchen Display"
        />
        <Select
          label="Assigned Board"
          value={selectedBoardId}
          onChange={e => setSelectedBoardId(e.target.value)}
          options={[
            { value: '', label: 'No board assigned' },
            ...boards.map(board => ({
              value: board.id,
              label: board.name
            }))
          ]}
        />
        <div className="modal-actions">
          <Button variant="ghost" onClick={() => setShowRenameModal(false)}>Cancel</Button>
          <Button onClick={handleSaveDevice} loading={saving}>
            Save Changes
          </Button>
        </div>
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============================================================================
// ACTIVITY PAGE
// ============================================================================

function ActivityPage() {
  const { currentAccount } = useAuth();
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (currentAccount?.id) loadActivity();
  }, [currentAccount?.id, page]);

  const loadActivity = async () => {
    try {
      setLoading(true);
      const result = await api.getAccountActivity(currentAccount.id, page, 50);
      setActivity(prev => page === 1 ? result.activity : [...prev, ...result.activity]);
      setHasMore(result.activity.length === 50);
    } catch (error) {
      console.error('Failed to load activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (item) => {
    const type = item?.actionRaw || item?.action_type || item?.action;
    switch (type) {
      case 'board_created': return Icons.Boards;
      case 'game_created': return Icons.Games;
      case 'device_paired': return Icons.Devices;
      case 'message_created': return Icons.Activity;
      case 'queue.create':
      case 'queue.update':
      case 'queue.delete':
      case 'queue.start':
      case 'queue.stop':
      case 'queue.item_add':
      case 'queue.item_remove':
      case 'queue.item_reorder':
      case 'queue.item_started':
      case 'queue.item_completed':
      case 'queue.completed':
      case 'queue.loop':
      case 'queue.reset':
        return Icons.Games;
      default: return Icons.Activity;
    }
  };

  const formatActivityTimestamp = (item) => {
    const rawTimestamp = item.createdAt || item.created_at;
    if (!rawTimestamp) return 'Time unavailable';
    const timestamp = new Date(rawTimestamp);
    if (Number.isNaN(timestamp.getTime())) {
      return 'Time unavailable';
    }
    return timestamp.toLocaleString();
  };

  const resolveActionLabel = (item) => {
    return item.action || item.action_description || item.action_type || item.actionRaw;
  };

  const resolveEntityLabel = (item) => {
    const name = item.entityName;
    if (!name) return null;
    const type = item.entityType ? item.entityType.replace(/_/g, ' ') : 'entity';
    return `${type}: ${name}`;
  };

  const resolveActivityDetails = (item) => {
    const metadata = item.metadata || {};
    const details = [];
    if (metadata.scheduledStartAt) {
      const scheduledAt = new Date(metadata.scheduledStartAt);
      if (!Number.isNaN(scheduledAt.getTime())) {
        details.push(`Starts ${scheduledAt.toLocaleString()}`);
      }
    }
    if (metadata.scheduleTarget) {
      details.push(metadata.scheduleTarget === 'device' ? 'Device' : 'Web');
    }
    if (metadata.deviceName) {
      details.push(`Device ${metadata.deviceName}`);
    }
    if (metadata.deviceGroupName) {
      details.push(`Group ${metadata.deviceGroupName}`);
    }
    if (metadata.joinCode) {
      details.push(`Code ${metadata.joinCode}`);
    }
    if (metadata.gameTitle) {
      details.push(`Game ${metadata.gameTitle}`);
    }
    if (metadata.position != null) {
      details.push(`Position ${metadata.position}`);
    }
    if (metadata.fromPosition != null && metadata.toPosition != null) {
      details.push(`Moved ${metadata.fromPosition} → ${metadata.toPosition}`);
    }
    if (metadata.playerCount != null) {
      details.push(`${metadata.playerCount} players`);
    }
    if (metadata.durationSeconds != null) {
      const minutes = Math.floor(metadata.durationSeconds / 60);
      const seconds = metadata.durationSeconds % 60;
      details.push(`Duration ${minutes}:${seconds.toString().padStart(2, '0')}`);
    }
    return details.length ? details.join(' • ') : null;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Activity Log</h1>
          <p className="page-subtitle">Recent actions in your organization</p>
        </div>
        <div className="page-header-actions">
          <a
            className="header-video-link"
            href="https://www.youtube.com/watch?v=wlqOc7LR9VQ"
            target="_blank"
            rel="noreferrer"
          >
            <Icons.Play /> Activity Log
          </a>
        </div>
      </div>

      {loading && page === 1 ? (
        <div className="loading-container">
          <Spinner size="lg" />
        </div>
      ) : activity.length === 0 ? (
        <EmptyState
          icon={Icons.Activity}
          title="No activity yet"
          description="Your activity log will appear here as you use SplitCast"
        />
      ) : (
        <div className="activity-list">
          {activity.map((item, index) => {
            const Icon = getActivityIcon(item);
            return (
              <GlassCard key={item.id || index} className="activity-item" padding="sm">
                <div className="activity-icon">
                  <Icon />
                </div>
                <div className="activity-content">
                  <p className="activity-action">{resolveActionLabel(item)}</p>
                  {resolveEntityLabel(item) && (
                    <p className="activity-entity">{resolveEntityLabel(item)}</p>
                  )}
                  {resolveActivityDetails(item) && (
                    <p className="activity-entity">{resolveActivityDetails(item)}</p>
                  )}
                  <p className="activity-meta">
                    <span>{item.userEmail || item.user_email || 'System'}</span>
                    <span>{formatActivityTimestamp(item)}</span>
                  </p>
                </div>
              </GlassCard>
            );
          })}
          {hasMore && (
            <Button 
              variant="ghost" 
              onClick={() => setPage(p => p + 1)}
              loading={loading}
              className="load-more"
            >
              Load More
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SETTINGS PAGE
// ============================================================================

function SettingsPage() {
  const { currentAccount, user, isAdmin, accountTier, isPro, refreshAccounts, logout } = useAuth();
  const [toast, setToast] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [inviting, setInviting] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [savingOrg, setSavingOrg] = useState(false);
  const [accountTimezone, setAccountTimezone] = useState('');
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const timezoneOptions = useMemo(() => {
    const fallback = [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Phoenix',
      'Europe/London',
      'Europe/Berlin',
      'Europe/Paris',
      'Europe/Madrid',
      'Asia/Tokyo',
      'Asia/Seoul',
      'Asia/Kolkata',
      'Australia/Sydney',
      'Pacific/Auckland'
    ];
    const supported = (typeof Intl !== 'undefined' && Intl.supportedValuesOf)
      ? Intl.supportedValuesOf('timeZone')
      : fallback;
    const unique = new Set(supported);
    if (accountTimezone && !unique.has(accountTimezone)) {
      unique.add(accountTimezone);
    }
    return Array.from(unique).map((tz) => ({ value: tz, label: tz }));
  }, [accountTimezone]);
  const isTrialingPro = isPro && accountTier?.billingStatus === 'trialing' && (accountTier?.trialDaysRemaining || 0) > 0;
  const billingPortalAvailable = !!accountTier?.billingPortalAvailable;
  const billingProvider = accountTier?.billingProvider || null;
  const isMobileStorePro = isPro && ['apple', 'google_play'].includes(billingProvider);
  const isReviewPro = isPro && billingProvider === 'review';
  const mobileStoreName = billingProvider === 'google_play' ? 'Google Play' : 'Apple App Store';
  const subscriptionLabel = isTrialingPro ? 'Pro Plan Trial' : isPro ? 'Pro Plan' : 'Free Plan';
  const subscriptionBadgeLabel = isTrialingPro ? `${accountTier?.trialDaysRemaining || 0} days left` : 'Active';

  useEffect(() => {
    if (currentAccount?.id) {
      setOrgName(currentAccount.name || '');
      setAccountTimezone(
        currentAccount.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
      );
      if (isAdmin) loadMembers();
      else setLoading(false);
    }
  }, [currentAccount?.id, isAdmin]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const result = await api.getAccountMembers(currentAccount.id);
      setMembers(result.members || []);
    } catch (error) {
      console.error('Failed to load members:', error);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleSaveOrgName = async () => {
    if (!orgName.trim()) {
      showToast('Organization name cannot be empty', 'error');
      return;
    }
    setSavingOrg(true);
    try {
      await api.updateAccount(currentAccount.id, { name: orgName.trim() });
      await refreshAccounts();
      showToast('Organization name updated!');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSavingOrg(false);
    }
  };

  const handleSaveTimezone = async () => {
    if (!accountTimezone.trim()) {
      showToast('Timezone cannot be empty', 'error');
      return;
    }
    const isKnownTimezone = timezoneOptions.some((option) => option.value === accountTimezone);
    if (!isKnownTimezone) {
      showToast('Please select a valid timezone', 'error');
      return;
    }
    setSavingTimezone(true);
    try {
      await api.updateAccount(currentAccount.id, { timezone: accountTimezone.trim() });
      await refreshAccounts();
      showToast('Timezone updated!');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSavingTimezone(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      showToast('Please enter an email address', 'error');
      return;
    }
    setInviting(true);
    try {
      await api.inviteMember(currentAccount.id, inviteEmail.trim(), inviteRole);
      showToast(`Invitation sent to ${inviteEmail}!`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('editor');
      loadMembers();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberId, memberEmail) => {
    if (!confirm(`Remove ${memberEmail} from this organization?`)) return;
    try {
      await api.removeMember(currentAccount.id, memberId);
      showToast('Member removed');
      loadMembers();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleUpgrade = async () => {
    try {
      const result = await api.createCheckout(currentAccount.id);
      if (result.url) {
        window.location.href = result.url;
      } else {
        showToast('Unable to start checkout. Please try again.', 'error');
      }
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const result = await api.getBillingPortal(currentAccount.id, currentAccount.billing_account_id);
      if (result.url) {
        window.location.href = result.url;
      } else {
        showToast('Unable to open billing portal. Please try again.', 'error');
      }
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleSubmitFeedback = async () => {
    const trimmed = feedbackMessage.trim();
    if (!trimmed) {
      showToast('Please enter feedback before submitting.', 'error');
      return;
    }

    setSendingFeedback(true);
    try {
      await api.submitAccountFeedback(currentAccount.id, trimmed, typeof window !== 'undefined' ? window.location.pathname : '/settings');
      setFeedbackMessage('');
      showToast('Feedback sent. Thank you.');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSendingFeedback(false);
    }
  };

  const handleDeleteUserAccount = async () => {
    const trimmedEmail = deleteConfirmEmail.trim();
    if (!trimmedEmail) {
      showToast('Enter your email address to confirm account deletion.', 'error');
      return;
    }

    if (trimmedEmail.toLowerCase() !== (user?.email || '').toLowerCase()) {
      showToast('Email confirmation does not match.', 'error');
      return;
    }

    setDeletingAccount(true);
    try {
      await api.deleteUserAccount(trimmedEmail);
      await logout();
      setShowDeleteAccountModal(false);
      setDeleteConfirmEmail('');
      window.location.href = '/';
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p className="page-subtitle">Manage your organization and account</p>
        </div>
        <div className="page-header-actions">
          <a
            className="header-video-link"
            href="https://www.youtube.com/watch?v=crqUG-ZwRGc"
            target="_blank"
            rel="noreferrer"
          >
            <Icons.Play /> Account & Plan
          </a>
          <a
            className="header-video-link"
            href="https://www.youtube.com/watch?v=hzSDnROz9tU"
            target="_blank"
            rel="noreferrer"
          >
            <Icons.Play /> Team & Orgs
          </a>
        </div>
      </div>

      <div className="settings-grid">
        {/* Organization Settings */}
        <GlassCard className="settings-section">
          <h2>Organization</h2>
          <div className="setting-item">
            <label>Organization Name</label>
            <div className="setting-input-row">
              <Input 
                value={orgName} 
                onChange={e => setOrgName(e.target.value)}
                disabled={!isAdmin} 
              />
              {isAdmin && orgName !== currentAccount?.name && (
                <Button size="sm" onClick={handleSaveOrgName} loading={savingOrg}>Save</Button>
              )}
            </div>
          </div>
          <div className="setting-item">
            <label>Account Timezone</label>
            <div className="setting-input-row">
              <Select
                value={accountTimezone}
                onChange={e => setAccountTimezone(e.target.value)}
                options={timezoneOptions}
                disabled={!isAdmin}
              />
              {isAdmin && accountTimezone !== currentAccount?.timezone && (
                <Button size="sm" onClick={handleSaveTimezone} loading={savingTimezone}>Save</Button>
              )}
            </div>
            <p className="input-help">Used to schedule game start times.</p>
          </div>
          <div className="setting-item">
            <label>Your Role</label>
            <Badge variant={isAdmin ? 'pro' : 'default'}>
              {currentAccount?.role || 'Member'}
            </Badge>
          </div>
        </GlassCard>

        {/* Subscription */}
        <GlassCard className="settings-section">
          <h2>Subscription</h2>
          <div className="subscription-status">
            <div className="plan-info">
              <span className="plan-name">{subscriptionLabel}</span>
              {isPro && <Badge variant="pro">{subscriptionBadgeLabel}</Badge>}
            </div>
            <p className="plan-price">{isPro ? '$19/month' : 'Free'}</p>
          </div>
          {isTrialingPro && (
            <p className="trial-plan-note">
              Your Pro trial is active for {accountTier?.trialDaysRemaining || 0} more day{(accountTier?.trialDaysRemaining || 0) === 1 ? '' : 's'}. Upgrade anytime to keep Pro active when the trial ends.
            </p>
          )}
          <div className="plan-features">
            <p><Icons.Check /> {isPro ? 'Unlimited devices' : `${accountTier?.features?.maxDevices || 1} device${(accountTier?.features?.maxDevices || 1) > 1 ? 's' : ''}`}</p>
            <p><Icons.Check /> Trivia Games</p>
            <p><Icons.Check /> {isPro ? 'Unlimited boards' : '1 board'}</p>
            <p className={!isPro ? 'is-missing' : ''}>{isPro ? <Icons.Check /> : <Icons.X />} AI Generated Questions</p>
            <p className={!isPro ? 'is-missing' : ''}>{isPro ? <Icons.Check /> : <Icons.X />} Unlimited Displays</p>
            <p className={!isPro ? 'is-missing' : ''}>{isPro ? <Icons.Check /> : <Icons.X />} Message Scheduling</p>
            <p className={!isPro ? 'is-missing' : ''}>{isPro ? <Icons.Check /> : <Icons.X />} Game Scheduling</p>
            <p className={!isPro ? 'is-missing' : ''}>{isPro ? <Icons.Check /> : <Icons.X />} Marathon Mode Game Queuing</p>
          </div>
          {isReviewPro ? (
            <p className="trial-plan-note">
              This review account has permanent Pro access. No subscription is required.
            </p>
          ) : isPro && billingProvider === 'stripe' && billingPortalAvailable ? (
            <Button variant="secondary" onClick={handleManageSubscription} className="manage-btn">
              Manage Subscription
            </Button>
          ) : isMobileStorePro ? (
            <p className="trial-plan-note">
              This subscription is billed through {mobileStoreName}. Manage it through the SplitCast mobile app.
            </p>
          ) : (
            <Button variant="primary" onClick={handleUpgrade} className="upgrade-btn">
              {isTrialingPro ? 'Upgrade to Paid Pro - $19/month' : 'Upgrade to Pro - $19/month'}
            </Button>
          )}
        </GlassCard>

        {/* Team Members */}
        {isAdmin && (
          <GlassCard className="settings-section full-width">
            <div className="section-header-row">
              <h2>Team Members</h2>
              <Button
                variant="secondary"
                size="sm"
                icon={Icons.Plus}
                onClick={() => setShowInviteModal(true)}
                disabled={!isPro}
                title={!isPro ? 'Only Available in Pro Subscription' : undefined}
              >
                Invite Member
              </Button>
            </div>
            {loading ? (
              <Spinner />
            ) : members.length === 0 ? (
              <p className="empty-members">No team members yet. Invite someone to collaborate!</p>
            ) : (
              <div className="members-list">
                {members.map(member => (
                  <div key={member.id} className="member-item">
                    <div className="member-info">
                      <div className="member-avatar">
                        {member.email?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="member-email">{member.email}</p>
                        <Badge variant={member.role === 'admin' ? 'pro' : 'default'} size="sm">
                          {member.role}
                        </Badge>
                      </div>
                    </div>
                    {member.id !== user?.id && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Icons.Trash}
                        onClick={() => handleRemoveMember(member.id, member.email)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        )}

        {/* Account */}
        <GlassCard className="settings-section">
          <h2>Account</h2>
          <div className="setting-item">
            <label>Email</label>
            <p className="setting-value">{user?.email}</p>
          </div>
          <Button
            variant="danger"
            onClick={() => {
              setDeleteConfirmEmail('');
              setShowDeleteAccountModal(true);
            }}
          >
            Delete Account
          </Button>
        </GlassCard>

        <GlassCard className="settings-section full-width feedback-section">
          <div className="feedback-section-header">
            <div>
              <h2>Quick Feedback</h2>
              <p className="feedback-section-copy">
                See a bug, rough edge, or something that feels off? Send it straight to support without leaving the dashboard.
              </p>
            </div>
            <span className="feedback-section-icon" aria-hidden="true">
              <Icons.MessageCircle />
            </span>
          </div>
          <textarea
            className="textarea-field feedback-textarea"
            value={feedbackMessage}
            onChange={(event) => setFeedbackMessage(event.target.value)}
            placeholder="What happened? What were you trying to do? What felt broken or confusing?"
            maxLength={2000}
          />
          <div className="feedback-section-actions">
            <p className="input-help feedback-help">{feedbackMessage.trim().length}/2000</p>
            <Button onClick={handleSubmitFeedback} loading={sendingFeedback}>
              Submit
            </Button>
          </div>
        </GlassCard>
      </div>

      {/* Invite Modal */}
      <Modal isOpen={showInviteModal} title="Invite Team Member" onClose={() => setShowInviteModal(false)}>
        <p className="modal-description">
          Send an invitation to join your organization.
        </p>
        <Input
          label="Email Address"
          type="email"
          value={inviteEmail}
          onChange={e => setInviteEmail(e.target.value)}
          placeholder="colleague@example.com"
        />
        <div className="input-group">
          <label className="input-label">Role</label>
          <div className="role-options">
            <button 
              className={`role-option ${inviteRole === 'editor' ? 'selected' : ''}`}
              onClick={() => setInviteRole('editor')}
            >
              <strong>Editor</strong>
              <span>Can create and edit content</span>
            </button>
            <button 
              className={`role-option ${inviteRole === 'admin' ? 'selected' : ''}`}
              onClick={() => setInviteRole('admin')}
            >
              <strong>Admin</strong>
              <span>Full access including billing</span>
            </button>
          </div>
        </div>
        <div className="modal-actions">
          <Button variant="ghost" onClick={() => setShowInviteModal(false)}>Cancel</Button>
          <Button onClick={handleInviteMember} loading={inviting}>
            Send Invitation
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteAccountModal}
        title="Delete Your Account"
        onClose={() => {
          if (deletingAccount) return;
          setShowDeleteAccountModal(false);
          setDeleteConfirmEmail('');
        }}
      >
        <p className="modal-description">
          This permanently deletes your user account and removes your data. Enter <strong>{user?.email}</strong> to confirm.
        </p>
        <Input
          label="Confirm Email"
          type="email"
          value={deleteConfirmEmail}
          onChange={(event) => setDeleteConfirmEmail(event.target.value)}
          placeholder={user?.email || 'you@example.com'}
          autoComplete="email"
        />
        <div className="modal-actions">
          <Button
            variant="ghost"
            onClick={() => {
              setShowDeleteAccountModal(false);
              setDeleteConfirmEmail('');
            }}
            disabled={deletingAccount}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteUserAccount}
            loading={deletingAccount}
          >
            Permanently Delete Account
          </Button>
        </div>
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============================================================================
// DASHBOARD LAYOUT
// ============================================================================

function Dashboard({ navigate, onLogout }) {
  const { user, accounts, currentAccount, createAccount, isModerator, refreshTier, accountTier } = useAuth();
  const [currentPage, setCurrentPage] = useState('boards');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [error, setError] = useState('');
  const [boardActivationAction, setBoardActivationAction] = useState(null);
  const isTrialingPro = accountTier?.tier === 'pro' && accountTier?.billingStatus === 'trialing' && (accountTier?.trialDaysRemaining || 0) > 0;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') !== 'success') return;
    if (!currentAccount?.id) return;

    let cancelled = false;

    const syncBilling = async () => {
      let verified = false;
      let paidStripeSubscriptionVerified = false;
      try {
        for (let attempt = 0; attempt < 5; attempt += 1) {
          try {
            await api.syncSubscription(currentAccount.id);
          } catch (err) {
            if (attempt === 4) {
              console.warn('Billing sync failed', err);
            }
          }

          if (cancelled) return;

          await refreshTier();
          const tierInfo = await api.getAccountTier(currentAccount.id);
          const isPaidStripeSubscription = tierInfo?.tier === 'pro'
            && tierInfo?.billingStatus === 'active'
            && tierInfo?.billingProvider === 'stripe';

          if (isPaidStripeSubscription) {
            verified = true;
            paidStripeSubscriptionVerified = true;
            break;
          }

          if (tierInfo?.tier === 'pro') {
            verified = true;
          }

          if (attempt < 4) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      } finally {
        if (!verified) {
          console.warn('Billing success return did not verify Pro tier before retry window expired', {
            accountId: currentAccount.id,
          });
        }
        if (cancelled) return;
        if (paidStripeSubscriptionVerified) {
          markSubscriptionCheckoutVerified(currentAccount.id);
          flushPendingSubscriptionConversion();
        }
        params.delete('billing');
        const cleanUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
        window.history.replaceState({}, '', cleanUrl);
      }
    };

    syncBilling();
    return () => {
      cancelled = true;
    };
  }, [currentAccount?.id, refreshTier]);

  async function handleCreateOrg() {
    if (!newOrgName.trim()) {
      setError('Please enter an organization name');
      return;
    }
    setCreatingOrg(true);
    setError('');
    try {
      await createAccount(newOrgName.trim());
      setNewOrgName('');
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingOrg(false);
    }
  }

  // New user with no accounts
  if (!currentAccount && accounts.length === 0) {
    return (
      <div className="onboarding-screen">
        <div className="onboarding-content">
          <div className="onboarding-logo">▣</div>
          <h1>Welcome to SplitCast</h1>
          <p>Create your first organization to get started with your displays and trivia games.</p>
          
          <GlassCard className="onboarding-form">
            <Input
              label="Organization Name"
              value={newOrgName}
              onChange={e => setNewOrgName(e.target.value)}
              placeholder="e.g., My Church, Coffee Shop"
              onKeyDown={e => e.key === 'Enter' && handleCreateOrg()}
              error={error}
            />
            <Button onClick={handleCreateOrg} loading={creatingOrg} className="full-width">
              Create Organization
            </Button>
          </GlassCard>
          
          <button className="text-link" onClick={onLogout}>Sign out</button>
        </div>
      </div>
    );
  }

  const handleNavigate = (page) => {
    setCurrentPage(page);
    setSidebarOpen(false);
  };

  const handleBoardAction = (action) => {
    setBoardActivationAction({ ...action, requestedAt: Date.now() });
    setCurrentPage('boards');
    setSidebarOpen(false);
  };

  const handleBoardActionHandled = () => {
    setBoardActivationAction(null);
  };

  return (
    <div className={`dashboard-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <Sidebar
        currentPage={currentPage}
        onNavigate={handleNavigate}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onLogout={onLogout}
        isOpen={sidebarOpen}
      />
      <main className="dashboard-main">
        <div className="dashboard-topbar">
          <button className="mobile-sidebar-toggle" onClick={() => setSidebarOpen(true)}>
            ☰
          </button>
          <span className="dashboard-topbar-title">Dashboard</span>
        </div>
        {isTrialingPro && (
          <div className="trial-banner">
            <Badge variant="pro">Free Trial</Badge>
            <span>Pro Plan Trial - {accountTier?.trialDaysRemaining || 0} Day{(accountTier?.trialDaysRemaining || 0) === 1 ? '' : 's'} Remaining</span>
          </div>
        )}
        {currentPage === 'boards' && (
          <BoardsPage
            activationAction={boardActivationAction}
            onActivationActionHandled={handleBoardActionHandled}
          />
        )}
        {currentPage === 'games' && <GamesPage onUpgrade={() => handleNavigate('settings')} />}
        {currentPage === 'schedules' && <SchedulesPage />}
        {currentPage === 'devices' && <DevicesPage onBoardAction={handleBoardAction} />}
        {currentPage === 'activity' && <ActivityPage />}
        {currentPage === 'settings' && <SettingsPage />}
        {currentPage === 'admin' && isModerator && <AdminModerationPage />}
      </main>
    </div>
  );
}

// ============================================================================
// SPLIT-FLAP DISPLAY COMPONENTS
// ============================================================================

const CHAR_SET = " ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.,;:!?@#$%&*()-+='/";

function SplitFlapChar({ char, targetChar, flipSpeed = 80 }) {
  const [currentChar, setCurrentChar] = useState(char || ' ');
  const [isFlipping, setIsFlipping] = useState(false);

  useEffect(() => {
    const target = (targetChar || ' ').toUpperCase();
    const current = (currentChar || ' ').toUpperCase();
    if (target === current) return;

    const timeout = setTimeout(() => {
      const currentIndex = CHAR_SET.indexOf(current);
      const targetIndex = CHAR_SET.indexOf(target);
      if (currentIndex === -1 || targetIndex === -1) {
        setCurrentChar(target);
        return;
      }
      const nextIndex = (currentIndex + 1) % CHAR_SET.length;
      const next = CHAR_SET[nextIndex];
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentChar(next);
        setIsFlipping(false);
      }, flipSpeed / 2);
    }, Math.random() * 50);

    return () => clearTimeout(timeout);
  }, [targetChar, currentChar, flipSpeed]);

  return (
    <div className={`split-flap-char ${isFlipping ? 'flipping' : ''}`}>
      <span>{currentChar}</span>
    </div>
  );
}

function SplitFlapBoard({ content, rows, cols, flipSpeed = 80, rowStyles = [] }) {
  const getRowContent = (rowIndex) => {
    const row = content[rowIndex] || '';
    return row.padEnd(cols, ' ').slice(0, cols).toUpperCase();
  };

  return (
    <div className="split-flap-board">
      {Array.from({ length: rows }).map((_, rowIndex) => {
        const style = rowStyles[rowIndex] || {};
        const rowContent = getRowContent(rowIndex);
        return (
          <div 
            key={rowIndex} 
            className="split-flap-row"
            style={{ color: style.color || '#f5f0e6' }}
          >
            {rowContent.split('').map((char, colIndex) => (
              <SplitFlapChar
                key={`${rowIndex}-${colIndex}`}
                char={char}
                targetChar={char}
                flipSpeed={flipSpeed}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function DemoSplitFlapBoard() {
  const [messageIndex, setMessageIndex] = useState(0);
  const messages = [
    ['  WELCOME TO THE  ', '   SPLIT-FLAP     ', '   EXPERIENCE     ', '                  '],
    ['  SUNDAY SERVICE  ', '    10:30 AM      ', '  MAIN SANCTUARY  ', '  ALL WELCOME     '],
    ['   RENT DUE IN    ', '     3 DAYS       ', '  PAY ONLINE AT   ', ' PORTAL.RENT.COM  '],
    ['  HAPPY HOUR      ', '   4PM - 7PM      ', ' HALF PRICE APPS  ', '  DRINK SPECIALS  '],
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(i => (i + 1) % messages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <SplitFlapBoard
      content={messages[messageIndex]}
      rows={4}
      cols={18}
      flipSpeed={70}
      rowStyles={[
        { align: 'center', color: '#3B82F6' },
        { align: 'center', color: '#F8FAFC' },
        { align: 'center', color: '#F8FAFC' },
        { align: 'center', color: '#60A5FA' },
      ]}
    />
  );
}

function DemoTriviaDisplay() {
  const [secondsRemaining, setSecondsRemaining] = useState(17);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setSecondsRemaining((current) => (current <= 7 ? 17 : current - 1));
    }, 1000);
    return () => window.clearInterval(interval);
  }, []);

  const answers = [
    { label: 'A', text: 'Athens' },
    { label: 'B', text: 'Paris' },
    { label: 'C', text: 'London' },
    { label: 'D', text: 'Rome' }
  ];

  return (
    <div className="campaign-trivia-screen" aria-label="Example SplitCast trivia question shown on a television">
      <div className="campaign-trivia-topline">
        <span className="campaign-live-pill"><span /> Live trivia</span>
        <span className="campaign-player-count">12 players connected</span>
      </div>

      <div className="campaign-trivia-join">
        <span>Join at splitcast.games</span>
        <strong>CODE 7K2P8M4Q</strong>
      </div>

      <div className="campaign-trivia-question-meta">
        <span>Question 2 of 5</span>
        <strong className="campaign-trivia-timer">{secondsRemaining}</strong>
      </div>

      <h3>Where were the first modern Olympic Games held?</h3>

      <div className="campaign-trivia-answers">
        {answers.map((answer, index) => {
          const isRemoved = (index === 2 && secondsRemaining <= 12)
            || (index === 3 && secondsRemaining <= 8);
          return (
            <div
              key={answer.label}
              className={`campaign-trivia-answer${isRemoved ? ' is-removed' : ''}`}
            >
              <span>{answer.label}</span>
              <strong>{answer.text}</strong>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProductDemoVideo({ placement, className = '' }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [embedBlocked, setEmbedBlocked] = useState(false);

  useEffect(() => {
    const handleCspViolation = (event) => {
      const isFrameViolation = event.effectiveDirective === 'frame-src'
        || event.violatedDirective?.startsWith('frame-src');
      if (isFrameViolation && event.blockedURI?.includes('youtube')) {
        setEmbedBlocked(true);
      }
    };

    document.addEventListener('securitypolicyviolation', handleCspViolation);
    return () => document.removeEventListener('securitypolicyviolation', handleCspViolation);
  }, []);

  const embedOrigin = typeof window !== 'undefined'
    ? `&origin=${encodeURIComponent(window.location.origin)}`
    : '';

  const handlePlay = () => {
    trackPublicMarketingEvent('product_demo_play', {
      placement,
      video_id: PRODUCT_DEMO_VIDEO_ID,
    });
    setEmbedBlocked(false);
    setIsPlaying(true);
  };

  const handleExternalWatch = () => {
    trackPublicMarketingEvent('product_demo_external_click', {
      placement,
      video_id: PRODUCT_DEMO_VIDEO_ID,
      reason: embedBlocked ? 'csp_blocked' : 'manual_fallback',
    });
  };

  const handleThumbnailError = (event) => {
    if (event.currentTarget.src !== PRODUCT_DEMO_FALLBACK_THUMBNAIL_URL) {
      event.currentTarget.src = PRODUCT_DEMO_FALLBACK_THUMBNAIL_URL;
    }
  };

  return (
    <div className={`product-demo-video ${className}`.trim()}>
      <div className="product-demo-frame">
        {isPlaying && embedBlocked ? (
          <div className="product-demo-blocked">
            <div>
              <strong>Open the demo on YouTube</strong>
              <p>Your browser or this environment blocked the embedded player.</p>
              <a
                href={PRODUCT_DEMO_WATCH_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleExternalWatch}
              >
                <Icons.Play /> Watch the Demo
              </a>
            </div>
          </div>
        ) : isPlaying ? (
          <iframe
            src={`${PRODUCT_DEMO_EMBED_URL}${embedOrigin}`}
            title="SplitCast message board and live trivia demo"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className="product-demo-poster"
            onClick={handlePlay}
            aria-label="Play the SplitCast message board and live trivia demo"
          >
            <img
              src={PRODUCT_DEMO_THUMBNAIL_URL}
              alt=""
              loading="lazy"
              onError={handleThumbnailError}
            />
            <span className="product-demo-overlay" aria-hidden="true">
              <span className="product-demo-play-icon"><Icons.Play /></span>
              <span className="product-demo-play-copy">
                <strong>Watch SplitCast in action</strong>
                <small>Message boards + live trivia on a real TV</small>
              </span>
            </span>
          </button>
        )}
      </div>
      {isPlaying && !embedBlocked && (
        <a
          className="product-demo-external-link"
          href={PRODUCT_DEMO_WATCH_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleExternalWatch}
        >
          Player not loading? Watch on YouTube
        </a>
      )}
    </div>
  );
}

// ============================================================================
// PUBLIC NAVIGATION
// ============================================================================

function PublicNav({ navigate, isLoggedIn }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <nav className="public-nav">
      <div className="nav-container">
        <div className="nav-brand" onClick={() => navigate('/')}>
          <span className="logo-icon">▣</span>
          <span className="logo-text">SPLITCAST</span>
        </div>
        
        <button className="mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <Icons.X /> : '☰'}
        </button>

        <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
          <a href="#features" onClick={(e) => { e.preventDefault(); navigate('/#features'); setMenuOpen(false); }}>Features</a>
          <a href="#how-it-works" onClick={(e) => { e.preventDefault(); navigate('/#how-it-works'); setMenuOpen(false); }}>How It Works</a>
          <a href="https://splitcast.games" target="_blank" rel="noreferrer" onClick={() => setMenuOpen(false)}>Join Game</a>
          <a onClick={() => { navigate('/community'); setMenuOpen(false); }}>Community</a>
          <a href="#pricing" onClick={(e) => { e.preventDefault(); navigate('/#pricing'); setMenuOpen(false); }}>Pricing</a>
          <button className="nav-btn-secondary" onClick={() => { navigate('/contact'); setMenuOpen(false); }}>Contact</button>
          {isLoggedIn ? (
            <button className="nav-btn-primary" onClick={() => navigate('/dashboard')}>Dashboard</button>
          ) : (
            <button className="nav-btn-primary" onClick={() => navigate('/login')}>Sign In</button>
          )}
        </div>
      </div>
    </nav>
  );
}

function PublicFooter({ navigate }) {
  return (
    <footer className="public-footer">
      <div className="footer-container">
        <div className="footer-brand">
          <div className="footer-logo">
            <span className="logo-icon">▣</span>
            <span className="logo-text">SPLITCAST</span>
          </div>
          <p>Classic split-flap displays, reimagined for the digital age.</p>
        </div>
        
        <div className="footer-links">
          <div className="footer-column">
            <h4>Product</h4>
            <a onClick={() => navigate('/#features')}>Features</a>
            <a onClick={() => navigate('/#security')}>Security</a>
            <a onClick={() => navigate('/#pricing')}>Pricing</a>
            <a onClick={() => navigate('/#how-it-works')}>How It Works</a>
          </div>
          <div className="footer-column">
            <h4>Resources</h4>
            <a onClick={() => navigate('/community')}>Community Games</a>
            <a onClick={() => navigate('/guide')}>User Guide</a>
            <a onClick={() => navigate('/contact')}>Contact</a>
          </div>
          <div className="footer-column">
            <h4>Legal</h4>
            <a onClick={() => navigate('/privacy')}>Privacy Policy</a>
            <a onClick={() => navigate('/terms')}>Terms of Service</a>
            <a onClick={() => navigate('/account-deletion')}>Delete Account</a>
          </div>
          <div className="footer-column">
            <h4>Support</h4>
            <a href="mailto:support@splitcast.app">support@splitcast.app</a>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <p>© 2025 SplitCast. All rights reserved.</p>
      </div>
    </footer>
  );
}

// ============================================================================
// HOME PAGE
// ============================================================================

function HomePage({ navigate }) {
  const { currentAccount } = useAuth();

  const handleProCheckout = async () => {
    if (!currentAccount?.id) {
      navigate('/login');
      return;
    }
    try {
      const result = await api.createCheckout(currentAccount.id);
      if (result.url) {
        window.location.href = result.url;
      } else {
        navigate('/login');
      }
    } catch (error) {
      alert(error.message || 'Unable to start checkout. Please try again.');
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge"><Icons.Sparkles /> Split-Flap Boards + Scheduled Multiplayer Trivia</div>
          <h1>Beautiful Displays.<br/>Live + Scheduled Games.</h1>
          <p>Turn any Fire TV or Roku into a modern split-flap board and trivia host. Pair displays and manage boards from the SplitCast mobile app or any browser, schedule games, and let players join instantly at splitcast.games.</p>
          <div className="hero-actions">
            <Button onClick={() => navigate('/login')}>Get Started Free</Button>
            <Button variant="secondary" icon={Icons.Play} onClick={() => navigate('/#product-demo')}>Watch the Demo</Button>
          </div>
          <div className="hero-note">Free forever: 1 display, 3 games, and 1 hosted game/month • Pro: $19/month for unlimited games, AI trivia, analytics, and advanced controls</div>
        </div>
        <div className="hero-visual">
          <div className="hero-demo">
            <DemoSplitFlapBoard />
          </div>
          <div className="hero-mobile-app">
            <div className="hero-mobile-app-label"><span /> Manage SplitCast from your phone</div>
            <div className="hero-store-badges">
              <AppStoreBadgeLink
                compact
                onClick={() => trackPublicMarketingEvent('home_app_store_click', {
                  store: 'apple',
                  placement: 'hero',
                })}
              />
              <GooglePlayBadgeLink
                compact
                onClick={() => trackPublicMarketingEvent('home_app_store_click', {
                  store: 'google_play',
                  placement: 'hero',
                })}
              />
            </div>
            <p>Pair displays, update boards, and launch trivia from the mobile app.</p>
          </div>
        </div>
      </section>

      <section className="product-demo-section" id="product-demo" aria-labelledby="home-product-demo-title">
        <div className="product-demo-section-inner">
          <div className="product-demo-heading">
            <span className="product-demo-eyebrow">Real product demo</span>
            <h2 id="home-product-demo-title">See SplitCast running on a TV</h2>
            <p>Watch a split-flap message board switch into a live trivia game on a real television.</p>
          </div>
          <ProductDemoVideo placement="homepage" />
        </div>
      </section>

      {/* Trust Bar */}
      <section className="trust-bar">
        <p>Trusted by churches, property managers, and businesses nationwide</p>
        <div className="trust-logos">
          <span>Churches</span>
          <span>Property Management</span>
          <span>Restaurants</span>
          <span>Fitness Studios</span>
          <span>Medical Offices</span>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="section-header">
          <h2>Everything you need for boards and games</h2>
          <p>Split-flap boards, scheduled trivia, and realtime play on Fire TV and Roku.</p>
        </div>
        
        <div className="features-grid">
          <GlassCard className="feature-card featured">
            <div className="feature-icon"><Icons.Trophy /></div>
            <h3>Free Multiplayer Trivia</h3>
            <p>Create up to 3 games with 10 questions each and host one 10-player session every month. Players join from their phones—no app needed.</p>
          </GlassCard>
          <GlassCard className="feature-card">
            <div className="feature-icon"><Icons.Clock /></div>
            <h3>Scheduled Game Sessions</h3>
            <p>Schedule games for web or device. Devices open lobbies early, and players see a countdown to start time.</p>
          </GlassCard>
          <GlassCard className="feature-card">
            <div className="feature-icon"><Icons.Sparkles /></div>
            <div className="feature-title-row">
              <h3>AI Trivia Generator</h3>
              <span className="feature-tag">Pro</span>
            </div>
            <p>Type a topic and SplitCast instantly builds a full trivia game with questions and answers. Perfect for last-minute events and weekly trivia nights.</p>
          </GlassCard>
          <GlassCard className="feature-card">
            <div className="feature-icon"><Icons.Globe /></div>
            <h3>Community Game Library</h3>
            <p>Browse community shared games, preview all questions, and copy them into your own library.</p>
          </GlassCard>
          <GlassCard className="feature-card">
            <div className="feature-icon"><Icons.Devices /></div>
            <h3>Fire TV + Roku Support</h3>
            <p>Works with Fire TV Stick, Fire TV Cube, and Roku devices. Pair and manage each display from the SplitCast mobile app or web dashboard.</p>
          </GlassCard>
          <GlassCard className="feature-card">
            <div className="feature-icon"><Icons.Boards /></div>
            <h3>Authentic Split-Flap</h3>
            <p>Real split-flap mechanics recreated digitally. The satisfying flip animation that commands attention—just like airport departure boards.</p>
          </GlassCard>
          <GlassCard className="feature-card">
            <div className="feature-icon"><Icons.Settings /></div>
            <h3>Customizable Styles</h3>
            <p>Choose colors, fonts, and layouts. Make each message uniquely yours with per-row styling.</p>
          </GlassCard>
          <GlassCard className="feature-card">
            <div className="feature-icon"><Icons.RotateCw /></div>
            <h3>Message Rotation</h3>
            <p>Create multiple messages per board and rotate them automatically. Set per-board timing, we handle the rest.</p>
          </GlassCard>
          <GlassCard className="feature-card">
            <div className="feature-icon"><Icons.Users /></div>
            <h3>Team Collaboration</h3>
            <p>Invite team members with different permission levels. Admins and editors working together.</p>
          </GlassCard>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works">
        <div className="section-header">
          <h2>Up and running in minutes</h2>
          <p>Three simple steps to transform your display</p>
        </div>
        
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Install the TV App</h3>
              <p>Download SplitCast from the Amazon App Store (Fire TV) or Roku Channel Store. Free on both platforms.</p>
            </div>
          </div>
          <div className="step-connector" />
          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>Pair Your Device</h3>
              <p>Enter the 8-character code shown on your TV in the SplitCast mobile app or web dashboard.</p>
            </div>
          </div>
          <div className="step-connector" />
          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Display or Play</h3>
              <p>Create boards, add messages, and host live or scheduled trivia from your phone or browser.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="use-cases">
        <div className="section-header">
          <h2>Perfect for every venue</h2>
          <p>See how organizations use SplitCast for signage and trivia</p>
        </div>
        
        <div className="use-case-grid">
          <GlassCard className="use-case-card featured">
            <div className="use-case-icon"><Icons.Trophy /></div>
            <h3>Bars & Breweries</h3>
            <p>Host FREE trivia nights that pack the house. Schedule games in advance and rotate drink specials between rounds.</p>
            <ul>
              <li>Weekly trivia nights</li>
              <li>Drink specials board</li>
              <li>Sports scores</li>
              <li>Event promotions</li>
            </ul>
          </GlassCard>
          <GlassCard className="use-case-card">
            <div className="use-case-icon"><Icons.BookOpen /></div>
            <h3>Churches & Places of Worship</h3>
            <p>Display service times and host Bible trivia for youth groups. Engage your congregation in a whole new way.</p>
            <ul>
              <li>Service schedules</li>
              <li>Youth group trivia</li>
              <li>Scripture displays</li>
              <li>Event announcements</li>
            </ul>
          </GlassCard>
          <GlassCard className="use-case-card">
            <div className="use-case-icon"><Icons.Boards /></div>
            <h3>Restaurants & Cafes</h3>
            <p>Showcase daily specials on split-flap boards and schedule trivia nights to boost weekday traffic.</p>
            <ul>
              <li>Daily specials</li>
              <li>Happy hour times</li>
              <li>Trivia Tuesday</li>
              <li>Wait times</li>
            </ul>
          </GlassCard>
          <GlassCard className="use-case-card">
            <div className="use-case-icon"><Icons.Users /></div>
            <h3>Corporate & Schools</h3>
            <p>Lobby displays, meeting room signage, and team-building trivia events—all from one platform.</p>
            <ul>
              <li>Lobby announcements</li>
              <li>Meeting room signs</li>
              <li>Team trivia events</li>
              <li>Training quizzes</li>
            </ul>
          </GlassCard>
        </div>
      </section>

      {/* Security Section */}
      <section className="security-section" id="security">
        <div className="section-header">
          <h2>Enterprise-grade security, startup simplicity</h2>
          <p>Your data protection is our top priority</p>
        </div>
        
        <div className="security-grid">
          <GlassCard className="security-card">
            <div className="security-icon"><Icons.Lock /></div>
            <h3>256-bit TLS Encryption</h3>
            <p>All data transmitted between your devices and our servers is encrypted using bank-grade TLS 1.3 encryption.</p>
          </GlassCard>
          <GlassCard className="security-card">
            <div className="security-icon"><Icons.Shield /></div>
            <h3>Secure Infrastructure</h3>
            <p>Hosted on enterprise-grade cloud infrastructure with encrypted data transmission and secure data centers.</p>
          </GlassCard>
          <GlassCard className="security-card">
            <div className="security-icon"><Icons.Key /></div>
            <h3>Passwordless Authentication</h3>
            <p>No passwords to steal or forget. Secure magic links and one-time codes keep your account protected.</p>
          </GlassCard>
          <GlassCard className="security-card">
            <div className="security-icon"><Icons.Devices /></div>
            <h3>Device-Level Security</h3>
            <p>Each display has a unique cryptographic token. Revoke devices instantly from the mobile app or web dashboard.</p>
          </GlassCard>
          <GlassCard className="security-card">
            <div className="security-icon"><Icons.RotateCw /></div>
            <h3>Offline Resilience</h3>
            <p>Displays cache content locally and continue operating even if internet connectivity is lost.</p>
          </GlassCard>
          <GlassCard className="security-card">
            <div className="security-icon"><Icons.FileText /></div>
            <h3>Audit Logging</h3>
            <p>Complete activity logs for compliance. Track who changed what and when across your organization.</p>
          </GlassCard>
        </div>
        
        <div className="security-badges">
          <div className="security-badge-item">
            <span className="badge-icon">✓</span>
            <span>HTTPS Only</span>
          </div>
          <div className="security-badge-item">
            <span className="badge-icon">✓</span>
            <span>GDPR Compliant</span>
          </div>
          <div className="security-badge-item">
            <span className="badge-icon">✓</span>
            <span>99.9% Uptime SLA</span>
          </div>
          <div className="security-badge-item">
            <span className="badge-icon">✓</span>
            <span>Daily Backups</span>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing" id="pricing">
        <div className="section-header">
          <h2>Simple, transparent pricing</h2>
          <p>Start free. Upgrade when you need more.</p>
        </div>
        
        <div className="pricing-cards-container">
          <GlassCard className="pricing-card free-tier">
            <div className="pricing-header">
              <div className="pricing-tier-label">Free</div>
              <span className="pricing-badge free">Free Forever</span>
              <div className="pricing-amount">
                <span className="price">$0</span>
                <span className="period">/month</span>
              </div>
            </div>
            <ul className="pricing-features">
              <li><Icons.Check /> 1 display device</li>
              <li><Icons.Check /> 1 split-flap board</li>
              <li><Icons.Check /> <strong>3 trivia games</strong></li>
              <li><Icons.Check /> 10 questions per game</li>
              <li><Icons.Check /> 10 players per live session</li>
              <li><Icons.Check /> 1 hosted game per month</li>
              <li><Icons.Check /> Browse & copy community games</li>
              <li><Icons.Check /> Fire TV & Roku support</li>
              <li className="is-missing"><Icons.X /> Scheduled game sessions</li>
              <li className="is-missing"><Icons.X /> Marathon Mode game queuing</li>
              <li className="is-missing"><Icons.X /> AI question generation</li>
              <li className="is-missing"><Icons.X /> Game history & analytics</li>
              <li className="is-missing"><Icons.X /> Community publishing</li>
              <li className="is-missing"><Icons.X /> Message scheduling</li>
              <li className="is-missing"><Icons.X /> Custom colors & fonts</li>
              <li className="is-missing"><Icons.X /> Unlimited displays & boards</li>
            </ul>
            <Button variant="secondary" onClick={() => navigate('/login')} className="full-width">Get Started Free</Button>
            <p className="pricing-note">No credit card required</p>
          </GlassCard>

          <GlassCard className="pricing-card pro-tier">
            <div className="pricing-header">
              <div className="pricing-tier-label">Pro</div>
              <span className="pricing-badge">Most Popular</span>
              <div className="pricing-amount">
                <span className="price">$19</span>
                <span className="period">/month</span>
              </div>
            </div>
            <ul className="pricing-features">
              <li><Icons.Check /> <strong>Marathon Mode game queuing</strong></li>
              <li><Icons.Check /> <strong>AI question generation</strong></li>
              <li><Icons.Check /> <strong>Schedule game sessions</strong></li>
              <li><Icons.Check /> <strong>Message scheduling</strong></li>
              <li><Icons.Check /> <strong>Custom colors & fonts</strong></li>
              <li><Icons.Check /> <strong>Team collaboration</strong></li>
              <li><Icons.Check /> Unlimited displays</li>
              <li><Icons.Check /> Unlimited boards</li>
              <li><Icons.Check /> Unlimited trivia games</li>
              <li><Icons.Check /> Game history & analytics</li>
              <li><Icons.Check /> Priority support</li>
            </ul>
            <Button
              onClick={handleProCheckout}
              className="full-width"
            >
              Start Pro Now
            </Button>
            <p className="pricing-note">$19/month. Cancel anytime.</p>
          </GlassCard>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <h2>Ready to captivate your audience?</h2>
        <p>Join hundreds of organizations using SplitCast for beautiful displays and free multiplayer trivia.</p>
        <Button onClick={() => navigate('/login')} size="lg">Get Started Free</Button>
      </section>
    </div>
  );
}

function getFireTvRokuTriviaAudience() {
  const params = new URLSearchParams(window.location.search);
  const audience = (params.get('audience') || '').toLowerCase();
  const utmSource = (params.get('utm_source') || '').toLowerCase();

  if (audience === 'party' || audience === 'game-night' || utmSource === 'instagram') {
    return 'party';
  }

  if (audience === 'waiting' || audience === 'salon' || audience === 'barbershop') {
    return 'waiting';
  }

  if (audience === 'bars' || audience === 'restaurant' || utmSource === 'facebook') {
    return 'bars';
  }

  return 'bars';
}

function AppStoreBadgeLink({ onClick, compact = false }) {
  return (
    <a
      className={`store-badge-link apple${compact ? ' compact' : ''}`}
      href={APPLE_APP_STORE_URL}
      onClick={onClick}
    >
      <img
        className="store-badge"
        src={APP_STORE_BADGE_IMAGE_URL}
        alt="Download SplitCast on the App Store"
      />
    </a>
  );
}

function GooglePlayBadgeLink({ onClick, compact = false }) {
  return (
    <a
      className={`store-badge-link google${compact ? ' compact' : ''}`}
      href={GOOGLE_PLAY_STORE_URL}
      onClick={onClick}
    >
      <img
        className="store-badge"
        src={GOOGLE_PLAY_BADGE_IMAGE_URL}
        alt="Get SplitCast on Google Play"
      />
    </a>
  );
}

function FireTvRokuTriviaLandingPage({ navigate }) {
  const { currentAccount } = useAuth();
  const [audience, setAudience] = useState(getFireTvRokuTriviaAudience);
  const isAppleMobile = typeof navigator !== 'undefined'
    && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroidMobile = typeof navigator !== 'undefined'
    && /Android/.test(navigator.userAgent);

  useEffect(() => {
    const handlePopState = () => setAudience(getFireTvRokuTriviaAudience());
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content');

    document.title = 'SplitCast for Roku & Fire TV | Live Boards and Trivia';
    description?.setAttribute(
      'content',
      'Put messages and live trivia on a Roku or Fire TV. Control SplitCast from iPhone, Android, or the web, and start free with one screen.'
    );

    return () => {
      document.title = previousTitle;
      if (description && previousDescription) {
        description.setAttribute('content', previousDescription);
      }
    };
  }, []);

  const variants = {
    bars: {
      label: 'Bars & Restaurants',
      heroLabel: 'Built for bars, breweries, and neighborhood spots',
      headline: 'Run trivia on the TV you already own.',
      subhead: 'Try a live five-question round now. When you are ready, pair a Roku or Fire TV from your iPhone, Android device, or browser and put the same experience in front of the room.',
      outcomeTitle: 'One screen for the quiet hours and the busy ones',
      outcomeCopy: 'Start with a board for specials, events, and announcements. Once that screen is useful every day, turn it into a live trivia display whenever you want the room involved.',
      valueCards: [
        {
          icon: Icons.Boards,
          title: 'Start with one useful screen',
          copy: 'Pair one TV, assign one board, and show today\'s special before building a larger content library.'
        },
        {
          icon: Icons.Trophy,
          title: 'Launch trivia without extra hardware',
          copy: 'Use the same TV for the lobby, questions, countdowns, and leaderboards when it is time to play.'
        },
        {
          icon: Icons.Users,
          title: 'Let every guest join instantly',
          copy: 'Players scan the QR code or visit splitcast.games from their phone browser. No player app is required.'
        }
      ],
      scenarios: [
        'Keep happy hour, wings, or live music visible before trivia begins',
        'Turn a weeknight into a recurring trivia draw without extra hardware',
        'Use one platform for passive display and active entertainment'
      ],
      finalHeadline: 'Put your first venue screen live for free.',
      finalCopy: 'Start with one display, one board, 3 saved games, and one hosted game each month. Upgrade for unlimited hosting, AI questions, analytics, and more screens.'
    },
    party: {
      label: 'Game Nights & House Parties',
      heroLabel: 'Built for house parties, hosts, and game-night groups',
      headline: 'Put game night on the big screen.',
      subhead: 'Use your iPhone, Android device, or web browser to pair a Roku or Fire TV. Show a welcome or the house rules first, then launch trivia when guests are ready to play.',
      outcomeTitle: 'One shared screen, no setup drama',
      outcomeCopy: 'Use the split-flap board to welcome guests, set the vibe, or post the rules. Then turn that same TV into a live game everyone can join from a phone browser.',
      valueCards: [
        {
          icon: Icons.Boards,
          title: 'Get the screen ready before guests arrive',
          copy: 'Pair one TV and put a welcome message live so you know everything is connected before game time.'
        },
        {
          icon: Icons.Zap,
          title: 'Get people playing fast',
          copy: 'Launch a game without passing controllers around or asking every guest to install another app.'
        },
        {
          icon: Icons.Sparkles,
          title: 'Keep future game nights fresh',
          copy: 'Free includes 3 saved games and one hosted game each month. Upgrade for unlimited hosting, AI-generated questions, and advanced controls.'
        }
      ],
      scenarios: [
        'Welcome guests with a board before the first round starts',
        'Run quick trivia rounds without teaching anyone a new app',
        'Reuse the same TV setup for the next party or game night'
      ],
      finalHeadline: 'Get one screen ready before guests arrive.',
      finalCopy: 'Start free with one display, one board, 3 saved games, and one hosted game each month. Add unlimited hosting and advanced controls when you need them.'
    },
    waiting: {
      label: 'Waiting Rooms, Salons & Barbershops',
      heroLabel: 'Built for waiting rooms, salons, barbershops, and service businesses',
      headline: 'Make the waiting-room TV useful.',
      subhead: 'Control SplitCast from your iPhone, Android device, or web browser. Pair a Roku or Fire TV, show services and promotions all day, then turn on trivia when customers want something to play.',
      outcomeTitle: 'A screen that works between appointments',
      outcomeCopy: 'Start with a board for services, retail products, reminders, and promotions. Add trivia when you want to make the wait feel shorter without changing the TV setup.',
      valueCards: [
        {
          icon: Icons.Boards,
          title: 'Put one useful message live first',
          copy: 'Pair one TV and start with the service, promotion, or reminder customers should see today.'
        },
        {
          icon: Icons.Clock,
          title: 'Turn dead time into game time',
          copy: 'Switch from passive messages to live trivia when guests need something interactive to do.'
        },
        {
          icon: Icons.Users,
          title: 'Keep the guest experience lightweight',
          copy: 'Customers play in their phone browser, so walk-in traffic never needs to download an app.'
        }
      ],
      scenarios: [
        'Show services, retail products, specials, and staff promotions',
        'Keep guests engaged while they wait for appointments',
        'Replace generic cable with something interactive and on-brand'
      ],
      finalHeadline: 'Put your first waiting-area screen live for free.',
      finalCopy: 'Start with one display, one board, 3 saved games, and one hosted game each month. Upgrade when unlimited hosting, scheduling, and additional screens become useful.'
    }
  };

  const variant = variants[audience] || variants.bars;

  const updateAudience = (nextAudience) => {
    setAudience(nextAudience);
    const params = new URLSearchParams(window.location.search);
    params.set('audience', nextAudience);
    const query = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    trackPublicMarketingEvent('landing_audience_change', { audience: nextAudience });
  };

  const handleStartFree = (source) => {
    const destination = currentAccount?.id ? 'dashboard' : 'login';
    trackPublicMarketingEvent('landing_start_free', { audience, source, destination });
    if (currentAccount?.id) {
      navigate('/dashboard');
      return;
    }

    window.location.assign(buildAttributedPath('/login', {
      sc_funnel: 'campaign-landing',
      sc_cta: source
    }));
  };

  const handlePlayNow = () => {
    trackPublicMarketingEvent('landing_preview_click', { audience });
    const previewHost = window.location.hostname.includes('dev.')
      ? 'https://dev.splitcast.games'
      : 'https://splitcast.games';
    const previewPath = buildAttributedPath('/play-preview', {
      sc_funnel: 'campaign-landing',
      sc_cta: 'play-five-question-demo'
    });
    window.location.assign(`${previewHost}${previewPath}`);
  };

  const handleSignIn = () => {
    trackPublicMarketingEvent('landing_sign_in', { audience, source: 'header' });
    window.location.assign(buildAttributedPath('/login', {
      sc_funnel: 'campaign-landing',
      sc_cta: 'header-sign-in'
    }));
  };

  const handleAppStoreClick = (store, source) => {
    trackPublicMarketingEvent('landing_app_store_click', {
      audience,
      source,
      store
    });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="campaign-landing-page">
      <header className="campaign-header">
        <div className="campaign-header-inner">
          <button className="campaign-brand" onClick={() => navigate('/')}>
            <span className="logo-icon">▣</span>
            <span className="logo-text">SPLITCAST</span>
          </button>
          <div className="campaign-header-actions">
            <button className="campaign-link-button" onClick={() => scrollToSection('campaign-pricing')}>Pricing</button>
            <Button className="campaign-sign-in-button" variant="secondary" size="sm" onClick={handleSignIn}>Sign In</Button>
            <Button size="sm" onClick={() => handleStartFree('header')}>Start Free</Button>
          </div>
        </div>
      </header>

      <main className="campaign-main">
        <section className="campaign-hero">
          <div className="campaign-hero-copy">
            <div className="campaign-kicker">Roku + Fire TV displays, controlled from your phone</div>
            <div className="campaign-audience-toggle" aria-label="Choose audience">
              <button
                className={audience === 'bars' ? 'active' : ''}
                onClick={() => updateAudience('bars')}
              >
                Bars & Restaurants
              </button>
              <button
                className={audience === 'party' ? 'active' : ''}
                onClick={() => updateAudience('party')}
              >
                Game Nights
              </button>
              <button
                className={audience === 'waiting' ? 'active' : ''}
                onClick={() => updateAudience('waiting')}
              >
                Waiting Rooms
              </button>
            </div>
            <div className="campaign-hero-label">{variant.heroLabel}</div>
            <h1>{variant.headline}</h1>
            <p>{variant.subhead}</p>
            <div className="campaign-hero-actions">
              <Button size="lg" icon={Icons.Play} onClick={handlePlayNow}>Play a 5-Question Demo</Button>
              <Button size="lg" variant="secondary" onClick={() => handleStartFree('hero')}>Start Free & Pair a TV</Button>
            </div>
            <div className="campaign-preview-note">No signup, app download, or credit card required to play.</div>
            {(isAppleMobile || isAndroidMobile) && (
              <div className="campaign-hero-store">
                <span>Prefer to manage SplitCast in the mobile app?</span>
                {isAppleMobile && (
                  <AppStoreBadgeLink compact onClick={() => handleAppStoreClick('apple', 'hero_secondary')} />
                )}
                {isAndroidMobile && (
                  <GooglePlayBadgeLink compact onClick={() => handleAppStoreClick('google_play', 'hero_secondary')} />
                )}
              </div>
            )}
            <div className="campaign-hero-points">
              <span><Icons.Check /> Five live questions, no signup</span>
              <span><Icons.Mobile /> Host from iPhone, Android, or web</span>
              <span><Icons.Users /> Players join in a browser, no app</span>
            </div>
          </div>

          <div className="campaign-hero-visual">
            <GlassCard className="campaign-demo-card">
              <div className="campaign-demo-heading">A preview of what guests see on your TV.</div>
              <p className="campaign-demo-disclaimer">
                This preview illustrates the game flow. The Roku and Fire TV apps display questions and answers
                using SplitCast&apos;s split-flap boards, with layouts that vary slightly by device.
              </p>
              <DemoTriviaDisplay />
              <div className="campaign-demo-metrics">
                <div>
                  <span className="metric-label">TV display</span>
                  <strong>Roku or Fire TV</strong>
                </div>
                <div>
                  <span className="metric-label">Players join</span>
                  <strong>QR code or browser</strong>
                </div>
                <div>
                  <span className="metric-label">Host controls</span>
                  <strong>iOS, Android, or web</strong>
                </div>
              </div>
            </GlassCard>
          </div>
        </section>

        <section className="campaign-product-demo-section" aria-labelledby="campaign-product-demo-title">
          <div className="campaign-product-demo-shell">
            <div className="product-demo-heading">
              <span className="product-demo-eyebrow">See the real TV experience</span>
              <h2 id="campaign-product-demo-title">From message board to live trivia</h2>
              <p>Watch SplitCast running on a television, then jump into a live five-question round from your phone.</p>
            </div>
            <ProductDemoVideo placement={`campaign-${audience}`} className="campaign-product-demo" />
            <div className="campaign-product-demo-actions">
              <Button size="lg" icon={Icons.Play} onClick={handlePlayNow}>Play a 5-Question Demo</Button>
              <div className="campaign-preview-note">No signup, app download, or credit card required to play.</div>
            </div>
          </div>
        </section>

        <section className="campaign-proof-strip">
          <div><Icons.Mobile /> iPhone app available now</div>
          <div><Icons.Mobile /> Android app available now</div>
          <div><Icons.Devices /> Roku + Fire TV display apps</div>
          <div><Icons.Globe /> Players join from any phone browser</div>
        </section>

        <section className="campaign-mobile-app">
          <GlassCard className="campaign-mobile-app-card">
            <div className="campaign-mobile-copy">
              <div className="campaign-app-status"><span /> Live on the App Store and Google Play</div>
              <h2>Viewing this on your phone? Start here.</h2>
              <p>Create your account, build a board, pair a display, add messages, and launch trivia from the SplitCast mobile app. The web dashboard remains available from any browser.</p>
              <div className="campaign-store-actions">
                <AppStoreBadgeLink onClick={() => handleAppStoreClick('apple', 'app_section')} />
                <GooglePlayBadgeLink onClick={() => handleAppStoreClick('google_play', 'app_section')} />
              </div>
              <p className="campaign-app-clarifier">The mobile app controls SplitCast. Install the SplitCast TV app separately on Roku or Fire TV to use the television as the display.</p>
            </div>
            <div className="campaign-control-path" aria-label="How SplitCast connects">
              <div>
                <span>01</span>
                <Icons.Mobile />
                <strong>You control</strong>
                <small>Mobile app or web dashboard</small>
              </div>
              <div>
                <span>02</span>
                <Icons.Devices />
                <strong>Your TV displays</strong>
                <small>Roku or Fire TV app</small>
              </div>
              <div>
                <span>03</span>
                <Icons.Users />
                <strong>Guests play</strong>
                <small>Any phone browser</small>
              </div>
            </div>
          </GlassCard>
        </section>

        <section className="campaign-activation-band">
          <div className="campaign-kicker">Start smaller</div>
          <h2>One paired TV. One useful board. One live message.</h2>
          <p>Do not build an entire content library first. Put one message on one screen, confirm it works in the room, then add trivia, schedules, and more displays as you need them.</p>
        </section>

        <section className="campaign-story">
          <div className="section-header">
            <h2>{variant.outcomeTitle}</h2>
            <p>{variant.outcomeCopy}</p>
          </div>
          <div className="campaign-story-grid">
            {variant.valueCards.map(({ icon: Icon, title, copy }) => (
              <GlassCard key={title} className="campaign-story-card">
                <div className="campaign-story-icon"><Icon /></div>
                <h3>{title}</h3>
                <p>{copy}</p>
              </GlassCard>
            ))}
          </div>
        </section>

        <section className="campaign-how" id="campaign-how">
          <div className="section-header">
            <h2>Get one screen live in three steps</h2>
            <p>The first goal is not a perfect setup. It is one useful message showing on the TV you already own.</p>
          </div>
          <div className="campaign-steps">
            <GlassCard className="campaign-step-card">
              <div className="campaign-step-number">01</div>
              <h3>Install SplitCast on the TV</h3>
              <p>Open SplitCast from the Roku Channel Store or Amazon Appstore. The TV will show a pairing code.</p>
            </GlassCard>
            <GlassCard className="campaign-step-card">
              <div className="campaign-step-number">02</div>
              <h3>Open SplitCast on mobile or web</h3>
              <p>Create a free account in the iPhone or Android app, or at splitcast.app, then enter the pairing code from the TV.</p>
            </GlassCard>
            <GlassCard className="campaign-step-card">
              <div className="campaign-step-number">03</div>
              <h3>Name it, choose a board, add a message</h3>
              <p>Give the TV a useful name, assign your board, and put the first message live before adding anything else.</p>
            </GlassCard>
          </div>
          <div className="campaign-after-live">
            <Icons.Trophy />
            <span>Once the board is live, launch trivia on the same TV. Players join with the QR code or at splitcast.games.</span>
          </div>
        </section>

        <section className="campaign-use-cases">
          <div className="section-header">
            <h2>{audience === 'bars' ? 'What this looks like in a venue' : audience === 'waiting' ? 'What this looks like in a waiting area' : 'What this looks like at home'}</h2>
            <p>{audience === 'bars' ? 'Use SplitCast as both entertainment and display software, without buying into a heavyweight trivia stack.' : audience === 'waiting' ? 'Use SplitCast to reduce dead screen time, entertain guests, and keep services and announcements visible while people wait.' : 'Use SplitCast as the shared screen for welcomes, rounds, and repeatable game-night energy.'}</p>
          </div>
          <div className="campaign-scenario-list">
            {variant.scenarios.map((scenario) => (
              <GlassCard key={scenario} className="campaign-scenario-card">
                <Icons.Check />
                <span>{scenario}</span>
              </GlassCard>
            ))}
          </div>
        </section>

        <section className="campaign-pricing" id="campaign-pricing">
          <div className="section-header">
            <h2>Get the first screen live on Free.</h2>
            <p>Upgrade only after SplitCast is useful in your space and you need scheduling, AI, or additional displays.</p>
          </div>

          <div className="pricing-cards-container campaign-pricing-cards">
            <GlassCard className="pricing-card free-tier">
              <div className="pricing-header">
                <div className="pricing-tier-label">Free</div>
                <span className="pricing-badge free">Start Here</span>
                <div className="pricing-amount">
                  <span className="price">$0</span>
                  <span className="period">/month</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li><Icons.Check /> 1 display device</li>
                <li><Icons.Check /> 1 split-flap board</li>
                <li><Icons.Check /> <strong>3 trivia games</strong></li>
                <li><Icons.Check /> 10 questions per game</li>
                <li><Icons.Check /> 10 players per live session</li>
                <li><Icons.Check /> 1 hosted game per month</li>
                <li><Icons.Check /> Browse & copy community games</li>
                <li><Icons.Check /> Fire TV & Roku support</li>
                <li className="is-missing"><Icons.X /> Scheduled game sessions</li>
                <li className="is-missing"><Icons.X /> Marathon Mode game queuing</li>
                <li className="is-missing"><Icons.X /> AI question generation</li>
                <li className="is-missing"><Icons.X /> Game history & analytics</li>
                <li className="is-missing"><Icons.X /> Community publishing</li>
                <li className="is-missing"><Icons.X /> Message scheduling</li>
                <li className="is-missing"><Icons.X /> Custom colors & fonts</li>
                <li className="is-missing"><Icons.X /> Unlimited displays & boards</li>
              </ul>
              <Button variant="secondary" onClick={() => handleStartFree('free_pricing')} className="full-width">Get One Screen Live</Button>
              <p className="pricing-note">No credit card required</p>
            </GlassCard>

            <GlassCard className="pricing-card pro-tier">
              <div className="pricing-header">
                <div className="pricing-tier-label">Pro</div>
                <span className="pricing-badge">When You Need More</span>
                <div className="pricing-amount">
                  <span className="price">$19</span>
                  <span className="period">/month</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li><Icons.Check /> <strong>Marathon Mode game queuing</strong></li>
                <li><Icons.Check /> <strong>AI question generation</strong></li>
                <li><Icons.Check /> <strong>Schedule game sessions</strong></li>
                <li><Icons.Check /> <strong>Message scheduling</strong></li>
                <li><Icons.Check /> <strong>Custom colors & fonts</strong></li>
                <li><Icons.Check /> <strong>Team collaboration</strong></li>
                <li><Icons.Check /> Unlimited displays</li>
                <li><Icons.Check /> Unlimited boards</li>
                <li><Icons.Check /> Unlimited trivia games</li>
                <li><Icons.Check /> Game history & analytics</li>
                <li><Icons.Check /> Priority support</li>
              </ul>
              <Button onClick={() => handleStartFree('pro_pricing')} className="full-width">Start Free, Upgrade Later</Button>
              <p className="pricing-note">$19/month when you upgrade. Cancel anytime.</p>
            </GlassCard>
          </div>
        </section>

        <section className="campaign-faq">
          <div className="section-header">
            <h2>Questions people ask before they start</h2>
            <p>Keep the first decision easy: connect one screen for free and upgrade only when the extra controls matter.</p>
          </div>
          <div className="campaign-faq-grid">
            <GlassCard className="campaign-faq-card">
              <h3>Can I manage SplitCast from my phone?</h3>
              <p>Yes. The iPhone and Android apps are available now, and the complete web dashboard works from any browser.</p>
            </GlassCard>
            <GlassCard className="campaign-faq-card">
              <h3>Do players need to download an app?</h3>
              <p>No. Players join from their phone browser with the QR code or at splitcast.games.</p>
            </GlassCard>
            <GlassCard className="campaign-faq-card">
              <h3>Can I use SplitCast just for the board?</h3>
              <p>Yes. The split-flap board works on its own. Launch trivia only when you want people interacting.</p>
            </GlassCard>
            <GlassCard className="campaign-faq-card">
              <h3>Does the free tier actually include trivia?</h3>
              <p>Yes. Free includes 3 games with up to 10 questions, one 10-player hosted game each month, one display, one board, and Fire TV plus Roku support.</p>
            </GlassCard>
          </div>
        </section>

        <section className="campaign-final-cta">
          <GlassCard className="campaign-final-card">
            <div className="campaign-final-copy">
              <div className="campaign-kicker">Start with one screen</div>
              <h2>{variant.finalHeadline}</h2>
              <p>{variant.finalCopy}</p>
            </div>
            <div className="campaign-final-actions">
              <Button size="lg" onClick={() => handleStartFree('final_cta')}>Start Free & Pair a TV</Button>
              {(isAppleMobile || isAndroidMobile) && (
                <div className="campaign-final-store">
                  {isAppleMobile && (
                    <AppStoreBadgeLink compact onClick={() => handleAppStoreClick('apple', 'final_cta_secondary')} />
                  )}
                  {isAndroidMobile && (
                    <GooglePlayBadgeLink compact onClick={() => handleAppStoreClick('google_play', 'final_cta_secondary')} />
                  )}
                </div>
              )}
            </div>
          </GlassCard>
        </section>
      </main>

      <footer className="campaign-footer">
        <div className="campaign-footer-inner">
          <div className="campaign-footer-brand">
            <span className="logo-icon">▣</span>
            <span className="logo-text">SPLITCAST</span>
          </div>
          <div className="campaign-footer-links">
            <button onClick={() => navigate('/guide')}>User Guide</button>
            <button onClick={() => navigate('/contact')}>Contact</button>
            <button onClick={() => navigate('/privacy')}>Privacy</button>
            <button onClick={() => navigate('/terms')}>Terms</button>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ============================================================================
// COMMUNITY PAGE (Public)
// ============================================================================

function CommunityPage({ navigate }) {
  const [games, setGames] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortFilter, setSortFilter] = useState('popular');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewGame, setPreviewGame] = useState(null);
  const [previewPage, setPreviewPage] = useState(0);

  const pageSize = 12;
  const previewPageSize = 6;

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const result = await api.getPublicCommunityCategories();
        setCategories(result.categories || []);
      } catch (err) {
        setCategories([]);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const loadGames = async () => {
      try {
        setLoading(true);
        setError('');
        const result = await api.getPublicCommunityGames({
          categoryId: categoryFilter || undefined,
          limit: pageSize,
          offset: page * pageSize,
          sort: sortFilter || 'popular'
        });
        setGames(result.games || []);
        setTotal(result.total || 0);
      } catch (err) {
        setError(err.message || 'Failed to load community games.');
        setGames([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };
    loadGames();
  }, [categoryFilter, sortFilter, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const openPreview = async (gameId) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewGame(null);
    setPreviewPage(0);
    try {
      const result = await api.getPublicCommunityDetails(gameId);
      setPreviewGame(result.game);
    } catch (err) {
      setPreviewGame(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const previewTotalPages = Math.ceil((previewGame?.questions?.length || 0) / previewPageSize);
  const previewStart = previewPage * previewPageSize;
  const previewSlice = previewGame?.questions?.slice(previewStart, previewStart + previewPageSize) || [];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Community Games</h1>
          <p className="page-subtitle">Browse shared trivia games and review questions before you play.</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/login')}>
          Create Your Own
        </Button>
        <a
          className="header-video-link"
          href="https://www.youtube.com/watch?v=bVOcMIrjL7s"
          target="_blank"
          rel="noreferrer"
        >
          <Icons.Play /> Browse & Preview
        </a>
      </div>

      <div className="games-filter-bar">
        <Select
          label="Category Filter"
          value={categoryFilter}
          onChange={e => {
            const nextValue = e.target.value === 'undefined' || e.target.value === 'null'
              ? ''
              : e.target.value;
            setCategoryFilter(nextValue);
            setPage(0);
          }}
          options={[
            { value: '', label: 'All Categories' },
            ...categories.map(cat => ({
              value: cat.id,
              label: `${cat.icon} ${cat.name}`
            }))
          ]}
        />
        <Select
          label="Sort By"
          value={sortFilter}
          onChange={e => { setSortFilter(e.target.value); setPage(0); }}
          options={[
            { value: 'popular', label: 'Most Played' },
            { value: 'most_used', label: 'Most Uses' },
            { value: 'newest', label: 'Newest' }
          ]}
        />
      </div>

      <GlassCard className="community-warning" padding="sm">
        <Icons.Help />
        <span>Community games are user-generated. Please review questions before playing.</span>
      </GlassCard>

      {loading ? (
        <div className="loading-container">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <EmptyState
          icon={Icons.Globe}
          title="Unable to load community games"
          description={error}
        />
      ) : games.length === 0 ? (
        <EmptyState
          icon={Icons.Globe}
          title="No Community Games"
          description="Check back soon for shared games from the community."
        />
      ) : (
        <>
          <div className="games-grid">
            {games.map(game => (
              <GlassCard
                key={game.id}
                className="game-card community-card"
                onClick={() => openPreview(game.id)}
              >
                <div className="game-card-header">
                  <h3>{game.title}</h3>
                  {game.category_name && (
                    <Badge variant="info">{game.category_icon} {game.category_name}</Badge>
                  )}
                </div>
                <p className="game-meta">
                  <span><Icons.BookOpen /> {game.question_count} questions</span>
                  <span><Icons.Users /> {game.play_count} plays</span>
                  <span>
                    <Icons.Copy /> {game.copy_count || 0} {(game.copy_count || 0) === 1 ? 'use' : 'uses'}
                  </span>
                </p>
                {game.description && <p className="game-description">{game.description}</p>}
                <div className="game-card-actions">
                  <Button variant="ghost" size="sm" onClick={(event) => { event.stopPropagation(); openPreview(game.id); }}>
                    View Details
                  </Button>
                </div>
              </GlassCard>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="community-preview-pagination">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <span>Page {page + 1} of {totalPages}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={previewOpen}
        title="Community Game Details"
        onClose={() => { setPreviewOpen(false); setPreviewGame(null); }}
        size="lg"
      >
        {previewLoading ? (
          <div className="community-preview-shimmer">
            <div className="shimmer-line shimmer-title" />
            <div className="shimmer-line shimmer-meta" />
            <div className="shimmer-card" />
            <div className="shimmer-card" />
            <div className="shimmer-card" />
          </div>
        ) : previewGame ? (
          <div className="community-preview">
            <div className="community-preview-header">
              <div>
                <h3>{previewGame.title}</h3>
                {previewGame.category_name && (
                  <Badge variant="info">
                    {previewGame.category_icon} {previewGame.category_name}
                  </Badge>
                )}
              </div>
              <div className="community-preview-meta">
                <span><Icons.Users /> {previewGame.play_count} plays</span>
                <span>
                  <Icons.Copy /> {previewGame.copy_count || 0} {(previewGame.copy_count || 0) === 1 ? 'use' : 'uses'}
                </span>
              </div>
            </div>
            {previewGame.description && (
              <p className="community-preview-description">{previewGame.description}</p>
            )}
            <div className="community-preview-questions">
              {previewSlice.map((question, index) => (
                <GlassCard key={question.id} className="community-question" padding="sm">
                  <div className="community-question-header">
                    <span className="question-number">Q{previewStart + index + 1}</span>
                    <span className="question-preview">{question.prompt}</span>
                  </div>
                  <div className="community-answers">
                    {(question.answers || []).map((answer) => (
                      <div
                        key={answer.id}
                        className={`community-answer ${answer.isCorrect ? 'correct' : ''}`}
                      >
                        <span>{answer.text}</span>
                        {answer.isCorrect && <Icons.Check className="correct-icon" />}
                      </div>
                    ))}
                  </div>
                  {question.explanation && (
                    <div className="community-explanation">{question.explanation}</div>
                  )}
                </GlassCard>
              ))}
            </div>
            {previewTotalPages > 1 && (
              <div className="community-preview-pagination">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewPage(Math.max(0, previewPage - 1))}
                  disabled={previewPage === 0}
                >
                  Previous
                </Button>
                <span>
                  Page {previewPage + 1} of {previewTotalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewPage(Math.min(previewTotalPages - 1, previewPage + 1))}
                  disabled={previewPage >= previewTotalPages - 1}
                >
                  Next
                </Button>
              </div>
            )}
            <div className="modal-actions">
              <Button variant="ghost" onClick={() => { setPreviewOpen(false); setPreviewGame(null); }}>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <p>Unable to load community game details.</p>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ============================================================================
// CONTACT PAGE
// ============================================================================

function ContactPage({ navigate }) {
  const [form, setForm] = useState({ name: '', email: '', organization: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await api.request('/public/contact', { method: 'POST', body: form });
      setSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="contact-page">
        <div className="contact-success">
          <div className="success-icon">✓</div>
          <h1>Message Sent!</h1>
          <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <div className="contact-container">
        <div className="contact-info">
          <h1>Get in Touch</h1>
          <p>Have questions about SplitCast? We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
          
        <div className="contact-details">
          <div className="contact-item">
              <span className="contact-icon"><Icons.Mail /></span>
              <div>
                <h4>Email</h4>
                <p>support@splitcast.app</p>
              </div>
            </div>
            <div className="contact-item">
              <span className="contact-icon"><Icons.MessageCircle /></span>
              <div>
                <h4>Response Time</h4>
                <p>Within 24 hours</p>
              </div>
            </div>
          </div>
        </div>
        
        <GlassCard className="contact-form">
          <form onSubmit={handleSubmit}>
            <Input
              label="Name"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              required
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              required
            />
            <Input
              label="Organization (optional)"
              value={form.organization}
              onChange={e => setForm({ ...form, organization: e.target.value })}
            />
            <div className="input-group">
              <label className="input-label">Message</label>
              <textarea
                className="textarea-field"
                value={form.message}
                onChange={e => setForm({ ...form, message: e.target.value })}
                rows={5}
                required
              />
            </div>
            {error && <div className="form-error">{error}</div>}
            <Button type="submit" loading={sending} className="full-width">Send Message</Button>
          </form>
        </GlassCard>
      </div>
    </div>
  );
}

// ============================================================================
// GUIDE PAGE
// ============================================================================

function GuidePage({ navigate }) {
  const [activeSection, setActiveSection] = useState('getting-started');
  
  const sections = [
    { id: 'getting-started', label: 'Getting Started', icon: Icons.Zap },
    { id: 'trivia-games', label: 'Trivia Games', icon: Icons.Trophy },
    { id: 'game-scheduling', label: 'Game Scheduling', icon: Icons.Clock },
    { id: 'marathon-mode', label: 'Marathon Mode', icon: Icons.RotateCw },
    { id: 'create-board', label: 'Creating Boards', icon: Icons.Boards },
    { id: 'community-library', label: 'Community Library', icon: Icons.Globe },
    { id: 'pair-device', label: 'Pairing Devices', icon: Icons.Devices },
    { id: 'settings', label: 'Settings & Teams', icon: Icons.Settings },
    { id: 'activity-log', label: 'Activity Log', icon: Icons.FileText },
    { id: 'troubleshooting', label: 'Troubleshooting', icon: Icons.Help },
  ];
  const sectionIcons = Object.fromEntries(sections.map((section) => [section.id, section.icon]));

  const getYouTubeId = (url) => {
    const match = url.match(/[?&]v=([^&]+)/);
    return match ? match[1] : '';
  };

  const GuideVideo = ({ title, url }) => {
    const videoId = getYouTubeId(url);
    return (
      <a className="guide-video" href={url} target="_blank" rel="noreferrer">
        <img
          src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
          alt={title}
          loading="lazy"
        />
        <div className="guide-video-content">
          <span className="guide-video-label">Watch video</span>
          <span className="guide-video-title">{title}</span>
        </div>
      </a>
    );
  };

  const GuideSectionTitle = ({ icon: Icon, children }) => (
    <h2 className="guide-section-title">
      <span className="guide-section-title-icon"><Icon /></span>
      <span>{children}</span>
    </h2>
  );

  return (
    <div className="guide-page">
      <div className="guide-header">
        <h1 className="guide-header-title"><Icons.BookOpen /> <span>SplitCast User Guide</span></h1>
        <p>Everything you need to know to get the most out of SplitCast</p>
      </div>
      
      <div className="guide-container">
        <nav className="guide-nav">
          {sections.map(section => (
            <button
              key={section.id}
              className={`guide-nav-item ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              <span className="guide-nav-icon"><section.icon /></span>
              <span>{section.label}</span>
            </button>
          ))}
        </nav>
        
        <GlassCard className="guide-content" padding="lg">
          {activeSection === 'getting-started' && (
            <div className="guide-section">
              <GuideSectionTitle icon={sectionIcons['getting-started']}>Getting Started</GuideSectionTitle>
              <p className="guide-intro">Welcome to SplitCast! This guide will walk you through everything you need to transform your Fire TV or Roku into a beautiful split-flap message board and trivia game host.</p>
              
              <h3>What You'll Need</h3>
              <ul className="guide-list">
                <li><strong>A Streaming Device:</strong> Fire TV Stick, Fire TV Cube, Roku Stick, Roku Ultra, or any Roku TV.</li>
                <li><strong>A TV or Display:</strong> Any screen with an HDMI input (or a Roku TV).</li>
                <li><strong>WiFi Connection:</strong> Both your device and display need internet access.</li>
                <li><strong>A SplitCast Account:</strong> Sign up in the mobile app or at splitcast.app (free to start!).</li>
              </ul>
              
              <h3>Quick Start (5 Minutes)</h3>
              <div className="guide-steps">
                <div className="guide-step">
                  <div className="guide-step-number">1</div>
                  <div className="guide-step-content">
                    <h4>Create Your Account</h4>
                    <p>Install the SplitCast mobile app or click "Get Started Free," then enter your email. We'll send you a sign-in code—no password needed!</p>
                  </div>
                </div>
                <div className="guide-step">
                  <div className="guide-step-number">2</div>
                  <div className="guide-step-content">
                    <h4>Install the TV App</h4>
                    <p><strong>Fire TV:</strong> Search for "SplitCast" in the Amazon App Store.<br/>
                    <strong>Roku:</strong> Search for "SplitCast" in the Roku Channel Store.</p>
                  </div>
                </div>
                <div className="guide-step">
                  <div className="guide-step-number">3</div>
                  <div className="guide-step-content">
                    <h4>Pair Your Device</h4>
                    <p>Open Devices in the mobile app or web dashboard and enter the 8-character code from your TV.</p>
                  </div>
                </div>
                <div className="guide-step">
                  <div className="guide-step-number">4</div>
                  <div className="guide-step-content">
                    <h4>Create Content</h4>
                    <p>Go to Boards to create a split-flap message board, or go to Games to host trivia!</p>
                  </div>
                </div>
              </div>
              
              <div className="guide-tip">
                <strong>Pro Tip:</strong> The free tier includes 1 display, 3 saved games, and one 10-player hosted game each month. Upgrade to Pro ($19/mo) for unlimited games, hosting, displays, analytics, and Marathon Mode Game Queuing.
              </div>
            </div>
          )}
          
          {activeSection === 'trivia-games' && (
            <div className="guide-section">
              <GuideSectionTitle icon={sectionIcons['trivia-games']}>Trivia Games</GuideSectionTitle>
              <p className="guide-intro">Free accounts can create 3 games with up to 10 questions and host one 10-player game each month. Players join from their phones by scanning a QR code or entering a game code at <strong>splitcast.games</strong>.</p>

              <div className="guide-video-list">
                <GuideVideo title="Games: Create & Edit" url="https://www.youtube.com/watch?v=llB49oqe5OE" />
                <GuideVideo title="Games: AI Game Builder (Pro)" url="https://www.youtube.com/watch?v=v4V0ylF5MUI" />
                <GuideVideo title="Games: Game Sessions" url="https://www.youtube.com/watch?v=DgyDAodp5Ec" />
              </div>
              
              <h3>Creating a Trivia Game</h3>
              <ol className="guide-list numbered">
                <li>Go to <strong>Games</strong> in the mobile app or web dashboard</li>
                <li>Click <strong>+ New Game</strong></li>
                <li>Enter a title (e.g., "Friday Night Trivia")</li>
                <li>Add questions with multiple choice answers (2-6 options)</li>
                <li>Mark the correct answer for each question</li>
                <li>Save your game</li>
              </ol>
              
              <div className="guide-tip">
                <strong>Pro Subscription:</strong> Use AI Game Assistance to generate trivia questions and answers from a keyword or phrase, and run Marathon Mode game queues for back-to-back sessions.
              </div>
              
              <h3>Going Live</h3>
              <ol className="guide-list numbered">
                <li>Select a game from your <strong>My Games</strong> list</li>
                <li>Click <strong>Go Live</strong></li>
                <li>Select the paired TV device to display on</li>
                <li>Set the lobby countdown (60-300 seconds)</li>
                <li>Click <strong>Start Session</strong></li>
              </ol>
              
              <h3>How Players Join</h3>
              <p>When your game goes live, your TV displays:</p>
              <ul className="guide-list">
                <li>A <strong>QR code</strong> players can scan with their phones</li>
                <li>An <strong>8-digit game code</strong> to enter at splitcast.games</li>
                <li>A <strong>countdown timer</strong> showing when the game starts</li>
                <li>A live list of players who have joined</li>
              </ul>
            </div>
          )}

          {activeSection === 'game-scheduling' && (
            <div className="guide-section">
              <GuideSectionTitle icon={sectionIcons['game-scheduling']}>Game Scheduling</GuideSectionTitle>
              <p className="guide-intro">Schedule games ahead of time for web or device. Devices open the lobby when the countdown window begins, and web players see the scheduled start time. <strong>(Pro feature)</strong></p>

              <div className="guide-video-list">
                <GuideVideo title="Schedules: Schedule a Game Session" url="https://www.youtube.com/watch?v=c82LZTGrDhI" />
                <GuideVideo title="Schedules: Manage Schedules" url="https://www.youtube.com/watch?v=4gpXKvv2SX4" />
              </div>

              <h3>Schedule a Game</h3>
              <ol className="guide-list numbered">
                <li>Go to <strong>Games → My Games</strong></li>
                <li>Click <strong>Schedule</strong> on a game</li>
                <li>Choose <strong>Web</strong> or <strong>Device</strong></li>
                <li>Select a start time and lobby countdown</li>
                <li>Click <strong>Schedule Session</strong></li>
              </ol>

              <h3>Manage Schedules</h3>
              <ul className="guide-list">
                <li>Visit the <strong>Schedules</strong> tab to reschedule or cancel</li>
                <li>Overlaps are blocked with “Device Unavailable” or “Timeslot Unavailable”</li>
                <li>Devices display the lobby at start time minus countdown</li>
              </ul>
            </div>
          )}

          {activeSection === 'marathon-mode' && (
            <div className="guide-section">
              <GuideSectionTitle icon={sectionIcons['marathon-mode']}>Marathon Mode Game Queuing</GuideSectionTitle>
              <p className="guide-intro">Marathon Mode lets you queue multiple games to run back-to-back on a paired device. Each game can include a lobby countdown, then the board can rotate messages during cooldown between games. <strong>(Pro feature)</strong></p>

              <div className="guide-video-list">
                <GuideVideo title="Marathon Mode: Create & Run Queues" url="https://www.youtube.com/watch?v=gYajfAFF1To" />
              </div>

              <h3>Create a Marathon Queue</h3>
              <ol className="guide-list numbered">
                <li>Go to <strong>Games → Marathon Mode</strong></li>
                <li>Click <strong>New Marathon</strong></li>
                <li>Set a marathon name and choose a paired device</li>
                <li>Set <strong>Lobby Countdown</strong> and <strong>Cooldown Between Games</strong></li>
                <li>Add games to the queue and use the up and down arrows to reorder as needed</li>
                <li>Click <strong>Start</strong> to begin the run</li>
              </ol>

              <h3>What Displays on TV</h3>
              <ul className="guide-list">
                <li>Lobby screen appears before each game so players can join</li>
                <li>Questions, leaderboard, and final results run for each queued game</li>
                <li>Board messages rotate during cooldown windows between queued games</li>
              </ul>

              <h3>Host Controls</h3>
              <ul className="guide-list">
                <li>Use <strong>Start</strong>, <strong>Stop</strong>, and <strong>Restart</strong> on the marathon detail screen</li>
                <li>Edit queue order anytime before starting the marathon</li>
                <li>Assign a stable paired device and keep it online for uninterrupted runs</li>
              </ul>

              <div className="guide-tip">
                <strong>Pro Subscription:</strong> Marathon Mode Game Queuing, game scheduling, AI question generation, and advanced board controls are Pro features.
              </div>
            </div>
          )}

          {activeSection === 'create-board' && (
            <div className="guide-section">
              <GuideSectionTitle icon={sectionIcons['create-board']}>Creating Boards</GuideSectionTitle>
              <p>Boards are the foundation of SplitCast. Each board defines the dimensions and style of your display, and can contain multiple messages that rotate.</p>

              <div className="guide-video-list">
                <GuideVideo title="Boards Basics" url="https://www.youtube.com/watch?v=1di4APAyhfo" />
                <GuideVideo title="Boards Power Features" url="https://www.youtube.com/watch?v=uyBMi_xgpH8" />
              </div>
              
              <h3>Create a New Board</h3>
              <ol className="guide-list numbered">
                <li>Open <strong>Boards</strong> in the mobile app or web dashboard</li>
                <li>Click the <strong>+ Create Board</strong> button</li>
                <li>Enter a name (e.g., "Lobby Display" or "Weekly Menu")</li>
                <li>Choose your dimensions (Rows: 1-10, Columns: 10-30)</li>
                <li>Select a font style <strong>(Pro feature)</strong></li>
                <li>Click <strong>Create</strong></li>
              </ol>
              
              <h3>Choosing the Right Dimensions</h3>
              <ul className="guide-list">
                <li><strong>Simple announcements:</strong> 4 rows × 20 columns</li>
                <li><strong>Menu or schedule:</strong> 8 rows × 25 columns</li>
                <li><strong>Lobby welcome:</strong> 3 rows × 20 columns</li>
              </ul>

              <h3>Message Rotation</h3>
              <ul className="guide-list">
                <li>Add multiple messages per board</li>
                <li>Set the <strong>Message Rotation</strong> slider on the board</li>
                <li>Preview changes instantly before saving</li>
              </ul>

              <div className="guide-tip">
                <strong>Pro Subscription:</strong> Custom colors & fonts, message scheduling, the Message Bank, and Marathon Mode Game Queuing are Pro features.
              </div>
            </div>
          )}

          {activeSection === 'community-library' && (
            <div className="guide-section">
              <GuideSectionTitle icon={sectionIcons['community-library']}>Community Library</GuideSectionTitle>
              <p>Browse community shared games, preview every question, and copy games into your account.</p>

              <div className="guide-video-list">
                <GuideVideo title="Community: Browse & Preview" url="https://www.youtube.com/watch?v=bVOcMIrjL7s" />
                <GuideVideo title="Community: Share & Moderate" url="https://www.youtube.com/watch?v=3sv2gjUfUMg" />
              </div>

              <h3>Using Community Games</h3>
              <ol className="guide-list numbered">
                <li>Go to <strong>Games → Community</strong></li>
                <li>Click a game card to preview questions and answers</li>
                <li>Click <strong>Use This Game</strong> to copy it into My Games</li>
              </ol>

              <h3>Sharing Your Games</h3>
              <ul className="guide-list">
                <li>Open <strong>My Games</strong> and click <strong>Share</strong></li>
                <li>Shared games may be reviewed before appearing publicly</li>
              </ul>
            </div>
          )}
          
          {activeSection === 'pair-device' && (
            <div className="guide-section">
              <GuideSectionTitle icon={sectionIcons['pair-device']}>Pairing Devices</GuideSectionTitle>
              <p>Connect your Fire TV or Roku devices to your SplitCast account with a simple 8-character code.</p>

              <div className="guide-video-list">
                <GuideVideo title="Devices: Pairing & Assignment" url="https://www.youtube.com/watch?v=d40kxl8jQaY" />
                <GuideVideo title="Devices: Management" url="https://www.youtube.com/watch?v=1XWl9sZRyU8" />
              </div>
              
              <h3>First-Time Setup</h3>
              <ol className="guide-list numbered">
                <li><strong>Install the App:</strong> Search for "SplitCast" on your device's app store</li>
                <li><strong>Open the App:</strong> Launch SplitCast and you'll see an 8-character pairing code</li>
                <li><strong>Enter the Code:</strong> Open Devices in the mobile app or web dashboard and choose the pairing action</li>
                <li><strong>Name Your Device:</strong> Give it a memorable name like "Lobby TV"</li>
                <li><strong>Done!</strong> Your device is now connected</li>
              </ol>
              
              <div className="guide-tip">
                <strong>Pro Tip:</strong> Pairing codes expire after 5 minutes for security. When it expires, a fresh code appears automatically—no need to restart the app.
                <ul>
                  <li>Roku screensaver: Settings &gt; Theme &gt; Screensaver wait time &gt; Disable.</li>
                  <li>Roku auto power savings: Settings &gt; System &gt; Power &gt; Auto power settings &gt; Uncheck any boxes (including “Reduce power after...” and “Turn off after...” ).</li>
                </ul>
              </div>
            </div>
          )}

          {activeSection === 'settings' && (
            <div className="guide-section">
              <GuideSectionTitle icon={sectionIcons['settings']}>Settings & Teams</GuideSectionTitle>
              <p>Manage your account, billing, and team members from the Settings tab.</p>

              <div className="guide-video-list">
                <GuideVideo title="Settings: Account & Plan" url="https://www.youtube.com/watch?v=crqUG-ZwRGc" />
                <GuideVideo title="Settings: Team & Orgs" url="https://www.youtube.com/watch?v=hzSDnROz9tU" />
              </div>

              <ul className="guide-list">
                <li>Update your organization name and timezone</li>
                <li>Manage your subscription and billing portal</li>
                <li>Invite team members and manage roles</li>
                <li>Create and manage sub-organizations</li>
              </ul>
            </div>
          )}

          {activeSection === 'activity-log' && (
            <div className="guide-section">
              <GuideSectionTitle icon={sectionIcons['activity-log']}>Activity Log</GuideSectionTitle>
              <p>Track changes across your organization, including boards, devices, games, and schedules.</p>

              <div className="guide-video-list">
                <GuideVideo title="Activity Log (Tab)" url="https://www.youtube.com/watch?v=wlqOc7LR9VQ" />
              </div>

              <ul className="guide-list">
                <li>See who changed what and when</li>
                <li>Review session schedules, reschedules, and live events</li>
                <li>Audit community shares and copies</li>
              </ul>
            </div>
          )}
          
          {activeSection === 'troubleshooting' && (
            <div className="guide-section">
              <GuideSectionTitle icon={sectionIcons['troubleshooting']}>Troubleshooting</GuideSectionTitle>
              
              <h3>Display Shows "No Board Assigned"</h3>
              <ul className="guide-list">
                <li>Open Devices in the mobile app or web dashboard</li>
                <li>Click on the device</li>
                <li>Select a board from the dropdown</li>
                <li>Make sure the board has at least one active message</li>
              </ul>
              
              <h3>Display Not Updating</h3>
              <ul className="guide-list">
                <li>Check that your Fire TV or Roku has internet connection</li>
                <li>Verify the device shows as "Online" in the mobile app or web dashboard</li>
                <li>Try restarting the SplitCast app on your device</li>
                <li>Changes typically appear within 15 seconds</li>
              </ul>
              
              <h3>Still Need Help?</h3>
              <p>Contact us at <strong>support@splitcast.app</strong> - we typically respond within 24 hours.</p>
            </div>
          )}
        </GlassCard>
      </div>
      
      <div className="guide-footer">
        <p>Ready to get started?</p>
        <Button onClick={() => navigate('/login')}>Go to Dashboard</Button>
      </div>
    </div>
  );
}

// ============================================================================
// PRIVACY PAGE
// ============================================================================

function PrivacyPage() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
          <p className="legal-date">Last updated: March 2026</p>
        
        <section>
          <h2>1. Information We Collect</h2>
          <p>We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support.</p>
          <ul>
            <li><strong>Account Information:</strong> Email address, organization name, and account preferences.</li>
            <li><strong>Content:</strong> Board messages, trivia games, questions, answers, and scheduling details you create.</li>
            <li><strong>Device Information:</strong> Information about Fire TV and Roku devices you pair with your account.</li>
            <li><strong>Player Data:</strong> Display names and join codes used to participate in trivia sessions.</li>
            <li><strong>Usage Data:</strong> Information about how you interact with our services, including game sessions and device activity.</li>
          </ul>
        </section>
        
        <section>
          <h2>2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve our services</li>
            <li>Send you technical notices and support messages</li>
            <li>Respond to your comments and questions</li>
            <li>Protect against fraudulent or illegal activity</li>
            <li>Moderate community submissions and enforce acceptable use</li>
          </ul>
        </section>
        
        <section>
          <h2>3. Information Sharing</h2>
          <p>We do not sell your personal information. We may share information in the following circumstances:</p>
          <ul>
            <li>With your consent</li>
            <li>To comply with legal obligations</li>
            <li>To protect our rights and safety</li>
            <li>With service providers who assist in our operations (hosting, analytics, communications)</li>
          </ul>
        </section>
        
        <section>
          <h2>4. Data Security</h2>
          <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, or destruction.</p>
        </section>

        <section>
          <h2>5. Your Rights</h2>
          <p>You have the right to access, correct, or delete your personal information. See our <a href="/account-deletion">account deletion instructions</a> or contact us at support@splitcast.app to exercise these rights.</p>
        </section>

        <section>
          <h2>6. Community Content</h2>
          <p>Shared community games may be reviewed to ensure they comply with our content guidelines. If your game is flagged, it may be held for moderation or removed.</p>
        </section>

        <section>
          <h2>7. Contact Us</h2>
          <p>If you have questions about this Privacy Policy, please contact us at support@splitcast.app.</p>
        </section>
      </div>
    </div>
  );
}

// ============================================================================
// TERMS PAGE
// ============================================================================

function TermsPage() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Terms of Service</h1>
        <p className="legal-date">Last updated: March 2026</p>
        
        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>By accessing or using SplitCast, you agree to be bound by these Terms of Service and all applicable laws and regulations.</p>
        </section>
        
        <section>
          <h2>2. Description of Service</h2>
          <p>SplitCast provides a digital signage and trivia gaming platform that enables users to display split-flap style messages, schedule game sessions, and host multiplayer trivia on Fire TV and Roku devices.</p>
        </section>
        
        <section>
          <h2>3. Account Registration</h2>
          <p>To use SplitCast, you must create an account using a valid email address. You are responsible for maintaining the confidentiality of your account.</p>
        </section>
        
        <section>
          <h2>4. Acceptable Use</h2>
          <p>You agree not to use SplitCast to:</p>
          <ul>
            <li>Display illegal, harmful, or offensive content</li>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe on intellectual property rights</li>
            <li>Distribute malware or engage in hacking activities</li>
            <li>Interfere with the service's operation</li>
          </ul>
        </section>

        <section>
          <h2>5. Community Sharing & Moderation</h2>
          <p>By sharing a game to the community library, you grant SplitCast permission to display and distribute that content to other users. We may review, approve, or reject submissions, and we may remove content that violates these Terms.</p>
        </section>
        
        <section>
          <h2>6. Subscription and Payment</h2>
          <p>SplitCast offers a free tier and a Pro tier at $19/month. Subscriptions renew automatically unless cancelled.</p>
        </section>
        
        <section>
          <h2>7. Termination</h2>
          <p>We may terminate or suspend your account at any time for violation of these terms. You may cancel your subscription at any time.</p>
        </section>
        
        <section>
          <h2>8. Limitation of Liability</h2>
          <p>SplitCast is provided "as is" without warranties of any kind. We shall not be liable for any indirect, incidental, or consequential damages.</p>
        </section>
        
        <section>
          <h2>9. Contact</h2>
          <p>For questions about these Terms of Service, contact us at support@splitcast.app.</p>
        </section>
      </div>
    </div>
  );
}

// ============================================================================
// ACCOUNT DELETION PAGE
// ============================================================================

function AccountDeletionPage() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Delete Your SplitCast Account</h1>
        <p className="legal-date">Last updated: July 2026</p>

        <section>
          <h2>Delete your account in the app</h2>
          <ol>
            <li>Open SplitCast and sign in.</li>
            <li>Open <strong>Settings</strong> and select <strong>Delete Account</strong>.</li>
            <li>Enter your SplitCast email address and confirm permanent deletion.</li>
          </ol>
          <p>You may need to remove other organization members and unpair active display devices before deletion can be completed.</p>
        </section>

        <section>
          <h2>Request deletion without the app</h2>
          <p>Email <a href="mailto:support@splitcast.app?subject=SplitCast%20Account%20Deletion%20Request">support@splitcast.app</a> from the email address registered to your SplitCast account. Use the subject <strong>SplitCast Account Deletion Request</strong>. We may ask you to verify account ownership, and verified requests are completed within 30 days.</p>
        </section>

        <section>
          <h2>Data that is deleted</h2>
          <p>Deletion removes your SplitCast user profile, email address, authentication records, account memberships, and solo organizations. Content belonging only to those organizations is also removed, including boards, board messages, trivia games, schedules, paired devices, and subscription status stored by SplitCast.</p>
        </section>

        <section>
          <h2>Data that may be retained</h2>
          <p>Security and audit records are anonymized so they no longer identify you. Limited payment and transaction records may be retained for up to seven years where required for tax, accounting, fraud-prevention, or other legal obligations. Apple, Google, Stripe, and RevenueCat may separately retain transaction records under their own legal obligations and privacy policies.</p>
        </section>

        <section>
          <h2>Subscriptions</h2>
          <p>Deleting your SplitCast account does not automatically cancel an Apple App Store or Google Play subscription. Cancel any active store subscription from your Apple or Google account before deleting your SplitCast account.</p>
        </section>
      </div>
    </div>
  );
}

// ============================================================================
// SMS TERMS PAGE
// ============================================================================

function SmsTermsPage() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>SplitCast SMS Messaging Terms</h1>
        <p className="legal-date">Last updated: April 2026</p>

        <section>
          <h2>Overview</h2>
          <p>SplitCast offers an optional SMS integration that allows account holders to send messages to their own SplitCast display boards by sending a text message. This feature is entirely opt-in and is available to Pro plan account holders only.</p>
        </section>

        <section>
          <h2>Who Sends and Receives Messages</h2>
          <p>SplitCast sends SMS messages only to verified account holders. The person who opts in to SMS is the same person who receives messages from SplitCast. We do not send messages to third parties, customers, or anyone who has not explicitly registered their own phone number in the SplitCast dashboard.</p>
        </section>

        <section>
          <h2>How to Opt In</h2>
          <ol>
            <li>Log in to your SplitCast account at <a href="https://splitcast.app" target="_blank" rel="noreferrer">splitcast.app</a></li>
            <li>Navigate to <strong>Settings → SMS Integration</strong></li>
            <li>Select the display board you want to link</li>
            <li>Enter your mobile phone number and click <strong>Send Verification Code</strong></li>
            <li>Enter the 6-digit code sent to your phone to confirm ownership</li>
            <li>Once verified, you can send messages to your board by texting from your registered number</li>
          </ol>
          <p>By completing the verification step, you are confirming that you own the phone number and consenting to receive SMS messages from SplitCast related to your account and board activity.</p>
        </section>

        <section>
          <h2>Types of Messages You May Receive</h2>
          <p>SplitCast will only send the following types of messages to registered numbers:</p>
          <ul>
            <li><strong>Verification codes</strong> — a one-time 6-digit code sent when you register or re-verify your phone number</li>
            <li><strong>Confirmation replies</strong> — an optional acknowledgement (&quot;Message added to your board&quot;) sent after your text is successfully posted to your board. This is off by default and can be enabled in your dashboard settings.</li>
          </ul>
          <p>We do not send marketing messages, promotions, or automated alerts unless you separately opt in to those communications.</p>
        </section>

        <section>
          <h2>Message Frequency</h2>
          <p>Message frequency depends entirely on your usage. Verification codes are sent once per registration. Confirmation replies, if enabled, are sent only when you text your board. There are no scheduled or recurring messages.</p>
        </section>

        <section>
          <h2>How to Opt Out</h2>
          <p>You can opt out at any time by:</p>
          <ul>
            <li>Removing your phone number from <strong>Settings → SMS Integration</strong> in your dashboard</li>
            <li>Replying <strong>STOP</strong> to any message from SplitCast — this will immediately unsubscribe your number and no further messages will be sent</li>
          </ul>
          <p>To re-enable SMS after opting out, return to your dashboard and complete the registration process again.</p>
        </section>

        <section>
          <h2>Help</h2>
          <p>Reply <strong>HELP</strong> to any SplitCast message for assistance, or contact us at:</p>
          <ul>
            <li>Email: <a href="mailto:support@splitcast.app">support@splitcast.app</a></li>
            <li>Website: <a href="https://splitcast.app" target="_blank" rel="noreferrer">splitcast.app</a></li>
          </ul>
        </section>

        <section>
          <h2>Messaging Rates</h2>
          <p>Standard message and data rates may apply depending on your mobile carrier plan. SplitCast does not charge separately for SMS messages.</p>
        </section>

        <section>
          <h2>Privacy</h2>
          <p>Phone numbers collected for SMS integration are used solely to route messages to your display board and to send verification codes. We do not share, sell, or use your phone number for any other purpose. For full details see our <a href="/privacy">Privacy Policy</a>.</p>
        </section>

        <section>
          <p><em>SplitCast is operated by EAI Technology, LTD. For questions about these terms contact <a href="mailto:support@splitcast.app">support@splitcast.app</a>.</em></p>
        </section>
      </div>
    </div>
  );
}

// ============================================================================
// LOGIN PAGE
// ============================================================================

function LoginPage({ navigate, onLogin, onLoginWithData }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRequestCode = async () => {
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.requestMagicLink(email.trim());
      setStep('code');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      setError('Please enter the code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await api.verifyCode(email, code.trim());
      trackGoogleAdsSignUp(data);
      onLoginWithData(data);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <span className="logo-icon large">▣</span>
          <h1>Welcome to SplitCast</h1>
          <p>{step === 'email' ? 'Enter your email to sign in or create an account' : `We sent a code to ${email}`}</p>
        </div>
        
        <GlassCard className="login-form">
          {step === 'email' ? (
            <>
              <Input
                label="Email Address"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                onKeyDown={e => e.key === 'Enter' && handleRequestCode()}
                error={error}
              />
              <Button onClick={handleRequestCode} loading={loading} className="full-width">
                Continue with Email
              </Button>
            </>
          ) : (
            <>
              <Input
                label="Verification Code"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                onKeyDown={e => e.key === 'Enter' && handleVerifyCode()}
                error={error}
              />
              <Button onClick={handleVerifyCode} loading={loading} className="full-width">
                Sign In
              </Button>
              <button className="text-link" onClick={() => { setStep('email'); setCode(''); setError(''); }}>
                Use a different email
              </button>
            </>
          )}
        </GlassCard>
        
        <p className="login-terms">
          By continuing, you agree to our <a onClick={() => navigate('/terms')}>Terms</a> and <a onClick={() => navigate('/privacy')}>Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}

function AuthCallbackPage({ navigate, onLogin }) {
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    
    if (token) {
      onLogin(token)
        .then(() => {
          window.history.replaceState({}, document.title, window.location.pathname);
          navigate('/dashboard');
        })
        .catch(err => setError(err.message));
    } else {
      setError('Invalid authentication link');
    }
  }, []);

  if (error) {
    return (
      <div className="auth-screen">
        <div className="auth-container">
          <GlassCard>
            <h2>Authentication Error</h2>
            <p>{error}</p>
            <Button onClick={() => navigate('/login')}>Try Again</Button>
          </GlassCard>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-container">
        <Spinner size="lg" />
        <p>Signing you in...</p>
      </div>
    </div>
  );
}

function ConsentBanner({ navigate }) {
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const consent = getCookieValue(CONSENT_COOKIE);
    if (consent !== PUBLIC_MARKETING_CONSENT) {
      setVisible(true);
    }
  }, []);

  const handleAccept = async () => {
    if (saving) return;
    setSaving(true);
    const consentId = getOrCreateConsentId();
    setCookieValue(CONSENT_COOKIE, PUBLIC_MARKETING_CONSENT, CONSENT_MAX_AGE_DAYS);
    try {
      await fetch(`${API_URL}/public/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consentId,
          essential: true,
          analytics: false,
          marketing: true
        })
      });
    } catch (error) {
      // Ignore consent logging failures to avoid blocking the UI.
    }
    window.dispatchEvent(new Event('public-consent-updated'));
    setVisible(false);
    setSaving(false);
  };

  if (!visible) return null;

  return (
    <div className="consent-banner">
      <div className="consent-card glass-card">
        <div className="consent-text">
          <strong>We use essential cookies</strong> to keep SplitCast secure and running smoothly,
          plus limited marketing measurement to understand ad-driven signups and subscriptions.
        </div>
        <div className="consent-actions">
          <span className="consent-link" onClick={() => navigate('/privacy')}>Privacy Policy</span>
          <button className="btn btn-primary btn-md consent-accept-button" onClick={handleAccept} disabled={saving}>
            {saving ? 'Saving…' : 'Accept'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PublicGoogleAdsTag() {
  useEffect(() => {
    const loadTag = () => {
      if (!hasMarketingConsent()) return;

      if (!window.dataLayer) {
        window.dataLayer = [];
      }

      if (!window.gtag) {
        window.gtag = function gtag() {
          window.dataLayer.push(arguments);
        };
        window.gtag('js', new Date());
      }

      const existingScript = document.getElementById('google-ads-gtag');
      if (!existingScript) {
        const script = document.createElement('script');
        script.id = 'google-ads-gtag';
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_TAG_ID}`;
        document.head.appendChild(script);
      }

      window.gtag('config', GOOGLE_ADS_TAG_ID);
      flushPendingSubscriptionConversion();
    };

    loadTag();
    window.addEventListener('public-consent-updated', loadTag);

    return () => {
      window.removeEventListener('public-consent-updated', loadTag);
    };
  }, []);

  return null;
}

// ============================================================================
// MAIN APP
// ============================================================================

export default function App() {
  const { path, navigate } = useRouter();

  return (
    <AuthProvider>
      <PublicGoogleAdsTag />
      <AppContent path={path} navigate={navigate} />
      <GlobalStyles />
    </AuthProvider>
  );
}

function AppContent({ path, navigate }) {
  const { user, loading, login, loginWithData, logout } = useAuth();

  if (loading) {
    return (
      <div className="app-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  if (path === '/auth/callback') {
    return <AuthCallbackPage navigate={navigate} onLogin={login} />;
  }

  if (path === '/dashboard' || path.startsWith('/dashboard/')) {
    if (!user) {
      navigate('/login');
      return null;
    }
    return (
      <>
        <ConsentBanner navigate={navigate} />
        <Dashboard navigate={navigate} onLogout={() => { logout(); navigate('/'); }} />
      </>
    );
  }

  const isCampaignLanding = path === '/firetv-roku-trivia' || path.startsWith('/firetv-roku-trivia?');
  if (isCampaignLanding) {
    return (
      <>
        <ConsentBanner navigate={navigate} />
        <FireTvRokuTriviaLandingPage navigate={navigate} />
      </>
    );
  }

  // Public pages with nav and footer
  const isLoggedIn = !!user;
  
  return (
    <div className="public-layout">
      <ConsentBanner navigate={navigate} />
      <PublicNav navigate={navigate} isLoggedIn={isLoggedIn} />
      <main className={`public-main${path === '/community' ? ' public-main-community' : ''}`}>
        {path === '/' && <HomePage navigate={navigate} />}
        {path === '/login' && <LoginPage navigate={navigate} onLogin={login} onLoginWithData={loginWithData} />}
        {path === '/community' && <CommunityPage navigate={navigate} />}
        {path === '/contact' && <ContactPage navigate={navigate} />}
        {path === '/guide' && <GuidePage navigate={navigate} />}
        {path === '/privacy' && <PrivacyPage />}
        {path === '/account-deletion' && <AccountDeletionPage />}
        {path === '/sms-terms' && <SmsTermsPage />}
        {path === '/terms' && <TermsPage />}
      </main>
      {path !== '/login' && <PublicFooter navigate={navigate} />}
    </div>
  );
}

// ============================================================================
// GLOBAL STYLES - Glassmorphism Design System
// ============================================================================

function GlobalStyles() {
  return (
    <style>{`
      /* ================================================================
         FONTS & CSS VARIABLES
         ================================================================ */
      @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

      :root {
        /* Color Palette - 60-30-10 */
        --bg-base: #0F172A;
        --bg-surface: #1E293B;
        --bg-elevated: #334155;
        --bg-glass: rgba(30, 41, 59, 0.7);
        --bg-glass-hover: rgba(51, 65, 85, 0.8);
        
        /* Accent - Electric Blue */
        --accent: #3B82F6;
        --accent-hover: #60A5FA;
        --accent-muted: rgba(59, 130, 246, 0.2);
        --accent-glow: 0 0 20px rgba(59, 130, 246, 0.4);
        
        /* Text */
        --text-primary: #F8FAFC;
        --text-secondary: #94A3B8;
        --text-muted: #64748B;
        
        /* Borders */
        --border-glass: rgba(255, 255, 255, 0.1);
        --border-accent: rgba(59, 130, 246, 0.3);
        
        /* Status Colors */
        --success: #10B981;
        --success-bg: rgba(16, 185, 129, 0.15);
        --warning: #F59E0B;
        --warning-bg: rgba(245, 158, 11, 0.15);
        --danger: #EF4444;
        --danger-bg: rgba(239, 68, 68, 0.15);
        --info: #3B82F6;
        --info-bg: rgba(59, 130, 246, 0.15);
        
        /* Spacing */
        --sidebar-width: 260px;
        --sidebar-collapsed: 72px;
        --header-height: 64px;
        
        /* Transitions */
        --transition-fast: 150ms ease;
        --transition-normal: 250ms ease;
        --transition-slow: 350ms ease;
        
        /* Border Radius */
        --radius-sm: 8px;
        --radius-md: 12px;
        --radius-lg: 16px;
        --radius-xl: 24px;
      }

      /* ================================================================
         RESET & BASE
         ================================================================ */
      *, *::before, *::after {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }

      html {
        scroll-behavior: smooth;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      body {
        font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        background: var(--bg-base);
        color: var(--text-primary);
        line-height: 1.6;
        min-height: 100vh;
      }

      /* Typography Hierarchy */
      h1, h2, h3, h4 {
        font-weight: 600;
        line-height: 1.3;
        color: var(--text-primary);
      }

      h1 { font-size: 1.875rem; letter-spacing: -0.025em; }
      h2 { font-size: 1.5rem; letter-spacing: -0.02em; }
      h3 { font-size: 1.25rem; }
      h4 { font-size: 1.125rem; }

      p { color: var(--text-secondary); }

      /* Monospace for data */
      .mono, code, .kpi-number {
        font-family: 'JetBrains Mono', monospace;
      }

      /* ================================================================
         GLASSMORPHISM CARDS
         ================================================================ */
      .glass-card {
        background: var(--bg-glass);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        transition: all var(--transition-normal);
      }

      .glass-card-hover:hover {
        background: var(--bg-glass-hover);
        border-color: var(--border-accent);
        transform: translateY(-2px);
      }

      .p-3 { padding: 12px; }
      .p-5 { padding: 20px; }
      .p-6 { padding: 24px; }
      .p-8 { padding: 32px; }

      /* ================================================================
         KPI CARDS
         ================================================================ */
      .kpi-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 16px;
        margin-bottom: 24px;
      }

      .kpi-card {
        padding: 20px;
      }

      .kpi-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 12px;
      }

      .kpi-title {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--text-secondary);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .kpi-icon {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--accent-muted);
        border-radius: var(--radius-sm);
        color: var(--accent);
      }

      .kpi-value {
        display: flex;
        align-items: baseline;
        gap: 4px;
        margin-bottom: 8px;
      }

      .kpi-number {
        font-size: 2rem;
        font-weight: 700;
        color: var(--text-primary);
        line-height: 1;
      }

      .kpi-suffix {
        font-size: 1rem;
        color: var(--text-muted);
      }

      .kpi-change {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 0.875rem;
      }

      .kpi-change svg {
        width: 14px;
        height: 14px;
      }

      .kpi-period {
        color: var(--text-muted);
        margin-left: 4px;
      }

      .text-emerald-400 { color: #34D399; }
      .text-rose-400 { color: #FB7185; }
      .text-slate-400 { color: var(--text-muted); }

      /* ================================================================
         BUTTONS
         ================================================================ */
      .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-family: inherit;
        font-weight: 500;
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
        border: none;
        outline: none;
        white-space: nowrap;
      }

      .btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .btn svg {
        width: 18px;
        height: 18px;
      }

      /* Sizes */
      .btn-sm { padding: 8px 12px; font-size: 0.8125rem; }
      .btn-md { padding: 10px 18px; font-size: 0.875rem; }
      .btn-lg { padding: 14px 24px; font-size: 1rem; }

      /* Primary - Glow Effect */
      .btn-primary {
        background: var(--accent);
        color: white;
        box-shadow: var(--accent-glow);
      }

      .btn-primary:hover:not(:disabled) {
        background: var(--accent-hover);
        box-shadow: 0 0 30px rgba(59, 130, 246, 0.5);
        transform: translateY(-1px);
      }

      .btn-live {
        background: var(--success);
        color: #fff;
        box-shadow: 0 0 22px rgba(34, 197, 94, 0.35);
      }

      .btn-live:disabled {
        opacity: 0.85;
      }

      /* Secondary - Outlined */
      .btn-secondary {
        background: transparent;
        color: var(--text-primary);
        border: 1px solid var(--border-glass);
      }

      .btn-secondary:hover:not(:disabled) {
        background: var(--bg-glass);
        border-color: var(--accent);
        color: var(--accent);
      }

      /* Ghost */
      .btn-ghost {
        background: transparent;
        color: var(--text-secondary);
      }

      .btn-ghost:hover:not(:disabled) {
        background: var(--bg-glass);
        color: var(--text-primary);
      }

      /* Danger */
      .btn-danger {
        background: var(--danger);
        color: white;
      }

      .btn-danger:hover:not(:disabled) {
        background: #DC2626;
      }

      .btn-danger-ghost {
        color: var(--danger);
      }

      .btn-danger-ghost:hover:not(:disabled) {
        background: var(--danger-bg);
      }

      .btn-spinner {
        width: 18px;
        height: 18px;
        border: 2px solid rgba(255,255,255,0.3);
        border-top-color: white;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
      }

      .full-width { width: 100%; }

      /* ================================================================
         INPUTS
         ================================================================ */
      .input-group {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-bottom: 16px;
      }

      .input-label {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--text-secondary);
      }

      .input-field, .select-field {
        width: 100%;
        padding: 12px 16px;
        font-family: inherit;
        font-size: 0.9375rem;
        color: var(--text-primary);
        background: var(--bg-surface);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        outline: none;
        transition: all var(--transition-fast);
      }

      .input-field::placeholder {
        color: var(--text-muted);
      }

      .input-field:focus, .select-field:focus {
        border-color: var(--accent);
        box-shadow: 0 0 0 3px var(--accent-muted);
      }

      .range-row {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .range-field {
        flex: 1;
        accent-color: var(--accent);
      }

      .range-value {
        min-width: 64px;
        text-align: right;
        font-size: 0.875rem;
        color: var(--text-secondary);
      }

      .input-error {
        border-color: var(--danger);
      }

      .input-error-text {
        font-size: 0.8125rem;
        color: var(--danger);
      }

      .select-field {
        cursor: pointer;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 12px center;
        padding-right: 40px;
      }

      /* ================================================================
         BADGES
         ================================================================ */
      .badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 0.75rem;
        font-weight: 500;
        padding: 4px 10px;
        border-radius: 100px;
        text-transform: uppercase;
        letter-spacing: 0.03em;
      }

      .badge-sm { padding: 2px 8px; font-size: 0.6875rem; }

      .badge-default {
        background: var(--bg-elevated);
        color: var(--text-secondary);
      }

      .badge-success {
        background: var(--success-bg);
        color: var(--success);
      }

      .badge-warning {
        background: var(--warning-bg);
        color: var(--warning);
      }

      .badge-danger {
        background: var(--danger-bg);
        color: var(--danger);
      }

      .badge-info {
        background: var(--info-bg);
        color: var(--info);
      }

      .badge-pro {
        background: linear-gradient(135deg, var(--accent-muted), rgba(139, 92, 246, 0.2));
        color: var(--accent);
        border: 1px solid var(--border-accent);
      }

      /* ================================================================
         TOAST NOTIFICATIONS
         ================================================================ */
      .toast {
        position: fixed;
        bottom: 24px;
        right: 24px;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px 20px;
        background: var(--bg-surface);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
        z-index: 9999;
        animation: slideIn 0.3s ease;
      }

      .toast-success { border-left: 3px solid var(--success); }
      .toast-error { border-left: 3px solid var(--danger); }
      .toast-info { border-left: 3px solid var(--info); }
      .toast-warning { border-left: 3px solid var(--warning); }

      .toast-close {
        background: none;
        border: none;
        color: var(--text-muted);
        cursor: pointer;
        padding: 4px;
      }

      .toast-close:hover {
        color: var(--text-primary);
      }

      .consent-banner {
        position: fixed;
        bottom: 24px;
        left: 24px;
        right: 24px;
        z-index: 999;
        display: flex;
        justify-content: center;
        pointer-events: none;
      }

      .consent-card {
        width: min(920px, 100%);
        display: flex;
        gap: 24px;
        align-items: center;
        justify-content: space-between;
        padding: 18px 22px;
        pointer-events: auto;
      }

      .consent-text {
        font-size: 14px;
        color: var(--text-secondary);
        line-height: 1.5;
      }

      .consent-text strong {
        color: var(--text-primary);
        font-weight: 600;
      }

      .consent-actions {
        display: flex;
        align-items: center;
        gap: 18px;
        flex-shrink: 0;
      }

      .consent-accept-button {
        min-width: 112px;
        min-height: 44px;
        font-weight: 700;
      }

      .consent-link {
        color: var(--accent);
        font-weight: 600;
        font-size: 13px;
        cursor: pointer;
        text-decoration: none;
      }

      .consent-link:hover {
        color: var(--accent-hover);
      }

      @media (max-width: 720px) {
        .consent-card {
          flex-direction: column;
          align-items: flex-start;
          gap: 22px;
        }

        .consent-actions {
          width: 100%;
          justify-content: space-between;
        }
      }

      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      /* ================================================================
         MODAL
         ================================================================ */
      .modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: 20px;
        animation: fadeIn 0.2s ease;
      }

      .modal-container {
        background: var(--bg-surface);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-lg);
        width: 100%;
        max-height: 90vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        animation: scaleIn 0.2s ease;
      }

      .modal-sm { max-width: 400px; }
      .modal-md { max-width: 500px; }
      .modal-lg { max-width: 700px; }
      .modal-xl { max-width: 900px; }

      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px 24px;
        border-bottom: 1px solid var(--border-glass);
      }

      .modal-title {
        font-size: 1.25rem;
        font-weight: 600;
      }

      .modal-close {
        background: none;
        border: none;
        color: var(--text-muted);
        cursor: pointer;
        padding: 8px;
        border-radius: var(--radius-sm);
        transition: all var(--transition-fast);
      }

      .modal-close:hover {
        background: var(--bg-elevated);
        color: var(--text-primary);
      }

      .modal-body {
        padding: 24px;
        overflow-y: auto;
      }

      .modal-description {
        color: var(--text-secondary);
        margin-bottom: 20px;
      }

      .pairing-note {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 12px 14px;
        border: 1px solid rgba(255, 184, 0, 0.28);
        border-radius: var(--radius-sm);
        background: rgba(255, 184, 0, 0.08);
        color: var(--text-secondary);
        font-size: 0.875rem;
        line-height: 1.5;
      }

      .pairing-note strong {
        color: var(--text-primary);
      }

      .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 24px;
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes scaleIn {
        from {
          transform: scale(0.95);
          opacity: 0;
        }
        to {
          transform: scale(1);
          opacity: 1;
        }
      }

      /* ================================================================
         EMPTY STATE
         ================================================================ */
      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        text-align: center;
      }

      .empty-state-icon {
        width: 80px;
        height: 80px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-glass);
        border-radius: var(--radius-lg);
        margin-bottom: 24px;
        color: var(--text-muted);
      }

      .empty-state-icon svg {
        width: 40px;
        height: 40px;
      }

      .empty-state-title {
        font-size: 1.25rem;
        margin-bottom: 8px;
      }

      .empty-state-description {
        color: var(--text-muted);
        max-width: 400px;
        margin-bottom: 24px;
      }

      /* ================================================================
         SPINNER
         ================================================================ */
      .spinner {
        border: 3px solid var(--border-glass);
        border-top-color: var(--accent);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }

      .spinner-sm { width: 16px; height: 16px; }
      .spinner-md { width: 24px; height: 24px; }
      .spinner-lg { width: 40px; height: 40px; }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      .loading-container {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 60px;
      }

      /* ================================================================
         SIDEBAR
         ================================================================ */
      .sidebar {
        position: fixed;
        left: 0;
        top: 0;
        bottom: 0;
        width: var(--sidebar-width);
        background: var(--bg-glass);
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        border-right: 1px solid var(--border-glass);
        display: flex;
        flex-direction: column;
        z-index: 100;
        transition: width var(--transition-normal);
      }

      .sidebar.sidebar-collapsed {
        width: var(--sidebar-collapsed);
      }

      .sidebar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px;
        border-bottom: 1px solid var(--border-glass);
      }

      .sidebar-logo {
        display: flex;
        align-items: center;
        gap: 12px;
        cursor: pointer;
      }

      .logo-icon {
        font-size: 28px;
        color: var(--accent);
      }

      .logo-text {
        font-size: 1.125rem;
        font-weight: 700;
        letter-spacing: 0.05em;
        color: var(--text-primary);
      }

      .sidebar.sidebar-collapsed .logo-text {
        display: none;
      }

      .sidebar-toggle {
        background: none;
        border: none;
        color: var(--text-muted);
        cursor: pointer;
        padding: 8px;
        border-radius: var(--radius-sm);
        transition: all var(--transition-fast);
      }

      .sidebar-toggle:hover {
        background: var(--bg-elevated);
        color: var(--text-primary);
      }

      .sidebar.sidebar-collapsed .sidebar-toggle {
        margin: 0 auto;
      }

      /* Account Selector */
      .sidebar-account {
        padding: 16px;
        border-bottom: 1px solid var(--border-glass);
        position: relative;
      }

      .account-button {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 10px;
        background: var(--bg-surface);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
        text-align: left;
      }

      .account-button:hover {
        border-color: var(--border-accent);
      }

      .account-avatar {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, var(--accent), #8B5CF6);
        border-radius: var(--radius-sm);
        font-weight: 600;
        font-size: 0.875rem;
        color: white;
        flex-shrink: 0;
      }

      .account-info {
        flex: 1;
        min-width: 0;
      }

      .account-name {
        display: block;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .account-role {
        font-size: 0.75rem;
        color: var(--text-muted);
        text-transform: capitalize;
      }

      .sidebar.sidebar-collapsed .account-info,
      .sidebar.sidebar-collapsed .account-button svg {
        display: none;
      }

      .account-dropdown {
        position: absolute;
        top: 100%;
        left: 16px;
        right: 16px;
        background: var(--bg-surface);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        padding: 8px;
        z-index: 10;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
      }

      .account-dropdown-divider {
        height: 1px;
        background: var(--border-glass);
        margin: 8px 4px;
      }

      .account-option {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 10px;
        background: none;
        border: none;
        border-radius: var(--radius-sm);
        cursor: pointer;
        color: var(--text-secondary);
        font-size: 0.875rem;
        transition: all var(--transition-fast);
      }

      .account-option:hover {
        background: var(--bg-elevated);
        color: var(--text-primary);
      }

      .account-option.active {
        background: var(--accent-muted);
        color: var(--accent);
      }

      .account-avatar-small {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-elevated);
        border-radius: var(--radius-sm);
        font-size: 0.75rem;
        font-weight: 500;
      }

      .account-action {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 10px;
        background: none;
        border: none;
        border-radius: var(--radius-sm);
        cursor: pointer;
        color: var(--text-secondary);
        font-size: 0.875rem;
        transition: all var(--transition-fast);
      }

      .account-action:hover {
        background: var(--bg-elevated);
        color: var(--text-primary);
      }

      .org-manage-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .org-manage-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px;
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        background: var(--bg-surface);
      }

      .org-manage-name {
        font-weight: 600;
        color: var(--text-primary);
      }

      .org-manage-meta {
        font-size: 0.8rem;
        color: var(--text-muted);
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .org-meta-item {
        background: rgba(255, 255, 255, 0.06);
        padding: 2px 8px;
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.08);
      }

      .org-manage-current {
        font-size: 0.8rem;
        color: var(--text-muted);
      }

      /* Navigation */
      .sidebar-nav {
        flex: 1;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        overflow-y: auto;
      }

      .nav-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        background: none;
        border: none;
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        font-size: 0.9375rem;
        font-weight: 500;
        cursor: pointer;
        transition: all var(--transition-fast);
        text-decoration: none;
        width: 100%;
        text-align: left;
      }

      .nav-item:hover {
        background: var(--bg-glass-hover);
        color: var(--text-primary);
      }

      .nav-item.active {
        background: var(--accent-muted);
        color: var(--accent);
      }

      .nav-item svg {
        flex-shrink: 0;
      }

      .sidebar.sidebar-collapsed .nav-item span {
        display: none;
      }

      .sidebar.sidebar-collapsed .nav-item {
        justify-content: center;
        padding: 12px;
      }

      /* Footer */
      .sidebar-footer {
        padding: 16px;
        border-top: 1px solid var(--border-glass);
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .nav-item.logout:hover {
        background: var(--danger-bg);
        color: var(--danger);
      }

      /* ================================================================
         DASHBOARD LAYOUT
         ================================================================ */
      .dashboard-layout {
        min-height: 100vh;
        padding-left: var(--sidebar-width);
        transition: padding-left var(--transition-normal);
      }

      .dashboard-layout.sidebar-collapsed {
        padding-left: var(--sidebar-collapsed);
      }

      .dashboard-main {
        min-height: 100vh;
        background: var(--bg-base);
      }

      .dashboard-topbar {
        display: none;
        align-items: center;
        gap: 12px;
        height: var(--header-height);
        padding: 0 16px;
        background: var(--bg-glass);
        border-bottom: 1px solid var(--border-glass);
        position: sticky;
        top: 0;
        z-index: 50;
      }

      .mobile-sidebar-toggle {
        background: none;
        border: 1px solid var(--border-glass);
        color: var(--text-primary);
        width: 40px;
        height: 40px;
        border-radius: var(--radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }

      .dashboard-topbar-title {
        color: var(--text-primary);
        font-weight: 600;
        font-size: 0.95rem;
      }

      .trial-banner {
        margin: 20px 32px 0;
        padding: 12px 16px;
        display: flex;
        align-items: center;
        gap: 10px;
        border-radius: var(--radius-md);
        background: rgba(59, 130, 246, 0.12);
        border: 1px solid rgba(59, 130, 246, 0.28);
        color: var(--text-primary);
      }

      .sidebar-overlay {
        position: fixed;
        inset: 0;
        background: rgba(2, 6, 23, 0.6);
        z-index: 90;
      }

      /* ================================================================
         PAGE CONTAINER
         ================================================================ */
      .page-container {
        padding: 32px;
        max-width: 1400px;
        margin: 0 auto;
      }

      .public-main-community {
        padding-top: 96px;
      }

      .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 32px;
      }
      .page-header-actions {
        display: flex;
        gap: 12px;
        align-items: center;
        flex-wrap: wrap;
      }

      .header-video-links {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
      }

      .header-video-link {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border-radius: 999px;
        border: 1px solid var(--border-glass);
        background: var(--bg-glass);
        color: var(--text-secondary);
        font-size: 0.8125rem;
        text-decoration: none;
        transition: all var(--transition-fast);
      }

      .header-video-link:hover {
        color: var(--text-primary);
        border-color: var(--accent);
        box-shadow: 0 0 0 2px var(--accent-muted);
      }

      .page-header h1 {
        margin-bottom: 4px;
      }

      .page-subtitle {
        color: var(--text-muted);
        font-size: 0.9375rem;
      }

      /* ================================================================
         TABS
         ================================================================ */
      .tabs-container {
        display: flex;
        gap: 4px;
        background: var(--bg-glass);
        padding: 4px;
        border-radius: var(--radius-md);
        margin-bottom: 24px;
        overflow-x: auto;
      }

      .tab-button {
        padding: 10px 20px;
        background: none;
        border: none;
        border-radius: var(--radius-sm);
        color: var(--text-secondary);
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
        transition: all var(--transition-fast);
        white-space: nowrap;
      }

      .tab-button:hover {
        color: var(--text-primary);
      }

      .tab-button.active {
        background: var(--accent);
        color: white;
        box-shadow: var(--accent-glow);
      }

      /* ================================================================
         BOARDS PAGE - LAYOUT & PREVIEW
         ================================================================ */
      .boards-layout {
        display: grid;
        grid-template-columns: 280px 1fr;
        gap: 24px;
        min-height: 600px;
      }

      .boards-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .boards-list h3 {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--text-muted);
        margin-bottom: 8px;
      }

      .board-list-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .board-list-item:hover {
        border-color: var(--accent);
        background: var(--bg-glass-hover);
      }

      .board-list-item.selected {
        border-color: var(--accent);
        background: var(--accent-muted);
      }

      .board-list-info {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .board-list-name {
        font-weight: 600;
        font-size: 0.9375rem;
      }

      .board-list-meta {
        font-size: 0.75rem;
        color: var(--text-muted);
      }

      .board-detail {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .board-preview-card {
        padding: 24px;
      }

      .board-preview-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
      }

      .board-preview-header h3 {
        font-size: 1.25rem;
        font-weight: 600;
      }

      .board-preview-actions {
        display: flex;
        gap: 8px;
      }

      /* Split Flap Preview */
      .split-flap-preview {
        display: flex;
        justify-content: center;
        padding: 24px 0;
        overflow-x: auto;
      }

      .flap-board {
        padding: 16px;
        border-radius: var(--radius-lg);
        border: 1px solid rgba(255, 255, 255, 0.04);
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, 0.025),
          0 10px 40px rgba(0, 0, 0, 0.5);
        display: inline-block;
        transition: background-color var(--transition-fast);
      }

      .flap-row {
        display: flex;
        gap: 2px;
        margin-bottom: 3px;
      }

      .flap-row:last-child {
        margin-bottom: 0;
      }

      .flap-char {
        width: 22px;
        height: 32px;
        background: linear-gradient(180deg, #272724 0%, #20201e 46%, #171715 50%, #1c1c1a 54%, #232320 100%);
        border-radius: 3px;
        border: 1px solid rgba(255, 255, 255, 0.025);
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'JetBrains Mono', monospace;
        font-size: 1.125rem;
        font-weight: 600;
        color: currentColor;
        position: relative;
        overflow: hidden;
        box-shadow:
          inset 0 2px 3px rgba(255, 255, 255, 0.025),
          inset 0 -2px 4px rgba(0, 0, 0, 0.55);
        text-shadow: 0 1px 1px rgba(0, 0, 0, 0.75);
      }

      .flap-char::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: 50%;
        height: 1px;
        background: rgba(0, 0, 0, 0.8);
        box-shadow: 0 1px 0 rgba(255, 255, 255, 0.045);
      }

      .flap-char.flipping {
        animation: flapFlip 0.15s ease-out;
      }

      @keyframes flapFlip {
        0% { transform: rotateX(0deg); }
        50% { transform: rotateX(-90deg); }
        100% { transform: rotateX(0deg); }
      }

      /* Font Variants */
      .font-mechanical .flap-char {
        font-family: 'JetBrains Mono', monospace;
        color: inherit;
        text-shadow: 0 1px 1px rgba(0, 0, 0, 0.75);
      }

      .font-terminal .flap-char {
        font-family: 'Courier New', monospace;
        color: inherit;
        text-shadow: 0 1px 1px rgba(0, 0, 0, 0.75);
      }

      .font-classic .flap-char {
        font-family: 'Times New Roman', serif;
        color: inherit;
        text-shadow: none;
      }

      .font-modern .flap-char {
        font-family: 'Arial', sans-serif;
        color: inherit;
        text-shadow: 0 1px 1px rgba(0, 0, 0, 0.75);
      }

      .font-condensed .flap-char {
        font-family: 'Arial Narrow', sans-serif;
        color: inherit;
        letter-spacing: -1px;
        text-shadow: 0 1px 1px rgba(0, 0, 0, 0.75);
      }

      .board-background-picker {
        gap: 10px;
      }

      .board-background-heading {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      .pro-feature-chip {
        padding: 2px 8px;
        border: 1px solid var(--border-glass);
        border-radius: 999px;
        color: var(--text-muted);
        font-size: 0.6875rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }

      .board-background-options {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 8px;
      }

      .board-background-option {
        min-height: 58px;
        padding: 7px;
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-sm);
        background: var(--bg-elevated);
        color: var(--text-secondary);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 5px;
        font: inherit;
        font-size: 0.6875rem;
        cursor: pointer;
        transition:
          border-color var(--transition-fast),
          background var(--transition-fast),
          color var(--transition-fast);
      }

      .board-background-option:hover:not(:disabled) {
        border-color: var(--accent);
        color: var(--text-primary);
      }

      .board-background-option.selected {
        border-color: var(--accent);
        background: var(--accent-muted);
        color: var(--text-primary);
      }

      .board-background-option:disabled,
      .board-background-option.disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }

      .board-background-swatch,
      .board-background-custom input {
        width: 30px;
        height: 22px;
        padding: 0;
        border: 1px solid rgba(255, 255, 255, 0.16);
        border-radius: 5px;
        box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.35);
      }

      .board-background-custom input {
        background: transparent;
        cursor: pointer;
      }

      .board-background-custom input::-webkit-color-swatch-wrapper {
        padding: 0;
      }

      .board-background-custom input::-webkit-color-swatch {
        border: 0;
        border-radius: 4px;
      }

      .board-background-summary {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }

      .board-background-summary-swatch {
        width: 14px;
        height: 14px;
        border: 1px solid rgba(255, 255, 255, 0.16);
        border-radius: 4px;
      }

      .preview-indicators {
        display: flex;
        justify-content: center;
        gap: 8px;
        margin-top: 16px;
      }

      .preview-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--bg-elevated);
        border: none;
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .preview-dot.active {
        background: var(--accent);
        transform: scale(1.25);
      }

      .board-info-bar {
        display: flex;
        justify-content: center;
        gap: 24px;
        padding-top: 16px;
        border-top: 1px solid var(--border-glass);
        margin-top: 16px;
        color: var(--text-muted);
        font-size: 0.8125rem;
      }

      .board-info-bar span {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      /* Messages Card */
      .board-messages-card {
        padding: 24px;
      }

      .messages-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
      }

      .messages-header h3 {
        font-size: 1.125rem;
        font-weight: 600;
      }

      .empty-messages {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 20px;
        text-align: center;
        color: var(--text-muted);
      }

      .empty-messages svg {
        width: 48px;
        height: 48px;
        margin-bottom: 12px;
        opacity: 0.5;
      }

      .empty-messages p {
        font-size: 1rem;
        color: var(--text-secondary);
        margin-bottom: 4px;
      }

      .empty-messages span {
        font-size: 0.875rem;
      }

      .messages-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .message-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        background: var(--bg-surface);
        border-radius: var(--radius-md);
        transition: background var(--transition-fast);
      }

      .message-item:hover {
        background: var(--bg-elevated);
      }

      .message-order {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
      }

      .message-order span {
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--text-muted);
        width: 20px;
        text-align: center;
      }

      .order-btn {
        background: none;
        border: none;
        color: var(--text-muted);
        cursor: pointer;
        padding: 2px;
        font-size: 0.75rem;
        line-height: 1;
        transition: color var(--transition-fast);
      }

      .order-btn:hover:not(:disabled) {
        color: var(--accent);
      }

      .order-btn:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }

      .message-content {
        flex: 1;
        min-width: 0;
      }

      .message-text {
        font-size: 0.9375rem;
        color: var(--text-primary);
        word-break: break-word;
      }

      .message-duration {
        font-size: 0.75rem;
        color: var(--text-muted);
      }

      .message-status-row {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 6px;
        flex-wrap: wrap;
      }

      .message-schedule {
        font-size: 0.75rem;
        color: var(--text-muted);
      }

      .message-actions {
        display: flex;
        gap: 4px;
      }

      .message-preview {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid var(--border-glass);
      }

      .message-preview .split-flap-preview {
        padding: 16px 0;
      }

      .message-preview .flap-board {
        transform: scale(0.85);
        transform-origin: center;
      }

      .message-bank-bar {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 12px;
        padding: 12px;
        background: var(--bg-elevated);
        border-radius: var(--radius-md);
        border: 1px solid var(--border-glass);
      }

      .message-bank-save {
        display: flex;
        align-items: flex-end;
        gap: 10px;
      }

      .message-bank-save .input-group {
        min-width: 220px;
      }

      .message-status-fields {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .message-schedule-fields {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 12px;
      }

      .template-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .template-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      .template-info {
        min-width: 0;
        flex: 1;
      }

      .template-name {
        font-weight: 600;
        margin-bottom: 4px;
      }

      .template-preview {
        font-size: 0.875rem;
        color: var(--text-muted);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .template-actions {
        display: flex;
        gap: 8px;
      }

      .row-style-grid {
        display: grid;
        gap: 12px;
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid var(--border-glass);
      }

      .row-style-item {
        display: grid;
        grid-template-columns: 80px 1fr 56px;
        align-items: center;
        gap: 12px;
      }

      .row-style-label {
        font-size: 0.8125rem;
        color: var(--text-secondary);
      }

      .color-field {
        width: 56px;
        height: 38px;
        padding: 0;
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-sm);
        background: var(--bg-surface);
        cursor: pointer;
      }

      /* Toggle Switch */
      .toggle-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 0;
      }

      .toggle-label {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .toggle-label span:first-child {
        font-weight: 500;
        color: var(--text-primary);
      }

      .toggle-hint {
        font-size: 0.8125rem;
        color: var(--text-muted);
      }

      .toggle-switch {
        width: 48px;
        height: 26px;
        background: var(--bg-elevated);
        border: 2px solid var(--border-glass);
        border-radius: 13px;
        position: relative;
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .toggle-switch.on {
        background: var(--accent);
        border-color: var(--accent);
      }

      .toggle-knob {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 18px;
        height: 18px;
        background: white;
        border-radius: 50%;
        transition: transform var(--transition-fast);
      }

      .toggle-switch.on .toggle-knob {
        transform: translateX(22px);
      }

      /* Settings Warning */
      .settings-warning {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 16px;
        background: var(--warning-bg);
        border: 1px solid rgba(245, 158, 11, 0.3);
        border-radius: var(--radius-md);
        color: var(--warning);
        font-size: 0.875rem;
        margin: 8px 0;
      }

      .settings-warning svg {
        flex-shrink: 0;
        margin-top: 2px;
      }

      .loading-container.small {
        padding: 40px;
      }

      .games-filter-bar {
        display: flex;
        align-items: center;
        gap: 16px;
        margin: 12px 0 4px;
      }

      .games-filter-bar .input-group {
        min-width: 240px;
      }

      /* ================================================================
         GRIDS
         ================================================================ */
      .boards-grid,
      .games-grid,
      .devices-grid,
      .queue-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
        gap: 20px;
      }

      /* ================================================================
         CARDS (Generic)
         ================================================================ */
      .board-card,
      .game-card,
      .device-card,
      .queue-card {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .board-card.selected,
      .game-card.selected {
        border-color: var(--accent);
        box-shadow: 0 0 0 2px var(--accent-muted);
      }

      .board-card-header,
      .game-card-header,
      .device-header,
      .queue-card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
      }

      .board-card-header h3,
      .game-card-header h3,
      .device-info h3,
      .queue-card-header h3 {
        font-size: 1.0625rem;
        font-weight: 600;
      }

      .game-badges {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
      }

      .board-card-meta,
      .game-meta,
      .device-meta,
      .queue-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        color: var(--text-muted);
        font-size: 0.875rem;
      }

      .board-stat,
      .game-meta span,
      .device-board,
      .device-seen {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .game-description {
        color: var(--text-secondary);
        font-size: 0.875rem;
        line-height: 1.5;
      }

      .game-schedule {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--text-secondary);
        font-size: 0.875rem;
      }

      .game-schedule svg {
        color: var(--accent);
      }

      .schedule-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .schedule-item {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .schedule-header {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        align-items: flex-start;
      }

      .schedule-header h3 {
        font-size: 1.0625rem;
        font-weight: 600;
        margin-bottom: 6px;
      }

      .schedule-badges {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
      }

      .schedule-meta {
        display: flex;
        flex-direction: column;
        gap: 6px;
        color: var(--text-muted);
        font-size: 0.875rem;
        align-items: flex-end;
      }

      .schedule-meta span {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .schedule-details {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        color: var(--text-secondary);
        font-size: 0.875rem;
      }

      .schedule-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .admin-list {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .admin-kpi-grid {
        margin-bottom: 20px;
      }

      .admin-metrics-loading {
        min-height: 96px;
        margin-bottom: 20px;
      }

      .admin-section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
      }

      .admin-section-header h2 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        color: var(--text-primary);
      }

      .admin-item {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .admin-header {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        align-items: flex-start;
      }

      .admin-header h3 {
        font-size: 1.0625rem;
        font-weight: 600;
        margin-bottom: 6px;
      }

      .admin-badges {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }

      .admin-meta {
        display: flex;
        flex-direction: column;
        gap: 6px;
        color: var(--text-muted);
        font-size: 0.875rem;
        align-items: flex-end;
      }

      .admin-description {
        color: var(--text-secondary);
        font-size: 0.875rem;
        line-height: 1.5;
      }

      .admin-notes {
        padding: 10px 12px;
        border-radius: var(--radius-md);
        background: rgba(245, 158, 11, 0.12);
        border: 1px solid rgba(245, 158, 11, 0.3);
        color: var(--text-secondary);
        font-size: 0.875rem;
      }

      .admin-actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }

      .marketing-campaign-card {
        margin-bottom: 24px;
      }

      .marketing-campaign-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
        gap: 10px;
      }

      .marketing-campaign-stat {
        display: flex;
        flex-direction: column;
        gap: 5px;
        padding: 12px;
        border-radius: var(--radius-md);
        background: var(--bg-surface);
        border: 1px solid var(--border-glass);
      }

      .marketing-campaign-stat span {
        color: var(--text-muted);
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }

      .marketing-campaign-stat strong {
        color: var(--text-primary);
        font-family: 'JetBrains Mono', monospace;
        font-size: 1.25rem;
      }

      .marketing-campaign-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 8px 16px;
        color: var(--text-muted);
        font-size: 0.8125rem;
      }

      .marketing-campaign-meta a {
        color: var(--accent);
      }

      .marketing-campaign-controls {
        display: grid;
        grid-template-columns: minmax(220px, 360px) auto;
        gap: 12px;
        align-items: end;
      }

      .marketing-campaign-controls .input-group {
        margin-bottom: 0;
      }

      .marketing-campaign-confirmation {
        max-width: 560px;
        margin-bottom: 0;
      }

      @media (max-width: 640px) {
        .marketing-campaign-controls {
          grid-template-columns: 1fr;
          align-items: stretch;
        }
      }

      .board-card-actions,
      .game-card-actions,
      .device-actions,
      .queue-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: auto;
      }

      .queue-card-active {
        border-color: rgba(16, 185, 129, 0.5);
        box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.18);
      }

      .queue-runtime {
        color: var(--text-secondary);
        font-size: 0.875rem;
      }

      .queue-detail {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .queue-detail-header {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        align-items: flex-start;
      }

      .queue-detail-meta {
        color: var(--text-muted);
        font-size: 0.875rem;
        margin-top: 4px;
      }

      .queue-detail-actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }

      .queue-duration-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 12px;
      }

      .queue-duration-grid .input-label {
        min-height: 36px;
        display: flex;
        align-items: flex-end;
      }

      .queue-items-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
      }

      .queue-add {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        align-items: flex-end;
      }

      .queue-add .input-group {
        margin-bottom: 0;
      }

      .queue-items-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .queue-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        padding: 12px 14px;
        border-radius: var(--radius-md);
        border: 1px solid var(--border);
        background: var(--bg-elevated);
      }

      .queue-item-active {
        border-color: rgba(16, 185, 129, 0.45);
        box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.2);
      }

      .queue-item-info {
        display: flex;
        gap: 12px;
        align-items: flex-start;
      }

      .queue-item-position {
        min-width: 26px;
        font-weight: 600;
        color: var(--text-muted);
      }

      .queue-item-meta {
        color: var(--text-muted);
        font-size: 0.8125rem;
        margin-top: 4px;
      }

      .queue-item-actions {
        display: flex;
        gap: 6px;
        align-items: center;
      }

      /* Device Card */
      .device-header {
        align-items: center;
      }

      .device-icon {
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-elevated);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
      }

      .device-icon svg {
        width: 24px;
        height: 24px;
      }

      .device-info {
        flex: 1;
      }

      .device-type {
        font-size: 0.8125rem;
        color: var(--text-muted);
      }

      /* ================================================================
         QUESTIONS LIST
         ================================================================ */
      .questions-list,
      .history-list,
      .activity-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .question-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
      }

      .question-content {
        flex: 1;
        min-width: 0;
      }

      .question-prompt {
        font-weight: 500;
        color: var(--text-primary);
        margin-bottom: 4px;
      }

      .question-meta {
        font-size: 0.8125rem;
        color: var(--text-muted);
        display: flex;
        gap: 12px;
      }

      .question-actions {
        flex-shrink: 0;
      }

      /* History Item */
      .history-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 24px;
        flex-wrap: wrap;
      }

      .history-info h4 {
        font-size: 1rem;
        margin-bottom: 4px;
      }

      .history-meta {
        display: flex;
        gap: 16px;
        font-size: 0.8125rem;
        color: var(--text-muted);
        flex-wrap: wrap;
      }

      .history-meta span {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .history-actions {
        margin-left: auto;
      }

      .history-rankings {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
      }

      .rank {
        font-size: 0.875rem;
        color: var(--text-secondary);
      }

      .history-detail {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .history-detail-header h3 {
        margin-bottom: 6px;
      }

      .history-detail-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        font-size: 0.8125rem;
        color: var(--text-muted);
      }

      .history-detail-meta span {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .history-summary {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .history-chip {
        padding: 4px 10px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.06);
        border: 1px solid rgba(255, 255, 255, 0.12);
        font-size: 0.8125rem;
        color: var(--text-secondary);
      }

      .history-leaderboard {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .history-leaderboard-header,
      .history-leaderboard-row {
        display: grid;
        grid-template-columns: 60px 1.4fr 80px 110px 90px 110px 90px;
        gap: 12px;
        align-items: center;
      }

      .history-leaderboard-header {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--text-muted);
      }

      .history-leaderboard-row {
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: var(--radius-md);
        padding: 8px 12px;
        font-size: 0.875rem;
        color: var(--text-secondary);
      }

      .history-player {
        font-weight: 600;
        color: var(--text-primary);
      }

      .history-rank {
        font-weight: 600;
        color: var(--accent);
      }

      /* Activity Item */
      .activity-item {
        display: flex;
        gap: 16px;
        align-items: center;
      }

      .activity-icon {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-elevated);
        border-radius: var(--radius-md);
        color: var(--text-muted);
        flex-shrink: 0;
      }

      .activity-content {
        flex: 1;
      }

      .activity-action {
        font-weight: 500;
        color: var(--text-primary);
        margin-bottom: 4px;
      }

      .activity-entity {
        font-size: 0.875rem;
        color: var(--text-secondary);
        margin-bottom: 6px;
      }

      .activity-meta {
        font-size: 0.8125rem;
        color: var(--text-muted);
        display: flex;
        gap: 12px;
      }

      .load-more {
        align-self: center;
        margin-top: 16px;
      }

      /* ================================================================
         COMMUNITY WARNING
         ================================================================ */
      .community-warning {
        display: flex;
        align-items: center;
        gap: 12px;
        background: var(--warning-bg);
        border: 1px solid rgba(245, 158, 11, 0.3);
        color: var(--warning);
        margin-bottom: 24px;
      }

      .community-card {
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
      }

      .community-card:hover {
        transform: translateY(-2px);
        border-color: rgba(255, 255, 255, 0.28);
        box-shadow: 0 18px 32px rgba(4, 12, 24, 0.24);
      }

      .community-preview-shimmer {
        display: flex;
        flex-direction: column;
        gap: 14px;
        padding: 8px 0;
      }

      .shimmer-line,
      .shimmer-card {
        position: relative;
        overflow: hidden;
        border-radius: var(--radius-md);
        background: rgba(255, 255, 255, 0.06);
      }

      .shimmer-line::after,
      .shimmer-card::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(
          110deg,
          rgba(255, 255, 255, 0.04) 0%,
          rgba(255, 255, 255, 0.18) 45%,
          rgba(255, 255, 255, 0.04) 90%
        );
        transform: translateX(-100%);
        animation: shimmer-slide 1.2s ease-in-out infinite;
      }

      .shimmer-title {
        height: 28px;
        width: 70%;
      }

      .shimmer-meta {
        height: 18px;
        width: 45%;
      }

      .shimmer-card {
        height: 88px;
      }

      .community-preview {
        display: flex;
        flex-direction: column;
        gap: 18px;
      }

      .community-preview-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
      }

      .community-preview-header h3 {
        font-size: 1.25rem;
        margin-bottom: 6px;
      }

      .community-preview-meta {
        display: flex;
        flex-direction: column;
        gap: 6px;
        font-size: 0.875rem;
        color: var(--text-muted);
        align-items: flex-end;
      }

      .community-preview-meta span {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .community-preview-description {
        color: var(--text-secondary);
        font-size: 0.9375rem;
        line-height: 1.6;
      }

      .community-preview-questions {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .community-question {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .community-question-header {
        display: flex;
        gap: 10px;
        align-items: center;
      }

      .question-number {
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--accent);
      }

      .question-preview {
        font-weight: 600;
        color: var(--text-primary);
      }

      .community-preview .question-preview {
        white-space: normal;
        overflow: visible;
        text-overflow: unset;
        word-break: break-word;
      }

      .community-answers {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 8px;
      }

      .community-answer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 8px 12px;
        border-radius: var(--radius-md);
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.08);
        color: var(--text-secondary);
        font-size: 0.875rem;
      }

      .community-answer.correct {
        border-color: rgba(34, 197, 94, 0.35);
        color: var(--text-primary);
      }

      .community-answer .correct-icon {
        color: var(--success);
      }

      .community-explanation {
        font-size: 0.875rem;
        color: var(--text-muted);
        border-left: 2px solid rgba(255, 255, 255, 0.16);
        padding-left: 12px;
      }

      .community-preview-pagination {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        color: var(--text-muted);
        font-size: 0.875rem;
      }

      .community-preview .modal-actions {
        margin-top: 4px;
      }

      @keyframes shimmer-slide {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }

      /* ================================================================
         SETTINGS PAGE
         ================================================================ */
      .settings-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
        gap: 24px;
      }

      .settings-section {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .settings-section h2 {
        font-size: 1.125rem;
        padding-bottom: 12px;
        border-bottom: 1px solid var(--border-glass);
      }

      .settings-section.full-width {
        grid-column: 1 / -1;
      }

      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .section-header h2 {
        border-bottom: none;
        padding-bottom: 0;
      }

      .setting-item {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .setting-item label {
        font-size: 0.875rem;
        color: var(--text-secondary);
      }

      .setting-value {
        font-family: 'JetBrains Mono', monospace;
        color: var(--text-primary);
      }

      .subscription-status {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .plan-info {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .plan-name {
        font-size: 1.125rem;
        font-weight: 600;
      }

      .plan-price {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--accent);
        font-family: 'JetBrains Mono', monospace;
      }

      .plan-features {
        display: flex;
        flex-direction: column;
        gap: 8px;
        color: var(--text-secondary);
        font-size: 0.9375rem;
      }

      .plan-features p {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--text-secondary);
      }

      .plan-features svg {
        color: var(--success);
      }

      .plan-features .is-missing svg {
        color: var(--danger);
      }

      .upgrade-btn {
        margin-top: 8px;
      }

      .members-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .member-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px;
        background: var(--bg-surface);
        border-radius: var(--radius-md);
      }

      .member-info {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .member-avatar {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-elevated);
        border-radius: 50%;
        font-weight: 500;
        font-size: 0.875rem;
      }

      .member-email {
        font-weight: 500;
        color: var(--text-primary);
        margin-bottom: 2px;
      }

      .danger-text {
        color: var(--danger);
      }

      .section-header-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
      }

      .section-header-row h2 {
        margin: 0;
        padding-bottom: 0;
        border-bottom: none;
      }

      .setting-input-row {
        display: flex;
        gap: 12px;
        align-items: flex-end;
      }

      .setting-input-row .input-group {
        flex: 1;
      }

      .empty-members {
        color: var(--text-muted);
        text-align: center;
        padding: 24px;
      }

      .manage-btn {
        margin-top: 8px;
      }

      .trial-plan-note {
        color: var(--text-secondary);
        font-size: 0.875rem;
        line-height: 1.5;
      }

      .feedback-section {
        gap: 16px;
      }

      .feedback-section-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
      }

      .feedback-section-header h2 {
        margin-bottom: 8px;
      }

      .feedback-section-copy {
        color: var(--text-secondary);
        max-width: 700px;
      }

      .feedback-section-icon {
        width: 40px;
        height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        color: var(--accent);
        background: var(--accent-muted);
        flex-shrink: 0;
      }

      .feedback-textarea {
        min-height: 100px;
      }

      .feedback-section-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      .feedback-help {
        margin: 0;
      }

      /* Role Options */
      .role-options {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .role-option {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 16px;
        background: var(--bg-surface);
        border: 2px solid var(--border-glass);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
        text-align: left;
      }

      .role-option:hover {
        border-color: var(--accent);
      }

      .role-option.selected {
        border-color: var(--accent);
        background: var(--accent-muted);
      }

      .role-option strong {
        color: var(--text-primary);
        margin-bottom: 4px;
      }

      .role-option span {
        color: var(--text-muted);
        font-size: 0.875rem;
      }

      /* Form Rows */
      .form-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
      }

      .modal-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      /* Game Editor */
      .game-editor {
        display: flex;
        flex-direction: column;
        gap: 24px;
        max-height: 70vh;
        overflow-y: auto;
      }

      .game-editor-header {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .ai-generate-card {
        display: flex;
        flex-direction: column;
        gap: 16px;
        border: 1px solid rgba(255, 255, 255, 0.16);
      }

      .ai-generate-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      .ai-generate-header h4 {
        margin: 0 0 4px 0;
        font-size: 1rem;
      }

      .ai-generate-header p {
        margin: 0;
        font-size: 0.875rem;
        color: var(--text-muted);
      }

      .ai-generate-controls {
        display: grid;
        grid-template-columns: 1.2fr 0.8fr;
        gap: 16px;
      }

      .ai-generate-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      .ai-generate-note {
        font-size: 0.8125rem;
        color: var(--text-muted);
      }

      .questions-section {
        border-top: 1px solid var(--border-glass);
        padding-top: 24px;
      }

      .questions-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
      }

      .question-header-actions {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .questions-header h3 {
        margin: 0;
      }

      .empty-questions {
        text-align: center;
        padding: 40px;
        color: var(--text-muted);
      }

      .empty-questions p {
        margin-bottom: 16px;
      }

      .questions-list-editor {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .question-editor-item {
        background: var(--bg-surface);
        border-radius: var(--radius-md);
        border: 1px solid var(--border-glass);
        overflow: hidden;
      }

      .question-editor-item.active {
        border-color: var(--accent);
      }

      .question-summary {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        cursor: pointer;
        transition: background var(--transition-fast);
      }

      .question-summary:hover {
        background: var(--bg-elevated);
      }

      .question-number {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--accent-muted);
        color: var(--accent);
        border-radius: 50%;
        font-size: 0.875rem;
        font-weight: 600;
        flex-shrink: 0;
      }

      .question-preview {
        flex: 1;
        color: var(--text-primary);
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .question-item-actions {
        display: flex;
        gap: 4px;
      }

      .question-editor-details {
        padding: 16px;
        background: var(--bg-base);
        border-top: 1px solid var(--border-glass);
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .answers-editor {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .answer-editor-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        background: var(--bg-surface);
        border: 2px solid var(--border-glass);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .answer-editor-item:hover {
        border-color: var(--accent);
      }

      .answer-editor-item.correct {
        border-color: var(--success);
        background: var(--success-bg);
      }

      .answer-letter {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-elevated);
        border-radius: 50%;
        font-size: 0.875rem;
        font-weight: 600;
        flex-shrink: 0;
      }

      .answer-editor-item.correct .answer-letter {
        background: var(--success);
        color: white;
      }

      .answer-editor-item input {
        flex: 1;
        background: transparent;
        border: none;
        outline: none;
        color: var(--text-primary);
        font-size: 0.9375rem;
      }

      .answer-editor-item input::placeholder {
        color: var(--text-muted);
      }

      .correct-icon {
        color: var(--success);
      }

      .question-settings {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
      }

      .question-bank-actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 12px;
      }

      /* Go Live Modal */
      .go-live-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        padding: 40px;
      }

      .go-live-modal {
        display: flex;
        flex-direction: column;
        gap: 16px;
        position: relative;
      }

      .go-live-game-info {
        text-align: center;
        padding: 12px;
        background: var(--bg-surface);
        border-radius: var(--radius-md);
      }

      .go-live-game-info h3 {
        margin-bottom: 4px;
      }

      .go-live-game-info p {
        color: var(--text-muted);
        font-size: 0.875rem;
      }

      .no-devices-warning {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        text-align: center;
        color: var(--text-muted);
      }

      .no-devices-warning svg {
        width: 48px;
        height: 48px;
        color: var(--warning);
      }

      .pill-toggle {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        border-radius: 999px;
        border: 1px solid var(--border-glass);
        background: var(--bg-surface);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .pill-toggle.active {
        color: var(--text-primary);
        border-color: var(--border-accent);
        box-shadow: 0 0 12px rgba(59, 130, 246, 0.25);
      }

      .pill-toggle:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .go-live-web-info {
        display: flex;
        align-items: center;
        gap: 12px;
        color: var(--text-secondary);
      }

      .go-live-modal .input-group {
        margin-bottom: 12px;
      }

      .go-live-modal .toggle-row {
        padding: 8px 0;
      }

      .go-live-modal .input-help {
        margin-top: 2px;
      }

      .go-live-modal .modal-actions {
        position: sticky;
        bottom: 0;
        background: var(--bg-surface);
        border-top: 1px solid var(--border-glass);
        padding-top: 16px;
        margin-top: 8px;
        z-index: 2;
      }

      .go-live-web-info svg {
        width: 22px;
        height: 22px;
        color: var(--accent);
      }

      .go-live-join-card {
        display: flex;
        flex-direction: column;
        gap: 12px;
        text-align: center;
      }

      .go-live-join-card .modal-actions {
        position: static;
        background: transparent;
        border-top: none;
        padding-top: 0;
        margin-top: 12px;
        width: 100%;
        justify-content: center;
        align-items: center;
      }

      .go-live-join-label {
        font-size: 0.875rem;
        color: var(--text-muted);
        text-transform: uppercase;
        letter-spacing: 0.08em;
      }

      .go-live-join-code {
        font-size: 2rem;
        font-weight: 700;
        letter-spacing: 0.2em;
        color: var(--accent);
      }

      .go-live-join-hint {
        color: var(--text-secondary);
        font-size: 0.875rem;
      }

      .go-live-scheduled {
        color: var(--text-secondary);
        font-size: 0.875rem;
      }

      .select-field {
        width: 100%;
        padding: 12px 16px;
        font-family: inherit;
        font-size: 0.9375rem;
        color: var(--text-primary);
        background: var(--bg-surface);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        outline: none;
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .select-field:focus {
        border-color: var(--accent);
        box-shadow: 0 0 0 3px var(--accent-muted);
      }

      .input-help {
        font-size: 0.8125rem;
        color: var(--text-muted);
        margin-top: 4px;
      }

      /* ================================================================
         UPGRADE PROMPT
         ================================================================ */
      .upgrade-prompt {
        display: flex;
        justify-content: center;
        padding: 60px 40px;
      }

      .upgrade-content {
        text-align: center;
        max-width: 400px;
      }

      .upgrade-icon {
        width: 80px;
        height: 80px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--accent-muted);
        border-radius: var(--radius-lg);
        margin: 0 auto 24px;
        color: var(--accent);
      }

      .upgrade-icon svg {
        width: 40px;
        height: 40px;
      }

      .upgrade-content h2 {
        margin-bottom: 12px;
      }

      .upgrade-content p {
        margin-bottom: 24px;
        line-height: 1.6;
      }

      .plan-limit-banner {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 18px;
        border-color: color-mix(in srgb, var(--accent) 45%, var(--border-glass));
      }

      .plan-limit-banner > svg {
        flex: 0 0 auto;
        color: var(--accent);
      }

      .plan-limit-copy {
        display: flex;
        flex: 1 1 auto;
        min-width: 0;
        flex-direction: column;
        gap: 3px;
      }

      .plan-limit-copy span {
        color: var(--text-secondary);
        line-height: 1.45;
      }

      @media (max-width: 640px) {
        .plan-limit-banner {
          align-items: flex-start;
          flex-wrap: wrap;
        }

        .plan-limit-banner .btn {
          width: 100%;
        }
      }

      /* ================================================================
         AUTH SCREENS
         ================================================================ */
      .auth-screen {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        background: linear-gradient(135deg, var(--bg-base) 0%, #1a1f3c 100%);
      }

      .auth-container {
        width: 100%;
        max-width: 420px;
        text-align: center;
      }

      .auth-logo {
        font-size: 48px;
        color: var(--accent);
        margin-bottom: 24px;
      }

      .auth-container h1 {
        margin-bottom: 32px;
      }

      .auth-form {
        text-align: left;
      }

      .auth-instruction {
        color: var(--text-secondary);
        margin-bottom: 20px;
        text-align: center;
      }

      .text-link {
        background: none;
        border: none;
        color: var(--text-muted);
        cursor: pointer;
        font-size: 0.875rem;
        margin-top: 16px;
        transition: color var(--transition-fast);
      }

      .text-link:hover {
        color: var(--accent);
      }

      /* ================================================================
         ONBOARDING SCREEN
         ================================================================ */
      .onboarding-screen {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        background: linear-gradient(135deg, var(--bg-base) 0%, #1a1f3c 100%);
      }

      .onboarding-content {
        width: 100%;
        max-width: 480px;
        text-align: center;
      }

      .onboarding-logo {
        font-size: 64px;
        color: var(--accent);
        margin-bottom: 24px;
      }

      .onboarding-content h1 {
        margin-bottom: 12px;
      }

      .onboarding-content > p {
        margin-bottom: 32px;
        line-height: 1.6;
      }

      .onboarding-form {
        text-align: left;
        margin-bottom: 24px;
      }

      /* ================================================================
         APP LOADING
         ================================================================ */
      .app-loading {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--bg-base);
      }

      /* ================================================================
         PUBLIC LAYOUT
         ================================================================ */
      .public-layout {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }

      .public-main {
        flex: 1;
      }

      /* ================================================================
         PUBLIC NAVIGATION
         ================================================================ */
      .public-nav {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 100;
        background: rgba(15, 23, 42, 0.8);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border-bottom: 1px solid var(--border-glass);
      }

      .public-nav .nav-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 16px 24px;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .public-nav .nav-brand {
        display: flex;
        align-items: center;
        gap: 12px;
        cursor: pointer;
      }

      .public-nav .logo-icon {
        font-size: 28px;
        color: var(--accent);
      }

      .public-nav .logo-text {
        font-size: 1.25rem;
        font-weight: 700;
        letter-spacing: 0.05em;
        color: var(--text-primary);
      }

      .public-nav .nav-links {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .public-nav .nav-links a {
        padding: 8px 16px;
        color: var(--text-secondary);
        text-decoration: none;
        font-size: 0.9375rem;
        font-weight: 500;
        transition: color var(--transition-fast);
        border-radius: var(--radius-sm);
      }

      .public-nav .nav-links a:hover {
        color: var(--text-primary);
      }

      .public-nav .nav-btn-secondary {
        padding: 8px 16px;
        background: transparent;
        border: 1px solid var(--border-glass);
        color: var(--text-primary);
        font-size: 0.9375rem;
        font-weight: 500;
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .public-nav .nav-btn-secondary:hover {
        border-color: var(--accent);
        color: var(--accent);
      }

      .public-nav .nav-btn-primary {
        padding: 8px 20px;
        background: var(--accent);
        border: none;
        color: white;
        font-size: 0.9375rem;
        font-weight: 500;
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
        box-shadow: var(--accent-glow);
      }

      .public-nav .nav-btn-primary:hover {
        background: var(--accent-hover);
      }

      .public-nav .mobile-menu-btn {
        display: none;
        background: none;
        border: none;
        color: var(--text-primary);
        font-size: 24px;
        cursor: pointer;
      }

      /* ================================================================
         PUBLIC FOOTER
         ================================================================ */
      .public-footer {
        background: var(--bg-surface);
        border-top: 1px solid var(--border-glass);
        padding: 60px 24px 24px;
      }

      .footer-container {
        max-width: 1200px;
        margin: 0 auto;
        display: grid;
        grid-template-columns: 2fr 3fr;
        gap: 60px;
      }

      .footer-brand .footer-logo {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
      }

      .footer-brand .logo-icon {
        font-size: 28px;
        color: var(--accent);
      }

      .footer-brand .logo-text {
        font-size: 1.125rem;
        font-weight: 700;
        letter-spacing: 0.05em;
        color: var(--text-primary);
      }

      .footer-brand p {
        color: var(--text-muted);
        font-size: 0.9375rem;
        line-height: 1.6;
      }

      .footer-links {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 32px;
      }

      .footer-column h4 {
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 16px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .footer-column a {
        display: block;
        color: var(--text-muted);
        text-decoration: none;
        font-size: 0.9375rem;
        margin-bottom: 10px;
        cursor: pointer;
        transition: color var(--transition-fast);
      }

      .footer-column a:hover {
        color: var(--accent);
      }

      .footer-bottom {
        max-width: 1200px;
        margin: 40px auto 0;
        padding-top: 24px;
        border-top: 1px solid var(--border-glass);
        text-align: center;
      }

      .footer-bottom p {
        color: var(--text-muted);
        font-size: 0.875rem;
      }

      /* ================================================================
         HOME PAGE
         ================================================================ */
      .home-page {
        padding-top: 80px;
      }

      /* Hero */
      .hero {
        max-width: 1200px;
        margin: 0 auto;
        padding: 80px 24px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 60px;
        align-items: center;
      }

      .hero-badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        background: var(--accent-muted);
        border: 1px solid var(--border-accent);
        border-radius: 100px;
        color: var(--accent);
        font-size: 0.875rem;
        font-weight: 500;
        margin-bottom: 24px;
      }

      .hero-badge svg {
        width: 16px;
        height: 16px;
      }

      .hero-content h1 {
        font-size: 3.5rem;
        font-weight: 700;
        line-height: 1.1;
        margin-bottom: 24px;
        background: linear-gradient(135deg, var(--text-primary), var(--accent));
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .hero-content {
        min-width: 0;
      }

      .hero-content p {
        font-size: 1.25rem;
        color: var(--text-secondary);
        line-height: 1.7;
        margin-bottom: 32px;
      }

      .hero-actions {
        display: flex;
        gap: 16px;
        margin-bottom: 24px;
      }

      .hero-note {
        font-size: 0.875rem;
        color: var(--text-muted);
      }

      .hero-visual {
        min-width: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 22px;
      }

      .hero-demo {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .hero-mobile-app {
        max-width: 430px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        text-align: center;
      }

      .hero-mobile-app-label {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        color: var(--text-primary);
        font-size: 0.8125rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }

      .hero-mobile-app-label span {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--success);
        box-shadow: 0 0 14px rgba(34, 197, 94, 0.7);
      }

      .hero-store-badges {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 10px;
      }

      .hero-mobile-app p {
        margin: 0;
        color: var(--text-muted);
        font-size: 0.9rem;
        line-height: 1.5;
      }

      /* Product demo video */
      .product-demo-section {
        position: relative;
        padding: 20px 24px 96px;
      }

      .product-demo-section-inner {
        max-width: 1040px;
        margin: 0 auto;
      }

      .product-demo-heading {
        max-width: 720px;
        margin: 0 auto 30px;
        text-align: center;
      }

      .product-demo-eyebrow {
        display: inline-block;
        margin-bottom: 12px;
        color: var(--accent-hover);
        font-size: 0.75rem;
        font-weight: 800;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }

      .product-demo-heading h2 {
        margin-bottom: 14px;
        font-size: clamp(2rem, 4vw, 3rem);
        line-height: 1.1;
        letter-spacing: -0.025em;
      }

      .product-demo-heading p {
        margin: 0;
        color: var(--text-secondary);
        font-size: 1.0625rem;
        line-height: 1.7;
      }

      .product-demo-video {
        width: 100%;
        max-width: 980px;
        margin: 0 auto;
      }

      .product-demo-frame {
        position: relative;
        overflow: hidden;
        aspect-ratio: 16 / 9;
        border: 1px solid rgba(96, 165, 250, 0.3);
        border-radius: 22px;
        background: #050b14;
        box-shadow:
          0 32px 80px rgba(2, 8, 23, 0.48),
          inset 0 1px 0 rgba(255, 255, 255, 0.06);
      }

      .product-demo-frame iframe,
      .product-demo-poster,
      .product-demo-poster img {
        display: block;
        width: 100%;
        height: 100%;
      }

      .product-demo-frame iframe {
        border: 0;
      }

      .product-demo-blocked {
        display: grid;
        width: 100%;
        height: 100%;
        place-items: center;
        padding: 30px;
        text-align: center;
        background:
          radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.22), transparent 44%),
          #050b14;
      }

      .product-demo-blocked strong {
        display: block;
        margin-bottom: 8px;
        color: #f8fafc;
        font-size: clamp(1.2rem, 3vw, 1.75rem);
      }

      .product-demo-blocked p {
        margin: 0 0 20px;
        color: #94a3b8;
      }

      .product-demo-blocked a {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 9px;
        padding: 12px 18px;
        border-radius: 10px;
        background: var(--accent);
        color: #fff;
        font-weight: 700;
        text-decoration: none;
      }

      .product-demo-external-link {
        display: block;
        width: fit-content;
        margin: 14px auto 0;
        color: var(--text-muted);
        font-size: 0.875rem;
        text-decoration: underline;
        text-underline-offset: 3px;
        transition: color 0.2s ease;
      }

      .product-demo-external-link:hover {
        color: var(--accent-hover);
      }

      .product-demo-poster {
        position: relative;
        padding: 0;
        overflow: hidden;
        border: 0;
        background: #050b14;
        color: #f8fafc;
        cursor: pointer;
        font: inherit;
        text-align: left;
      }

      .product-demo-poster::after {
        content: '';
        position: absolute;
        inset: 0;
        background:
          linear-gradient(90deg, rgba(2, 8, 23, 0.82) 0%, rgba(2, 8, 23, 0.28) 52%, transparent 78%),
          linear-gradient(0deg, rgba(2, 8, 23, 0.42), transparent 55%);
        transition: background 0.25s ease;
      }

      .product-demo-poster img {
        object-fit: cover;
        transition: transform 0.45s ease, filter 0.3s ease;
      }

      .product-demo-poster:hover img {
        transform: scale(1.025);
        filter: brightness(1.08);
      }

      .product-demo-poster:focus-visible {
        outline: 4px solid rgba(96, 165, 250, 0.92);
        outline-offset: -4px;
      }

      .product-demo-overlay {
        position: absolute;
        z-index: 1;
        left: clamp(22px, 5vw, 54px);
        bottom: clamp(22px, 5vw, 50px);
        display: flex;
        align-items: center;
        gap: 18px;
      }

      .product-demo-play-icon {
        display: grid;
        width: clamp(58px, 8vw, 78px);
        height: clamp(58px, 8vw, 78px);
        flex: 0 0 auto;
        place-items: center;
        border: 1px solid rgba(255, 255, 255, 0.52);
        border-radius: 50%;
        background: rgba(37, 99, 235, 0.92);
        box-shadow: 0 18px 42px rgba(2, 8, 23, 0.44);
        transition: transform 0.2s ease, background 0.2s ease;
      }

      .product-demo-play-icon svg {
        width: 26px;
        height: 26px;
        margin-left: 3px;
        fill: currentColor;
      }

      .product-demo-poster:hover .product-demo-play-icon {
        transform: scale(1.06);
        background: var(--accent);
      }

      .product-demo-play-copy {
        display: flex;
        flex-direction: column;
        gap: 5px;
        text-shadow: 0 2px 14px rgba(2, 8, 23, 0.9);
      }

      .product-demo-play-copy strong {
        font-size: clamp(1.05rem, 2.4vw, 1.55rem);
        line-height: 1.2;
      }

      .product-demo-play-copy small {
        color: #cbd5e1;
        font-size: clamp(0.75rem, 1.6vw, 0.95rem);
      }

      /* Split Flap Board Styles */
      .split-flap-board {
        background: #1a1a1a;
        padding: 20px;
        border-radius: var(--radius-lg);
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
      }

      .split-flap-row {
        display: flex;
        gap: 2px;
        margin-bottom: 4px;
      }

      .split-flap-char {
        width: 24px;
        height: 36px;
        background: #2a2a2a;
        border-radius: 3px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'JetBrains Mono', monospace;
        font-size: 1.25rem;
        font-weight: 600;
        position: relative;
        overflow: hidden;
      }

      .split-flap-char::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: 50%;
        height: 1px;
        background: rgba(0, 0, 0, 0.5);
      }

      .split-flap-char.flipping {
        animation: flip 0.1s ease-out;
      }

      @keyframes flip {
        0% { transform: rotateX(0deg); }
        50% { transform: rotateX(-90deg); }
        100% { transform: rotateX(0deg); }
      }

      /* Trust Bar */
      .trust-bar {
        background: var(--bg-surface);
        padding: 40px 24px;
        text-align: center;
      }

      .trust-bar p {
        color: var(--text-muted);
        font-size: 0.875rem;
        margin-bottom: 20px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .trust-logos {
        display: flex;
        justify-content: center;
        gap: 40px;
        flex-wrap: wrap;
      }

      .trust-logos span {
        color: var(--text-secondary);
        font-size: 0.9375rem;
      }

      /* Sections */
      .features,
      .how-it-works,
      .use-cases,
      .security-section,
      .pricing {
        max-width: 1200px;
        margin: 0 auto;
        padding: 100px 24px;
      }

      .section-header {
        text-align: center;
        max-width: 700px;
        margin: 0 auto 60px;
      }

      .section-header h2 {
        font-size: 2.5rem;
        margin-bottom: 16px;
      }

      .section-header p {
        font-size: 1.125rem;
        color: var(--text-secondary);
      }

      /* Features Grid */
      .features-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 24px;
      }

      .feature-card {
        padding: 32px;
      }

      .feature-card.featured {
        grid-column: span 1;
        background: linear-gradient(135deg, var(--bg-glass), var(--accent-muted));
        border-color: var(--border-accent);
      }

      .feature-icon {
        width: 52px;
        height: 52px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 14px;
        background: rgba(59, 130, 246, 0.12);
        color: var(--accent);
        margin-bottom: 20px;
      }

      .feature-icon svg {
        width: 24px;
        height: 24px;
      }

      .feature-title-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      .feature-tag {
        background: rgba(245, 158, 11, 0.18);
        color: #92400e;
        border-radius: 999px;
        padding: 4px 10px;
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }

      .feature-card h3 {
        font-size: 1.25rem;
        margin-bottom: 12px;
      }

      .feature-card p {
        color: var(--text-secondary);
        line-height: 1.6;
      }

      /* How It Works */
      .steps {
        display: flex;
        align-items: flex-start;
        justify-content: center;
        gap: 0;
      }

      .step {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        max-width: 280px;
        padding: 0 20px;
      }

      .step-number {
        width: 64px;
        height: 64px;
        background: var(--accent);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
        font-weight: 700;
        color: white;
        margin-bottom: 24px;
        box-shadow: var(--accent-glow);
      }

      .step-content h3 {
        font-size: 1.25rem;
        margin-bottom: 12px;
      }

      .step-content p {
        color: var(--text-secondary);
        line-height: 1.6;
      }

      .step-connector {
        width: 80px;
        height: 2px;
        background: var(--border-glass);
        margin-top: 32px;
        flex-shrink: 0;
      }

      /* Use Cases */
      .use-case-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 24px;
      }

      .use-case-card {
        padding: 32px;
      }

      .use-case-card.featured {
        background: linear-gradient(135deg, var(--bg-glass), var(--accent-muted));
        border-color: var(--border-accent);
      }

      .use-case-icon {
        width: 52px;
        height: 52px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 14px;
        background: rgba(59, 130, 246, 0.12);
        color: var(--accent);
        margin-bottom: 20px;
      }

      .use-case-icon svg {
        width: 24px;
        height: 24px;
      }

      .use-case-card h3 {
        font-size: 1.25rem;
        margin-bottom: 12px;
      }

      .use-case-card p {
        color: var(--text-secondary);
        margin-bottom: 16px;
        line-height: 1.6;
      }

      .use-case-card ul {
        list-style: none;
      }

      .use-case-card li {
        color: var(--text-muted);
        font-size: 0.9375rem;
        padding: 6px 0;
        padding-left: 20px;
        position: relative;
      }

      .use-case-card li::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: var(--success);
      }

      /* Security */
      .security-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 24px;
        margin-bottom: 40px;
      }

      .security-card {
        padding: 28px;
        text-align: center;
      }

      .security-icon {
        width: 52px;
        height: 52px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: rgba(59, 130, 246, 0.12);
        color: var(--accent);
        margin-bottom: 16px;
      }

      .security-icon svg {
        width: 24px;
        height: 24px;
      }

      .security-card h3 {
        font-size: 1.125rem;
        margin-bottom: 12px;
      }

      .security-card p {
        color: var(--text-secondary);
        font-size: 0.9375rem;
        line-height: 1.6;
      }

      .security-badges {
        display: flex;
        justify-content: center;
        gap: 32px;
        flex-wrap: wrap;
      }

      .security-badge-item {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--text-secondary);
        font-size: 0.9375rem;
      }

      .security-badge-item .badge-icon {
        color: var(--success);
        font-weight: 700;
      }

      /* Pricing */
      .pricing-cards-container {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 32px;
        max-width: 800px;
        margin: 0 auto;
      }

      .pricing-card {
        padding: 40px;
        text-align: center;
      }

      .pricing-card.pro-tier {
        background: linear-gradient(135deg, var(--bg-glass), var(--accent-muted));
        border-color: var(--accent);
        position: relative;
      }

      .pricing-header {
        margin-bottom: 32px;
      }
      .pricing-tier-label {
        font-size: 0.75rem;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        color: var(--text-secondary);
        margin-bottom: 10px;
        font-weight: 600;
      }

      .pricing-badge {
        display: inline-block;
        padding: 6px 14px;
        background: var(--accent);
        border-radius: 100px;
        color: white;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 16px;
      }

      .pricing-badge.free {
        background: var(--success);
      }

      .pricing-amount {
        display: flex;
        align-items: baseline;
        justify-content: center;
        gap: 4px;
      }

      .pricing-amount .price {
        font-size: 3rem;
        font-weight: 700;
        color: var(--text-primary);
        font-family: 'JetBrains Mono', monospace;
      }

      .pricing-amount .period {
        color: var(--text-muted);
        font-size: 1rem;
      }

      .pricing-features {
        list-style: none;
        text-align: left;
        margin-bottom: 32px;
      }

      .pricing-features li {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 0;
        color: var(--text-secondary);
        font-size: 0.9375rem;
        border-bottom: 1px solid var(--border-glass);
      }

      .pricing-features li:last-child {
        border-bottom: none;
      }

      .pricing-features li svg {
        color: var(--success);
        flex-shrink: 0;
      }

      .pricing-features li.is-missing svg {
        color: var(--danger);
      }

      .pricing-note {
        color: var(--text-muted);
        font-size: 0.875rem;
        margin-top: 12px;
      }

      /* CTA */
      .cta {
        background: linear-gradient(135deg, var(--bg-surface), var(--accent-muted));
        padding: 100px 24px;
        text-align: center;
      }

      .cta h2 {
        font-size: 2.5rem;
        margin-bottom: 16px;
      }

      .cta p {
        font-size: 1.125rem;
        color: var(--text-secondary);
        margin-bottom: 32px;
        max-width: 600px;
        margin-left: auto;
        margin-right: auto;
      }

      /* ================================================================
         CONTACT PAGE
         ================================================================ */
      .contact-page {
        padding: 120px 24px 80px;
        min-height: 100vh;
      }

      .contact-container {
        max-width: 1000px;
        margin: 0 auto;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 60px;
        align-items: start;
      }

      .contact-info h1 {
        font-size: 2.5rem;
        margin-bottom: 16px;
      }

      .contact-info > p {
        color: var(--text-secondary);
        font-size: 1.125rem;
        line-height: 1.7;
        margin-bottom: 40px;
      }

      .contact-details {
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      .contact-item {
        display: flex;
        gap: 16px;
        align-items: flex-start;
      }

      .contact-icon {
        width: 24px;
        height: 24px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--accent);
        flex-shrink: 0;
      }

      .contact-icon svg {
        width: 20px;
        height: 20px;
      }

      .contact-item h4 {
        font-size: 1rem;
        margin-bottom: 4px;
      }

      .contact-item p {
        color: var(--text-muted);
      }

      .contact-form form {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .textarea-field {
        width: 100%;
        padding: 12px 16px;
        font-family: inherit;
        font-size: 0.9375rem;
        color: var(--text-primary);
        background: var(--bg-surface);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        outline: none;
        resize: vertical;
        min-height: 120px;
        transition: all var(--transition-fast);
      }

      .textarea-field:focus {
        border-color: var(--accent);
        box-shadow: 0 0 0 3px var(--accent-muted);
      }

      .contact-success {
        max-width: 500px;
        margin: 0 auto;
        text-align: center;
        padding: 60px 0;
      }

      .contact-success .success-icon {
        width: 80px;
        height: 80px;
        background: var(--success-bg);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 2rem;
        color: var(--success);
        margin: 0 auto 24px;
      }

      .contact-success h1 {
        margin-bottom: 12px;
      }

      .contact-success p {
        color: var(--text-secondary);
        margin-bottom: 32px;
      }

      .form-error {
        color: var(--danger);
        font-size: 0.875rem;
        margin-bottom: 16px;
      }

      /* ================================================================
         GUIDE PAGE
         ================================================================ */
      .guide-page {
        padding: 100px 24px 60px;
        min-height: 100vh;
      }

      .guide-header {
        text-align: center;
        max-width: 700px;
        margin: 0 auto 60px;
      }

      .guide-header h1 {
        font-size: 2.5rem;
        margin-bottom: 12px;
      }

      .guide-header-title {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
      }

      .guide-header-title svg {
        width: 28px;
        height: 28px;
        color: var(--accent);
        flex-shrink: 0;
      }

      .guide-header p {
        color: var(--text-secondary);
        font-size: 1.125rem;
      }

      .guide-container {
        max-width: 1100px;
        margin: 0 auto;
        display: grid;
        grid-template-columns: 260px 1fr;
        gap: 40px;
      }

      .guide-nav {
        display: flex;
        flex-direction: column;
        gap: 4px;
        position: sticky;
        top: 100px;
      }

      .guide-nav-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        background: none;
        border: none;
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        font-size: 0.9375rem;
        font-weight: 500;
        cursor: pointer;
        transition: all var(--transition-fast);
        text-align: left;
      }

      .guide-nav-item:hover {
        background: var(--bg-glass);
        color: var(--text-primary);
      }

      .guide-nav-item.active {
        background: var(--accent-muted);
        color: var(--accent);
      }

      .guide-nav-icon {
        width: 20px;
        height: 20px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .guide-nav-icon svg {
        width: 18px;
        height: 18px;
      }

      .guide-content {
        min-height: 500px;
      }

      .guide-section h2 {
        font-size: 1.75rem;
        margin-bottom: 20px;
      }

      .guide-section-title {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .guide-section-title-icon {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--accent);
        flex-shrink: 0;
      }

      .guide-section-title-icon svg {
        width: 22px;
        height: 22px;
      }

      .guide-section h3 {
        font-size: 1.25rem;
        margin-top: 32px;
        margin-bottom: 16px;
      }

      .guide-intro {
        font-size: 1.0625rem;
        color: var(--text-secondary);
        line-height: 1.7;
        margin-bottom: 24px;
      }

      .guide-list {
        margin-left: 24px;
        margin-bottom: 24px;
      }

      .guide-list li {
        color: var(--text-secondary);
        padding: 8px 0;
        line-height: 1.6;
      }

      .guide-list.numbered {
        list-style-type: decimal;
      }

      .guide-video-list {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 16px;
        margin: 20px 0 28px;
      }

      .guide-video {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 12px;
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        background: var(--bg-glass);
        text-decoration: none;
        transition: all var(--transition-fast);
      }

      .guide-video:hover {
        border-color: var(--accent);
        box-shadow: 0 0 0 2px var(--accent-muted);
      }

      .guide-video img {
        width: 100%;
        border-radius: var(--radius-sm);
        display: block;
      }

      .guide-video-content {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .guide-video-label {
        font-size: 0.75rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--text-muted);
      }

      .guide-video-title {
        font-size: 0.9375rem;
        font-weight: 600;
        color: var(--text-primary);
      }

      .guide-steps {
        display: flex;
        flex-direction: column;
        gap: 20px;
        margin-bottom: 32px;
      }

      .guide-step {
        display: flex;
        gap: 20px;
        align-items: flex-start;
      }

      .guide-step-number {
        width: 40px;
        height: 40px;
        background: var(--accent);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        color: white;
        flex-shrink: 0;
      }

      .guide-step-content h4 {
        font-size: 1.0625rem;
        margin-bottom: 8px;
      }

      .guide-step-content p {
        color: var(--text-secondary);
        line-height: 1.6;
      }

      .guide-tip {
        background: var(--info-bg);
        border: 1px solid var(--border-accent);
        border-radius: var(--radius-md);
        padding: 16px 20px;
        color: var(--text-secondary);
        line-height: 1.6;
        margin-top: 24px;
      }

      .guide-tip strong {
        color: var(--accent);
      }

      .guide-footer {
        max-width: 1100px;
        margin: 60px auto 0;
        text-align: center;
        padding-top: 40px;
        border-top: 1px solid var(--border-glass);
      }

      .guide-footer p {
        color: var(--text-secondary);
        margin-bottom: 16px;
      }

      /* ================================================================
         LEGAL PAGES (Privacy, Terms)
         ================================================================ */
      .legal-page {
        padding: 100px 24px 60px;
        min-height: 100vh;
      }

      .legal-container {
        max-width: 800px;
        margin: 0 auto;
      }

      .legal-container h1 {
        font-size: 2.5rem;
        margin-bottom: 8px;
      }

      .legal-date {
        color: var(--text-muted);
        font-size: 0.9375rem;
        margin-bottom: 48px;
      }

      .legal-container section {
        margin-bottom: 40px;
      }

      .legal-container section h2 {
        font-size: 1.375rem;
        margin-bottom: 16px;
        color: var(--text-primary);
      }

      .legal-container section p {
        color: var(--text-secondary);
        line-height: 1.8;
        margin-bottom: 16px;
      }

      .legal-container section ul {
        margin-left: 24px;
        margin-bottom: 16px;
      }

      .legal-container section ol {
        margin-left: 24px;
        margin-bottom: 16px;
      }

      .legal-container section li {
        color: var(--text-secondary);
        padding: 6px 0;
        line-height: 1.6;
      }

      .legal-container section a {
        color: var(--accent);
        text-decoration: none;
      }

      .legal-container section a:hover {
        text-decoration: underline;
      }

      /* ================================================================
         LOGIN PAGE
         ================================================================ */
      .login-page {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 40px 24px;
        background: linear-gradient(135deg, var(--bg-base) 0%, #1a1f3c 100%);
      }

      .login-card {
        width: 100%;
        max-width: 420px;
        text-align: center;
      }

      .login-header {
        margin-bottom: 32px;
      }

      .login-header .logo-icon.large {
        font-size: 48px;
        color: var(--accent);
        display: block;
        margin-bottom: 24px;
      }

      .login-header h1 {
        font-size: 1.75rem;
        margin-bottom: 8px;
      }

      .login-header p {
        color: var(--text-secondary);
      }

      .login-form {
        text-align: left;
        padding: 32px;
      }

      .login-terms {
        color: var(--text-muted);
        font-size: 0.8125rem;
        margin-top: 24px;
      }

      .login-terms a {
        color: var(--accent);
        cursor: pointer;
        text-decoration: none;
      }

      .login-terms a:hover {
        text-decoration: underline;
      }

      /* ================================================================
         FIRE TV + ROKU CAMPAIGN LANDING PAGE
         ================================================================ */
      .campaign-landing-page {
        min-height: 100vh;
        background:
          radial-gradient(circle at top left, rgba(59, 130, 246, 0.16), transparent 30%),
          radial-gradient(circle at bottom right, rgba(16, 185, 129, 0.14), transparent 26%),
          linear-gradient(180deg, #0b1221 0%, #111827 100%);
      }

      .campaign-header {
        position: sticky;
        top: 0;
        z-index: 40;
        backdrop-filter: blur(16px);
        background: rgba(15, 23, 42, 0.82);
        border-bottom: 1px solid var(--border-glass);
      }

      .campaign-header-inner,
      .campaign-footer-inner {
        max-width: 1180px;
        margin: 0 auto;
        padding: 18px 24px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
      }

      .campaign-brand,
      .campaign-link-button,
      .campaign-footer-links button {
        border: 0;
        background: transparent;
        color: inherit;
        cursor: pointer;
      }

      .campaign-brand {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        font-size: 0.9375rem;
        font-weight: 700;
        letter-spacing: 0.14em;
      }

      .campaign-header-actions,
      .campaign-footer-links {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .campaign-link-button,
      .campaign-footer-links button {
        color: var(--text-secondary);
        font-size: 0.9375rem;
        transition: color 0.2s ease;
      }

      .campaign-link-button:hover,
      .campaign-footer-links button:hover {
        color: var(--text-primary);
      }

      .campaign-main {
        max-width: 1180px;
        margin: 0 auto;
        padding: 40px 24px 80px;
      }

      .campaign-hero {
        display: grid;
        grid-template-columns: minmax(0, 1.1fr) minmax(360px, 0.9fr);
        gap: 36px;
        align-items: center;
        padding: 32px 0 48px;
      }

      .campaign-kicker {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--accent-hover);
        margin-bottom: 18px;
      }

      .campaign-audience-toggle {
        display: inline-flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        padding: 8px;
        margin-bottom: 18px;
        border: 1px solid var(--border-glass);
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.72);
      }

      .campaign-audience-toggle button {
        border: 0;
        background: transparent;
        color: var(--text-secondary);
        padding: 10px 16px;
        border-radius: 999px;
        font-size: 0.875rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .campaign-audience-toggle button.active {
        background: rgba(59, 130, 246, 0.18);
        color: var(--text-primary);
        box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.35);
      }

      .campaign-hero-label {
        color: var(--success);
        font-weight: 600;
        margin-bottom: 12px;
      }

      .campaign-hero h1 {
        font-size: clamp(2.6rem, 5vw, 4.5rem);
        line-height: 1.02;
        letter-spacing: -0.03em;
        margin-bottom: 18px;
        max-width: 11ch;
      }

      .campaign-hero p {
        font-size: 1.0625rem;
        line-height: 1.75;
        color: var(--text-secondary);
        max-width: 640px;
      }

      .campaign-hero-actions,
      .campaign-final-actions {
        display: flex;
        flex-wrap: wrap;
        align-items: stretch;
        gap: 14px;
        margin-top: 28px;
      }

      .campaign-preview-note {
        margin-top: 12px;
        color: var(--text-muted);
        font-size: 0.875rem;
      }

      .campaign-hero-store {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 18px;
        color: var(--text-muted);
        font-size: 0.8125rem;
      }

      .campaign-final-store {
        display: flex;
        align-items: center;
      }

      .store-badge-link {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        align-self: center;
        flex: 0 0 auto;
        border-radius: 8px;
        line-height: 0;
        text-decoration: none;
        transition: transform 0.2s ease, filter 0.2s ease;
      }

      .store-badge-link:hover {
        transform: translateY(-2px);
        filter: brightness(1.08);
      }

      .store-badge-link:focus-visible {
        outline: 3px solid rgba(96, 165, 250, 0.88);
        outline-offset: 4px;
      }

      .store-badge {
        display: block;
        height: auto;
        max-width: 100%;
      }

      .store-badge-link.apple .store-badge {
        width: 144px;
      }

      .store-badge-link.google .store-badge {
        width: 186px;
      }

      .store-badge-link.apple.compact .store-badge {
        width: 120px;
      }

      .store-badge-link.google.compact .store-badge {
        width: 155px;
      }

      .campaign-hero-points {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 22px;
      }

      .campaign-hero-points span,
      .campaign-proof-strip div,
      .campaign-scenario-card {
        display: inline-flex;
        align-items: center;
        gap: 10px;
      }

      .campaign-hero-points span {
        padding: 10px 14px;
        background: rgba(15, 23, 42, 0.65);
        border: 1px solid var(--border-glass);
        border-radius: 999px;
        color: var(--text-secondary);
        font-size: 0.9375rem;
      }

      .campaign-hero-points svg,
      .campaign-proof-strip svg,
      .campaign-scenario-card svg {
        color: var(--accent);
        flex-shrink: 0;
      }

      .campaign-demo-card {
        padding: 24px;
      }

      .campaign-demo-heading {
        font-size: 0.9375rem;
        font-weight: 600;
        color: var(--text-secondary);
        margin-bottom: 6px;
      }

      .campaign-demo-disclaimer {
        max-width: 620px;
        margin: 0 0 18px;
        color: var(--text-muted);
        font-size: 0.8125rem;
        line-height: 1.5;
      }

      .campaign-trivia-screen {
        position: relative;
        overflow: hidden;
        margin-bottom: 20px;
        padding: 18px;
        border: 1px solid rgba(96, 165, 250, 0.26);
        border-radius: 18px;
        background:
          radial-gradient(circle at 84% 12%, rgba(16, 185, 129, 0.16), transparent 34%),
          linear-gradient(145deg, #071426 0%, #10294b 58%, #0b1d35 100%);
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, 0.05),
          0 18px 40px rgba(2, 8, 23, 0.32);
      }

      .campaign-trivia-screen::before {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        opacity: 0.16;
        background-image:
          linear-gradient(rgba(148, 163, 184, 0.11) 1px, transparent 1px),
          linear-gradient(90deg, rgba(148, 163, 184, 0.11) 1px, transparent 1px);
        background-size: 22px 22px;
        mask-image: linear-gradient(to bottom, black, transparent 78%);
      }

      .campaign-trivia-topline,
      .campaign-trivia-join,
      .campaign-trivia-question-meta,
      .campaign-trivia-answers,
      .campaign-trivia-screen h3 {
        position: relative;
        z-index: 1;
      }

      .campaign-trivia-topline,
      .campaign-trivia-join,
      .campaign-trivia-question-meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      .campaign-live-pill {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        color: #6ee7b7;
        font-size: 0.7rem;
        font-weight: 800;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }

      .campaign-live-pill > span {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #34d399;
        box-shadow: 0 0 0 5px rgba(52, 211, 153, 0.12);
      }

      .campaign-player-count {
        color: #94a3b8;
        font-size: 0.72rem;
      }

      .campaign-trivia-join {
        margin: 16px 0;
        padding: 10px 12px;
        border: 1px solid rgba(96, 165, 250, 0.2);
        border-radius: 10px;
        background: rgba(15, 23, 42, 0.68);
        color: #cbd5e1;
        font-size: 0.72rem;
      }

      .campaign-trivia-join strong {
        color: #93c5fd;
        font-size: 0.72rem;
        letter-spacing: 0.08em;
      }

      .campaign-trivia-question-meta {
        color: #94a3b8;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }

      .campaign-trivia-timer {
        display: grid;
        width: 34px;
        height: 34px;
        place-items: center;
        border: 2px solid rgba(96, 165, 250, 0.72);
        border-radius: 50%;
        color: #f8fafc;
        font-size: 0.9rem;
        box-shadow: 0 0 20px rgba(59, 130, 246, 0.18);
      }

      .campaign-trivia-screen h3 {
        margin: 12px 0 16px;
        color: #f8fafc;
        font-size: clamp(1rem, 2vw, 1.3rem);
        line-height: 1.35;
        letter-spacing: -0.01em;
      }

      .campaign-trivia-answers {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
      }

      .campaign-trivia-answer {
        display: grid;
        grid-template-columns: 28px minmax(0, 1fr);
        align-items: center;
        min-height: 48px;
        padding: 8px 10px;
        border: 1px solid rgba(148, 163, 184, 0.2);
        border-radius: 10px;
        background: rgba(30, 41, 59, 0.84);
        color: #e2e8f0;
        transition: opacity 0.25s ease, filter 0.25s ease, transform 0.25s ease;
      }

      .campaign-trivia-answer > span {
        display: grid;
        width: 22px;
        height: 22px;
        place-items: center;
        border-radius: 6px;
        background: rgba(59, 130, 246, 0.18);
        color: #93c5fd;
        font-size: 0.7rem;
        font-weight: 800;
      }

      .campaign-trivia-answer strong {
        min-width: 0;
        font-size: 0.78rem;
        overflow-wrap: anywhere;
      }

      .campaign-trivia-answer.is-removed {
        opacity: 0.3;
        filter: grayscale(1);
        transform: scale(0.985);
      }

      .campaign-demo-card .demo-board {
        margin-bottom: 20px;
      }

      .campaign-demo-metrics {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 14px;
      }

      .campaign-demo-metrics > div {
        padding: 14px;
        background: rgba(15, 23, 42, 0.55);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
      }

      .campaign-demo-metrics strong {
        display: block;
        font-size: 0.95rem;
      }

      .campaign-product-demo-section {
        padding: 8px 0 48px;
      }

      .campaign-product-demo-shell {
        padding: clamp(26px, 5vw, 52px);
        border: 1px solid var(--border-glass);
        border-radius: 28px;
        background:
          radial-gradient(circle at 8% 0%, rgba(59, 130, 246, 0.2), transparent 36%),
          radial-gradient(circle at 94% 100%, rgba(16, 185, 129, 0.13), transparent 34%),
          rgba(15, 23, 42, 0.68);
        box-shadow: 0 28px 70px rgba(2, 8, 23, 0.3);
      }

      .campaign-product-demo-section .product-demo-heading {
        margin-bottom: 28px;
      }

      .campaign-product-demo-actions {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        margin-top: 26px;
        text-align: center;
      }

      .campaign-product-demo-actions .campaign-preview-note {
        margin-top: 0;
      }

      .metric-label {
        display: block;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: var(--text-muted);
        margin-bottom: 8px;
      }

      .campaign-proof-strip {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 14px;
        margin: 0 0 48px;
      }

      .campaign-proof-strip div {
        padding: 16px 18px;
        background: rgba(15, 23, 42, 0.6);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
      }

      .campaign-mobile-app {
        padding: 16px 0 48px;
      }

      .campaign-mobile-app-card {
        position: relative;
        overflow: hidden;
        display: grid;
        grid-template-columns: minmax(0, 1.05fr) minmax(360px, 0.95fr);
        gap: 42px;
        padding: 38px;
        background:
          radial-gradient(circle at 14% 0%, rgba(16, 185, 129, 0.19), transparent 34%),
          radial-gradient(circle at 100% 100%, rgba(59, 130, 246, 0.17), transparent 36%),
          rgba(15, 23, 42, 0.72);
      }

      .campaign-mobile-app-card::after {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        opacity: 0.18;
        background-image:
          linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px);
        background-size: 38px 38px;
        mask-image: linear-gradient(90deg, transparent 34%, #000);
      }

      .campaign-mobile-copy,
      .campaign-control-path {
        position: relative;
        z-index: 1;
      }

      .campaign-app-status {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;
        color: #6ee7b7;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }

      .campaign-app-status span {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #34d399;
        box-shadow: 0 0 0 5px rgba(52, 211, 153, 0.12);
      }

      .campaign-mobile-copy h2 {
        max-width: 13ch;
        margin-bottom: 14px;
        font-size: clamp(2rem, 4vw, 3.25rem);
        line-height: 1.06;
        letter-spacing: -0.03em;
      }

      .campaign-mobile-copy > p {
        max-width: 62ch;
        color: var(--text-secondary);
        line-height: 1.7;
      }

      .campaign-store-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 24px;
      }

      .campaign-app-clarifier {
        margin-top: 16px;
        font-size: 0.84rem;
        color: var(--text-muted) !important;
      }

      .campaign-control-path {
        display: grid;
        gap: 12px;
        align-content: center;
      }

      .campaign-control-path > div {
        position: relative;
        display: grid;
        grid-template-columns: 34px 42px minmax(0, 1fr);
        grid-template-rows: auto auto;
        align-items: center;
        column-gap: 12px;
        padding: 16px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 16px;
        background: rgba(7, 12, 22, 0.72);
      }

      .campaign-control-path > div > span {
        grid-row: 1 / 3;
        color: var(--text-muted);
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.12em;
      }

      .campaign-control-path > div > svg {
        grid-row: 1 / 3;
        width: 24px;
        height: 24px;
        color: #6ee7b7;
      }

      .campaign-control-path strong {
        font-size: 0.98rem;
      }

      .campaign-control-path small {
        margin-top: 2px;
        color: var(--text-secondary);
        font-size: 0.8rem;
      }

      .campaign-activation-band {
        max-width: 820px;
        margin: 20px auto 12px;
        padding: 42px;
        text-align: center;
        border-top: 1px solid rgba(110, 231, 183, 0.34);
        border-bottom: 1px solid rgba(110, 231, 183, 0.2);
      }

      .campaign-activation-band h2 {
        margin: 0 auto 16px;
        font-size: clamp(2rem, 4vw, 3rem);
        line-height: 1.1;
        letter-spacing: -0.03em;
        text-wrap: balance;
      }

      .campaign-activation-band p {
        max-width: 66ch;
        margin: 0 auto;
        color: var(--text-secondary);
        line-height: 1.75;
      }

      .campaign-story,
      .campaign-how,
      .campaign-use-cases,
      .campaign-pricing,
      .campaign-faq,
      .campaign-final-cta {
        padding: 48px 0;
      }

      .campaign-main .section-header {
        max-width: 760px;
        margin: 0 auto 56px;
      }

      .campaign-main .section-header h2 {
        max-width: 15ch;
        margin: 0 auto 18px;
        line-height: 1.12;
        letter-spacing: -0.025em;
        text-wrap: balance;
      }

      .campaign-main .section-header p {
        max-width: 62ch;
        margin: 0 auto;
        line-height: 1.7;
        text-wrap: pretty;
      }

      .campaign-story-grid,
      .campaign-steps,
      .campaign-faq-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 20px;
      }

      .campaign-story-card,
      .campaign-step-card,
      .campaign-faq-card,
      .campaign-scenario-card,
      .campaign-final-card {
        padding: 24px;
        min-width: 0;
      }

      .campaign-story-icon {
        width: 44px;
        height: 44px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 12px;
        margin-bottom: 18px;
        background: rgba(59, 130, 246, 0.16);
        color: var(--accent);
      }

      .campaign-story-card h3,
      .campaign-step-card h3,
      .campaign-faq-card h3 {
        font-size: 1.125rem;
        margin-bottom: 10px;
      }

      .campaign-story-card p,
      .campaign-step-card p,
      .campaign-faq-card p,
      .campaign-final-copy p {
        color: var(--text-secondary);
        line-height: 1.7;
      }

      .campaign-step-number {
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--accent-hover);
        margin-bottom: 14px;
      }

      .campaign-after-live {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 18px;
        padding: 18px 20px;
        border: 1px solid rgba(59, 130, 246, 0.2);
        border-radius: var(--radius-md);
        background: rgba(59, 130, 246, 0.08);
        color: var(--text-secondary);
        line-height: 1.6;
      }

      .campaign-after-live svg {
        flex-shrink: 0;
        color: var(--accent-hover);
      }

      .campaign-scenario-list {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 16px;
      }

      .campaign-scenario-card {
        justify-content: flex-start;
        color: var(--text-secondary);
        line-height: 1.6;
      }

      .campaign-pricing-cards {
        margin-top: 8px;
      }

      .campaign-final-card {
        display: grid;
        grid-template-columns: minmax(0, 1.1fr) auto;
        align-items: center;
        gap: 24px;
      }

      .campaign-final-actions {
        max-width: 320px;
      }

      .campaign-final-copy h2 {
        font-size: clamp(2rem, 4vw, 3rem);
        line-height: 1.08;
        margin-bottom: 14px;
        max-width: 12ch;
      }

      .campaign-footer {
        border-top: 1px solid var(--border-glass);
        background: rgba(15, 23, 42, 0.62);
      }

      .campaign-demo-card .split-flap-board {
        width: fit-content;
        max-width: 100%;
        margin: 0 auto 20px;
      }

      .campaign-demo-card .split-flap-row {
        justify-content: center;
      }

      /* ================================================================
         RESPONSIVE
         ================================================================ */
      @media (max-width: 1024px) {
        .campaign-hero,
        .campaign-final-card,
        .campaign-mobile-app-card {
          grid-template-columns: 1fr;
        }

        .campaign-control-path {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        .campaign-control-path > div {
          grid-template-columns: 28px minmax(0, 1fr);
          grid-template-rows: 34px auto auto;
          align-items: start;
        }

        .campaign-control-path > div > span {
          grid-row: 1;
        }

        .campaign-control-path > div > svg {
          grid-row: 1;
        }

        .campaign-control-path strong,
        .campaign-control-path small {
          grid-column: 1 / 3;
        }

        .campaign-proof-strip,
        .campaign-story-grid,
        .campaign-steps,
        .campaign-scenario-list,
        .campaign-faq-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .sidebar {
          transform: translateX(-100%);
        }

        .sidebar.sidebar-open {
          transform: translateX(0);
        }

        .dashboard-layout {
          padding-left: 0;
        }

        .dashboard-layout.sidebar-collapsed {
          padding-left: 0;
        }

        .dashboard-topbar {
          display: flex;
        }

        .page-container {
          padding: 20px;
        }

        .kpi-grid {
          grid-template-columns: repeat(2, 1fr);
        }

        .boards-layout {
          grid-template-columns: 240px 1fr;
        }

        .hero {
          grid-template-columns: 1fr;
          text-align: center;
        }

        .hero-visual {
          order: -1;
        }

        .hero-actions {
          justify-content: center;
        }

        .hero-demo {
          order: -1;
        }

        .split-flap-board {
          max-width: 100%;
          padding: 16px;
        }

        .split-flap-char {
          width: 20px;
          height: 30px;
          font-size: 1rem;
        }

        .features-grid {
          grid-template-columns: repeat(2, 1fr);
        }

        .security-grid {
          grid-template-columns: repeat(2, 1fr);
        }

        .guide-container {
          grid-template-columns: 1fr;
        }

        .guide-nav {
          flex-direction: row;
          overflow-x: auto;
          position: static;
          padding-bottom: 8px;
        }

        .guide-nav-item {
          white-space: nowrap;
        }
      }

      @media (max-width: 768px) {
        .campaign-header-inner,
        .campaign-footer-inner,
        .campaign-main {
          padding-left: 16px;
          padding-right: 16px;
        }

        .campaign-footer-inner {
          flex-direction: column;
          align-items: stretch;
        }

        .campaign-header-inner {
          min-height: 64px;
          flex-direction: row;
          align-items: center;
          padding-top: 10px;
          padding-bottom: 10px;
        }

        .campaign-header-actions,
        .campaign-footer-links {
          justify-content: space-between;
          flex-wrap: wrap;
        }

        .campaign-header-actions {
          flex-direction: row;
          align-items: center;
        }

        .campaign-header-actions .campaign-link-button,
        .campaign-header-actions .campaign-sign-in-button {
          display: none;
        }

        .campaign-header-actions .btn {
          width: auto;
        }

        .campaign-hero {
          padding-top: 20px;
          gap: 24px;
        }

        .campaign-product-demo-section {
          padding-bottom: 36px;
        }

        .campaign-product-demo-shell {
          padding: 20px;
          border-radius: 20px;
        }

        .campaign-product-demo-actions .btn {
          width: 100%;
          justify-content: center;
        }

        .campaign-hero h1,
        .campaign-final-copy h2 {
          max-width: none;
        }

        .campaign-main .section-header {
          max-width: none;
          margin-bottom: 40px;
          text-align: left;
        }

        .campaign-main .section-header h2 {
          font-size: clamp(1.5rem, 5.8vw, 1.9rem);
          max-width: 100%;
          margin-bottom: 18px;
          line-height: 1.26;
          letter-spacing: 0;
          font-weight: 600;
          text-wrap: normal;
        }

        .campaign-main .section-header p {
          font-size: 0.98rem;
          max-width: 100%;
          margin: 0;
          line-height: 1.72;
        }

        .campaign-demo-metrics,
        .campaign-proof-strip,
        .campaign-story-grid,
        .campaign-steps,
        .campaign-scenario-list,
        .campaign-faq-grid,
        .campaign-control-path {
          grid-template-columns: 1fr;
        }

        .campaign-audience-toggle {
          width: 100%;
          justify-content: stretch;
          border-radius: 20px;
        }

        .campaign-audience-toggle button {
          flex: 1 1 calc(50% - 8px);
          text-align: center;
          min-width: 0;
          padding: 10px 12px;
          font-size: 0.8125rem;
        }

        .campaign-hero-points {
          flex-direction: column;
          align-items: stretch;
        }

        .campaign-hero-actions,
        .campaign-final-actions {
          flex-direction: column;
          align-items: stretch;
        }

        .campaign-hero-actions .btn,
        .campaign-final-actions .btn {
          width: 100%;
          justify-content: center;
        }

        .campaign-hero-store {
          align-items: flex-start;
          flex-direction: column;
        }

        .campaign-final-store {
          justify-content: center;
        }

        .campaign-mobile-app-card {
          gap: 28px;
          padding: 24px;
        }

        .campaign-mobile-copy h2 {
          max-width: none;
          font-size: clamp(1.8rem, 8vw, 2.5rem);
        }

        .campaign-control-path > div {
          grid-template-columns: 34px 42px minmax(0, 1fr);
          grid-template-rows: auto auto;
          align-items: center;
        }

        .campaign-control-path > div > span,
        .campaign-control-path > div > svg {
          grid-row: 1 / 3;
        }

        .campaign-control-path strong,
        .campaign-control-path small {
          grid-column: auto;
        }

        .campaign-activation-band {
          padding: 34px 16px;
          text-align: left;
        }

        .campaign-activation-band h2 {
          font-size: clamp(1.7rem, 7vw, 2.25rem);
        }

        .campaign-after-live {
          align-items: flex-start;
        }

        .campaign-final-actions {
          max-width: none;
        }

        .campaign-hero-points span,
        .campaign-proof-strip div,
        .campaign-scenario-card {
          width: 100%;
          justify-content: flex-start;
          white-space: normal;
        }

        .campaign-demo-card {
          padding: 18px;
        }

        .campaign-trivia-screen {
          padding: 14px;
        }

        .campaign-trivia-answers {
          grid-template-columns: 1fr;
        }

        .campaign-trivia-join {
          align-items: flex-start;
          flex-direction: column;
          gap: 4px;
        }

        .campaign-demo-card .split-flap-board {
          padding: 10px;
        }

        .campaign-demo-card .split-flap-char {
          width: 14px;
          height: 22px;
          font-size: 0.72rem;
        }

        .campaign-demo-card .split-flap-row {
          gap: 1px;
          margin-bottom: 2px;
        }

        .campaign-demo-metrics > div {
          min-width: 0;
        }

        .campaign-demo-metrics strong {
          font-size: 0.85rem;
          overflow-wrap: anywhere;
        }

        .public-main-community {
          padding-top: 112px;
        }

        .page-header {
          flex-direction: column;
          gap: 16px;
        }

        .trial-banner {
          margin: 16px 16px 0;
          flex-direction: column;
          align-items: flex-start;
        }

        .kpi-grid {
          grid-template-columns: 1fr;
        }

        .boards-layout {
          grid-template-columns: 1fr;
        }

        .boards-list {
          flex-direction: row;
          overflow-x: auto;
          padding-bottom: 8px;
        }

        .board-list-item {
          flex-shrink: 0;
          min-width: 180px;
        }

        .board-preview-header {
          flex-direction: column;
          gap: 12px;
        }

        .board-preview-actions {
          width: 100%;
          justify-content: flex-start;
        }

        .board-info-bar {
          flex-direction: column;
          gap: 8px;
        }

        .flap-char {
          width: 18px;
          height: 26px;
          font-size: 0.875rem;
        }

        .boards-grid,
        .games-grid,
        .devices-grid {
          grid-template-columns: 1fr;
        }

        .games-filter-bar {
          flex-direction: column;
          align-items: stretch;
        }

        .games-filter-bar .input-group {
          width: 100%;
        }

        .ai-generate-controls {
          grid-template-columns: 1fr;
        }

        .ai-generate-actions {
          flex-direction: column;
          align-items: flex-start;
        }

        .message-bank-bar {
          flex-direction: column;
          align-items: stretch;
        }

        .message-bank-save {
          flex-direction: column;
          align-items: stretch;
        }

        .message-bank-save .input-group {
          min-width: 0;
        }

        .settings-grid {
          grid-template-columns: 1fr;
        }

        .feedback-section-header,
        .feedback-section-actions {
          flex-direction: column;
          align-items: stretch;
        }

        .feedback-section-icon {
          display: none;
        }

        .tabs-container {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .history-item {
          flex-direction: column;
          align-items: flex-start;
        }

        .public-nav .mobile-menu-btn {
          display: block;
        }

        .public-nav .nav-links {
          display: none;
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: var(--bg-surface);
          padding: 20px;
          flex-direction: column;
          border-top: 1px solid var(--border-glass);
        }

        .public-nav .nav-links.open {
          display: flex;
        }

        .public-nav .nav-links a,
        .public-nav .nav-btn-secondary,
        .public-nav .nav-btn-primary {
          width: 100%;
          text-align: center;
          padding: 12px;
        }

        .hero-content h1 {
          font-size: 2.5rem;
        }

        .product-demo-section {
          padding: 4px 16px 72px;
        }

        .product-demo-heading {
          margin-bottom: 22px;
        }

        .product-demo-heading h2 {
          font-size: clamp(1.75rem, 7vw, 2.3rem);
        }

        .product-demo-heading p {
          font-size: 0.98rem;
        }

        .product-demo-frame {
          border-radius: 16px;
        }

        .product-demo-overlay {
          gap: 12px;
        }

        .split-flap-board {
          padding: 14px;
        }

        .split-flap-char {
          width: 18px;
          height: 26px;
          font-size: 0.95rem;
        }

        .features-grid,
        .use-case-grid,
        .security-grid {
          grid-template-columns: 1fr;
        }

        .steps {
          flex-direction: column;
          gap: 32px;
        }

        .step-connector {
          display: none;
        }

        .pricing-cards-container {
          grid-template-columns: 1fr;
        }

        .footer-container {
          grid-template-columns: 1fr;
          gap: 40px;
        }

        .footer-links {
          grid-template-columns: repeat(2, 1fr);
        }

        .contact-container {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 480px) {
        .campaign-main {
          padding-top: 28px;
          padding-bottom: 56px;
        }

        .campaign-header-inner,
        .campaign-footer-inner,
        .campaign-main {
          padding-left: 12px;
          padding-right: 12px;
        }

        .campaign-hero h1 {
          font-size: 2.15rem;
        }

        .campaign-brand {
          gap: 7px;
          font-size: 0.82rem;
          letter-spacing: 0.1em;
        }

        .campaign-header-actions .btn {
          padding-left: 14px;
          padding-right: 14px;
        }

        .campaign-main .section-header h2 {
          font-size: 1.36rem;
          line-height: 1.32;
          letter-spacing: 0;
          margin-bottom: 16px;
        }

        .campaign-hero p,
        .campaign-story-card p,
        .campaign-step-card p,
        .campaign-faq-card p,
        .campaign-final-copy p {
          font-size: 0.95rem;
          line-height: 1.65;
        }

        .campaign-audience-toggle {
          padding: 6px;
          gap: 6px;
        }

        .campaign-audience-toggle button {
          flex-basis: 100%;
          padding: 9px 8px;
          font-size: 0.75rem;
        }

        .campaign-story-card,
        .campaign-step-card,
        .campaign-faq-card,
        .campaign-scenario-card,
        .campaign-final-card {
          padding: 18px;
        }

        .campaign-mobile-app-card {
          padding: 18px;
        }

        .campaign-store-actions {
          justify-content: center;
          margin-top: 20px;
        }

        .campaign-control-path > div {
          padding: 14px 12px;
        }

        .campaign-demo-card {
          padding: 14px;
        }

        .campaign-product-demo-shell {
          padding: 14px;
          border-radius: 16px;
        }

        .product-demo-play-copy small {
          display: none;
        }

        .campaign-trivia-screen {
          padding: 12px;
          border-radius: 14px;
        }

        .campaign-player-count {
          max-width: 100px;
          text-align: right;
        }

        .campaign-demo-heading {
          font-size: 0.875rem;
        }

        .campaign-demo-disclaimer {
          margin-bottom: 14px;
          font-size: 0.75rem;
        }

        .campaign-demo-card .split-flap-board {
          padding: 8px;
        }

        .campaign-demo-card .split-flap-char {
          width: 12px;
          height: 19px;
          font-size: 0.64rem;
        }

        .campaign-demo-card .split-flap-row {
          gap: 1px;
          margin-bottom: 2px;
        }

        .campaign-kicker,
        .metric-label {
          letter-spacing: 0.08em;
        }

        .page-container {
          padding: 16px;
        }

        .kpi-number {
          font-size: 1.5rem;
        }

        .modal-overlay {
          padding: 12px;
        }

        .modal-container {
          margin: 8px;
          max-height: 92vh;
        }

        .modal-header {
          padding: 16px;
        }

        .modal-title {
          font-size: 1.1rem;
        }

        .modal-body {
          padding: 16px;
        }

        .modal-actions {
          flex-direction: column;
          align-items: stretch;
        }

        .modal-actions button {
          width: 100%;
        }

        .form-row,
        .question-settings,
        .ai-generate-controls {
          grid-template-columns: 1fr;
        }

        .ai-generate-actions,
        .questions-header {
          flex-direction: column;
          align-items: stretch;
        }

        .hero-content h1 {
          font-size: 2rem;
        }

        .hero {
          width: 100%;
          padding-left: 16px;
          padding-right: 16px;
        }

        .hero-visual,
        .hero-demo,
        .hero-mobile-app {
          width: 100%;
          min-width: 0;
        }

        .hero-demo .split-flap-board {
          max-width: calc(100vw - 32px);
          padding: 10px;
        }

        .hero-demo .split-flap-char {
          width: calc((100vw - 86px) / 18);
          max-width: 16px;
          flex: 0 0 calc((100vw - 86px) / 18);
          height: 24px;
          font-size: 0.85rem;
        }

        .hero-mobile-app-label {
          max-width: 100%;
          justify-content: center;
          font-size: 0.75rem;
        }

        .hero-store-badges {
          width: 100%;
          gap: 8px;
        }

        .section-header h2 {
          font-size: 1.75rem;
        }

        .footer-links {
          grid-template-columns: 1fr;
        }
      }
    `}</style>
  );
}
